import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import confetti from "canvas-confetti";
import axios from "axios";
import { useTranslation } from "react-i18next";
import i18n from "./i18n";
import DOMPurify from "dompurify";
import {
  BookOpen, Mic, Headphones, Edit3, Home, User, Award, 
  LogOut, Settings, Users, FileText, Upload, ChevronRight,
  Play, Pause, Check, X, Timer, Star, TrendingUp, Volume2,
  CreditCard, Menu, ArrowLeft, Eye, EyeOff, Building2, Copy, Phone, Mail,
  MessageSquare, Send, Bell, ChevronDown, ChevronUp, ClipboardCopy, Loader,
  Folder, FolderOpen, RefreshCw, Calendar, FileBarChart, DollarSign, Package,
  Download, Image, Sparkles, Trophy, ArrowUp, Globe, Trash2, Database, Clock, BarChart3
} from "lucide-react";
import "@/index.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sanitize HTML to prevent XSS attacks
const sanitizeHtml = (html) => DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });

// Language-aware text helper for use outside React components
const getText = (afText, enText) => {
  const lang = i18n.language || localStorage.getItem('language') || 'af';
  return lang === 'en' ? enText : afText;
};

// Auth Context
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        setUser(res.data);
        // Sync i18n language with user's preferred language
        if (res.data?.preferred_language && res.data.preferred_language !== i18n.language) {
          i18n.changeLanguage(res.data.preferred_language);
        }
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem("token");
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [i18n]);

  const login = async (type, credentials) => {
    const endpoint = type === "admin" ? "/auth/admin/login" : "/auth/learner/login";
    const res = await axios.post(`${API}${endpoint}`, credentials);
    localStorage.setItem("token", res.data.access_token);
    const userRes = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${res.data.access_token}` }
    });
    setUser(userRes.data);
    // Sync i18n language with user's preferred language
    if (userRes.data?.preferred_language) {
      i18n.changeLanguage(userRes.data.preferred_language);
    }
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/learner/register`, data);
    localStorage.setItem("token", res.data.access_token);
    try {
      const userRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${res.data.access_token}` }
      });
      setUser(userRes.data);
      // Sync i18n language with user's preferred language
      if (userRes.data?.preferred_language) {
        i18n.changeLanguage(userRes.data.preferred_language);
      }
    } catch (meError) {
      // Registration succeeded but /me failed - still return success
      // User will be redirected to login page where they can sign in
      console.warn("Registration succeeded but user fetch failed:", meError);
      // Set minimal user state so navigation can proceed
      setUser({ user_type: "learner", id: res.data.user_id });
    }
    return res.data;
  };

  const logout = () => {
    // Check if user is admin and should be reminded about backup
    const currentUser = user;
    const lastBackup = localStorage.getItem('lastBackupTime');
    const lastUpload = localStorage.getItem('lastUploadTime');
    
    if (currentUser?.user_type === 'admin') {
      // Check if there were uploads since last backup
      const shouldRemind = lastUpload && (!lastBackup || new Date(lastUpload) > new Date(lastBackup));
      
      if (shouldRemind) {
        const message = getText(
          '⚠️ HERINNERING: Jy het oudio opgelaai sedert jou laaste backup.\n\n' +
          'Dit word sterk aanbeveel om \'n backup af te laai voordat jy uitlog, ' +
          'sodat jy nie data verloor na \'n herontplooiing nie.\n\n' +
          'Wil jy steeds uitlog sonder \'n backup?',
          '⚠️ REMINDER: You have uploaded audio since your last backup.\n\n' +
          'It is strongly recommended to download a backup before logging out, ' +
          'so you don\'t lose data after a redeployment.\n\n' +
          'Do you still want to log out without a backup?'
        );
        const confirmLogout = window.confirm(message);
        if (!confirmLogout) return;
      }
    }
    
    localStorage.removeItem("token");
    localStorage.removeItem("lastUploadTime");
    setUser(null);
  };

  return { user, loading, login, register, logout, setUser };
};

// API Helper
const api = axios.create({ baseURL: API });
api.interceptors.request.use(config => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Components
const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.3 }}
  >
    {children}
  </motion.div>
);

// Language Selector Component
const LanguageSelector = ({ compact = false, onLanguageChange = null }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  
  const changeLanguage = async (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    setIsOpen(false);
    
    // If user is logged in as a learner, sync language preference to backend
    const token = localStorage.getItem("token");
    if (token) {
      try {
        await api.put("/learner/preferred-language", { language: lng });
      } catch (err) {
        // Silently fail if not a learner or other error
        console.log("Could not sync language preference to backend");
      }
    }
    
    // Call optional callback
    if (onLanguageChange) {
      onLanguageChange(lng);
    }
  };
  
  const currentLang = i18n.language === 'en' ? 'English' : 'Afrikaans';
  const flag = i18n.language === 'en' ? '🇬🇧' : '🇿🇦';
  
  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1 text-sm rounded-lg hover:bg-slate-100 transition-colors"
          data-testid="language-selector-compact"
        >
          <Globe className="w-4 h-4" />
          <span>{flag}</span>
        </button>
        {isOpen && (
          <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-[120px]">
            <button
              onClick={() => changeLanguage('af')}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 ${i18n.language === 'af' ? 'bg-primary-50 text-primary-600' : ''}`}
            >
              🇿🇦 Afrikaans
            </button>
            <button
              onClick={() => changeLanguage('en')}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 ${i18n.language === 'en' ? 'bg-primary-50 text-primary-600' : ''}`}
            >
              🇬🇧 English
            </button>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2">
      <Globe className="w-5 h-5 text-text-muted" />
      <select
        value={i18n.language}
        onChange={(e) => changeLanguage(e.target.value)}
        className="bg-white border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        data-testid="language-selector"
      >
        <option value="af">🇿🇦 Afrikaans</option>
        <option value="en">🇬🇧 English</option>
      </select>
    </div>
  );
};

const Card = ({ children, className = "", onClick, testId }) => (
  <motion.div
    whileHover={{ y: -4, boxShadow: "0 8px 30px -12px rgba(0,0,0,0.15)" }}
    className={`card ${className}`}
    onClick={onClick}
    data-testid={testId}
  >
    {children}
  </motion.div>
);

const Button = ({ children, variant = "primary", className = "", disabled, onClick, testId, type = "button" }) => (
  <motion.button
    whileHover={{ scale: disabled ? 1 : 1.02 }}
    whileTap={{ scale: disabled ? 1 : 0.98 }}
    className={`${variant === "primary" ? "btn-primary" : "btn-secondary"} ${className} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    disabled={disabled}
    onClick={onClick}
    data-testid={testId}
    type={type}
  >
    {children}
  </motion.button>
);

const Input = ({ label, error, testId, ...props }) => (
  <div className="mb-4">
    {label && <label className="block text-sm font-semibold text-text-secondary mb-2">{label}</label>}
    <input className={`input-field ${error ? "border-accent-500" : ""}`} data-testid={testId} {...props} />
    {error && <p className="text-accent-500 text-sm mt-1">{error}</p>}
  </div>
);

// Password Input with visibility toggle
const PasswordInput = ({ label, error, testId, value, onChange, ...props }) => {
  const [showPassword, setShowPassword] = useState(false);
  
  return (
    <div className="mb-4">
      {label && <label className="block text-sm font-semibold text-text-secondary mb-2">{label}</label>}
      <div className="relative">
        <input 
          type={showPassword ? "text" : "password"}
          className={`input-field pr-12 ${error ? "border-accent-500" : ""}`} 
          data-testid={testId}
          value={value}
          onChange={onChange}
          {...props} 
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text-primary transition-colors"
          data-testid={`${testId}-toggle`}
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
      {error && <p className="text-accent-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

// Rich Text Editor for Formatted Instructions
const RichTextEditor = ({ value, onChange, placeholder, testId, minHeight = "100px" }) => {
  const textareaRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Insert text at cursor position
  const insertAtCursor = (before, after = "") => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = value || "";
    const selectedText = text.substring(start, end);
    
    const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
    onChange({ target: { value: newText } });
    
    // Set cursor position after inserted text
    setTimeout(() => {
      textarea.focus();
      const newPos = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };
  
  // Toggle bold on selected text
  const toggleBold = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = value || "";
    const selectedText = text.substring(start, end);
    
    if (selectedText) {
      // Check if already bold
      if (selectedText.startsWith("**") && selectedText.endsWith("**")) {
        const newText = text.substring(0, start) + selectedText.slice(2, -2) + text.substring(end);
        onChange({ target: { value: newText } });
      } else {
        insertAtCursor("**", "**");
      }
    } else {
      insertAtCursor("**teks**", "");
    }
  };
  
  // Add list item
  const addListItem = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const text = value || "";
    const start = textarea.selectionStart;
    
    // Find the start of current line
    let lineStart = start;
    while (lineStart > 0 && text[lineStart - 1] !== "\n") {
      lineStart--;
    }
    
    // Check if line already starts with bullet
    const lineText = text.substring(lineStart, start);
    if (lineText.startsWith("• ") || lineText.startsWith("- ")) {
      return; // Already a list item
    }
    
    // Insert bullet at line start
    const newText = text.substring(0, lineStart) + "• " + text.substring(lineStart);
    onChange({ target: { value: newText } });
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + 2, start + 2);
    }, 0);
  };
  
  // Parse markdown-like syntax to HTML for preview
  const parseToHtml = (text) => {
    if (!text) return "";
    
    // Escape HTML
    let html = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    
    // Bold: **text** or __text__
    html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/__(.+?)__/g, "<strong>$1</strong>");
    
    // List items: lines starting with • or -
    html = html.split("\n").map(line => {
      if (line.trim().startsWith("• ") || line.trim().startsWith("- ")) {
        return `<li>${line.trim().substring(2)}</li>`;
      }
      return line;
    }).join("\n");
    
    // Wrap consecutive <li> in <ul>
    html = html.replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul class="list-disc pl-5 my-2">${match}</ul>`);
    
    // Convert remaining newlines to <br>
    html = html.replace(/\n/g, "<br>");
    
    return html;
  };
  
  return (
    <div className="rich-text-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-slate-100 rounded-t-lg border border-b-0 border-slate-200">
        <button
          type="button"
          onClick={toggleBold}
          className="p-2 hover:bg-slate-200 rounded font-bold text-sm"
          title="Bold / Vetdruk (Ctrl+B)"
          data-testid={`${testId}-bold-btn`}
        >
          B
        </button>
        <button
          type="button"
          onClick={addListItem}
          className="p-2 hover:bg-slate-200 rounded text-sm"
          title="List / Lys"
          data-testid={`${testId}-list-btn`}
        >
          • Lys
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className={`px-3 py-1 text-xs rounded ${showPreview ? "bg-primary-500 text-white" : "bg-slate-200 text-slate-700"}`}
          data-testid={`${testId}-preview-btn`}
        >
          {showPreview ? "Edit" : "Preview"}
        </button>
      </div>
      
      {/* Editor or Preview */}
      {showPreview ? (
        <div 
          className="p-3 border border-slate-200 rounded-b-lg bg-white prose prose-sm max-w-none"
          style={{ minHeight }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(parseToHtml(value) || "<span class='text-slate-400'>No content / Geen inhoud</span>") }}
          data-testid={`${testId}-preview`}
        />
      ) : (
        <textarea
          ref={textareaRef}
          className="input-field rounded-t-none border-t-0"
          style={{ minHeight }}
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          data-testid={testId}
        />
      )}
      
      {/* Help text */}
      <p className="text-xs text-slate-500 mt-1">
        **bold** vir vetdruk, • of - aan begin van lyn vir lys
      </p>
    </div>
  );
};

const ProgressBar = ({ value, max = 100, label }) => (
  <div className="mb-4">
    {label && <div className="flex justify-between mb-2">
      <span className="text-sm font-medium text-text-secondary">{label}</span>
      <span className="text-sm font-bold text-primary-500">{Math.round((value/max)*100)}%</span>
    </div>}
    <div className="progress-bar">
      <motion.div 
        className="progress-bar-fill" 
        initial={{ width: 0 }}
        animate={{ width: `${(value/max)*100}%` }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      />
    </div>
  </div>
);

// Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  return (
    <PageTransition>
      <div className="min-h-screen bg-gradient-to-b from-orange-50 via-amber-50 to-yellow-50">
        {/* Language Selector - Top Right */}
        <div className="absolute top-4 right-4 z-50">
          <LanguageSelector compact />
        </div>
        
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-amber-50 to-yellow-100 opacity-70" />
          <div className="max-w-6xl mx-auto px-4 py-16 relative">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Logo */}
              <img 
                src="/logo.png" 
                alt="Lees is Duidelik" 
                className="h-32 md:h-48 mx-auto mb-6 drop-shadow-lg"
              />
              <p className="text-lg md:text-xl text-text-secondary max-w-2xl mx-auto mb-8">
                Helping South African children read with confidence / Help Suid-Afrikaanse kinders met selfvertroue lees
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate("/register")} testId="register-btn">
                  Get Started / Begin
                </Button>
                <Button variant="secondary" onClick={() => navigate("/login")} testId="login-btn">
                  Login / Meld Aan
                </Button>
              </div>
            </motion.div>
            
            <motion.div 
              className="mt-12 relative"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Cartoon illustration hero */}
              <div className="bg-gradient-to-br from-orange-100 via-amber-100 to-yellow-100 rounded-3xl shadow-2xl mx-auto max-w-3xl w-full p-8 md:p-12 border border-orange-200">
                <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                  <div className="text-center">
                    <div className="text-8xl md:text-9xl mb-4">📚</div>
                    <p className="text-xl font-heading font-bold text-orange-600">Read / Lees</p>
                  </div>
                  <div className="text-center">
                    <div className="text-8xl md:text-9xl mb-4">🎧</div>
                    <p className="text-xl font-heading font-bold text-amber-600">Listen / Luister</p>
                  </div>
                  <div className="text-center">
                    <div className="text-8xl md:text-9xl mb-4">✍️</div>
                    <p className="text-xl font-heading font-bold text-yellow-600">Write / Skryf</p>
                  </div>
                  <div className="text-center">
                    <div className="text-8xl md:text-9xl mb-4">⭐</div>
                    <p className="text-xl font-heading font-bold text-red-500">Grow / Groei</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Features */}
        <div className="max-w-6xl mx-auto px-4 py-16 bg-gradient-to-r from-amber-50/50 via-orange-50/30 to-yellow-50/50">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">
            How Does It Work? / Hoe Werk Dit?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: BookOpen, title: "Comprehension / Begripstoetse", desc: "Read stories and answer questions / Lees stories en beantwoord vrae", color: "text-orange-500" },
              { icon: Mic, title: "Read Aloud / Hardoplees", desc: "Record yourself and get feedback / Neem jouself op en kry terugvoer", color: "text-amber-500" },
              { icon: Headphones, title: "Listening Tests / Luistertoetse", desc: "Listen to texts and improve / Luister na tekste en verbeter", color: "text-yellow-600" },
              { icon: Edit3, title: "Spelling Tests / Speltoetse", desc: "Practice age-appropriate words / Oefen ouderdomsgepaste woorde", color: "text-red-400" }
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="text-center h-full bg-white/80 backdrop-blur-sm border border-orange-100">
                  <feature.icon className={`w-12 h-12 ${feature.color} mx-auto mb-4`} strokeWidth={2} />
                  <h3 className="font-heading text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-text-secondary">{feature.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div className="py-16 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-4">
              Pricing / Pryse
            </h2>
            <p className="text-text-secondary text-center mb-12">{i18n.language === 'en' ? 'With invitation code: 7 days free trial!' : 'Met uitnodigingskode: 7 dae gratis proeftydperk!'}</p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-2 border-primary-500">
                <div className="text-center">
                  <h3 className="font-heading text-2xl font-bold mb-2">Monthly / Maandeliks</h3>
                  <div className="text-4xl font-bold text-primary-500 mb-4">R100<span className="text-lg text-text-muted">/month / maand</span></div>
                  <ul className="text-left space-y-2 mb-6">
                    <li className="flex items-center gap-2"><Check className="w-5 h-5 text-primary-500" /> All exercises / Alle oefeninge</li>
                    <li className="flex items-center gap-2"><Check className="w-5 h-5 text-primary-500" /> Progress tracking / Vordering-opsporing</li>
                    <li className="flex items-center gap-2"><Check className="w-5 h-5 text-primary-500" /> Cancel anytime / Kanselleer enige tyd</li>
                  </ul>
                  <Button onClick={() => navigate("/register")} className="w-full" testId="monthly-plan-btn">
                    Choose Monthly / Kies Maandeliks
                  </Button>
                </div>
              </Card>
              <Card className="border-2 border-secondary-500 relative overflow-hidden">
                <div className="absolute top-4 right-4 bg-secondary-500 text-text-primary text-xs font-bold px-3 py-1 rounded-full">
                  SAVE / SPAAR 66%
                </div>
                <div className="text-center">
                  <h3 className="font-heading text-2xl font-bold mb-2">Once-off / Eenmalig</h3>
                  <div className="text-4xl font-bold text-secondary-500 mb-4">R399<span className="text-lg text-text-muted"> total / totaal</span></div>
                  <ul className="text-left space-y-2 mb-6">
                    <li className="flex items-center gap-2"><Check className="w-5 h-5 text-primary-500" /> All exercises / Alle oefeninge</li>
                    <li className="flex items-center gap-2"><Check className="w-5 h-5 text-primary-500" /> Progress tracking / Vordering-opsporing</li>
                    <li className="flex items-center gap-2"><Check className="w-5 h-5 text-primary-500" /> Lifetime access / Lewenslange toegang</li>
                  </ul>
                  <Button onClick={() => navigate("/register")} className="w-full bg-secondary-500" testId="once-off-plan-btn">
                    Choose Once-off / Kies Eenmalig
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Online Tutoring Section */}
        <div className="py-16 bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-100">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="border-2 border-orange-300 bg-white/90 backdrop-blur-sm overflow-hidden">
                <div className="md:flex items-center gap-8">
                  <div className="md:w-1/3 text-center mb-6 md:mb-0">
                    <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageSquare className="w-12 h-12 text-orange-600" />
                    </div>
                    <div className="text-4xl font-bold text-orange-600">R100</div>
                    <div className="text-text-muted">/week</div>
                  </div>
                  <div className="md:w-2/3">
                    <h3 className="font-heading text-2xl font-bold mb-3 text-orange-700">
                      Online Tutoring / Aanlyn Tutoriale
                    </h3>
                    <p className="text-text-secondary mb-4">
                      Personal one-on-one sessions with a qualified teacher. Currently focusing on <strong>reading skills</strong>. /
                      Persoonlike een-op-een sessies met 'n gekwalifiseerde onderwyser. Fokus tans op <strong>leesvaardighede</strong>.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-orange-500" /> Personal attention / Persoonlike aandag
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-orange-500" /> Flexible times / Buigsame tye
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-orange-500" /> Focus on reading & comprehension / Fokus op lees & begrip
                      </li>
                    </ul>
                    <Button 
                      onClick={() => navigate("/register")} 
                      className="bg-orange-500 hover:bg-orange-600"
                      testId="tutoring-enquire-btn"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Enquire / Vra Aan
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gradient-to-r from-orange-700 via-amber-700 to-orange-800 text-white py-8">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <img src="/logo.png" alt="Lees is Duidelik" className="h-16 mx-auto mb-2 brightness-110" />
            <p className="text-orange-200">Built with love for South African learners / Gebou met liefde vir Suid-Afrikaanse leerders</p>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
};

// Auth Pages
const LoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loginType, setLoginType] = useState("learner"); // "learner", "parent", "admin"
  const [form, setForm] = useState({ username: "", password: "", email: "", whatsapp: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showParentRegister, setShowParentRegister] = useState(false);
  const [parentForm, setParentForm] = useState({ name: "", email: "", whatsapp: "", password: "" });
  const [parentLoginMethod, setParentLoginMethod] = useState("whatsapp"); // "whatsapp" or "email"

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (loginType === "admin") {
        await onLogin("admin", { email: form.email, password: form.password });
        navigate("/admin");
      } else if (loginType === "parent") {
        const loginData = parentLoginMethod === "email" 
          ? { email: form.email, password: form.password }
          : { whatsapp: form.whatsapp, password: form.password };
        const res = await api.post("/parent/login", loginData);
        localStorage.setItem("token", res.data.access_token);
        navigate("/parent-dashboard");
      } else {
        await onLogin("learner", { username: form.username, password: form.password });
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.invalidCredentials'));
    }
    setLoading(false);
  };

  const handleParentRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/parent/register", parentForm);
      localStorage.setItem("token", res.data.access_token);
      toast.success(t('toast.registrationSuccess'));
      navigate("/parent-dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Registrasie het misluk");
    }
    setLoading(false);
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => navigate("/")} className="flex items-center gap-2 text-text-muted hover:text-text-primary text-sm">
              <ArrowLeft className="w-4 h-4" /> {t('back')}
            </button>
            <LanguageSelector compact />
          </div>
          <h1 className="font-heading text-2xl font-bold text-center mb-5">
            {loginType === "admin" ? "Admin" : loginType === "parent" ? (i18n.language === "en" ? "Parent" : "Ouer") : (i18n.language === "en" ? "Learner" : "Leerder")}
          </h1>
          
          <div className="flex gap-2 mb-5">
            <button
              onClick={() => { setLoginType("learner"); setShowParentRegister(false); }}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${loginType === "learner" ? "bg-primary-500 text-white" : "bg-slate-100 text-text-secondary"}`}
              data-testid="learner-tab"
            >
              {i18n.language === "en" ? "Learner" : "Leerder"}
            </button>
            <button
              onClick={() => { setLoginType("parent"); setShowParentRegister(false); }}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${loginType === "parent" ? "bg-primary-500 text-white" : "bg-slate-100 text-text-secondary"}`}
              data-testid="parent-tab"
            >
              {i18n.language === "en" ? "Parent" : "Ouer"}
            </button>
            <button
              onClick={() => { setLoginType("admin"); setShowParentRegister(false); }}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${loginType === "admin" ? "bg-primary-500 text-white" : "bg-slate-100 text-text-secondary"}`}
              data-testid="admin-tab"
            >
              Admin
            </button>
          </div>

          {loginType === "parent" && showParentRegister ? (
            <form onSubmit={handleParentRegister}>
              <Input
                label="Naam / Name"
                value={parentForm.name}
                onChange={(e) => setParentForm({...parentForm, name: e.target.value})}
                required
                testId="parent-name-input"
              />
              <Input
                label="WhatsApp"
                value={parentForm.whatsapp}
                onChange={(e) => setParentForm({...parentForm, whatsapp: e.target.value})}
                placeholder="bv. 0821234567"
                required
                testId="parent-whatsapp-input"
              />
              <Input
                label="E-pos / Email (opsioneel)"
                type="email"
                value={parentForm.email}
                onChange={(e) => setParentForm({...parentForm, email: e.target.value})}
                testId="parent-email-reg-input"
              />
              <PasswordInput
                label="Wagwoord / Password"
                value={parentForm.password}
                onChange={(e) => setParentForm({...parentForm, password: e.target.value})}
                required
                testId="parent-password-input"
              />
              
              {error && <p className="text-accent-500 text-sm mb-4">{error}</p>}
              
              <Button type="submit" disabled={loading} className="w-full" testId="parent-register-submit">
                {loading ? "Laai..." : "Registreer / Register"}
              </Button>
              
              <p className="text-center mt-4 text-text-secondary text-sm">
                Reeds geregistreer?{" "}
                <button type="button" onClick={() => setShowParentRegister(false)} className="text-primary-500 font-semibold">
                  Meld aan
                </button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              {loginType === "admin" ? (
                <Input
                  label="Email / E-pos"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({...form, email: e.target.value})}
                  required
                  testId="email-input"
                />
              ) : loginType === "parent" ? (
                <>
                  <div className="flex gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => setParentLoginMethod("whatsapp")}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${parentLoginMethod === "whatsapp" ? "bg-primary-100 text-primary-700 border-2 border-primary-500" : "bg-slate-100 text-text-secondary"}`}
                    >
                      WhatsApp
                    </button>
                    <button
                      type="button"
                      onClick={() => setParentLoginMethod("email")}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${parentLoginMethod === "email" ? "bg-primary-100 text-primary-700 border-2 border-primary-500" : "bg-slate-100 text-text-secondary"}`}
                    >
                      Email / E-pos
                    </button>
                  </div>
                  {parentLoginMethod === "email" ? (
                    <Input
                      label="Email / E-pos"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      required
                      testId="email-input"
                    />
                  ) : (
                    <Input
                      label="WhatsApp Number / WhatsApp Nommer"
                      value={form.whatsapp}
                      onChange={(e) => setForm({...form, whatsapp: e.target.value})}
                      placeholder="e.g. 0821234567"
                      required
                      testId="whatsapp-input"
                    />
                  )}
                </>
              ) : (
                <Input
                  label="Gebruikersnaam / Username"
                  value={form.username}
                  onChange={(e) => setForm({...form, username: e.target.value})}
                  required
                  testId="username-input"
                />
              )}
              <PasswordInput
                label="Wagwoord / Password"
                value={form.password}
                onChange={(e) => setForm({...form, password: e.target.value})}
                required
                testId="password-input"
              />
              
              {error && <p className="text-accent-500 text-sm mb-4">{error}</p>}
              
              <Button type="submit" disabled={loading} className="w-full" testId="login-submit">
                {loading ? "Laai..." : "Meld Aan / Login"}
              </Button>
            </form>
          )}

          {loginType === "learner" && (
            <p className="text-center mt-4 text-text-secondary text-sm">
              Nog nie geregistreer?{" "}
              <button onClick={() => navigate("/register")} className="text-primary-500 font-semibold" data-testid="go-to-register">
                Registreer hier
              </button>
            </p>
          )}
          
          {loginType === "parent" && !showParentRegister && (
            <p className="text-center mt-4 text-text-secondary">
              Nog nie geregistreer nie?{" "}
              <button type="button" onClick={() => setShowParentRegister(true)} className="text-primary-500 font-semibold" data-testid="go-to-parent-register">
                Registreer hier
              </button>
            </p>
          )}
        </Card>
      </div>
    </PageTransition>
  );
};

const RegisterPage = ({ onRegister }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [registrationType, setRegistrationType] = useState("learner"); // "learner" or "school"
  const [form, setForm] = useState({
    name: "", surname: "", grade: 1, username: "", password: "", whatsapp: "", parent_permission: false, invitation_code: "",
    parent_email: "", parent_whatsapp: "",
    request_tutoring: false, tutoring_days: [], tutoring_times: [],
    preferred_language: i18n.language, // Store user's language preference
    subscription_type: "once_off", // "once_off" or "monthly"
    current_reading_level: "unknown" // "unknown" or grade number (1-9)
  });
  const [schoolForm, setSchoolForm] = useState({
    school_name: "",
    contact_person: "",
    contact_email: "",
    contact_whatsapp: "",
    contact_phone: "",
    principal_contact: "",
    school_email: "",
    learner_count: 10
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [schoolSubmitted, setSchoolSubmitted] = useState(false);

  // Track page visit for analytics
  useEffect(() => {
    const trackVisit = async () => {
      try {
        await axios.post(`${API}/analytics/track`, {
          event_type: "page_visit",
          page: "/register",
          user_agent: navigator.userAgent,
          referrer: document.referrer || "direct"
        });
      } catch (err) {
        // Silently fail - don't interrupt user experience
        console.log("Analytics tracking failed");
      }
    };
    trackVisit();
  }, []);

  const availableDays = i18n.language === 'en' 
    ? ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
    : ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrydag", "Saterdag"];
  const availableTimes = ["08:00-09:00", "09:00-10:00", "10:00-11:00", "14:00-15:00", "15:00-16:00", "16:00-17:00", "17:00-18:00"];

  const toggleDay = (day) => {
    setForm(prev => ({
      ...prev,
      tutoring_days: prev.tutoring_days.includes(day) 
        ? prev.tutoring_days.filter(d => d !== day)
        : [...prev.tutoring_days, day]
    }));
  };

  const toggleTime = (time) => {
    setForm(prev => ({
      ...prev,
      tutoring_times: prev.tutoring_times.includes(time)
        ? prev.tutoring_times.filter(t => t !== time)
        : [...prev.tutoring_times, time]
    }));
  };

  // Check for invitation code in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    if (code) {
      setForm(prev => ({...prev, invitation_code: code}));
    }
  }, []);

  const handleLearnerSubmit = async (e) => {
    e.preventDefault();
    if (!form.parent_permission) {
      setError("Ouer toestemming is nodig");
      return;
    }
    // invitation_code is now optional - removed validation
    if (!form.parent_whatsapp || form.parent_whatsapp.trim() === "") {
      setError(i18n.language === 'en' ? "Parent WhatsApp / Cell number is required" : "Ouer WhatsApp / Sel nommer is verpligtend");
      return;
    }
    setLoading(true);
    setError("");
    try {
      // Include the current UI language as preferred_language
      const response = await onRegister({ 
        ...form, 
        grade: parseInt(form.grade), 
        preferred_language: i18n.language || "af",
        current_reading_level: form.current_reading_level  // "unknown" or grade number
      });
      
      // Check if user has invitation code (gets immediate access) or needs to wait for payment
      if (form.invitationCode) {
        toast.success(t('toast.registrationSuccess'));
        // Small delay to ensure state updates
        await new Promise(resolve => setTimeout(resolve, 500));
        navigate("/reading-test", { replace: true });
      } else {
        // No invitation code - show payment pending message
        toast.success(
          i18n.language === "en" 
            ? "Registration successful! Please send proof of payment to activate your account."
            : "Registrasie suksesvol! Stuur asseblief bewys van betaling om jou rekening te aktiveer."
        );
        // Navigate to a pending payment screen instead of reading test
        navigate("/", { replace: true });
      }
    } catch (err) {
      console.error("Registration error:", err);
      // Check if it's a network error vs API error
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        setError(i18n.language === 'en' 
          ? "Network error - please check your internet connection and try again" 
          : "Netwerkfout - kontroleer asseblief jou internetverbinding en probeer weer");
      } else if (err.response?.status === 500) {
        setError(i18n.language === 'en'
          ? "Server error - please try again in a few moments"
          : "Bedienerfout - probeer asseblief weer oor 'n paar oomblikke");
      } else {
        setError(err.response?.data?.detail || (i18n.language === 'en' ? "Registration failed" : "Registrasie het misluk"));
      }
    }
    setLoading(false);
  };

  const handleSchoolSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/schools/register`, schoolForm);
      setSchoolSubmitted(true);
      toast.success(t('toast.schoolRegistrationReceived'));
    } catch (err) {
      setError(err.response?.data?.detail || "Registrasie het misluk");
    }
    setLoading(false);
  };

  if (schoolSubmitted) {
    return (
      <PageTransition>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md text-center">
            <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-heading text-3xl font-bold mb-4">
              {i18n.language === "en" ? "Thank you!" : "Dankie!"}
            </h1>
            <p className="text-text-secondary mb-6">
              {i18n.language === "en" 
                ? "We have received your school registration. We will contact you soon to create a school code for your learners."
                : "Ons het jou skool se registrasie ontvang. Ons sal binnekort kontak maak om 'n skoolkode te skep vir jou leerders."}
            </p>
            <Button onClick={() => navigate("/")} className="w-full" testId="back-home-btn">
              {i18n.language === "en" ? "Back to Home" : "Terug na Tuisblad"}
            </Button>
          </Card>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-background flex items-center justify-center p-4 py-8">
        <Card className="w-full max-w-md">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => navigate("/")} className="flex items-center gap-2 text-text-muted hover:text-text-primary">
              <ArrowLeft className="w-4 h-4" /> {t('back')}
            </button>
            <LanguageSelector compact />
          </div>
          <h1 className="font-heading text-3xl font-bold text-center mb-2">{t('auth.register')}</h1>
          <p className="text-center text-text-secondary mb-6">{t('auth.trialInfo')}</p>
          
          {/* Registration Type Toggle */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setRegistrationType("learner")}
              className={`flex-1 py-3 rounded-full font-semibold transition-colors flex items-center justify-center gap-2 ${registrationType === "learner" ? "bg-primary-500 text-white" : "bg-slate-100 text-text-secondary"}`}
              data-testid="learner-reg-tab"
            >
              <User className="w-4 h-4" /> {t('auth.learner')}
            </button>
            <button
              onClick={() => setRegistrationType("school")}
              className={`flex-1 py-3 rounded-full font-semibold transition-colors flex items-center justify-center gap-2 ${registrationType === "school" ? "bg-primary-500 text-white" : "bg-slate-100 text-text-secondary"}`}
              data-testid="school-reg-tab"
            >
              <Building2 className="w-4 h-4" /> {t('auth.school')}
            </button>
          </div>

          {registrationType === "learner" ? (
            <form onSubmit={handleLearnerSubmit}>
              <Input
                label={`${t('auth.invitationCode')} (${i18n.language === 'en' ? 'Optional - for 7 day free trial' : 'Opsioneel - vir 7 dae gratis proeftydperk'})`}
                value={form.invitation_code}
                onChange={(e) => setForm({...form, invitation_code: e.target.value.toUpperCase()})}
                placeholder={i18n.language === 'en' ? "Leave empty = pay first to access" : "Los leeg = betaal eers vir toegang"}
                testId="invitation-code-input"
              />
              
              {/* Subscription Type Selection - Only show if no invitation code */}
              {!form.invitation_code && (
                <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100">
                  <label className="block text-sm font-semibold text-text-secondary mb-3">
                    {i18n.language === 'en' ? 'Payment Option' : 'Betaling Opsie'} *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setForm({...form, subscription_type: "once_off"})}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        form.subscription_type === "once_off" 
                          ? "border-primary-500 bg-primary-50 shadow-md" 
                          : "border-slate-200 bg-white hover:border-primary-300"
                      }`}
                      data-testid="once-off-option"
                    >
                      <div className="font-bold text-lg text-primary-600">R399</div>
                      <div className="text-sm font-medium text-text-primary">
                        {i18n.language === 'en' ? 'Once-off' : 'Eenmalig'}
                      </div>
                      <div className="text-xs text-text-muted mt-1">
                        {i18n.language === 'en' ? 'Lifetime access' : 'Lewenslange toegang'}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({...form, subscription_type: "monthly"})}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        form.subscription_type === "monthly" 
                          ? "border-secondary-500 bg-secondary-50 shadow-md" 
                          : "border-slate-200 bg-white hover:border-secondary-300"
                      }`}
                      data-testid="monthly-option"
                    >
                      <div className="font-bold text-lg text-secondary-600">R100</div>
                      <div className="text-sm font-medium text-text-primary">
                        {i18n.language === 'en' ? 'Monthly' : 'Maandeliks'}
                      </div>
                      <div className="text-xs text-text-muted mt-1">
                        {i18n.language === 'en' ? 'Per month' : 'Per maand'}
                      </div>
                    </button>
                  </div>
                  {form.subscription_type === "monthly" && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {i18n.language === 'en' 
                        ? 'Monthly subscription: Your account will be reviewed each month for payment confirmation.' 
                        : 'Maandelikse subskripsie: Jou rekening sal elke maand hersien word vir betalingbevestiging.'}
                    </p>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('auth.name')}
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  required
                  testId="name-input"
                />
                <Input
                  label={t('auth.surname')}
                  value={form.surname}
                  onChange={(e) => setForm({...form, surname: e.target.value})}
                  required
                  testId="surname-input"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-semibold text-text-secondary mb-2">{t('auth.grade')}</label>
                <select
                  className="input-field"
                  value={form.grade}
                  onChange={(e) => setForm({...form, grade: e.target.value})}
                  data-testid="grade-select"
                >
                  {[1,2,3,4,5,6,7,8,9].map(g => (
                    <option key={g} value={g}>{i18n.language === 'en' ? `Grade ${g}` : `Graad ${g}`}</option>
                  ))}
                </select>
              </div>
              
              {/* Current Reading Level at School */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-text-secondary mb-2">
                  {i18n.language === 'en' ? 'Current Reading Level at School' : 'Huidige Leesvlak by die Skool'}
                </label>
                <p className="text-xs text-text-muted mb-2">
                  {i18n.language === 'en' 
                    ? 'What reading level are you currently on at school? If you don\'t know, select "I don\'t know".'
                    : 'Op watter leesvlak is jy tans by die skool? As jy nie weet nie, kies "Ek weet nie".'}
                </p>
                <select
                  className="input-field"
                  value={form.current_reading_level}
                  onChange={(e) => setForm({...form, current_reading_level: e.target.value})}
                  data-testid="reading-level-select"
                >
                  <option value="unknown">{i18n.language === 'en' ? "I don't know" : "Ek weet nie"}</option>
                  {[1,2,3,4,5,6,7,8,9].map(g => (
                    <option key={g} value={g}>
                      {i18n.language === 'en' ? `Level ${g}` : `Vlak ${g}`}
                    </option>
                  ))}
                </select>
              </div>
              
              <Input
                label={t('auth.username')}
                value={form.username}
                onChange={(e) => setForm({...form, username: e.target.value})}
                required
                testId="reg-username-input"
              />
              <PasswordInput
                label={t('auth.password')}
                value={form.password}
                onChange={(e) => setForm({...form, password: e.target.value})}
                required
                testId="reg-password-input"
              />
              
              <Input
                label={t('auth.whatsapp')}
                value={form.whatsapp}
                onChange={(e) => setForm({...form, whatsapp: e.target.value})}
                placeholder={i18n.language === 'en' ? "e.g. 0821234567" : "bv. 0821234567"}
                required
                testId="learner-whatsapp-input"
              />
              
              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-text-secondary mb-3">
                  <strong>{t('auth.parentDetails')}</strong><br/>
                  {i18n.language === 'en' ? 'Add your parent\'s details so they can access the parent portal.' : 'Voeg jou ouer se besonderhede by sodat hulle toegang kry tot die ouerportaal.'}
                </p>
                <Input
                  label={t('auth.parentEmail') + ' (' + (i18n.language === 'en' ? 'Optional' : 'Opsioneel') + ')'}
                  type="email"
                  value={form.parent_email}
                  onChange={(e) => setForm({...form, parent_email: e.target.value})}
                  placeholder={i18n.language === 'en' ? "e.g. parent@email.com" : "bv. ouer@email.com"}
                  testId="parent-email-input"
                />
                <Input
                  label={t('auth.parentWhatsapp') + ' *'}
                  value={form.parent_whatsapp}
                  onChange={(e) => setForm({...form, parent_whatsapp: e.target.value})}
                  placeholder={i18n.language === 'en' ? "e.g. 0821234567" : "bv. 0821234567"}
                  required
                  testId="parent-whatsapp-input"
                />
              </div>
              
              <label className="flex items-start gap-3 mb-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.parent_permission}
                  onChange={(e) => setForm({...form, parent_permission: e.target.checked})}
                  className="mt-1 w-5 h-5 rounded border-slate-300"
                  data-testid="parent-permission-checkbox"
                />
                <span className="text-sm text-text-secondary">
                  {i18n.language === "en" 
                    ? "I confirm that I have parent/guardian permission to register this child."
                    : "Ek bevestig dat ek ouer/voog toestemming het om hierdie kind te registreer."}
                </span>
              </label>

              {/* Online Tutoring Option */}
              <div className="border rounded-xl p-4 mb-4 bg-secondary-50">
                <label className="flex items-start gap-3 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.request_tutoring}
                    onChange={(e) => setForm({...form, request_tutoring: e.target.checked})}
                    className="mt-1 w-5 h-5 rounded border-slate-300"
                    data-testid="tutoring-checkbox"
                  />
                  <div>
                    <span className="font-semibold text-secondary-700">
                      {i18n.language === "en" ? "Online Tutoring Request (R100/week)" : "Online Tutoring Aanvraag (R100/week)"}
                    </span>
                    <p className="text-xs text-text-muted mt-1">
                      {i18n.language === "en" ? "One-on-one online sessions with a teacher" : "Een-op-een online sessies met 'n onderwyser"}
                    </p>
                  </div>
                </label>
                
                {form.request_tutoring && (
                  <div className="mt-3 space-y-3 border-t pt-3">
                    <div>
                      <p className="text-sm font-semibold mb-2">Voorkeur Dae:</p>
                      <div className="flex flex-wrap gap-2">
                        {availableDays.map(day => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleDay(day)}
                            className={`px-3 py-1 rounded-full text-sm ${
                              form.tutoring_days.includes(day) 
                                ? 'bg-secondary-500 text-white' 
                                : 'bg-white border text-text-secondary hover:border-secondary-500'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold mb-2">Voorkeur Tye:</p>
                      <div className="flex flex-wrap gap-2">
                        {availableTimes.map(time => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => toggleTime(time)}
                            className={`px-3 py-1 rounded-full text-sm ${
                              form.tutoring_times.includes(time)
                                ? 'bg-secondary-500 text-white'
                                : 'bg-white border text-text-secondary hover:border-secondary-500'
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {error && <p className="text-accent-500 text-sm mb-4">{error}</p>}
              
              <Button type="submit" disabled={loading} className="w-full" testId="register-submit">
                {loading 
                  ? (i18n.language === "en" ? "Loading..." : "Besig...") 
                  : (i18n.language === "en" ? "Register" : "Registreer")}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSchoolSubmit}>
              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-text-secondary">
                  {i18n.language === "en" 
                    ? <><strong>For schools:</strong> Register your school and we will create a special school code that all your learners can use to register.</>
                    : <><strong>Vir skole:</strong> Registreer jou skool en ons sal 'n spesiale skoolkode skep wat al jou leerders kan gebruik om te registreer.</>}
                </p>
              </div>
              
              <Input
                label={i18n.language === "en" ? "School Name" : "Skool Naam"}
                value={schoolForm.school_name}
                onChange={(e) => setSchoolForm({...schoolForm, school_name: e.target.value})}
                placeholder={i18n.language === "en" ? "e.g. Pretoria East Primary" : "bv. Laerskool Pretoria-Oos"}
                required
                testId="school-name-input"
              />
              
              <Input
                label={i18n.language === "en" ? "Contact Person (Name)" : "Kontakpersoon (Naam)"}
                value={schoolForm.contact_person}
                onChange={(e) => setSchoolForm({...schoolForm, contact_person: e.target.value})}
                required
                testId="contact-person-input"
              />
              
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={i18n.language === "en" ? "Contact Person Email" : "Kontakpersoon E-pos"}
                  type="email"
                  value={schoolForm.contact_email}
                  onChange={(e) => setSchoolForm({...schoolForm, contact_email: e.target.value})}
                  required
                  testId="contact-email-input"
                />
                <Input
                  label={i18n.language === "en" ? "WhatsApp Number" : "WhatsApp Nommer"}
                  value={schoolForm.contact_whatsapp}
                  onChange={(e) => setSchoolForm({...schoolForm, contact_whatsapp: e.target.value})}
                  placeholder={i18n.language === "en" ? "e.g. 0821234567" : "bv. 0821234567"}
                  required
                  testId="contact-whatsapp-input"
                />
              </div>
              
              <Input
                label={i18n.language === "en" ? "Phone Number" : "Telefoonnommer"}
                value={schoolForm.contact_phone}
                onChange={(e) => setSchoolForm({...schoolForm, contact_phone: e.target.value})}
                placeholder={i18n.language === "en" ? "e.g. 0121234567" : "bv. 0121234567"}
                testId="contact-phone-input"
              />
              
              <Input
                label={i18n.language === "en" ? "School Email" : "Skool E-pos"}
                type="email"
                value={schoolForm.school_email}
                onChange={(e) => setSchoolForm({...schoolForm, school_email: e.target.value})}
                placeholder={i18n.language === "en" ? "e.g. admin@school.co.za" : "bv. admin@skool.co.za"}
                testId="school-email-input"
              />
              
              <Input
                label={i18n.language === "en" ? "Principal Contact Details" : "Skoolhoof Kontakbesonderhede"}
                value={schoolForm.principal_contact}
                onChange={(e) => setSchoolForm({...schoolForm, principal_contact: e.target.value})}
                placeholder={i18n.language === "en" ? "Name and phone number" : "Naam en telefoonnommer"}
                testId="principal-contact-input"
              />
              
              <div className="mb-4">
                <label className="block text-sm font-semibold text-text-secondary mb-2">
                  {i18n.language === "en" ? "Number of Learners" : "Aantal Leerders"}
                </label>
                <input
                  type="number"
                  className="input-field"
                  min="1"
                  max="5000"
                  value={schoolForm.learner_count}
                  onChange={(e) => setSchoolForm({...schoolForm, learner_count: parseInt(e.target.value) || 1})}
                  placeholder={i18n.language === "en" ? "e.g. 250" : "bv. 250"}
                  data-testid="learner-count-input"
                />
                <p className="text-xs text-text-muted mt-1">
                  {i18n.language === "en" ? "Enter the exact or estimated number of learners" : "Tik die presiese of geskatte aantal leerders in"}
                </p>
              </div>
              
              {error && <p className="text-accent-500 text-sm mb-4">{error}</p>}
              
              <Button type="submit" disabled={loading} className="w-full" testId="school-register-submit">
                {loading 
                  ? (i18n.language === "en" ? "Loading..." : "Besig...") 
                  : (i18n.language === "en" ? "Submit School Registration" : "Dien Skool Registrasie In")}
              </Button>
            </form>
          )}

          <p className="text-center mt-4 text-text-secondary">
            {i18n.language === "en" ? "Already registered?" : "Reeds geregistreer?"}{" "}
            <button onClick={() => navigate("/login")} className="text-primary-500 font-semibold" data-testid="go-to-login">
              {i18n.language === "en" ? "Log in" : "Meld aan"}
            </button>
          </p>
        </Card>
      </div>
    </PageTransition>
  );
};

// Reading Level Test
const ReadingTestPage = ({ user }) => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [stage, setStage] = useState("intro"); // intro, reading, result
  const [startTime, setStartTime] = useState(null);
  const [result, setResult] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [errors, setErrors] = useState(0);
  const [encouragement, setEncouragement] = useState("");
  const [audioUrl, setAudioUrl] = useState(null);
  const [gradeTexts, setGradeTexts] = useState({});
  const [loadingTexts, setLoadingTexts] = useState(true);
  const [previousResult, setPreviousResult] = useState(null);  // For comparison
  
  // Determine language from i18n or user preference
  const userLanguage = i18n.language || user?.preferred_language || "af";
  
  // Fetch reading test texts, encouragement message, and previous results
  useEffect(() => {
    // Get exercise instructions
    api.get("/exercise-instructions")
      .then(res => {
        // Get encouragement message
        if (userLanguage === "en" && res.data?.reading_level_encouragement_en) {
          setEncouragement(res.data.reading_level_encouragement_en);
        } else if (res.data?.reading_level_encouragement) {
          setEncouragement(res.data.reading_level_encouragement);
        }
        // Get language-specific audio URL
        if (userLanguage === "en" && res.data?.reading_level_audio_url_en) {
          setAudioUrl(res.data.reading_level_audio_url_en);
        } else if (res.data?.reading_level_audio_url) {
          setAudioUrl(res.data.reading_level_audio_url);
        }
        // Get reading test texts from API
        if (userLanguage === "en" && res.data?.reading_test_texts_en) {
          setGradeTexts(res.data.reading_test_texts_en);
        } else if (res.data?.reading_test_texts_af) {
          setGradeTexts(res.data.reading_test_texts_af);
        }
        setLoadingTexts(false);
      })
      .catch(() => {
        setLoadingTexts(false);
      });
    
    // Get previous reading test result for comparison
    api.get("/reading-test/status")
      .then(res => {
        if (res.data?.current_wpm || res.data?.initial_wpm) {
          setPreviousResult({
            wpm: res.data.current_wpm || res.data.initial_wpm,
            level: res.data.current_level || res.data.initial_level,
            date: res.data.last_test_date
          });
        }
      })
      .catch(() => {});
  }, [userLanguage]);
  
  // Fallback texts if API fails
  const fallbackTextsAf = {
    "1": "Die kat sit. Die hond hardloop. Ma gee kos. Pa lees. Ek speel bal.",
    "2": "Die kat sit op die mat. Die hond hardloop in die tuin.",
    "3": "Die son skyn helder oor die plaas. Boer Jan stap na die skuur."
  };
  
  const fallbackTextsEn = {
    "1": "The cat sits. The dog runs. Mom gives food. Dad reads. I play ball.",
    "2": "The cat sits on the mat. The dog runs in the garden.",
    "3": "The sun shines bright over the farm. Farmer John walks to the barn."
  };
  
  const learnerGrade = user?.grade || 3;
  const effectiveTexts = Object.keys(gradeTexts).length > 0 
    ? gradeTexts 
    : (userLanguage === "en" ? fallbackTextsEn : fallbackTextsAf);
  const testText = effectiveTexts[String(learnerGrade)] || effectiveTexts["3"] || "Loading...";
  const wordCountTotal = testText.split(/\s+/).length;

  const startTest = () => {
    setStage("reading");
    setStartTime(Date.now());
  };

  const finishTest = async () => {
    const endTime = Date.now();
    const timeSeconds = (endTime - startTime) / 1000;
    const wordsRead = wordCount || wordCountTotal;
    
    try {
      const res = await api.post("/reading-test/submit", {
        words_read: wordsRead,
        time_seconds: timeSeconds,
        errors: errors
      });
      setResult(res.data);
      setStage("result");
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
    } catch (err) {
      toast.error(t('toast.couldNotSave'));
    }
  };

  if (stage === "intro") {
    return (
      <PageTransition>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl text-center">
            <div className="w-24 h-24 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-12 h-12 text-white" strokeWidth={2} />
            </div>
            <h1 className="font-heading text-3xl font-bold mb-4">{t('readingTestExt.title')}</h1>
            <p className="text-text-secondary mb-2">
              {t('auth.grade')} {learnerGrade} - {wordCountTotal} {t('readingTestExt.wordsText')}
            </p>
            <p className="text-text-secondary mb-6">
              {t('readingTestExt.readText')} {t('readingTestExt.determineLevel')}
            </p>
            <Button onClick={startTest} testId="start-test-btn">
              {t('readingTestExt.startTest')}
            </Button>
          </Card>
        </div>
      </PageTransition>
    );
  }

  if (stage === "reading") {
    return (
      <PageTransition>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold">{t('readingTestExt.readAloud')}</h2>
              <div className="flex items-center gap-2 text-accent-500">
                <Timer className="w-5 h-5" />
                <span className="font-mono">{t('readingTestExt.timing')}</span>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-6 mb-6">
              <p className="text-lg leading-relaxed">{testText}</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-text-secondary mb-2">
                {t('readingTestExt.howManyWords')}
              </label>
              <input
                type="number"
                className="input-field"
                placeholder={wordCountTotal}
                value={wordCount || ""}
                onChange={(e) => setWordCount(parseInt(e.target.value) || 0)}
                data-testid="word-count-input"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-text-secondary mb-2">
                {t('readingTestExt.howManyErrors')}
              </label>
              <input
                type="number"
                className="input-field"
                value={errors}
                onChange={(e) => setErrors(parseInt(e.target.value) || 0)}
                min="0"
                data-testid="errors-input"
              />
            </div>
            
            <Button onClick={finishTest} className="w-full" testId="finish-test-btn">
              {t('readingTestExt.doneReading')}
            </Button>
          </Card>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Star className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-4">{t('readingTestExt.excellent')}</h1>
          <p className="text-text-secondary mb-4">{result?.message}</p>
          
          <div className="bg-slate-50 rounded-2xl p-6 mb-4">
            <div className="text-4xl font-bold text-primary-500 mb-2">{result?.wpm}</div>
            <div className="text-text-secondary">{t('readingTestExt.wordsPerMinute')}</div>
          </div>
          
          {/* Comparison with Previous Result */}
          {previousResult && previousResult.wpm && (
            <div className={`rounded-2xl p-4 mb-4 ${
              result?.wpm > previousResult.wpm 
                ? 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200' 
                : result?.wpm < previousResult.wpm 
                  ? 'bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200'
                  : 'bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200'
            }`}>
              <p className="text-sm font-semibold mb-2">
                {userLanguage === "en" ? "📊 Compared to your previous test:" : "📊 Vergelyk met jou vorige toets:"}
              </p>
              <div className="flex items-center justify-center gap-4">
                <div className="text-center">
                  <p className="text-xs text-text-muted">{userLanguage === "en" ? "Previous" : "Vorige"}</p>
                  <p className="text-xl font-bold text-slate-600">{previousResult.wpm}</p>
                  <p className="text-xs text-text-muted">WPM</p>
                </div>
                <div className="text-2xl">
                  {result?.wpm > previousResult.wpm ? (
                    <span className="text-green-500">↑ +{result.wpm - previousResult.wpm}</span>
                  ) : result?.wpm < previousResult.wpm ? (
                    <span className="text-orange-500">↓ {result.wpm - previousResult.wpm}</span>
                  ) : (
                    <span className="text-blue-500">= 0</span>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-xs text-text-muted">{userLanguage === "en" ? "Now" : "Nou"}</p>
                  <p className="text-xl font-bold text-primary-600">{result?.wpm}</p>
                  <p className="text-xs text-text-muted">WPM</p>
                </div>
              </div>
              {result?.wpm > previousResult.wpm && (
                <p className="text-sm text-green-700 mt-2 font-medium">
                  🎉 {userLanguage === "en" ? "Great improvement! Keep it up!" : "Fantastiese verbetering! Hou so aan!"}
                </p>
              )}
              {result?.wpm < previousResult.wpm && (
                <p className="text-sm text-orange-700 mt-2">
                  💪 {userLanguage === "en" ? "Keep practicing, you can do better!" : "Bly oefen, jy kan beter doen!"}
                </p>
              )}
              {previousResult.date && (
                <p className="text-xs text-text-muted mt-2">
                  {userLanguage === "en" ? "Last test:" : "Vorige toets:"} {new Date(previousResult.date).toLocaleDateString('af-ZA')}
                </p>
              )}
            </div>
          )}
          
          {/* Encouragement Message from Admin */}
          {encouragement && (
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-4 mb-4 text-left">
              <p className="text-sm text-yellow-800 whitespace-pre-line">{encouragement}</p>
            </div>
          )}
          
          {/* Audio Message if available */}
          {audioUrl && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-4 mb-4">
              <p className="text-sm text-purple-700 mb-2 font-medium">🔊 {userLanguage === "en" ? "Listen to a message:" : "Luister na 'n boodskap:"}</p>
              <audio 
                controls 
                autoPlay
                src={`${API}${audioUrl}`}
                className="w-full"
              />
            </div>
          )}
          
          {/* Level Choice - if determined level is lower than school grade */}
          {result?.determined_level && user?.grade && result.determined_level < user.grade && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-4">
              <h3 className="font-semibold text-blue-800 mb-3">{t('readingTestExt.chooseLevel')}</h3>
              <p className="text-sm text-blue-600 mb-4">
                {t('readingTestExt.yourLevelIs', { level: result.determined_level, grade: user.grade })}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={async () => {
                    try {
                      await api.put("/learner/practice-level", { practice_level: result.determined_level });
                      toast.success(t('readingTestExt.practiceAt', { grade: result.determined_level }));
                      navigate("/dashboard");
                    } catch (err) {
                      toast.error(t('readingTestExt.couldNotSave'));
                    }
                  }}
                  className="p-3 bg-green-100 border-2 border-green-300 rounded-xl hover:bg-green-200 transition-colors"
                  data-testid="choose-reading-level"
                >
                  <div className="text-2xl font-bold text-green-700">{t('auth.grade')} {result.determined_level}</div>
                  <div className="text-xs text-green-600">{t('readingTestExt.myReadingLevel')}</div>
                </button>
                <button
                  onClick={async () => {
                    try {
                      await api.put("/learner/practice-level", { practice_level: user.grade });
                      toast.success(t('readingTestExt.practiceAt', { grade: user.grade }));
                      navigate("/dashboard");
                    } catch (err) {
                      toast.error(t('readingTestExt.couldNotSave'));
                    }
                  }}
                  className="p-3 bg-purple-100 border-2 border-purple-300 rounded-xl hover:bg-purple-200 transition-colors"
                  data-testid="choose-school-grade"
                >
                  <div className="text-2xl font-bold text-purple-700">{t('auth.grade')} {user.grade}</div>
                  <div className="text-xs text-purple-600">{t('readingTestExt.mySchoolGrade')}</div>
                </button>
              </div>
            </div>
          )}
          
          {/* If levels are the same, just show continue button */}
          {(!result?.determined_level || !user?.grade || result.determined_level >= user.grade) && (
            <Button onClick={() => navigate("/dashboard")} className="w-full" testId="go-to-dashboard">
              {t('readingTestExt.goToDashboard')}
            </Button>
          )}
        </Card>
      </div>
    </PageTransition>
  );
};

// Parent Dashboard
const ParentDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [parent, setParent] = useState(null);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [learnerProgress, setLearnerProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkForm, setLinkForm] = useState({ name: "", surname: "" });
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    api.get("/parent/me")
      .then(res => {
        setParent(res.data);
        if (res.data.learners?.length > 0) {
          setSelectedLearner(res.data.learners[0]);
        }
      })
      .catch(err => {
        if (err.response?.status === 401) {
          navigate("/login");
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (selectedLearner) {
      api.get(`/parent/learner-progress/${selectedLearner.id}`)
        .then(res => setLearnerProgress(res.data))
        .catch(console.error);
    }
  }, [selectedLearner]);

  // Generate weekly progress text for manual sharing
  const [progressText, setProgressText] = useState("");
  const [generatingText, setGeneratingText] = useState(false);
  const [showProgressText, setShowProgressText] = useState(false);

  const generateProgressText = async () => {
    setGeneratingText(true);
    try {
      const res = await api.get("/parent/weekly-progress-text");
      setProgressText(res.data.text);
      setShowProgressText(true);
      toast.success(t('toast.summaryGenerated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.couldNotGenerate'));
    }
    setGeneratingText(false);
  };

  const copyProgressText = () => {
    navigator.clipboard.writeText(progressText);
    toast.success(t('toast.copiedToClipboard'));
  };

  const handleLinkLearner = async () => {
    if (!linkForm.name.trim() || !linkForm.surname.trim()) {
      toast.error(t('form.enterName'));
      return;
    }
    setLinking(true);
    try {
      const res = await api.post("/parent/link-learner", { 
        learner_name: linkForm.name.trim(), 
        learner_surname: linkForm.surname.trim() 
      });
      toast.success(res.data.message);
      if (res.data.email_sent) {
      toast.success(t('toast.confirmationEmailSent'));
      }
      // Refresh parent data
      const parentRes = await api.get("/parent/me");
      setParent(parentRes.data);
      if (!selectedLearner && parentRes.data.learners?.length > 0) {
        setSelectedLearner(parentRes.data.learners[0]);
      }
      setLinkForm({ name: "", surname: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.couldNotConnect'));
    }
    setLinking(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader className="w-8 h-8 animate-spin text-primary-500" /></div>;
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <nav className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="max-w-6xl mx-auto flex justify-between items-center">
            <h1 className="font-heading text-xl font-bold">Ouer Portaal</h1>
            <div className="flex items-center gap-4">
              <span className="text-text-secondary">Welkom, {parent?.name}</span>
              <button onClick={handleLogout} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-6xl mx-auto p-6 space-y-6">
          {/* Link Learner Card */}
          <Card testId="link-learner-card">
            <h3 className="font-heading font-bold mb-4">Koppel 'n Leerder</h3>
            <p className="text-text-secondary mb-4">
              Voer jou kind se <strong>naam en van</strong> in om hul vordering te sien.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input
                type="text"
                className="input-field"
                value={linkForm.name}
                onChange={(e) => setLinkForm({...linkForm, name: e.target.value})}
                placeholder="Naam (bv. Jan)"
                data-testid="link-name-input"
              />
              <input
                type="text"
                className="input-field"
                value={linkForm.surname}
                onChange={(e) => setLinkForm({...linkForm, surname: e.target.value})}
                placeholder="Van (bv. van der Berg)"
                data-testid="link-surname-input"
              />
            </div>
            <Button onClick={handleLinkLearner} disabled={linking} testId="link-learner-btn" className="w-full">
              {linking ? "Koppel..." : "Koppel Leerder"}
            </Button>
          </Card>

          {/* Weekly Progress - Generate & Copy for WhatsApp */}
          {parent?.learners?.length > 0 && (
            <Card testId="weekly-progress-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-heading font-bold">Weeklikse Opsomming</h3>
                  <p className="text-sm text-text-muted">Genereer en stuur via WhatsApp/SMS (gratis!)</p>
                </div>
                <Button 
                  onClick={generateProgressText} 
                  disabled={generatingText}
                  variant="secondary"
                  testId="generate-progress-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {generatingText ? "Genereer..." : "Genereer"}
                </Button>
              </div>
              
              {showProgressText && progressText && (
                <div className="mt-4">
                  <div className="bg-slate-50 rounded-xl p-4 mb-3 max-h-64 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm font-sans">{progressText}</pre>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={copyProgressText} testId="copy-progress-btn" className="flex-1">
                      <Copy className="w-4 h-4 mr-2" />
                      Kopieer vir WhatsApp
                    </Button>
                    <Button 
                      variant="secondary" 
                      onClick={() => setShowProgressText(false)}
                      testId="close-progress-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Linked Learners */}
          {parent?.learners?.length > 0 ? (
            <>
              <Card testId="learners-list">
                <h3 className="font-heading font-bold mb-4">Gekoppelde Leerders</h3>
                <div className="flex flex-wrap gap-3">
                  {parent.learners.map(learner => (
                    <button
                      key={learner.id}
                      onClick={() => setSelectedLearner(learner)}
                      className={`px-4 py-2 rounded-full font-semibold transition-colors ${
                        selectedLearner?.id === learner.id 
                          ? "bg-primary-500 text-white" 
                          : "bg-slate-100 text-text-secondary hover:bg-slate-200"
                      }`}
                      data-testid={`learner-btn-${learner.id}`}
                    >
                      {learner.name} {learner.surname} (Gr. {learner.grade})
                    </button>
                  ))}
                </div>
              </Card>

              {/* Selected Learner Progress */}
              {selectedLearner && learnerProgress && (
                <Card testId="learner-progress">
                  <h3 className="font-heading font-bold mb-4">
                    {selectedLearner.name} se Vordering
                  </h3>
                  
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-primary-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-primary-600">
                        Gr. {selectedLearner.current_reading_level || selectedLearner.grade}
                      </div>
                      <div className="text-sm text-text-muted">Leesvlak</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {learnerProgress.results?.length || 0}
                      </div>
                      <div className="text-sm text-text-muted">Oefeninge Voltooi</div>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {learnerProgress.subscription?.active ? "Aktief" : "Onaktief"}
                      </div>
                      <div className="text-sm text-text-muted">Subskripsie</div>
                    </div>
                  </div>

                  {/* Recent Results */}
                  <h4 className="font-semibold mb-3">Onlangse Resultate</h4>
                  {learnerProgress.results?.length > 0 ? (
                    <div className="space-y-2">
                      {learnerProgress.results.slice(0, 10).map((result, i) => (
                        <div key={result.id || result.exercise_id || `result-${i}-${result.timestamp || result.completed_at}`} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                          <div>
                            <span className="font-medium">{result.text_title || "Oefening"}</span>
                            <span className="text-sm text-text-muted ml-2">
                              ({result.exercise_type === "comprehension" ? "Begrip" : 
                                result.exercise_type === "reading" ? "Lees" : 
                                result.exercise_type === "spelling" ? "Speltoets" : "Luister"})
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`font-bold ${result.score >= 70 ? "text-green-600" : "text-accent-500"}`}>
                              {result.score}%
                            </span>
                            <span className="text-xs text-text-muted">
                              {new Date(result.completed_at).toLocaleDateString("af-ZA")}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-text-muted text-center py-4">Nog geen oefeninge voltooi nie</p>
                  )}
                </Card>
              )}
            </>
          ) : (
            <Card className="text-center py-8">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">Geen Leerders Gekoppel</h3>
              <p className="text-text-secondary">
                Koppel jou kind se rekening deur hul gebruikersnaam hierbo in te voer.
              </p>
            </Card>
          )}
        </div>
      </div>
    </PageTransition>
  );
};

// Learner Dashboard
const LearnerDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readingTestStatus, setReadingTestStatus] = useState(null);
  const [levelProgress, setLevelProgress] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [practiceLevel, setPracticeLevel] = useState(null);
  const [exerciseInstructions, setExerciseInstructions] = useState({});
  // Use ref instead of state to prevent multiple audio plays
  const currentAudioRef = useRef(null);
  const isAudioPlayingRef = useRef(false);

  // Load exercise instructions (for oral audio)
  useEffect(() => {
    api.get("/exercise-instructions")
      .then(res => setExerciseInstructions(res.data || {}))
      .catch(() => {});
  }, []);

  // Play exercise audio - use uploaded audio if available, NO fallback to TTS
  // Returns a Promise that resolves when audio finishes (or immediately if no audio)
  const playExerciseAudio = (exerciseId, title) => {
    return new Promise((resolve) => {
      // Prevent multiple plays - check ref immediately (not state which has delay)
      if (isAudioPlayingRef.current) {
        resolve(); // Already playing, resolve immediately
        return;
      }
      
      const userLang = user?.preferred_language || "af";
      const audioKey = userLang === "en" ? `${exerciseId}_audio_url_en` : `${exerciseId}_audio_url`;
      const audioUrl = exerciseInstructions[audioKey];
      
      if (audioUrl) {
        // Mark as playing immediately
        isAudioPlayingRef.current = true;
        
        // Stop any currently playing audio first
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
        }
        
        // Create and play new audio
        const fullUrl = `${API}${audioUrl}`;
        const audio = new Audio(fullUrl);
        currentAudioRef.current = audio;
        
        // Reset playing flag and resolve when audio ends
        audio.onended = () => {
          isAudioPlayingRef.current = false;
          resolve();
        };
        audio.onerror = () => {
          isAudioPlayingRef.current = false;
          resolve(); // Resolve even on error so navigation can proceed
        };
        
        audio.play().catch(err => {
          console.log("Audio play error:", err);
          isAudioPlayingRef.current = false;
          resolve();
        });
      } else {
        resolve(); // No audio, resolve immediately
      }
    });
  };

  // Cleanup audio when component unmounts
  useEffect(() => {
    return () => {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      isAudioPlayingRef.current = false;
    };
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/progress"),
      api.get("/reading-test/status").catch(() => ({ data: null })),
      api.get("/learner/progress").catch(() => ({ data: null })),
      api.get("/learner/practice-level").catch(() => ({ data: null }))
    ])
      .then(([progressRes, testStatusRes, levelProgressRes, practiceLevelRes]) => {
        setProgress(progressRes.data);
        setReadingTestStatus(testStatusRes.data);
        setLevelProgress(levelProgressRes.data);
        setPracticeLevel(practiceLevelRes.data);
        // Show modal if all exercises completed
        if (levelProgressRes.data?.all_completed) {
          setShowProgressModal(true);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Use practice_level if set, otherwise current_reading_level, otherwise grade
  const learnerGrade = user?.practice_level || user?.current_reading_level || user?.grade || 1;
  const isGrade1to6 = learnerGrade <= 6;
  const isGrade1to3 = learnerGrade <= 3;
  
  // Show audio icons for ALL grades (not just Grade 1-3)
  const showAudioInstructions = true;
  
  // Use useMemo with i18n.language dependency to ensure exerciseTypes updates when language changes
  const exerciseTypes = useMemo(() => [
    { id: "comprehension", icon: BookOpen, title: t('exerciseTypes.comprehension'), desc: t('exerciseTypes.comprehensionDesc'), color: "bg-primary-500" },
    { id: "reading", icon: Mic, title: t('exerciseTypes.reading'), desc: t('exerciseTypes.readingDesc'), color: "bg-accent-500" },
    { id: "listening", icon: Headphones, title: t('exerciseTypes.listening'), desc: t('exerciseTypes.listeningDesc'), color: "bg-secondary-500" },
    { id: "spelling", icon: Edit3, title: t('exerciseTypes.spelling'), desc: t('exerciseTypes.spellingDesc'), color: "bg-purple-500" },
    // Grade 1-6 specific exercises (Woordbou & Klanktoets)
    ...(isGrade1to6 ? [
      { id: "woordbou", icon: BookOpen, title: t('exerciseTypes.woordbou'), desc: t('exerciseTypes.woordbouDesc'), color: "bg-emerald-500" },
      { id: "klanktoets", icon: Volume2, title: t('exerciseTypes.klanktoets'), desc: t('exerciseTypes.klanktoetsDesc'), color: "bg-orange-500" },
    ] : []),
    // Grade 1-3 specific: Alfabet Helper
    ...(isGrade1to3 ? [
      { id: "alfabet", icon: BookOpen, title: t('exerciseTypes.alfabet'), desc: t('exerciseTypes.alfabetDesc'), color: "bg-pink-500" },
    ] : []),
  ], [t, isGrade1to6, isGrade1to3, i18n.language]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('loading')}</div>;
  }

  const subscription = user?.subscription || {};

  return (
    <PageTransition>
      <div className="min-h-screen bg-background pb-24 md:pb-8">
        {/* BLOCKING OVERLAY for pending payment - must pay before accessing anything */}
        {(subscription.status === "pending_payment" || subscription.status === "monthly_expired") && (
          <div className="fixed inset-0 bg-gradient-to-br from-blue-900/95 to-purple-900/95 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-8 max-w-lg w-full text-center shadow-2xl"
            >
              <div className={`w-20 h-20 ${subscription.status === "monthly_expired" ? "bg-gradient-to-br from-red-400 to-orange-500" : "bg-gradient-to-br from-amber-400 to-orange-500"} rounded-full flex items-center justify-center mx-auto mb-6`}>
                <Clock className="w-10 h-10 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-4 text-gray-800">
                {subscription.status === "monthly_expired" 
                  ? (i18n.language === "en" ? "Monthly Subscription Expired" : "Maandelikse Subskripsie Verval")
                  : (i18n.language === "en" ? "Waiting for Payment Confirmation" : "Wag vir Betaling Bevestiging")
                }
              </h2>
              <p className="text-gray-600 mb-6">
                {subscription.status === "monthly_expired"
                  ? (i18n.language === "en" 
                      ? "Your 30-day subscription period has ended. Please send your monthly payment and wait for admin confirmation to continue learning."
                      : "Jou 30-dae subskripsie tydperk het geëindig. Stuur asseblief jou maandelikse betaling en wag vir admin bevestiging om voort te gaan met leer.")
                  : (i18n.language === "en" 
                      ? "Your account has been created! To access the learning platform, please send proof of payment and wait for admin confirmation."
                      : "Jou rekening is geskep! Om toegang tot die leerplatform te kry, stuur asseblief bewys van betaling en wag vir admin bevestiging.")
                }
              </p>
              
              {/* Show subscription type badge for monthly expired */}
              {subscription.status === "monthly_expired" && (
                <div className="bg-orange-100 border border-orange-300 rounded-xl p-3 mb-4">
                  <p className="text-orange-700 font-medium text-sm">
                    {i18n.language === "en" 
                      ? "📅 Monthly Subscription (R100/month)" 
                      : "📅 Maandelikse Subskripsie (R100/maand)"}
                  </p>
                </div>
              )}
              
              <div className="bg-blue-50 rounded-xl p-4 mb-6 text-left">
                <h3 className="font-bold text-blue-800 mb-3">
                  {i18n.language === "en" ? "Send proof of payment to:" : "Stuur bewys van betaling na:"}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-blue-700">
                    <Mail className="w-5 h-5" />
                    <a href="mailto:admin@leesisduidelik.co.za" className="font-medium hover:underline">
                      admin@leesisduidelik.co.za
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-blue-700">
                    <Phone className="w-5 h-5" />
                    <a href="https://wa.me/27753449273" target="_blank" rel="noopener noreferrer" className="font-medium hover:underline">
                      WhatsApp: 075 344 9273
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-50 rounded-xl p-4 mb-6">
                <p className="text-amber-800 text-sm">
                  {subscription.status === "monthly_expired"
                    ? (i18n.language === "en"
                        ? "Once your payment is re-confirmed by the administrator, your access will be restored for another 30 days."
                        : "Sodra jou betaling weer deur die administrateur bevestig is, sal jou toegang vir nog 30 dae herstel word.")
                    : (i18n.language === "en"
                        ? "Once your payment is confirmed by the administrator, you will receive full access to all learning activities."
                        : "Sodra jou betaling deur die administrateur bevestig is, sal jy volle toegang tot alle leeraktiwiteite ontvang.")
                  }
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="secondary" 
                  onClick={onLogout}
                  className="flex-1"
                  testId="pending-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {i18n.language === "en" ? "Logout" : "Teken Uit"}
                </Button>
                <Button 
                  variant="primary" 
                  onClick={() => window.location.reload()}
                  className="flex-1"
                  testId="pending-refresh-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {i18n.language === "en" ? "Check Status" : "Kyk Status"}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
        
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <div>
              <h1 className="font-heading text-xl font-bold">{t('dashboard.hello')}, {user?.name}!</h1>
              <p className="text-text-muted text-sm">{t('auth.grade')} {learnerGrade}</p>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSelector compact />
              <button onClick={onLogout} className="p-2 text-text-muted hover:text-accent-500" data-testid="logout-btn">
                <LogOut className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Subscription Banner */}
        {!subscription.active && subscription.status !== "pending_payment" && (
          <div className="bg-accent-500 text-white px-4 py-3">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <p>{t('subscription.trialExpired')}</p>
              <Button variant="secondary" onClick={() => navigate("/subscription")} className="!py-2 !px-4" testId="subscribe-banner-btn">
                {t('subscription.subscribe')}
              </Button>
            </div>
          </div>
        )}
        {/* Trial period banner - show for users with invitation code */}
        {subscription.active && subscription.type === "trial" && (
          <div className="bg-secondary-500 text-text-primary px-4 py-3">
            <div className="max-w-4xl mx-auto">
              <p>{t('subscription.trial')}: {subscription.days_left} {t('subscription.daysLeft')}</p>
            </div>
          </div>
        )}
        
        {/* BLOCKING OVERLAY for INITIAL reading test (first-time learners) 
            IMPORTANT: Only show AFTER account is activated (subscription.active) 
            Payment blocking takes priority over reading test */}
        {subscription.active && readingTestStatus?.needs_initial_test && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-8 max-w-lg w-full text-center shadow-2xl"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-4 text-primary-700">
                {i18n.language === "en" ? "Welcome! Let's Check Your Reading Level" : "Welkom! Kom Ons Toets Jou Leesvlak"}
              </h2>
              <p className="text-text-secondary mb-2">
                {i18n.language === "en" 
                  ? "Before you start, we need to determine your reading level so we can give you the right exercises." 
                  : "Voordat jy begin, moet ons jou leesvlak bepaal sodat ons vir jou die regte oefeninge kan gee."}
              </p>
              <p className="text-text-muted text-sm mb-6">
                {i18n.language === "en"
                  ? "This quick test will only take a few minutes."
                  : "Hierdie vinnige toets sal net 'n paar minute neem."}
              </p>
              <Button 
                variant="primary" 
                onClick={() => navigate("/reading-test")}
                className="w-full"
                testId="initial-reading-test-btn"
              >
                {i18n.language === "en" ? "Start Reading Test" : "Begin Leesvlaktoets"}
              </Button>
            </motion.div>
          </div>
        )}
        
        {/* Mandatory 3-month Reading Level Retest Banner */}
        {readingTestStatus?.needs_retest && (
          <div className="bg-purple-500 text-white px-4 py-3">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <p>{t('profile.timeForRetest')}</p>
              </div>
              <Button variant="secondary" onClick={() => navigate("/reading-test")} className="!py-2 !px-4" testId="mandatory-retest-btn">
                {t('profile.takeTestNow')}
              </Button>
            </div>
          </div>
        )}

        {/* BLOCKING OVERLAY for mandatory 3-month retest */}
        {readingTestStatus?.needs_retest && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-8 max-w-lg w-full text-center shadow-2xl"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-10 h-10 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-4 text-purple-700">
                {i18n.language === "en" ? "Time for Your Reading Test!" : "Tyd vir Jou Leesvlaktoets!"}
              </h2>
              <p className="text-text-secondary mb-2">
                {i18n.language === "en" 
                  ? "It's been 3 months since your last reading level test." 
                  : "Dit is 3 maande sedert jou laaste leesvlaktoets."}
              </p>
              <p className="text-text-secondary mb-6">
                {i18n.language === "en" 
                  ? "Complete the test to continue using the app and see your improvement!" 
                  : "Voltooi die toets om voort te gaan met die app en sien jou verbetering!"}
              </p>
              
              {/* Previous result info */}
              {readingTestStatus?.current_wpm && (
                <div className="bg-purple-50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-purple-700 mb-2">{i18n.language === "en" ? "Your previous result:" : "Jou vorige resultaat:"}</p>
                  <div className="flex items-center justify-center gap-6">
                    <div>
                      <p className="text-3xl font-bold text-purple-600">{readingTestStatus.current_wpm}</p>
                      <p className="text-xs text-purple-500">WPM</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-purple-600">{i18n.language === "en" ? "Grade" : "Graad"} {readingTestStatus.current_level}</p>
                      <p className="text-xs text-purple-500">{i18n.language === "en" ? "Reading Level" : "Leesvlak"}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <Button 
                onClick={() => navigate("/reading-test")} 
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                testId="start-mandatory-retest-btn"
              >
                {i18n.language === "en" ? "Start Reading Test Now" : "Begin Nou Met Leesvlaktoets"}
              </Button>
            </motion.div>
          </div>
        )}

        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Level Progress Modal */}
          {showProgressModal && levelProgress?.all_completed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                className="bg-white rounded-2xl p-6 max-w-md w-full text-center"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-10 h-10 text-white" />
                </div>
                <h2 className="font-heading text-2xl font-bold mb-2 text-primary-600">
                  {t('progress.congratulations')} 🎉
                </h2>
                <p className="text-text-secondary mb-6">
                  {t('progress.completedAll', { level: levelProgress.current_level })} {t('progress.whatNext')}
                </p>
                <div className="space-y-3">
                  {levelProgress.can_progress && (
                    <Button
                      onClick={async () => {
                        try {
                          const res = await api.post("/learner/progress-to-next-level");
                          toast.success(res.data.message);
                          setShowProgressModal(false);
                          window.location.reload();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || t('progress.couldNotProgress'));
                        }
                      }}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500"
                      testId="progress-to-next-level"
                    >
                      <ArrowUp className="w-5 h-5 mr-2" />
                      {t('progress.goToGrade', { level: levelProgress.next_level })}
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/reading-test")}
                    className="w-full"
                    testId="retake-test-modal"
                  >
                    <RefreshCw className="w-5 h-5 mr-2" />
                    {t('progressModal.retakeTest')}
                  </Button>
                  <button
                    onClick={() => setShowProgressModal(false)}
                    className="text-sm text-text-muted hover:text-text-secondary"
                    data-testid="close-progress-modal"
                  >
                    {t('progressModal.stayOnLevel', { level: levelProgress.current_level })}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Progress Summary */}
          <Card className="mb-6" testId="progress-card">
            <h2 className="font-heading text-lg font-bold mb-4">{t('dashboard.yourProgress')}</h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-primary-500">{progress?.exercises_completed || 0}</div>
                <div className="text-sm text-text-muted">{t('dashboard.exercises')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-secondary-500">{progress?.average_score || 0}%</div>
                <div className="text-sm text-text-muted">{t('dashboard.average')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-accent-500">{progress?.reading_wpm || 0}</div>
                <div className="text-sm text-text-muted">{t('dashboard.wpm')}</div>
              </div>
            </div>
            
            {/* Level Progress Bar */}
            {levelProgress && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{t('dashboard.levelProgress')}: {t('auth.grade')} {levelProgress.current_level}</span>
                  <span className="text-sm text-text-muted">
                    {levelProgress.completed_exercises}/{levelProgress.total_exercises} {t('dashboard.exercises').toLowerCase()}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${levelProgress.progress_percentage}%` }}
                    transition={{ duration: 0.5 }}
                    className={`h-3 rounded-full ${
                      levelProgress.all_completed 
                        ? "bg-gradient-to-r from-green-400 to-emerald-500"
                        : "bg-gradient-to-r from-primary-400 to-primary-600"
                    }`}
                  />
                </div>
                {levelProgress.all_completed && (
                  <p className="text-sm text-green-600 font-medium mt-2 flex items-center gap-1">
                    <Check className="w-4 h-4" /> {t('profile.allExercisesComplete')} 
                    <button 
                      onClick={() => setShowProgressModal(true)}
                      className="underline ml-1"
                    >
                      {t('profile.seeOptions')}
                    </button>
                  </p>
                )}
              </div>
            )}
            
            {/* Retake Reading Level Test Button */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t('profile.currentReadingLevel')}: {t('auth.grade')} {user?.current_reading_level || user?.grade}</p>
                  <p className="text-xs text-text-muted">{t('profile.tooHighOrLow')}</p>
                </div>
                <button
                  onClick={() => navigate("/reading-test")}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                  data-testid="retake-reading-test-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('profile.retakeTest')}
                </button>
              </div>
            </div>
            
            {/* Practice Level Choice - if reading level differs from school grade */}
            {practiceLevel?.can_change && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-medium mb-2">{t('profile.practiceLevel')}</p>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      try {
                        await api.put("/learner/practice-level", { practice_level: practiceLevel.current_reading_level });
                        toast.success(t('progress.levelChanged', { level: practiceLevel.current_reading_level }));
                        window.location.reload();
                      } catch (err) {
                        toast.error(t('progress.couldNotChange'));
                      }
                    }}
                    className={`flex-1 p-2 rounded-lg text-sm font-medium transition-colors ${
                      learnerGrade === practiceLevel.current_reading_level
                        ? "bg-green-500 text-white"
                        : "bg-green-100 text-green-700 hover:bg-green-200"
                    }`}
                    data-testid="switch-to-reading-level"
                  >
                    {t('auth.grade')} {practiceLevel.current_reading_level} ({t('profile.readingLevelLabel')})
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await api.put("/learner/practice-level", { practice_level: practiceLevel.school_grade });
                        toast.success(t('progress.levelChanged', { level: practiceLevel.school_grade }));
                        window.location.reload();
                      } catch (err) {
                        toast.error(t('progress.couldNotChange'));
                      }
                    }}
                    className={`flex-1 p-2 rounded-lg text-sm font-medium transition-colors ${
                      learnerGrade === practiceLevel.school_grade
                        ? "bg-purple-500 text-white"
                        : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                    }`}
                    data-testid="switch-to-school-grade"
                  >
                    {t('auth.grade')} {practiceLevel.school_grade} ({t('profile.schoolGradeLabel')})
                  </button>
                </div>
              </div>
            )}
          </Card>

          {/* Exercise Types */}
          <h2 className="font-heading text-lg font-bold mb-4">{t('dashboard.exercises')}</h2>
          {/* Audio helper message - only show if there's uploaded audio for this language */}
          {showAudioInstructions && Object.keys(exerciseInstructions).some(k => 
            k.endsWith(user?.preferred_language === "en" ? "_audio_url_en" : "_audio_url") && 
            exerciseInstructions[k]
          ) && (
            <p className="text-sm text-primary-600 bg-primary-50 rounded-lg p-2 mb-4 flex items-center gap-2">
              <Volume2 className="w-4 h-4" />
              {user?.preferred_language === "en" 
                ? "Click on any button to hear what it says!" 
                : "Klik op enige knoppie om te hoor wat dit sê!"}
            </p>
          )}
          <div className="grid grid-cols-2 gap-4">
            {exerciseTypes.map((ex) => {
              // Check if this exercise has uploaded audio
              const audioKey = user?.preferred_language === "en" ? `${ex.id}_audio_url_en` : `${ex.id}_audio_url`;
              const hasAudio = exerciseInstructions[audioKey];
              
              return (
              <Card
                key={ex.id}
                className="cursor-pointer"
                onClick={async () => {
                  // Define navigation function
                  const doNavigate = () => {
                    if (ex.id === "woordbou") {
                      navigate("/woordbou");
                    } else if (ex.id === "klanktoets") {
                      navigate("/klanktoets");
                    } else if (ex.id === "alfabet") {
                      navigate("/alfabet");
                    } else {
                      navigate(`/exercise/${ex.id}`);
                    }
                  };
                  
                  // Play oral instruction audio for ALL grades (if uploaded)
                  // Wait for audio to finish, then navigate
                  if (showAudioInstructions && hasAudio) {
                    await playExerciseAudio(ex.id, ex.title);
                  }
                  doNavigate();
                }}
                testId={`exercise-${ex.id}`}
              >
                <div className={`w-12 h-12 ${ex.color} rounded-xl flex items-center justify-center mb-3`}>
                  <ex.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-semibold flex items-center gap-2">
                  {ex.title}
                  {showAudioInstructions && hasAudio && <Volume2 className="w-4 h-4 text-primary-400" />}
                </h3>
                <p className="text-sm text-text-muted">{ex.desc}</p>
              </Card>
            )})}
          </div>
        </div>

        {/* Bottom Navigation */}
        <nav className="nav-bottom md:hidden">
          <div className="flex justify-around">
            <button className="nav-bottom-item active" data-testid="nav-home">
              <Home className="w-6 h-6" />
              <span className="text-xs mt-1">{user?.preferred_language === "en" ? "Home" : "Tuis"}</span>
            </button>
            <button className="nav-bottom-item" onClick={() => navigate("/progress")} data-testid="nav-progress">
              <TrendingUp className="w-6 h-6" />
              <span className="text-xs mt-1">{user?.preferred_language === "en" ? "Progress" : "Vordering"}</span>
            </button>
            <button className="nav-bottom-item" onClick={() => navigate("/subscription")} data-testid="nav-subscription">
              <CreditCard className="w-6 h-6" />
              <span className="text-xs mt-1">{user?.preferred_language === "en" ? "Subscription" : "Subskripsie"}</span>
            </button>
          </div>
        </nav>
      </div>
    </PageTransition>
  );
};

// Exercise Page
const ExercisePage = ({ user }) => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [exercises, setExercises] = useState([]);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [answers, setAnswers] = useState([]);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  
  // Reading settings state
  const learnerGrade = user?.current_reading_level || user?.grade || 1;
  const [readingSettings, setReadingSettings] = useState(() => {
    // Grade-specific defaults
    if (learnerGrade <= 3) {
      return { fontSize: 22, lineHeight: 2.2, darkMode: false };
    } else if (learnerGrade <= 7) {
      return { fontSize: 18, lineHeight: 1.8, darkMode: false };
    } else {
      return { fontSize: 16, lineHeight: 1.6, darkMode: false };
    }
  });
  
  // Redo functionality state
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [lastSubmissionResult, setLastSubmissionResult] = useState(null);
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
  const [instructions, setInstructions] = useState({});
  
  const exerciseType = window.location.pathname.split("/").pop();
  // Use learner's preferred language from user object, fallback to i18n, then default to "af"
  const userLanguage = user?.preferred_language || i18n.language || "af";
  
  // Helper to get instruction in correct language
  const getInstruction = (type) => {
    if (!instructions) return null;
    if (userLanguage === "en") {
      return instructions[`${type}_en`] || instructions[type];
    }
    return instructions[type];
  };
  
  const typeLabels = {
    comprehension: userLanguage === "en" ? "Comprehension" : "Begrip",
    reading: userLanguage === "en" ? "Read Aloud" : "Hardoplees",
    listening: userLanguage === "en" ? "Listening" : "Luister",
    spelling: "Speltoets"
  };

  useEffect(() => {
    // Load exercise instructions
    api.get("/exercise-instructions")
      .then(res => setInstructions(res.data))
      .catch(() => {});
    
    api.get(`/exercises/${exerciseType}`)
      .then(res => {
        setExercises(res.data.exercises || []);
        if (res.data.exercises?.length > 0) {
          setCurrentExercise(res.data.exercises[0]);
        }
        setLoadError(null);
      })
      .catch(err => {
        if (err.response?.status === 402) {
          toast.error(t('toast.subscriptionRequired'));
          navigate("/subscription");
        } else if (err.response?.status === 403) {
          toast.error(err.response?.data?.detail || t('toast.accessDenied'));
          navigate("/dashboard");
        } else {
          setLoadError(err.response?.data?.detail || t('toast.couldNotLoad'));
        }
      })
      .finally(() => setLoading(false));
  }, [exerciseType, navigate]);

  // Auto-play oral instructions for Grade 1-4
  const isGrade1to4 = learnerGrade <= 4;
  
  useEffect(() => {
    // Only for Gr 1-4 learners, play oral instruction audio if available
    if (isGrade1to4 && instructions) {
      const audioKey = userLanguage === "en" 
        ? `${exerciseType}_audio_url_en` 
        : `${exerciseType}_audio_url`;
      const audioUrl = instructions[audioKey];
      
      if (audioUrl) {
        // Small delay to let page render first
        const timer = setTimeout(() => {
          const audio = new Audio(`${api.defaults.baseURL?.replace('/api', '')}${audioUrl}`);
          audio.play().catch(err => console.log("Auto-play blocked:", err));
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [isGrade1to4, instructions, exerciseType, userLanguage]);

  // Reset state when exercise changes
  useEffect(() => {
    setAttemptNumber(1);
    setLastSubmissionResult(null);
    setShowCorrectAnswers(false);
    setAnswers([]);
  }, [currentExercise?.id]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        chunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setRecording(true);
    } catch (err) {
      toast.error(t('toast.couldNotGetMic'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const submitRecording = async () => {
    if (!audioBlob || !currentExercise) return;
    
    const formData = new FormData();
    formData.append("file", audioBlob, "recording.webm");
    formData.append("text_id", currentExercise.id);
    formData.append("expected_text", currentExercise.content);
    
    try {
      const res = await api.post("/reading-aloud/analyze", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setAnalysisResult(res.data);
      if (res.data.analysis_success) {
        confetti({ particleCount: 50, spread: 60 });
        toast.success(t('toast.wellDone'));
      } else {
        toast.error(res.data.feedback_message || t('toast.tryAgain'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.couldNotAnalyze'));
    }
  };

  const submitAnswers = async () => {
    try {
      const res = await api.post("/exercises/submit", {
        exercise_id: currentExercise.id,
        answers: answers,
        attempt_number: attemptNumber
      });
      
      // Calculate displayed score - 50% max on second attempt
      let displayScore = res.data.score;
      if (attemptNumber === 2) {
        displayScore = Math.round(res.data.score * 0.5);
      }
      
      setLastSubmissionResult({
        ...res.data,
        displayScore,
        attemptNumber
      });
      
      if (attemptNumber === 2) {
        // Show correct answers after second attempt
        setShowCorrectAnswers(true);
        toast.success(t('exercises.secondAttempt') + `: ${displayScore}% (50% ${t('of')} ${res.data.score}%)`);
      } else {
        toast.success(res.data.message);
      }
      
      confetti({ particleCount: 50, spread: 60 });
    } catch (err) {
      toast.error(t('toast.couldNotSubmit'));
    }
  };

  // Handle redo - second attempt
  const handleRedo = () => {
    if (attemptNumber >= 2) {
      toast.error(t('toast.alreadyTwoAttempts'));
      return;
    }
    setAttemptNumber(2);
    setAnswers([]);
    setLastSubmissionResult(null);
    toast.info(userLanguage === "en" ? "Second chance! Note: This attempt counts only 50%" : "Tweede kans! Let op: Hierdie poging tel slegs 50%");
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{userLanguage === "en" ? "Loading..." : "Laai..."}</div>;
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <button onClick={() => navigate("/dashboard")} className="p-2" data-testid="back-btn">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="font-heading text-xl font-bold">{typeLabels[exerciseType]}</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6">
          {loading ? (
            <Card className="text-center py-12">
              <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-500" />
              <p className="text-text-muted">{userLanguage === "en" ? "Loading exercises..." : "Laai oefeninge..."}</p>
            </Card>
          ) : exercises.length === 0 ? (
            <Card className="text-center py-8">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2">Geen Oefeninge Beskikbaar</h3>
              <p className="text-text-secondary mb-2">
                Daar is tans geen {typeLabels[exerciseType]?.toLowerCase() || 'oefeninge'} vir jou graad nie.
              </p>
              <p className="text-sm text-text-muted mb-6">{userLanguage === "en" ? "The admin needs to add content first." : "Die admin moet eers inhoud byvoeg."}</p>
              <Button onClick={() => navigate("/dashboard")} testId="back-to-dashboard">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {userLanguage === "en" ? "Back to Dashboard" : "Terug na Dashboard"}
              </Button>
            </Card>
          ) : (
            <Card testId="exercise-content">
              <h2 className="font-heading text-lg font-bold mb-4">{currentExercise?.title}</h2>
              
              {/* Instructions from Admin - NOT shown for spelling as it has its own section */}
              {getInstruction(exerciseType) && exerciseType !== "spelling" && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                  <div className="text-sm text-blue-800">
                    <div className="flex items-center justify-between mb-2">
                      <strong>📋 {userLanguage === "en" ? "Instructions:" : "Instruksies:"}</strong>
                      {/* Audio play button for Gr 1-4 */}
                      {isGrade1to4 && instructions[userLanguage === "en" ? `${exerciseType}_audio_url_en` : `${exerciseType}_audio_url`] && (
                        <button
                          onClick={() => {
                            const audioKey = userLanguage === "en" ? `${exerciseType}_audio_url_en` : `${exerciseType}_audio_url`;
                            const audioUrl = instructions[audioKey];
                            if (audioUrl) {
                              const audio = new Audio(`${api.defaults.baseURL?.replace('/api', '')}${audioUrl}`);
                              audio.play();
                            }
                          }}
                          className="flex items-center gap-1 px-3 py-1 bg-blue-200 text-blue-800 rounded-lg text-xs font-medium hover:bg-blue-300"
                          data-testid="play-instructions-btn"
                        >
                          <Volume2 className="w-4 h-4" />
                          {userLanguage === "en" ? "Listen" : "Luister"}
                        </button>
                      )}
                    </div>
                    <div 
                      className="prose prose-sm prose-blue max-w-none"
                      dangerouslySetInnerHTML={{ 
                        __html: sanitizeHtml((getInstruction(exerciseType) || "")
                          .replace(/&/g, "&amp;")
                          .replace(/</g, "&lt;")
                          .replace(/>/g, "&gt;")
                          .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
                          .replace(/__(.+?)__/g, "<strong>$1</strong>")
                          .split("\n")
                          .map(line => {
                            if (line.trim().startsWith("• ") || line.trim().startsWith("- ")) {
                              return `<li class="ml-4">${line.trim().substring(2)}</li>`;
                            }
                            return line;
                          })
                          .join("\n")
                          .replace(/(<li[^>]*>.*<\/li>\n?)+/g, (match) => `<ul class="list-disc pl-4 my-2">${match}</ul>`)
                          .replace(/\n/g, "<br>"))
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Content Display - HIDE for listening and spelling tests */}
              {exerciseType !== "listening" && exerciseType !== "spelling" && (
                <div className="bg-slate-50 rounded-2xl p-6 mb-6">
                  {/* Show image for Grade 1-3 if available */}
                  {currentExercise?.image_url && (
                    <div className="mb-4 text-center">
                      <img 
                        src={`${API}${currentExercise.image_url}`} 
                        alt={currentExercise.title} 
                        className="max-w-full md:max-w-md mx-auto rounded-xl shadow-md"
                      />
                    </div>
                  )}
                  
                  {/* Reading Controls - Interactive */}
                  <div className="flex items-center justify-between mb-4 p-3 bg-slate-100 rounded-xl">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-text-muted">{userLanguage === "en" ? "Text:" : "Teks:"}</span>
                      <button
                        onClick={() => setReadingSettings(s => ({...s, fontSize: Math.max(14, s.fontSize - 2)}))}
                        className="w-8 h-8 bg-white rounded-lg shadow-sm flex items-center justify-center text-lg font-bold hover:bg-slate-50"
                        title={userLanguage === "en" ? "Smaller text" : "Kleiner teks"}
                      >
                        A-
                      </button>
                      <span className="text-sm font-medium w-8 text-center">{readingSettings.fontSize}</span>
                      <button
                        onClick={() => setReadingSettings(s => ({...s, fontSize: Math.min(32, s.fontSize + 2)}))}
                        className="w-8 h-8 bg-white rounded-lg shadow-sm flex items-center justify-center text-lg font-bold hover:bg-slate-50"
                        title={userLanguage === "en" ? "Larger text" : "Groter teks"}
                      >
                        A+
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-text-muted">{userLanguage === "en" ? "Spacing:" : "Spasiëring:"}</span>
                      <select
                        value={readingSettings.lineHeight}
                        onChange={(e) => setReadingSettings(s => ({...s, lineHeight: parseFloat(e.target.value)}))}
                        className="text-sm bg-white border rounded-lg px-2 py-1"
                      >
                        <option value="1.4">{userLanguage === "en" ? "Compact" : "Kompak"}</option>
                        <option value="1.8">{userLanguage === "en" ? "Normal" : "Normaal"}</option>
                        <option value="2.2">{userLanguage === "en" ? "Wide" : "Wyd"}</option>
                        <option value="2.6">{userLanguage === "en" ? "Extra Wide" : "Ekstra Wyd"}</option>
                      </select>
                    </div>
                    
                    <button
                      onClick={() => setReadingSettings(s => ({...s, darkMode: !s.darkMode}))}
                      className={`w-8 h-8 rounded-lg shadow-sm flex items-center justify-center ${
                        readingSettings.darkMode ? 'bg-slate-800 text-yellow-400' : 'bg-white text-slate-600'
                      }`}
                      title={readingSettings.darkMode ? "Lig modus" : "Donker modus"}
                    >
                      {readingSettings.darkMode ? '☀️' : '🌙'}
                    </button>
                  </div>
                  
                  {/* Formatted Text Content */}
                  <div 
                    className={`rounded-2xl p-6 transition-colors ${
                      readingSettings.darkMode 
                        ? 'bg-slate-800 text-slate-100' 
                        : 'bg-white border border-slate-200'
                    }`}
                    style={{
                      fontSize: `${readingSettings.fontSize}px`,
                      lineHeight: readingSettings.lineHeight,
                    }}
                  >
                    {/* Format content with proper paragraphs */}
                    {currentExercise?.content?.split(/\n\n+/).map((paragraph, idx) => (
                      <p 
                        key={idx} 
                        className={`mb-4 last:mb-0 ${learnerGrade <= 3 ? 'font-medium' : ''}`}
                        style={{ maxWidth: '65ch' }}
                      >
                        {learnerGrade <= 7 && (
                          <span className={`inline-block w-6 h-6 rounded-full text-center text-sm mr-2 ${
                            readingSettings.darkMode ? 'bg-slate-700' : 'bg-primary-100 text-primary-700'
                          }`}>
                            {idx + 1}
                          </span>
                        )}
                        {paragraph.trim()}
                      </p>
                    ))}
                  </div>
                  
                  {/* Grade-specific helper for young learners */}
                  {learnerGrade <= 3 && (
                    <p className="text-center text-sm text-text-muted mt-3">
                      💡 {userLanguage === "en" 
                        ? "Read slowly and clearly. You can make the text bigger with the A+ button." 
                        : "Lees stadig en duidelik. Jy kan die teks groter maak met die A+ knoppie."}
                    </p>
                  )}
                </div>
              )}
              
              {/* Audio instruction for listening/spelling */}
              {(exerciseType === "listening" || exerciseType === "spelling") && (
                <div className="bg-primary-50 rounded-2xl p-6 mb-6 text-center">
                  <Headphones className="w-12 h-12 text-primary-500 mx-auto mb-3" />
                  <p className="text-lg font-medium text-primary-700">
                    {exerciseType === "listening" 
                      ? (userLanguage === "en" ? "Listen to the audio and answer the questions" : "Luister na die oudio en beantwoord die vrae")
                      : (userLanguage === "en" ? "Listen to the word and type it correctly" : "Luister na die woord en skryf dit korrek")}
                  </p>
                  <p className="text-sm text-primary-600 mt-2">
                    {userLanguage === "en" ? "Click on the audio player below to start" : "Klik op die oudio speler hieronder om te begin"}
                  </p>
                </div>
              )}

              {/* Reading Aloud */}
              {exerciseType === "reading" && (
                <div className="space-y-4">
                  <div className="flex justify-center gap-4">
                    {!recording ? (
                      <Button onClick={startRecording} testId="start-recording-btn">
                        <Mic className="w-5 h-5 mr-2" /> {userLanguage === "en" ? "Start Recording" : "Begin Opname"}
                      </Button>
                    ) : (
                      <Button onClick={stopRecording} className="bg-accent-500" testId="stop-recording-btn">
                        <Pause className="w-5 h-5 mr-2" /> {userLanguage === "en" ? "Stop Recording" : "Stop Opname"}
                      </Button>
                    )}
                  </div>
                  
                  {recording && (
                    <div className="flex items-center justify-center gap-2">
                      <div className="recording-indicator" />
                      <span className="text-accent-500 font-medium">{userLanguage === "en" ? "Recording..." : "Neem op..."}</span>
                    </div>
                  )}
                  
                  {audioBlob && !recording && (
                    <div className="text-center">
                      <audio controls src={URL.createObjectURL(audioBlob)} className="mx-auto mb-4" />
                      <Button onClick={submitRecording} testId="submit-recording-btn">
                        {userLanguage === "en" ? "Submit for Analysis" : "Dien In vir Ontleding"}
                      </Button>
                    </div>
                  )}
                  
                  {analysisResult && (
                    <Card className={`${analysisResult.analysis_success ? 'bg-primary-50 border-primary-500' : 'bg-yellow-50 border-yellow-500'}`}>
                      <h3 className="font-heading font-bold mb-2">
                        {analysisResult.analysis_success 
                          ? (userLanguage === "en" ? "Your Result" : "Jou Resultaat") 
                          : (userLanguage === "en" ? "Could Not Analyze" : "Kon Nie Ontleed Nie")}
                      </h3>
                      
                      {/* Feedback message */}
                      {analysisResult.feedback_message && (
                        <div className={`p-3 rounded-lg mb-4 ${analysisResult.analysis_success ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          <p className="font-medium">{analysisResult.feedback_message}</p>
                        </div>
                      )}
                      
                      {/* Quality issues - show prominently */}
                      {analysisResult.quality_issues?.length > 0 && (
                        <div className="bg-accent-50 border border-accent-200 rounded-lg p-3 mb-4">
                          <p className="text-sm font-semibold text-accent-700 mb-2">{userLanguage === "en" ? "Recording issues:" : "Probleme met opname:"}</p>
                          <ul className="space-y-1">
                            {analysisResult.quality_issues.map((issue, i) => (
                              <li key={i} className="text-sm text-accent-600 flex items-start gap-2">
                                <span className="text-accent-500">⚠️</span>
                                {issue}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {analysisResult.analysis_success && (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-2xl font-bold text-primary-500">{analysisResult.wpm}</div>
                              <div className="text-sm text-text-muted">WPM</div>
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-secondary-500">{analysisResult.word_count}</div>
                              <div className="text-sm text-text-muted">Woorde</div>
                            </div>
                          </div>
                          
                          {analysisResult.errors?.length > 0 && (
                            <div className="mt-4">
                              <p className="text-sm font-semibold">Moontlike foute:</p>
                              <ul className="text-sm text-text-muted">
                                {analysisResult.errors.slice(0, 5).map((e, i) => (
                                  <li key={i}>{e}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Try again button if failed */}
                      {!analysisResult.analysis_success && (
                        <Button 
                          onClick={() => {
                            setAnalysisResult(null);
                            setAudioBlob(null);
                          }} 
                          className="w-full mt-4"
                          testId="retry-recording-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          {userLanguage === "en" ? "Try Again" : "Probeer Weer"}
                        </Button>
                      )}
                    </Card>
                  )}
                </div>
              )}

              {/* Comprehension/Spelling - Questions from database */}
              {(exerciseType === "comprehension" || exerciseType === "spelling") && (
                <div className="space-y-4">
                  {/* Audio player for spelling tests */}
                  {exerciseType === "spelling" && currentExercise?.audio_url && (
                    <div className="bg-secondary-50 rounded-xl p-4 mb-4">
                      <p className="text-sm font-semibold text-secondary-700 mb-2">🔊 {userLanguage === "en" ? "Listen to the word:" : "Luister na die woord:"}</p>
                      <audio 
                        controls 
                        src={`${API}${currentExercise.audio_url}`} 
                        className="w-full"
                      />
                    </div>
                  )}
                  
                  {exerciseType === "spelling" && !currentExercise?.audio_url && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4 text-center">
                      <p className="text-yellow-700">⚠️ {userLanguage === "en" ? "No audio available for this test - ask your teacher to read the words to you" : "Geen oudio beskikbaar vir hierdie toets nie - vra jou onderwyser om die woorde vir jou te lees"}</p>
                    </div>
                  )}
                  
                  {/* Special handling for spelling tests without questions but with content (word list) */}
                  {exerciseType === "spelling" && (!currentExercise?.questions || currentExercise.questions.length === 0) && currentExercise?.content ? (
                    <>
                      {/* Calculate word list - use admin-specified count if available */}
                      {(() => {
                        const allWords = currentExercise.content.split(/[,\n]+/).filter(w => w.trim());
                        const wordCount = currentExercise.spelling_word_count && currentExercise.spelling_word_count > 0 
                          ? Math.min(currentExercise.spelling_word_count, allWords.length)
                          : allWords.length;
                        const wordsToUse = allWords.slice(0, wordCount);
                        
                        return (
                          <>
                            <Card className="bg-purple-50 border-purple-200">
                              <h4 className="font-semibold text-purple-700 mb-2">📝 {userLanguage === "en" ? "Spelling Test Instructions:" : "Speltoets Instruksies:"}</h4>
                              {getInstruction("spelling") ? (
                                <div 
                                  className="text-purple-600 text-sm prose prose-sm prose-purple max-w-none"
                                  dangerouslySetInnerHTML={{ 
                                    __html: sanitizeHtml((getInstruction("spelling") || "")
                                      .replace(/&/g, "&amp;")
                                      .replace(/</g, "&lt;")
                                      .replace(/>/g, "&gt;")
                                      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
                                      .replace(/__(.+?)__/g, "<strong>$1</strong>")
                                      .split("\n")
                                      .map(line => {
                                        if (line.trim().startsWith("• ") || line.trim().startsWith("- ")) {
                                          return `<li class="ml-4">${line.trim().substring(2)}</li>`;
                                        }
                                        return line;
                                      })
                                      .join("\n")
                                      .replace(/(<li[^>]*>.*<\/li>\n?)+/g, (match) => `<ul class="list-disc pl-4 my-1">${match}</ul>`)
                                      .replace(/\n/g, "<br>"))
                                  }}
                                />
                              ) : (
                                <p className="text-purple-600 text-sm">
                                  {userLanguage === "en" 
                                    ? <>Listen to each word and type it in the box. There are <strong>{wordCount}</strong> words.</>
                                    : <>Luister na elke woord en skryf dit in die blokkie. Daar is <strong>{wordCount}</strong> woorde.</>
                                  }
                                </p>
                              )}
                            </Card>
                            
                            {/* Parse words from content and create input fields */}
                            {wordsToUse.map((word, idx) => {
                              // Clean the word - remove numbering, asterisks, and definitions
                              const cleanWord = word
                                .replace(/^\d+\.\s*/, '')  // Remove "1. " prefix
                                .replace(/\*\*/g, '')       // Remove **bold** markers
                                .split(' - ')[0]            // Remove " - definition" part
                                .split(' – ')[0]            // Remove en-dash definitions too
                                .trim();
                              
                              // Check if there's uploaded audio for this word
                              const hasUploadedAudio = currentExercise?.word_audio?.[idx];
                              
                              return (
                                <div key={idx} className="bg-slate-50 rounded-xl p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <p className="font-semibold text-primary-600">{userLanguage === "en" ? "Word" : "Woord"} {idx + 1}</p>
                                    {hasUploadedAudio ? (
                                      /* Play uploaded audio */
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const audio = new Audio(`${API}${currentExercise.word_audio[idx]}`);
                                          audio.play();
                                        }}
                                        className="px-4 py-2 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors"
                                        data-testid={`play-word-btn-${idx + 1}`}
                                      >
                                        🔊 {userLanguage === "en" ? "Play Word" : "Speel Woord"}
                                      </button>
                                    ) : (
                                      /* Fallback to TTS if no uploaded audio */
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          try {
                                            const btn = document.getElementById(`play-word-${idx}`);
                                            if (btn) btn.innerHTML = '<span class="animate-spin">⏳</span>';
                                            
                                            const response = await api.post('/tts/spelling-word', {
                                              text: cleanWord,
                                              voice: 'nova'
                                            });
                                            
                                            const audioData = response.data.audio_base64;
                                            const audio = new Audio(`data:audio/mp3;base64,${audioData}`);
                                            audio.play();
                                            
                                            if (btn) btn.innerHTML = '🔊 ' + (userLanguage === "en" ? "Play (AI)" : "Speel (KI)");
                                          } catch (err) {
                                            toast.error(userLanguage === "en" ? "Could not play word" : "Kon nie woord speel nie");
                                            const btn = document.getElementById(`play-word-${idx}`);
                                            if (btn) btn.innerHTML = '🔊 ' + (userLanguage === "en" ? "Play (AI)" : "Speel (KI)");
                                          }
                                        }}
                                        id={`play-word-${idx}`}
                                        className="px-4 py-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors"
                                        data-testid={`play-word-btn-${idx + 1}`}
                                        title={userLanguage === "en" ? "AI voice (teacher can upload custom audio)" : "KI stem (onderwyser kan eie oudio oplaai)"}
                                      >
                                        🔊 {userLanguage === "en" ? "Play (AI)" : "Speel (KI)"}
                                      </button>
                                    )}
                                  </div>
                                  <input
                                    type="text"
                                    className="input-field"
                                    placeholder={userLanguage === "en" ? `Type word ${idx + 1} here...` : `Skryf woord ${idx + 1} hier...`}
                                    value={answers[idx]?.answer || answers[idx] || ""}
                                    onChange={(e) => {
                                      const newAnswers = [...answers];
                                      newAnswers[idx] = { 
                                        question_id: `word_${idx}`, 
                                        answer: e.target.value,
                                        correct_answer: cleanWord.toLowerCase()
                                      };
                                      setAnswers(newAnswers);
                                    }}
                                    data-testid={`spelling-word-${idx + 1}`}
                                  />
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </>
                  ) : (!currentExercise?.questions || currentExercise.questions.length === 0) ? (
                    /* No questions AND no content - show error */
                    <Card className="bg-yellow-50 border-yellow-200 text-center">
                      <X className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                      <h3 className="font-heading font-bold text-yellow-700 mb-2">
                        {userLanguage === "en" ? "No Questions Available" : "Geen Vrae Beskikbaar"}
                      </h3>
                      <p className="text-yellow-600">
                        {userLanguage === "en" 
                          ? `This ${exerciseType === "comprehension" ? "comprehension test" : "spelling test"} doesn't have questions yet.`
                          : `Hierdie ${exerciseType === "comprehension" ? "begripstoets" : "speltoets"} het nog nie vrae nie.`
                        }
                      </p>
                      <p className="text-sm text-yellow-500 mt-2">
                        {userLanguage === "en" ? "Contact the admin to add questions." : "Kontak die admin om vrae by te voeg."}
                      </p>
                      <Button 
                        variant="secondary" 
                        onClick={() => navigate("/dashboard")} 
                        className="mt-4"
                      >
                        {userLanguage === "en" ? "Back to Dashboard" : "Terug na Dashboard"}
                      </Button>
                    </Card>
                  ) : currentExercise?.questions && currentExercise.questions.length > 0 ? (
                    <>
                      {currentExercise.questions.map((question, idx) => (
                        <div key={question.id || idx} className="bg-slate-50 rounded-xl p-4">
                          <p className="font-semibold mb-3">{idx + 1}. {question.question_text}</p>
                          
                          {question.question_type === "multiple_choice" && question.options ? (
                            <div className="space-y-2">
                              {question.options.map((option, optIdx) => (
                                <label key={optIdx} className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-primary-50 transition-colors">
                                  <input
                                    type="radio"
                                    name={`question-${question.id}`}
                                    value={option}
                                    checked={answers[idx]?.answer === option}
                                    onChange={() => {
                                      const newAnswers = [...answers];
                                      newAnswers[idx] = { question_id: question.id, answer: option };
                                      setAnswers(newAnswers);
                                    }}
                                    className="w-5 h-5"
                                  />
                                  <span>{option}</span>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <Input
                              placeholder={userLanguage === "en" ? "Type your answer here..." : "Tik jou antwoord hier..."}
                              value={answers[idx]?.answer || ""}
                              onChange={(e) => {
                                const newAnswers = [...answers];
                                newAnswers[idx] = { question_id: question.id, answer: e.target.value };
                                setAnswers(newAnswers);
                              }}
                              testId={`answer-${idx}`}
                            />
                          )}
                        </div>
                      ))}
                    </>
                  ) : null}
                  
                  {/* Attempt indicator for comprehension */}
                  {attemptNumber === 2 && !lastSubmissionResult && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                      <p className="text-yellow-800 font-semibold flex items-center gap-2">
                        <RefreshCw className="w-5 h-5" />
                        Tweede Kans - Hierdie poging tel slegs 50%
                      </p>
                    </div>
                  )}
                  
                  {/* Show result and redo option */}
                  {lastSubmissionResult ? (
                    <div className="space-y-4">
                      <Card className={`${lastSubmissionResult.displayScore >= 70 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                        <div className="text-center">
                          <div className="text-4xl font-bold mb-2">
                            {lastSubmissionResult.displayScore}%
                          </div>
                          <p className="text-text-secondary">
                            {lastSubmissionResult.attemptNumber === 2 
                              ? `(50% van ${lastSubmissionResult.score}% op 2de poging)` 
                              : `${lastSubmissionResult.earned_points}/${lastSubmissionResult.total_points} ${userLanguage === "en" ? "points" : "punte"}`}
                          </p>
                        </div>
                      </Card>
                      
                      {/* Show correct answers after 2nd attempt */}
                      {showCorrectAnswers && currentExercise?.questions && (
                        <Card className="bg-blue-50 border-blue-200">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <Check className="w-5 h-5 text-blue-600" />
                            {userLanguage === "en" ? "Correct Answers:" : "Korrekte Antwoorde:"}
                          </h4>
                          <div className="space-y-2">
                            {currentExercise.questions.map((q, idx) => {
                              const userAnswer = lastSubmissionResult.graded_answers?.[idx];
                              return (
                                <div key={q.id || idx} className="p-3 bg-white rounded-lg">
                                  <p className="font-medium text-sm">{idx + 1}. {q.question_text}</p>
                                  <div className="mt-1 flex items-center gap-2">
                                    <span className="text-sm text-green-600 font-semibold">
                                      {userLanguage === "en" ? "Answer:" : "Antwoord:"} {q.correct_answer}
                                    </span>
                                    {userAnswer && (
                                      <span className={`text-xs px-2 py-0.5 rounded ${userAnswer.is_correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {userAnswer.is_correct 
                                          ? (userLanguage === "en" ? '✓ Correct' : '✓ Korrek') 
                                          : `✗ ${userLanguage === "en" ? "Your answer:" : "Jou antwoord:"} ${userAnswer.user_answer || '-'}`}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </Card>
                      )}
                      
                      {/* Redo button - only show after first attempt, not after second */}
                      {attemptNumber === 1 && lastSubmissionResult.score < 100 && (
                        <Button 
                          onClick={handleRedo} 
                          variant="secondary" 
                          className="w-full"
                          testId="redo-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          {userLanguage === "en" ? "Try Again (2nd chance - 50% score)" : "Probeer Weer (2de kans - tel 50%)"}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button 
                      onClick={submitAnswers} 
                      className="w-full" 
                      testId="submit-answers-btn"
                      disabled={
                        exerciseType === "spelling" 
                          ? (!currentExercise?.content || answers.filter(a => a?.answer || a).length === 0)
                          : (!currentExercise?.questions || currentExercise.questions.length === 0)
                      }
                    >
                      {userLanguage === "en" ? "Submit" : "Dien In"} {attemptNumber === 2 && (userLanguage === "en" ? "(50% score)" : "(50% telling)")}
                    </Button>
                  )}
                </div>
              )}

              {/* Listening - Audio Player with Questions */}
              {exerciseType === "listening" && (
                <div className="space-y-4">
                  {/* Audio Player */}
                  {currentExercise?.audio_url ? (
                    <div className="bg-slate-50 rounded-xl p-4 mb-6">
                      <p className="text-sm text-text-muted mb-2">{userLanguage === "en" ? "Listen to the recording:" : "Luister na die opname:"}</p>
                      <audio 
                        controls 
                        src={`${API}${currentExercise.audio_url}`} 
                        className="w-full"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 mb-6 p-4 bg-slate-50 rounded-xl">
                      <Volume2 className="w-6 h-6 text-primary-500" />
                      <span className="text-text-muted">{userLanguage === "en" ? "No audio uploaded" : "Geen oudio opgelaai nie"}</span>
                    </div>
                  )}
                  
                  {/* Questions */}
                  {currentExercise?.questions && currentExercise.questions.length > 0 ? (
                    currentExercise.questions.map((question, idx) => (
                      <div key={question.id || idx} className="bg-slate-50 rounded-xl p-4">
                        <p className="font-semibold mb-3">{idx + 1}. {question.question_text}</p>
                        
                        {question.question_type === "multiple_choice" && question.options ? (
                          <div className="space-y-2">
                            {question.options.map((option, optIdx) => (
                              <label key={optIdx} className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-primary-50 transition-colors">
                                <input
                                  type="radio"
                                  name={`question-${question.id}`}
                                  value={option}
                                  checked={answers[idx]?.answer === option}
                                  onChange={() => {
                                    const newAnswers = [...answers];
                                    newAnswers[idx] = { question_id: question.id, answer: option };
                                    setAnswers(newAnswers);
                                  }}
                                  className="w-5 h-5"
                                />
                                <span>{option}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <Input
                            placeholder={userLanguage === "en" ? "Type your answer here..." : "Tik jou antwoord hier..."}
                            value={answers[idx]?.answer || ""}
                            onChange={(e) => {
                              const newAnswers = [...answers];
                              newAnswers[idx] = { question_id: question.id, answer: e.target.value };
                              setAnswers(newAnswers);
                            }}
                            testId={`listening-answer-${idx}`}
                          />
                        )}
                      </div>
                    ))
                  ) : (
                    <>
                      {/* Listening: 5 questions */}
                      {[1, 2, 3, 4, 5].map((num) => (
                        <Input
                          key={num}
                          label={`Antwoord ${num}`}
                          value={answers[num - 1]?.answer || ""}
                          onChange={(e) => {
                            const newAnswers = [...answers];
                            newAnswers[num - 1] = { question_id: `q${num}`, answer: e.target.value };
                            setAnswers(newAnswers);
                          }}
                          testId={`listening-answer-${num}`}
                        />
                      ))}
                    </>
                  )}
                  <Button onClick={submitAnswers} className="w-full" testId="submit-listening-btn">
                    Dien In
                  </Button>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </PageTransition>
  );
};

// Woordbou Exercise Page - Drag and drop letters to build words (Grade 1-3)
const WoordbouExercisePage = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [exercises, setExercises] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [builtWord, setBuiltWord] = useState([]);
  const [availableLetters, setAvailableLetters] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [startTime] = useState(Date.now());
  const [myStats, setMyStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  useEffect(() => {
    // Fetch exercises
    api.get("/woordbou")
      .then(res => {
        setExercises(res.data.exercises || []);
        if (res.data.exercises?.length > 0) {
          setAvailableLetters(shuffleArray([...res.data.exercises[0].available_letters]));
        }
      })
      .catch(err => {
        if (err.response?.status === 402) {
          toast.error(t('subscription.trialExpired'));
          navigate("/subscription");
        }
      })
      .finally(() => setLoading(false));
    
    // Fetch stats and leaderboard
    api.get("/woordbou/my-stats").then(res => setMyStats(res.data)).catch(() => {});
    api.get("/woordbou/leaderboard").then(res => setLeaderboard(res.data.leaderboard || [])).catch(() => {});
  }, [navigate]);

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const currentExercise = exercises[currentIndex];

  const handleLetterClick = (letter, index) => {
    // Add letter to built word
    setBuiltWord([...builtWord, letter]);
    // Remove from available
    const newAvailable = [...availableLetters];
    newAvailable.splice(index, 1);
    setAvailableLetters(newAvailable);
  };

  const handleRemoveLetter = (index) => {
    // Remove from built word
    const letter = builtWord[index];
    const newBuilt = [...builtWord];
    newBuilt.splice(index, 1);
    setBuiltWord(newBuilt);
    // Add back to available
    setAvailableLetters([...availableLetters, letter]);
  };

  const handleSubmit = async () => {
    if (!currentExercise) return;
    
    const timeSeconds = (Date.now() - startTime) / 1000;
    try {
      const res = await api.post("/woordbou/submit", {
        woordbou_id: currentExercise.id,
        built_word: builtWord.join(""),
        time_seconds: timeSeconds
      });
      setResult(res.data);
      setShowResult(true);
      if (res.data.correct) {
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
        // Refresh stats after correct answer
        api.get("/woordbou/my-stats").then(r => setMyStats(r.data)).catch(() => {});
        api.get("/woordbou/leaderboard").then(r => setLeaderboard(r.data.leaderboard || [])).catch(() => {});
      }
    } catch (err) {
      toast.error(t('toast.couldNotSubmit'));
    }
  };

  const handleNext = () => {
    if (currentIndex < exercises.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      setBuiltWord([]);
      setAvailableLetters(shuffleArray([...exercises[nextIndex].available_letters]));
      setShowResult(false);
      setResult(null);
    } else {
      navigate("/dashboard");
      toast.success(t('toast.allExercisesComplete'));
    }
  };

  const handleReset = () => {
    setBuiltWord([]);
    setAvailableLetters(shuffleArray([...currentExercise.available_letters]));
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader className="w-8 h-8 animate-spin" /></div>;
  }

  if (exercises.length === 0) {
    return (
      <PageTransition>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="text-center">
            <BookOpen className="w-12 h-12 text-primary-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">{t('woordbou.noExercises')}</h2>
            <p className="text-text-muted mb-4">{t('woordbou.noExercisesDesc')}</p>
            <Button onClick={() => navigate("/dashboard")} testId="back-dashboard">{t('common.backToDashboard')}</Button>
          </Card>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 p-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-text-muted hover:text-text-primary">
              <ArrowLeft className="w-5 h-5" /> {t('common.back')}
            </button>
            <span className="text-sm font-medium bg-white px-3 py-1 rounded-full">
              {currentIndex + 1} / {exercises.length}
            </span>
          </div>

          <Card className="mb-6" testId="woordbou-exercise">
            <h2 className="font-heading text-xl font-bold text-center mb-4">{currentExercise?.title}</h2>
            
            {/* Image if available */}
            {currentExercise?.image_url && (
              <div className="flex justify-center mb-6">
                <img 
                  src={`${API}${currentExercise.image_url}`} 
                  alt="Woordkaart" 
                  className="max-w-xs rounded-xl border-4 border-emerald-200 shadow-lg"
                />
              </div>
            )}

            {/* Built word area */}
            <div className="bg-emerald-50 rounded-xl p-6 mb-6 min-h-[80px]">
              <p className="text-xs text-text-muted mb-2 text-center">{t('woordbou.buildWord')}:</p>
              <div className="flex justify-center gap-2 flex-wrap">
                {builtWord.length === 0 ? (
                  <span className="text-text-muted">Tik op letters hieronder om te begin bou</span>
                ) : (
                  builtWord.map((letter, idx) => (
                    <motion.button
                      key={idx}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      onClick={() => handleRemoveLetter(idx)}
                      className="w-14 h-14 bg-emerald-500 text-white text-2xl font-bold rounded-xl shadow-md hover:bg-emerald-400 transition-colors flex items-center justify-center uppercase"
                      whileTap={{ scale: 0.9 }}
                      data-testid={`built-letter-${idx}`}
                    >
                      {letter}
                    </motion.button>
                  ))
                )}
              </div>
            </div>

            {/* Available letters */}
            <div className="mb-6">
              <p className="text-xs text-text-muted mb-2 text-center">Beskikbare letters (tik om by te voeg):</p>
              <div className="flex justify-center gap-3 flex-wrap">
                {availableLetters.map((letter, idx) => (
                  <motion.button
                    key={idx}
                    onClick={() => handleLetterClick(letter, idx)}
                    className="w-14 h-14 bg-white border-2 border-emerald-300 text-emerald-700 text-2xl font-bold rounded-xl shadow hover:bg-emerald-100 transition-colors flex items-center justify-center uppercase"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    data-testid={`available-letter-${idx}`}
                  >
                    {letter}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Actions */}
            {!showResult ? (
              <div className="flex gap-3">
                <Button variant="secondary" onClick={handleReset} testId="reset-woordbou" className="flex-1">
                  {t('woordbou.tryAgain')}
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={builtWord.length === 0}
                  testId="submit-woordbou"
                  className="flex-1"
                >
                  {t('woordbou.checkAnswer')}
                </Button>
              </div>
            ) : (
              <div className="text-center">
                {result?.correct ? (
                  <div className="bg-green-100 rounded-xl p-6 mb-4">
                    <Check className="w-16 h-16 text-green-500 mx-auto mb-2" />
                    <h3 className="font-heading text-2xl font-bold text-green-700">{t('woordbou.correct')}</h3>
                    <p className="text-green-600">{result.message}</p>
                  </div>
                ) : (
                  <div className="bg-orange-100 rounded-xl p-6 mb-4">
                    <X className="w-16 h-16 text-orange-500 mx-auto mb-2" />
                    <h3 className="font-heading text-xl font-bold text-orange-700">{t('woordbou.tryAgain')}</h3>
                    <p className="text-orange-600">{result?.message}</p>
                  </div>
                )}
                <Button onClick={handleNext} testId="next-woordbou" className="w-full">
                  {currentIndex < exercises.length - 1 
                    ? (i18n.language === "en" ? "Next Word" : "Volgende Woord") 
                    : (i18n.language === "en" ? "Done!" : "Klaar!")}
                </Button>
              </div>
            )}
          </Card>
          
          {/* Woordbou Challenge Stats */}
          {myStats && (
            <Card className="mb-4" testId="woordbou-stats">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">{t('woordbou.challenge')}</h3>
                  <p className="text-xs text-text-muted">{i18n.language === "en" ? "Compete with other learners!" : "Kompeteer met ander leerders!"}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-2 mb-4">
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{myStats.correct_count}</p>
                  <p className="text-xs text-text-muted">{i18n.language === "en" ? "Correct" : "Korrek"}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-orange-600">{myStats.streak}</p>
                  <p className="text-xs text-text-muted">{t('woordbou.streak')}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{myStats.accuracy}%</p>
                  <p className="text-xs text-text-muted">Akkuraatheid</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-purple-600">#{myStats.rank || "-"}</p>
                  <p className="text-xs text-text-muted">Rang</p>
                </div>
              </div>
              
              <Button 
                variant="secondary" 
                onClick={() => setShowLeaderboard(!showLeaderboard)} 
                className="w-full"
                testId="toggle-leaderboard"
              >
                <Trophy className="w-4 h-4 mr-2" />
                {showLeaderboard ? "Verberg Ranglys" : "Wys Ranglys"}
              </Button>
              
              {showLeaderboard && leaderboard.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="font-semibold text-sm">Top 10 Woordbouers</h4>
                  {leaderboard.map((entry, idx) => (
                    <div 
                      key={entry.learner_id}
                      className={`flex items-center gap-3 p-2 rounded-lg ${
                        entry.learner_id === user?.user_id ? "bg-amber-100 border border-amber-300" : "bg-slate-50"
                      }`}
                    >
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        idx === 0 ? "bg-yellow-400 text-yellow-900" :
                        idx === 1 ? "bg-gray-300 text-gray-700" :
                        idx === 2 ? "bg-orange-300 text-orange-800" :
                        "bg-slate-200 text-slate-600"
                      }`}>
                        {entry.rank}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{entry.name}</p>
                        {entry.school && <p className="text-xs text-text-muted">{entry.school}</p>}
                      </div>
                      <span className="font-bold text-emerald-600">{entry.correct_count}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </PageTransition>
  );
};

// Klanktoets Exercise Page - See word and record yourself saying it (Grade 1-6)
const KlanktoetsExercisePage = ({ user }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [exercises, setExercises] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [completedItems, setCompletedItems] = useState([]);
  
  // Multi-step flow: 1 = Say word, 2 = Spell word, 3 = Submit/Result
  const [currentStep, setCurrentStep] = useState(1);
  const [spellingInput, setSpellingInput] = useState("");
  const [spellingCorrect, setSpellingCorrect] = useState(null);

  // Rainbow colors for variety
  const itemColors = [
    "from-red-400 to-red-500",
    "from-orange-400 to-orange-500",
    "from-amber-400 to-amber-500",
    "from-yellow-400 to-yellow-500",
    "from-lime-400 to-lime-500",
    "from-green-400 to-green-500",
    "from-emerald-400 to-emerald-500",
    "from-teal-400 to-teal-500",
    "from-cyan-400 to-cyan-500",
    "from-sky-400 to-sky-500",
    "from-blue-400 to-blue-500",
    "from-indigo-400 to-indigo-500",
    "from-violet-400 to-violet-500",
    "from-purple-400 to-purple-500",
    "from-fuchsia-400 to-fuchsia-500",
    "from-pink-400 to-pink-500",
  ];

  useEffect(() => {
    api.get("/klanktoets")
      .then(res => {
        setExercises(res.data.exercises || []);
      })
      .catch(err => {
        if (err.response?.status === 402) {
          toast.error(t('subscription.trialExpired'));
          navigate("/subscription");
        }
      })
      .finally(() => setLoading(false));
  }, [navigate, t]);

  const currentExercise = exercises[currentIndex];
  const currentItem = currentExercise?.items?.[currentItemIndex];
  const totalItems = currentExercise?.items?.length || 0;

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setRecordedAudio(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      toast.error(t('toast.couldNotGetMic'));
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  // Play recorded audio
  const playRecordedAudio = () => {
    if (recordedAudio) {
      const url = URL.createObjectURL(recordedAudio);
      const audio = new Audio(url);
      audio.play();
    }
  };

  // Submit recording for AI analysis
  const submitRecording = async () => {
    if (!recordedAudio || !currentItem) return;
    
    setSubmitting(true);
    setAiAnalysis(null);
    try {
      const formData = new FormData();
      formData.append('file', recordedAudio, `klanktoets_${currentItem.correct_answer}.webm`);
      formData.append('klanktoets_id', currentExercise.id);
      formData.append('item_index', currentItemIndex);
      formData.append('expected_word', currentItem.correct_answer);
      formData.append('learner_id', user?.id || 'anonymous');
      
      const response = await api.post('/learner/submit-klanktoets-recording', formData);
      
      setAiAnalysis({
        isCorrect: response.data.is_correct,
        confidence: response.data.confidence,
        feedback: response.data.feedback,
        transcription: response.data.transcription
      });
      
      // Move to step 2 (spelling) after recording is analyzed
      setCurrentStep(2);
      
    } catch (err) {
      toast.error(t('toast.couldNotSubmit'));
    }
    setSubmitting(false);
  };

  // Move to next item
  const handleNextItem = () => {
    setRecordedAudio(null);
    setAiAnalysis(null);
    setCurrentStep(1);
    setSpellingInput("");
    setSpellingCorrect(null);
    
    if (currentItemIndex < totalItems - 1) {
      setCurrentItemIndex(prev => prev + 1);
    } else {
      // All items done, show final result
      const correctCount = completedItems.filter(i => i.correct).length + (aiAnalysis?.isCorrect && spellingCorrect ? 1 : 0);
      setResult({
        score: Math.round((correctCount / totalItems) * 100),
        correct_count: correctCount,
        total_points: totalItems
      });
      setShowResult(true);
    }
  };

  // Check spelling
  const checkSpelling = () => {
    const expected = currentItem?.correct_answer?.toLowerCase().trim();
    const userInput = spellingInput.toLowerCase().trim();
    const isCorrect = expected === userInput;
    setSpellingCorrect(isCorrect);
    
    // Track completed item
    setCompletedItems(prev => [...prev, { 
      itemIndex: currentItemIndex, 
      correct: isCorrect && aiAnalysis?.isCorrect
    }]);
    
    if (isCorrect && aiAnalysis?.isCorrect) {
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
    }
    
    setCurrentStep(3);
  };

  // Move to next exercise set
  const handleNextExercise = () => {
    if (currentIndex < exercises.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setCurrentItemIndex(0);
      setCompletedItems([]);
      setShowResult(false);
      setResult(null);
      setRecordedAudio(null);
      setAiAnalysis(null);
    } else {
      navigate("/dashboard");
      toast.success(t('toast.allExercisesComplete'));
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader className="w-8 h-8 animate-spin" /></div>;
  }

  if (exercises.length === 0) {
    return (
      <PageTransition>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="text-center">
            <Volume2 className="w-12 h-12 text-orange-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">{t('klanktoets.noExercises')}</h2>
            <p className="text-text-muted mb-4">{t('klanktoets.noExercisesDesc')}</p>
            <Button onClick={() => navigate("/dashboard")} testId="back-dashboard">{t('common.backToDashboard')}</Button>
          </Card>
        </div>
      </PageTransition>
    );
  }

  const currentColor = itemColors[currentItemIndex % itemColors.length];

  return (
    <PageTransition>
      <div className={`min-h-screen bg-gradient-to-br ${currentColor} p-4 transition-all duration-500`}>
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={() => navigate("/dashboard")} 
              className="flex items-center gap-2 text-white/80 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" /> {t('common.back')}
            </button>
            <span className="text-sm font-medium bg-white/20 text-white px-3 py-1 rounded-full">
              {i18n.language === "en" ? "Word" : "Woord"} {currentItemIndex + 1} / {totalItems}
            </span>
          </div>

          {!showResult ? (
            <motion.div
              key={`item-${currentItemIndex}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              {/* Exercise Title */}
              <h2 className="text-white/80 text-lg mb-4">{currentExercise?.title}</h2>
              
              {/* Big Word Display */}
              <motion.div 
                className="bg-white rounded-3xl shadow-2xl p-8 mb-6"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
              >
                {/* Image first if available - BIG display */}
                {currentItem?.image_url && (
                  <div className="mb-6">
                    <img 
                      src={`${API}${currentItem.image_url}`} 
                      alt={currentItem.correct_answer}
                      className="w-48 h-48 sm:w-64 sm:h-64 object-cover rounded-2xl mx-auto border-4 border-slate-100 shadow-lg"
                    />
                  </div>
                )}
                
                <p className="text-slate-500 text-sm mb-2">
                  {i18n.language === "en" ? "Say this word:" : "Sê hierdie woord:"}
                </p>
                <motion.p 
                  className={`text-6xl sm:text-8xl font-bold bg-gradient-to-br ${currentColor} bg-clip-text text-transparent`}
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {currentItem?.correct_answer || "..."}
                </motion.p>
              </motion.div>

              {/* Step indicators */}
              <div className="flex justify-center gap-2 mb-4">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${currentStep >= 1 ? 'bg-white text-slate-700' : 'bg-white/30 text-white'}`}>
                  1. {i18n.language === "en" ? "Say" : "Sê"}
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${currentStep >= 2 ? 'bg-white text-slate-700' : 'bg-white/30 text-white'}`}>
                  2. {i18n.language === "en" ? "Spell" : "Spel"}
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${currentStep >= 3 ? 'bg-white text-slate-700' : 'bg-white/30 text-white'}`}>
                  3. {i18n.language === "en" ? "Done" : "Klaar"}
                </div>
              </div>

              {/* STEP 1: Say the word */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <p className="text-white/80 text-center mb-2">
                    {i18n.language === "en" ? "Step 1: Say the word out loud" : "Stap 1: Sê die woord hardop"}
                  </p>
                  
                  {!isRecording && !recordedAudio && (
                    <motion.button
                      onClick={startRecording}
                      className="w-full py-4 bg-white text-slate-700 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transition-all"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid="klank-record-btn"
                    >
                      <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse" />
                      {i18n.language === "en" ? "Tap to Record" : "Tik om Op te Neem"}
                    </motion.button>
                  )}

                  {isRecording && (
                    <motion.button
                      onClick={stopRecording}
                      className="w-full py-4 bg-red-500 text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-3 shadow-lg"
                      animate={{ scale: [1, 1.02, 1] }}
                      transition={{ duration: 0.5, repeat: Infinity }}
                      data-testid="klank-stop-btn"
                    >
                      <div className="w-4 h-4 bg-white rounded-sm" />
                      {i18n.language === "en" ? "Stop Recording" : "Stop Opname"}
                    </motion.button>
                  )}

                  {recordedAudio && (
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <button
                          onClick={playRecordedAudio}
                          className="flex-1 py-3 bg-white/20 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
                        >
                          <Play className="w-5 h-5" />
                          {i18n.language === "en" ? "Play" : "Speel"}
                        </button>
                        <button
                          onClick={() => setRecordedAudio(null)}
                          className="flex-1 py-3 bg-white/20 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
                        >
                          <RefreshCw className="w-5 h-5" />
                          {i18n.language === "en" ? "Redo" : "Herdoen"}
                        </button>
                      </div>
                      <button
                        onClick={submitRecording}
                        disabled={submitting}
                        className="w-full py-4 bg-white text-green-600 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 shadow-lg disabled:opacity-50"
                        data-testid="klank-submit-btn"
                      >
                        {submitting ? (
                          <Loader className="w-5 h-5 animate-spin" />
                        ) : (
                          <ChevronRight className="w-5 h-5" />
                        )}
                        {i18n.language === "en" ? "Next: Spell the word" : "Volgende: Spel die woord"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2: Spell the word */}
              {currentStep === 2 && (
                <motion.div 
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {/* Show AI feedback from recording */}
                  {aiAnalysis && (
                    <div className={`p-3 rounded-xl text-center ${aiAnalysis.isCorrect ? 'bg-green-500/30' : 'bg-yellow-500/30'}`}>
                      <span className="text-2xl mr-2">{aiAnalysis.isCorrect ? '🌟' : '👂'}</span>
                      <span className="text-white font-medium">{aiAnalysis.feedback}</span>
                    </div>
                  )}
                  
                  <p className="text-white/80 text-center">
                    {i18n.language === "en" ? "Step 2: Now spell the word" : "Stap 2: Spel nou die woord"}
                  </p>
                  
                  <input
                    type="text"
                    value={spellingInput}
                    onChange={(e) => setSpellingInput(e.target.value)}
                    placeholder={i18n.language === "en" ? "Type the word here..." : "Tik die woord hier..."}
                    className="w-full px-6 py-4 text-2xl text-center font-bold rounded-2xl border-4 border-white/30 bg-white/10 text-white placeholder-white/50 focus:border-white focus:outline-none"
                    autoFocus
                    data-testid="klank-spelling-input"
                  />
                  
                  <button
                    onClick={checkSpelling}
                    disabled={!spellingInput.trim()}
                    className="w-full py-4 bg-white text-green-600 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 shadow-lg disabled:opacity-50"
                    data-testid="klank-check-spelling-btn"
                  >
                    <Check className="w-5 h-5" />
                    {i18n.language === "en" ? "Check Spelling" : "Kontroleer Spelling"}
                  </button>
                </motion.div>
              )}

              {/* STEP 3: Results */}
              {currentStep === 3 && (
                <motion.div 
                  className={`p-6 rounded-2xl ${spellingCorrect && aiAnalysis?.isCorrect ? 'bg-green-500/30' : 'bg-orange-500/30'}`}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                >
                  <div className="text-center">
                    <div className="text-6xl mb-3">
                      {spellingCorrect && aiAnalysis?.isCorrect ? '🏆' : spellingCorrect || aiAnalysis?.isCorrect ? '👍' : '💪'}
                    </div>
                    
                    {/* Pronunciation result */}
                    <div className={`mb-2 p-2 rounded-lg ${aiAnalysis?.isCorrect ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      <span className="text-white">
                        {i18n.language === "en" ? "Pronunciation:" : "Uitspraak:"} 
                        {aiAnalysis?.isCorrect 
                          ? (i18n.language === "en" ? " ✓ Correct!" : " ✓ Korrek!") 
                          : (i18n.language === "en" ? " ✗ Try again" : " ✗ Probeer weer")}
                      </span>
                    </div>
                    
                    {/* Spelling result */}
                    <div className={`mb-4 p-2 rounded-lg ${spellingCorrect ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      <span className="text-white">
                        {i18n.language === "en" ? "Spelling:" : "Spelling:"} 
                        {spellingCorrect 
                          ? (i18n.language === "en" ? " ✓ Correct!" : " ✓ Korrek!") 
                          : (i18n.language === "en" ? ` ✗ Should be: ${currentItem?.correct_answer}` : ` ✗ Moet wees: ${currentItem?.correct_answer}`)}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap justify-center gap-3">
                      <button
                        onClick={playRecordedAudio}
                        className="px-5 py-2 bg-white/30 rounded-full text-white font-medium flex items-center gap-2"
                      >
                        <Play className="w-5 h-5" />
                        {i18n.language === "en" ? "Play Recording" : "Speel Opname"}
                      </button>
                      <button
                        onClick={() => { setCurrentStep(1); setRecordedAudio(null); setAiAnalysis(null); setSpellingInput(""); setSpellingCorrect(null); }}
                        className="px-5 py-2 bg-white/30 rounded-full text-white font-medium flex items-center gap-2"
                      >
                        <RefreshCw className="w-5 h-5" />
                        {i18n.language === "en" ? "Try Again" : "Probeer Weer"}
                      </button>
                      <button
                        onClick={handleNextItem}
                        className="px-5 py-2 bg-white text-slate-700 rounded-full font-semibold flex items-center gap-2"
                      >
                        <ChevronRight className="w-5 h-5" />
                        {currentItemIndex < totalItems - 1 
                          ? (i18n.language === "en" ? "Next Word" : "Volgende Woord")
                          : (i18n.language === "en" ? "See Results" : "Sien Resultate")}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Progress dots */}
              <div className="flex justify-center gap-2 mt-6">
                {currentExercise?.items?.map((_, idx) => (
                  <div 
                    key={idx} 
                    className={`w-3 h-3 rounded-full transition-all ${
                      idx < currentItemIndex 
                        ? 'bg-white' 
                        : idx === currentItemIndex 
                          ? 'bg-white scale-125' 
                          : 'bg-white/30'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          ) : (
            /* Final Results */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="text-center">
                <div className={`text-6xl mb-4 ${result.score >= 80 ? '' : result.score >= 50 ? '' : ''}`}>
                  {result.score >= 80 ? '🏆' : result.score >= 50 ? '👍' : '💪'}
                </div>
                <h2 className="font-heading text-2xl font-bold mb-2">
                  {i18n.language === "en" ? "Well Done!" : "Goed Gedoen!"}
                </h2>
                <div className={`text-5xl font-bold mb-2 ${
                  result.score >= 80 ? 'text-green-500' : result.score >= 50 ? 'text-yellow-500' : 'text-orange-500'
                }`}>
                  {result.correct_count} / {result.total_points}
                </div>
                <p className="text-text-muted mb-6">
                  {i18n.language === "en" ? "words correct" : "woorde korrek"}
                </p>
                <Button onClick={handleNextExercise} className="w-full" testId="next-klanktoets">
                  {currentIndex < exercises.length - 1 
                    ? (i18n.language === "en" ? "Next Exercise" : "Volgende Oefening")
                    : (i18n.language === "en" ? "Back to Dashboard" : "Terug na Dashboard")}
                </Button>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </PageTransition>
  );
};

// Alfabet Helper Page for Grade 1-3
const AlphabetPage = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [alphabet, setAlphabet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [audioRef] = useState(new Audio());
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null); // Stores AI feedback after recording submission

  // Rainbow colors for the letter grid (child-friendly)
  const letterColors = [
    { bg: "from-red-400 to-red-500", text: "text-white", selected: "bg-red-500" },
    { bg: "from-orange-400 to-orange-500", text: "text-white", selected: "bg-orange-500" },
    { bg: "from-amber-400 to-amber-500", text: "text-white", selected: "bg-amber-500" },
    { bg: "from-yellow-400 to-yellow-500", text: "text-yellow-900", selected: "bg-yellow-500" },
    { bg: "from-lime-400 to-lime-500", text: "text-white", selected: "bg-lime-500" },
    { bg: "from-green-400 to-green-500", text: "text-white", selected: "bg-green-500" },
    { bg: "from-emerald-400 to-emerald-500", text: "text-white", selected: "bg-emerald-500" },
    { bg: "from-teal-400 to-teal-500", text: "text-white", selected: "bg-teal-500" },
    { bg: "from-cyan-400 to-cyan-500", text: "text-white", selected: "bg-cyan-500" },
    { bg: "from-sky-400 to-sky-500", text: "text-white", selected: "bg-sky-500" },
    { bg: "from-blue-400 to-blue-500", text: "text-white", selected: "bg-blue-500" },
    { bg: "from-indigo-400 to-indigo-500", text: "text-white", selected: "bg-indigo-500" },
    { bg: "from-violet-400 to-violet-500", text: "text-white", selected: "bg-violet-500" },
    { bg: "from-purple-400 to-purple-500", text: "text-white", selected: "bg-purple-500" },
    { bg: "from-fuchsia-400 to-fuchsia-500", text: "text-white", selected: "bg-fuchsia-500" },
    { bg: "from-pink-400 to-pink-500", text: "text-white", selected: "bg-pink-500" },
    { bg: "from-rose-400 to-rose-500", text: "text-white", selected: "bg-rose-500" },
  ];

  // Rainbow colors for the grid tiles
  const getColorForIndex = (index) => {
    return letterColors[index % letterColors.length];
  };

  // Use a consistent blue/teal color for the fullscreen modal (user preference)
  const getFullscreenColor = (index) => {
    // Always return a sea-green/teal color for the modal, regardless of the tile color
    return "from-teal-500 to-cyan-600";
  };

  useEffect(() => {
    const lang = user?.preferred_language || "af";
    api.get(`/alphabet?language=${lang}`)
      .then(res => setAlphabet(res.data))
      .catch(err => console.error("Error loading alphabet:", err))
      .finally(() => setLoading(false));
    
    return () => {
      audioRef.pause();
      audioRef.src = "";
    };
  }, [user]);

  const playAudio = (audioUrl) => {
    if (audioUrl) {
      audioRef.src = `${API}${audioUrl}`;
      audioRef.play().catch(e => console.log("Audio play error:", e));
    }
  };

  const selectLetter = (letter, index) => {
    setSelectedLetter({ ...letter, colorIndex: index });
    setShowFullScreen(true);
    setRecordedAudio(null);
    setAiAnalysis(null); // Reset AI analysis when selecting a new letter
    if (letter.audio_url) {
      playAudio(letter.audio_url);
    }
  };

  const closeFullScreen = () => {
    setShowFullScreen(false);
    setRecordedAudio(null);
    setAiAnalysis(null); // Reset AI analysis when closing
    if (isRecording) {
      stopRecording();
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setRecordedAudio(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      toast.error(t('toast.couldNotGetMic'));
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  // Submit recording for analysis
  const submitRecording = async () => {
    if (!recordedAudio || !selectedLetter) return;
    
    setSubmitting(true);
    setAiAnalysis(null);
    try {
      const formData = new FormData();
      formData.append('file', recordedAudio, `letter_${selectedLetter.letter}.webm`);
      formData.append('letter', selectedLetter.letter);
      formData.append('learner_id', user?.id || 'anonymous');
      formData.append('expected_word', selectedLetter.word || selectedLetter.letter);
      
      const response = await api.post('/learner/submit-alphabet-recording', formData);
      
      // Store the AI analysis result
      setAiAnalysis({
        isCorrect: response.data.is_correct,
        confidence: response.data.confidence,
        feedback: response.data.feedback,
        transcription: response.data.transcription
      });
      
      if (response.data.is_correct) {
        toast.success(t('toast.wellDone'));
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      } else {
        toast.info(response.data.feedback);
      }
    } catch (err) {
      toast.error(t('toast.couldNotSubmit'));
    }
    setSubmitting(false);
  };

  // Play recorded audio
  const playRecordedAudio = () => {
    if (recordedAudio) {
      const url = URL.createObjectURL(recordedAudio);
      const audio = new Audio(url);
      audio.play();
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('loading')}</div>;
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 pb-24 md:pb-8">
        {/* Full Screen Letter Modal - Sea Green/Blue */}
        <AnimatePresence>
          {showFullScreen && selectedLetter && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={`fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br ${getFullscreenColor(selectedLetter.colorIndex)}`}
              onClick={closeFullScreen}
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="text-center p-8 max-w-2xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button */}
                <button
                  onClick={closeFullScreen}
                  className="absolute top-4 right-4 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white text-2xl"
                  data-testid="close-fullscreen-letter"
                >
                  ✕
                </button>

                {/* Giant Letter */}
                <motion.div 
                  className="w-40 h-40 sm:w-52 sm:h-52 mx-auto bg-white rounded-3xl flex items-center justify-center shadow-2xl mb-6"
                  animate={{ rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <span className={`text-[100px] sm:text-[140px] font-bold bg-gradient-to-br ${getFullscreenColor(selectedLetter.colorIndex)} bg-clip-text text-transparent`}>
                    {selectedLetter.letter}
                  </span>
                </motion.div>

                {/* Large Image */}
                {selectedLetter.image_url && (
                  <motion.div 
                    className="w-40 h-40 sm:w-52 sm:h-52 mx-auto bg-white rounded-3xl overflow-hidden shadow-2xl mb-6"
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <img 
                      src={`${API}${selectedLetter.image_url}`} 
                      alt={selectedLetter.word}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                )}

                {/* Word Display */}
                <motion.p 
                  className="text-3xl sm:text-5xl font-bold text-white mb-4 drop-shadow-lg"
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  {selectedLetter.letter} {user?.preferred_language === "en" ? "is for" : "is vir"} <span className="underline decoration-wavy">{selectedLetter.word || "..."}</span>
                </motion.p>

                {/* Audio & Recording Controls */}
                <motion.div 
                  className="flex flex-wrap justify-center gap-3 mt-6"
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  {/* Listen Button */}
                  {selectedLetter.audio_url && (
                    <button
                      onClick={() => playAudio(selectedLetter.audio_url)}
                      className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-full text-white font-semibold flex items-center gap-2"
                      data-testid="play-letter-audio-fullscreen"
                    >
                      <Volume2 className="w-6 h-6" /> 
                      {user?.preferred_language === "en" ? "Listen" : "Luister"}
                    </button>
                  )}

                  {/* Record Button */}
                  {!isRecording && !recordedAudio && (
                    <button
                      onClick={startRecording}
                      className="px-6 py-3 bg-red-500 hover:bg-red-600 rounded-full text-white font-semibold flex items-center gap-2 animate-pulse"
                      data-testid="record-letter-btn"
                    >
                      <Mic className="w-6 h-6" /> 
                      {user?.preferred_language === "en" ? "Record Me!" : "Neem My Op!"}
                    </button>
                  )}

                  {/* Stop Recording Button */}
                  {isRecording && (
                    <button
                      onClick={stopRecording}
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-full text-white font-semibold flex items-center gap-2"
                      data-testid="stop-recording-btn"
                    >
                      <div className="w-4 h-4 bg-white rounded-sm animate-pulse" /> 
                      {user?.preferred_language === "en" ? "Stop" : "Stop"}
                    </button>
                  )}
                </motion.div>

                {/* Recorded Audio Controls */}
                {recordedAudio && !aiAnalysis && (
                  <motion.div 
                    className="mt-6 p-4 bg-white/20 rounded-2xl"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                  >
                    <p className="text-white font-semibold mb-3">
                      {user?.preferred_language === "en" ? "🎉 Great! Listen to yourself:" : "🎉 Mooi so! Luister na jouself:"}
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                      <button
                        onClick={playRecordedAudio}
                        className="px-5 py-2 bg-white/30 hover:bg-white/40 rounded-full text-white font-medium flex items-center gap-2"
                      >
                        <Play className="w-5 h-5" /> 
                        {user?.preferred_language === "en" ? "Play" : "Speel"}
                      </button>
                      <button
                        onClick={() => setRecordedAudio(null)}
                        className="px-5 py-2 bg-white/30 hover:bg-white/40 rounded-full text-white font-medium flex items-center gap-2"
                      >
                        <RefreshCw className="w-5 h-5" /> 
                        {user?.preferred_language === "en" ? "Try Again" : "Probeer Weer"}
                      </button>
                      <button
                        onClick={submitRecording}
                        disabled={submitting}
                        className="px-5 py-2 bg-green-500 hover:bg-green-600 rounded-full text-white font-medium flex items-center gap-2 disabled:opacity-50"
                        data-testid="submit-recording-btn"
                      >
                        {submitting ? (
                          <Loader className="w-5 h-5 animate-spin" />
                        ) : (
                          <Check className="w-5 h-5" />
                        )}
                        {user?.preferred_language === "en" ? "Submit!" : "Indien!"}
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* AI Analysis Result */}
                {aiAnalysis && (
                  <motion.div 
                    className={`mt-6 p-6 rounded-2xl ${aiAnalysis.isCorrect ? 'bg-green-500/30' : 'bg-orange-500/30'}`}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                  >
                    <div className="text-center">
                      <div className="text-6xl mb-3">
                        {aiAnalysis.isCorrect ? '🌟' : '💪'}
                      </div>
                      <p className="text-white text-xl font-bold mb-2">
                        {aiAnalysis.feedback}
                      </p>
                      <div className="flex flex-wrap justify-center gap-3 mt-4">
                        <button
                          onClick={playRecordedAudio}
                          className="px-5 py-2 bg-white/30 hover:bg-white/40 rounded-full text-white font-medium flex items-center gap-2"
                        >
                          <Play className="w-5 h-5" /> 
                          {user?.preferred_language === "en" ? "Play My Recording" : "Speel My Opname"}
                        </button>
                        <button
                          onClick={() => { setRecordedAudio(null); setAiAnalysis(null); }}
                          className="px-5 py-2 bg-white/30 hover:bg-white/40 rounded-full text-white font-medium flex items-center gap-2"
                        >
                          <RefreshCw className="w-5 h-5" /> 
                          {user?.preferred_language === "en" ? "Try Again" : "Probeer Weer"}
                        </button>
                        {aiAnalysis.isCorrect && (
                          <button
                            onClick={closeFullScreen}
                            className="px-5 py-2 bg-white/50 hover:bg-white/60 rounded-full text-white font-medium flex items-center gap-2"
                          >
                            <Check className="w-5 h-5" /> 
                            {user?.preferred_language === "en" ? "Next Letter" : "Volgende Letter"}
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Tap to close hint */}
                <p className="text-white/60 mt-6 text-sm">
                  {user?.preferred_language === "en" ? "Tap anywhere to close" : "Tik enige plek om te sluit"}
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <button onClick={() => navigate("/learner")} className="p-2 hover:bg-slate-100 rounded-xl" data-testid="back-from-alphabet">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="font-heading text-xl font-bold">{t('alfabet.title')}</h1>
              <p className="text-text-muted text-sm">{t('alfabet.clickToLearn')}</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Selected Letter Preview (smaller version) */}
          {selectedLetter && !showFullScreen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-6"
            >
              <Card className={`bg-gradient-to-br ${getFullscreenColor(selectedLetter.colorIndex)} border-0 cursor-pointer`} onClick={() => setShowFullScreen(true)}>
                <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
                  {/* Large Letter */}
                  <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                    <span className={`text-5xl font-bold bg-gradient-to-br ${getFullscreenColor(selectedLetter.colorIndex)} bg-clip-text text-transparent`}>
                      {selectedLetter.letter}
                    </span>
                  </div>
                  
                  {/* Image */}
                  {selectedLetter.image_url && (
                    <div className="w-24 h-24 bg-white rounded-2xl overflow-hidden shadow-lg">
                      <img 
                        src={`${API}${selectedLetter.image_url}`} 
                        alt={selectedLetter.word}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Word and tap hint */}
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-white mb-2 drop-shadow-md">
                      {selectedLetter.letter} {user?.preferred_language === "en" ? "is for" : "is vir"} {selectedLetter.word || "..."}
                    </p>
                    <p className="text-white/70 text-sm">
                      {user?.preferred_language === "en" ? "👆 Tap to see bigger & record yourself!" : "👆 Tik om groter te sien & jouself op te neem!"}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Alphabet Grid - Rainbow colors */}
          <Card>
            <h3 className="font-heading font-bold text-lg mb-4 text-center">{t('alfabet.clickToLearn')}</h3>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 gap-3">
              {alphabet.map((item, index) => {
                const color = getColorForIndex(index);
                return (
                  <motion.button
                    key={item.letter}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => selectLetter(item, index)}
                    className={`w-16 h-16 sm:w-18 sm:h-18 rounded-xl flex items-center justify-center text-2xl sm:text-3xl font-bold transition-all shadow-md ${
                      selectedLetter?.letter === item.letter
                        ? `${color.selected} text-white ring-4 ring-offset-2 ring-${color.selected.replace('bg-', '')}`
                        : `bg-gradient-to-br ${color.bg} ${color.text} hover:shadow-lg`
                    }`}
                    data-testid={`alphabet-letter-${item.letter}`}
                  >
                    {item.letter}
                  </motion.button>
                );
              })}
            </div>
          </Card>

          {/* Instructions */}
          <div className="mt-6 p-4 bg-white/80 rounded-xl text-center">
            <p className="text-text-secondary text-sm">
              💡 <strong>{t('alfabet.tip')}</strong>
            </p>
            <p className="text-text-muted text-xs mt-2">
              🎤 {user?.preferred_language === "en" ? "Click a letter and record yourself saying it!" : "Klik op 'n letter en neem jouself op terwyl jy dit sê!"}
            </p>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

// Subscription Page
const SubscriptionPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [myPayments, setMyPayments] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    reference_used: user?.username || "",
    amount_paid: 0,
    payment_date: new Date().toISOString().split('T')[0],
    proof_description: ""
  });
  
  // Tutoring request state
  const [requestTutoring, setRequestTutoring] = useState(false);
  const [tutoringDays, setTutoringDays] = useState([]);
  const [tutoringTimes, setTutoringTimes] = useState([]);
  const [submittingTutoring, setSubmittingTutoring] = useState(false);
  
  const availableDays = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrydag", "Saterdag"];
  const availableTimes = ["08:00-09:00", "09:00-10:00", "10:00-11:00", "14:00-15:00", "15:00-16:00", "16:00-17:00", "17:00-18:00"];

  const toggleDay = (day) => {
    setTutoringDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const toggleTime = (time) => {
    setTutoringTimes(prev => prev.includes(time) ? prev.filter(t => t !== time) : [...prev, time]);
  };

  const submitTutoringRequest = async () => {
    if (tutoringDays.length === 0 || tutoringTimes.length === 0) {
      toast.error(t('toast.selectPreferredDaysTimes'));
      return;
    }
    setSubmittingTutoring(true);
    try {
      await api.post("/tutoring/request", {
        requester_name: user?.name || user?.username || "Leerder",
        whatsapp: user?.whatsapp || "",
        preferred_days: tutoringDays,
        preferred_times: tutoringTimes,
        notes: "Aanvraag vanaf subskripsie bladsy"
      });
      toast.success(t('toast.tutoringRequestSubmitted'));
      setRequestTutoring(false);
      setTutoringDays([]);
      setTutoringTimes([]);
    } catch (err) {
      toast.error(t('toast.couldNotSubmitRequest'));
    }
    setSubmittingTutoring(false);
  };

  useEffect(() => {
    // Fetch subscription status, bank details, and payment history
    Promise.all([
      api.get("/subscription/status"),
      api.get("/payments/bank-details"),
      api.get("/payments/eft/my-payments").catch(() => ({ data: { payments: [] } }))
    ]).then(([subRes, bankRes, paymentsRes]) => {
      setSubscription(subRes.data);
      setBankDetails(bankRes.data);
      setMyPayments(paymentsRes.data.payments || []);
    }).catch(console.error);
  }, []);

  const handleSubmitPayment = async () => {
    if (!selectedPackage) {
      toast.error(t('toast.selectPackageFirst'));
      return;
    }
    if (!paymentForm.reference_used) {
      toast.error(t('toast.enterReference'));
      return;
    }
    if (!paymentForm.amount_paid || paymentForm.amount_paid <= 0) {
      toast.error(t('toast.enterAmount'));
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/payments/eft/submit", {
        package_id: selectedPackage,
        reference_used: paymentForm.reference_used,
        amount_paid: parseFloat(paymentForm.amount_paid),
        payment_date: paymentForm.payment_date,
        proof_description: paymentForm.proof_description
      });
      toast.success(res.data.message);
      setShowPaymentForm(false);
      setSelectedPackage(null);
      // Refresh payments list
      const paymentsRes = await api.get("/payments/eft/my-payments");
      setMyPayments(paymentsRes.data.payments || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.couldNotSubmitPayment'));
    }
    setLoading(false);
  };

  const selectPackage = (packageId, amount) => {
    setSelectedPackage(packageId);
    setPaymentForm(prev => ({ ...prev, amount_paid: amount }));
    setShowPaymentForm(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('toast.copied'));
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background pb-8">
        <div className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <button onClick={() => navigate("/dashboard")} className="p-2" data-testid="back-from-sub">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="font-heading text-xl font-bold">Subskripsie & Betaling</h1>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Current Subscription Status */}
          {subscription?.active && (
            <Card className="border-primary-500 bg-primary-50" testId="subscription-status">
              <div className="flex items-center gap-3">
                <Check className="w-8 h-8 text-primary-500" />
                <div>
                  <p className="font-bold">Aktiewe Subskripsie</p>
                  <p className="text-sm text-text-secondary">
                    {subscription.type === "trial" && `Proeftydperk - ${subscription.days_left} dae oor`}
                    {subscription.type === "monthly" && `Maandeliks - ${subscription.days_left} dae oor`}
                    {subscription.type === "lifetime" && "Lewenslange toegang"}
                    {subscription.type === "school" && `Skool - ${subscription.days_left} dae oor`}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Bank Details Card */}
          {bankDetails && (
            <Card testId="bank-details-card">
              <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary-500" />
                Bankbesonderhede vir EFT
              </h3>
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Bank:</span>
                  <span className="font-semibold">{bankDetails.bank_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Rekeninghouer:</span>
                  <span className="font-semibold">{bankDetails.account_holder}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Rekeningnommer:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold font-mono">{bankDetails.account_number}</span>
                    <button 
                      onClick={() => copyToClipboard(bankDetails.account_number)}
                      className="p-1 hover:bg-slate-200 rounded"
                      data-testid="copy-account"
                    >
                      <Copy className="w-4 h-4 text-text-muted" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Takkode:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold font-mono">{bankDetails.branch_code}</span>
                    <button 
                      onClick={() => copyToClipboard(bankDetails.branch_code)}
                      className="p-1 hover:bg-slate-200 rounded"
                      data-testid="copy-branch"
                    >
                      <Copy className="w-4 h-4 text-text-muted" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Tipe Rekening:</span>
                  <span className="font-semibold">{bankDetails.account_type}</span>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-sm text-primary-600 font-medium">
                    <strong>Verwysing:</strong> {bankDetails.reference_instructions}
                  </p>
                  <p className="text-xs text-text-muted mt-1">
                    Jou gebruikersnaam: <strong>{user?.username}</strong>
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Package Selection */}
          {!showPaymentForm ? (
            <div className="space-y-4">
              <h3 className="font-heading font-bold text-lg">Kies 'n Pakket</h3>
              
              <Card className="border-2 border-slate-200 hover:border-primary-500 transition-colors cursor-pointer" 
                    onClick={() => selectPackage("monthly", 100)}
                    testId="monthly-option">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-heading text-lg font-bold">Maandeliks</h4>
                    <p className="text-text-secondary text-sm">Kanselleer enige tyd</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary-600">R100</div>
                    <div className="text-sm text-text-muted">/maand</div>
                  </div>
                </div>
              </Card>

              <Card className="border-2 border-secondary-500 relative cursor-pointer hover:shadow-lg transition-shadow" 
                    onClick={() => selectPackage("once_off", 399)}
                    testId="once-off-option">
                <div className="absolute -top-3 right-4 bg-secondary-500 text-text-primary text-xs font-bold px-3 py-1 rounded-full">
                  BESTE WAARDE
                </div>
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-heading text-lg font-bold">Eenmalig</h4>
                    <p className="text-text-secondary text-sm">Lewenslange toegang</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-secondary-600">R399</div>
                    <div className="text-sm text-text-muted">eenmalig</div>
                  </div>
                </div>
              </Card>

              {/* Online Tutoring Option */}
              <Card className="border-2 border-green-400 bg-green-50 mt-4" testId="tutoring-option">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={requestTutoring}
                    onChange={(e) => setRequestTutoring(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-slate-300"
                    data-testid="tutoring-checkbox-subscription"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-heading text-lg font-bold text-green-800">Online Tutoring</h4>
                        <p className="text-green-700 text-sm">Een-op-een sessies met 'n onderwyser</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-600">R100</div>
                        <div className="text-sm text-green-600">/week</div>
                      </div>
                    </div>
                    
                    {requestTutoring && (
                      <div className="mt-4 pt-4 border-t border-green-200">
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm font-semibold text-green-800 mb-2">Voorkeur Dae:</p>
                            <div className="flex flex-wrap gap-2">
                              {availableDays.map(day => (
                                <button
                                  key={day}
                                  type="button"
                                  onClick={() => toggleDay(day)}
                                  className={`px-3 py-1 rounded-full text-sm ${
                                    tutoringDays.includes(day) 
                                      ? 'bg-green-600 text-white' 
                                      : 'bg-white border border-green-300 text-green-700 hover:border-green-500'
                                  }`}
                                >
                                  {day}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-green-800 mb-2">Voorkeur Tye:</p>
                            <div className="flex flex-wrap gap-2">
                              {availableTimes.map(time => (
                                <button
                                  key={time}
                                  type="button"
                                  onClick={() => toggleTime(time)}
                                  className={`px-3 py-1 rounded-full text-sm ${
                                    tutoringTimes.includes(time)
                                      ? 'bg-green-600 text-white'
                                      : 'bg-white border border-green-300 text-green-700 hover:border-green-500'
                                  }`}
                                >
                                  {time}
                                </button>
                              ))}
                            </div>
                          </div>
                          <Button 
                            onClick={submitTutoringRequest}
                            disabled={submittingTutoring}
                            className="w-full bg-green-600 hover:bg-green-700"
                            testId="submit-tutoring-btn"
                          >
                            {submittingTutoring ? "Stuur..." : "Dien Tutoring Aanvraag In"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          ) : (
            /* Payment Submission Form */
            <Card testId="payment-form">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-lg">Betaling Kennisgewing</h3>
                <button 
                  onClick={() => { setShowPaymentForm(false); setSelectedPackage(null); }}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="bg-primary-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-primary-700">
                  <strong>Gekose Pakket:</strong> {selectedPackage === "monthly" ? "Maandeliks (R100)" : "Eenmalig (R399)"}
                </p>
              </div>

              <div className="space-y-4">
                <Input
                  label="Verwysing wat jy gebruik het"
                  value={paymentForm.reference_used}
                  onChange={(e) => setPaymentForm({...paymentForm, reference_used: e.target.value})}
                  placeholder="bv. jou gebruikersnaam"
                  testId="payment-reference"
                />
                
                <Input
                  label="Bedrag Betaal (R)"
                  type="number"
                  value={paymentForm.amount_paid}
                  onChange={(e) => setPaymentForm({...paymentForm, amount_paid: e.target.value})}
                  testId="payment-amount"
                />
                
                <Input
                  label="Datum van Betaling"
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                  testId="payment-date"
                />
                
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Addisionele Inligting (Opsioneel)
                  </label>
                  <textarea
                    className="input-field"
                    rows="2"
                    value={paymentForm.proof_description}
                    onChange={(e) => setPaymentForm({...paymentForm, proof_description: e.target.value})}
                    placeholder="bv. betaal via FNB app, verwysing nommer..."
                    data-testid="payment-description"
                  />
                </div>

                <Button 
                  onClick={handleSubmitPayment} 
                  disabled={loading} 
                  className="w-full"
                  testId="submit-payment"
                >
                  {loading ? "Besig..." : "Dien Betaling In"}
                </Button>
              </div>
            </Card>
          )}

          {/* Payment History */}
          {myPayments.length > 0 && (
            <Card testId="payment-history">
              <h3 className="font-heading font-bold text-lg mb-4">Betaling Geskiedenis</h3>
              <div className="space-y-3">
                {myPayments.map((payment, i) => (
                  <div key={payment.id || i} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                    <div>
                      <p className="font-medium">{payment.package_name || payment.package_id}</p>
                      <p className="text-xs text-text-muted">
                        {new Date(payment.created_at).toLocaleDateString("af-ZA")} - Verw: {payment.reference_used}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">R{payment.amount_paid}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        payment.payment_status === "paid" ? "bg-green-100 text-green-700" :
                        payment.payment_status === "pending" ? "bg-yellow-100 text-yellow-700" :
                        "bg-red-100 text-red-700"
                      }`}>
                        {payment.payment_status === "paid" ? "Bevestig" : 
                         payment.payment_status === "pending" ? "Wag vir bevestiging" : "Afgekeur"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </PageTransition>
  );
};

// Texts Tab Component with questions, audio support, and folder view
const TextsTab = ({ texts, setTexts }) => {
  const { t, i18n } = useTranslation();
  const [newText, setNewText] = useState({ 
    title: "", content: "", grade_level: 1, text_type: "comprehension", term: 1, questions: [], spelling_word_count: null, language: "af"
  });
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState({
    question_text: "", question_type: "typed", options: ["", "", "", ""], correct_answer: "", points: 10, keywords: ""
  });
  const [expandedText, setExpandedText] = useState(null);
  const [uploadingAudio, setUploadingAudio] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const audioInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const excelInputRef = useRef(null);
  
  // Language filter state - separate tabs for AF and EN
  const [selectedLanguage, setSelectedLanguage] = useState("af");
  
  // Sync newText.language with selectedLanguage when component mounts or selectedLanguage changes
  useEffect(() => {
    setNewText(prev => ({...prev, language: selectedLanguage}));
  }, [selectedLanguage]);
  
  // Edit mode state
  const [editingText, setEditingText] = useState(null);
  const [editForm, setEditForm] = useState(null);
  
  // Audio Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTextId, setRecordingTextId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Folder View State
  const [viewMode, setViewMode] = useState("folders"); // "folders" or "list"
  const [expandedGrade, setExpandedGrade] = useState(null);
  const [expandedType, setExpandedType] = useState(null);
  
  // Text type labels - bilingual
  const textTypeLabels = {
    comprehension: selectedLanguage === "en" ? "Comprehension" : "Begripstoets",
    reading: selectedLanguage === "en" ? "Read Aloud" : "Hardoplees",
    spelling: "Speltoets",
    listening: selectedLanguage === "en" ? "Listening" : "Luistertoets"
  };
  
  // Filter texts by selected language
  const filteredTexts = texts.filter(text => {
    const textLang = text.language || "af"; // Default to Afrikaans for legacy texts
    return textLang === selectedLanguage;
  });
  
  // Organize texts by grade and type (using filtered texts)
  const getTextsByGradeAndType = () => {
    const organized = {};
    for (let grade = 1; grade <= 9; grade++) {
      organized[grade] = {
        comprehension: [],
        reading: [],
        spelling: [],
        listening: []
      };
    }
    filteredTexts.forEach(text => {
      const grade = text.grade_level;
      const type = text.text_type;
      if (organized[grade] && organized[grade][type]) {
        organized[grade][type].push(text);
      }
    });
    return organized;
  };
  
  const organizedTexts = getTextsByGradeAndType();
  
  // Count texts by grade
  const getGradeCount = (grade) => {
    const gradeTexts = organizedTexts[grade];
    return Object.values(gradeTexts).reduce((acc, arr) => acc + arr.length, 0);
  };
  
  // Count texts by type within a grade
  const getTypeCount = (grade, type) => {
    return organizedTexts[grade][type].length;
  };

  const addQuestion = () => {
    if (!currentQuestion.question_text || !currentQuestion.correct_answer) {
      toast.error(t('toast.questionAndAnswerRequired'));
      return;
    }
    // Parse keywords from comma-separated string
    const keywordsArray = currentQuestion.keywords 
      ? currentQuestion.keywords.split(',').map(k => k.trim()).filter(k => k.length > 0)
      : [];
    
    const questionToAdd = {
      ...currentQuestion,
      id: `q_${Date.now()}`,
      options: currentQuestion.question_type === "multiple_choice" 
        ? currentQuestion.options.filter(o => o.trim()) 
        : [],
      keywords: keywordsArray
    };
    setNewText({
      ...newText,
      questions: [...newText.questions, questionToAdd]
    });
    setCurrentQuestion({
      question_text: "", question_type: "typed", options: ["", "", "", ""], correct_answer: "", points: 10, keywords: ""
    });
    setShowQuestionForm(false);
    toast.success(t('toast.questionAdded'));
  };

  const removeQuestion = (index) => {
    setNewText({
      ...newText,
      questions: newText.questions.filter((_, i) => i !== index)
    });
  };

  const handleCreateText = async () => {
    if (!newText.title || !newText.content) {
      toast.error(t('toast.titleContentRequired'));
      return;
    }
    try {
      await api.post("/texts", {...newText, language: newText.language || selectedLanguage});
      toast.success(t('toast.textSaved'));
      const res = await api.get("/texts");
      setTexts(res.data);
      setNewText({ title: "", content: "", grade_level: 1, text_type: "comprehension", term: 1, questions: [], spelling_word_count: null, language: selectedLanguage });
    } catch (err) {
      toast.error(t('toast.couldNotSave'));
    }
  };

  const handleDeleteText = async (textId) => {
    try {
      await api.delete(`/texts/${textId}`);
      toast.success(t('toast.deleted'));
      const res = await api.get("/texts");
      setTexts(res.data);
    } catch (err) {
      toast.error(t('toast.couldNotDelete'));
    }
  };

  const handleAudioUpload = async (textId, file) => {
    setUploadingAudio(textId);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await api.post(`/texts/${textId}/audio`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(t('toast.audioUploaded'));
      // Track upload time for backup reminder
      localStorage.setItem('lastUploadTime', new Date().toISOString());
      const res = await api.get("/texts");
      setTexts(res.data);
    } catch (err) {
      toast.error(t('toast.couldNotUpload'));
    }
    setUploadingAudio(null);
  };

  // Image upload for texts
  const handleImageUpload = async (textId, file) => {
    setUploadingImage(textId);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await api.post(`/texts/${textId}/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(t('toast.imageUploaded'));
      const res = await api.get("/texts");
      setTexts(res.data);
    } catch (err) {
      toast.error(t('toast.couldNotUpload'));
    }
    setUploadingImage(null);
  };

  // Excel bulk upload
  const handleExcelUpload = async (file) => {
    setUploadingExcel(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post("/texts/upload/excel", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(res.data.message);
      if (res.data.errors && res.data.errors.length > 0) {
        toast.error(`${res.data.errors.length} foute gevind`);
      }
      const textsRes = await api.get("/texts");
      setTexts(textsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.couldNotProcessExcel'));
    }
    setUploadingExcel(false);
  };

  // Download Excel template
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/texts/template/excel", { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tekste_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('toast.templateDownloaded'));
    } catch (err) {
      toast.error(t('toast.couldNotDownloadTemplate'));
    }
  };

  // Start editing a text
  const handleStartEdit = (text) => {
    setEditingText(text.id);
    setEditForm({
      title: text.title,
      content: text.content,
      grade_level: text.grade_level,
      text_type: text.text_type,
      questions: text.questions || [],
      spelling_word_count: text.spelling_word_count || null
    });
  };

  // Save edited text
  const handleSaveEdit = async () => {
    if (!editForm.title || !editForm.content) {
      toast.error(t('toast.titleContentRequired'));
      return;
    }
    try {
      await api.put(`/texts/${editingText}`, editForm);
      toast.success(t('toast.textUpdated'));
      const res = await api.get("/texts");
      setTexts(res.data);
      setEditingText(null);
      setEditForm(null);
    } catch (err) {
      toast.error(t('toast.couldNotUpdateText'));
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingText(null);
    setEditForm(null);
  };

  // Start audio recording
  const startRecording = async (textId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // Upload the recorded audio
        const formData = new FormData();
        formData.append("file", audioBlob, "recording.webm");
        
        try {
          await api.post(`/texts/${textId}/audio`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          toast.success(t('toast.recordingUploaded'));
          const res = await api.get("/texts");
          setTexts(res.data);
        } catch (err) {
          toast.error(t('toast.couldNotUploadRecording'));
        }
        setRecordingTextId(null);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTextId(textId);
      toast.success(t('toast.recordingStarted'));
    } catch (err) {
      toast.error(t('toast.couldNotGetMicPermission'));
    }
  };

  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success(t('toast.recordingStopped'));
    }
  };
        
  // Render text item for folder view - now with audio support
  const renderTextItem = (text) => (
    <div key={text.id} className="bg-white border rounded-lg p-3 hover:border-primary-200 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          <FileText className="w-4 h-4 text-primary-500" />
          <span className="font-medium text-sm">{text.title}</span>
          {text.audio_url && <Volume2 className="w-3 h-3 text-primary-500" title="Het oudio" />}
        </div>
        <div className="flex gap-1">
          <button 
            onClick={() => handleStartEdit(text)}
            className="p-1 text-primary-500 hover:bg-primary-50 rounded"
            data-testid={`edit-text-folder-${text.id}`}
            title="Wysig"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button 
            onClick={() => handleDeleteText(text.id)}
            className="p-1 text-accent-500 hover:bg-accent-50 rounded"
            data-testid={`delete-text-folder-${text.id}`}
            title="Verwyder"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="text-xs text-text-muted mt-1 ml-6">
        {text.language === "en" ? "🇬🇧" : "🇿🇦"} {text.is_ai_generated ? "AI" : "Handmatig"}
        {text.term && ` | Term ${text.term}`}
        {text.questions?.length > 0 && ` | ${text.questions.length} vrae`}
        {text.image_url && " | 🖼️ Prent"}
      </p>
      
      {/* Image Upload for Grade 1-3 */}
      {text.grade_level <= 3 && (
        <div className="mt-2 ml-6">
          {text.image_url ? (
            <div className="mb-2">
              <img src={`${API}${text.image_url}`} alt={text.title} className="max-w-[150px] h-auto rounded border" />
            </div>
          ) : (
            <p className="text-xs text-blue-600 mb-2">💡 Graad 1-3: Voeg 'n prent by</p>
          )}
          <input
            type="file"
            accept="image/*"
            id={`image-upload-folder-${text.id}`}
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleImageUpload(text.id, e.target.files[0])}
          />
          <button
            onClick={() => document.getElementById(`image-upload-folder-${text.id}`)?.click()}
            className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            disabled={uploadingImage === text.id}
            data-testid={`upload-image-folder-${text.id}`}
          >
            <Image className="w-3 h-3" />
            {uploadingImage === text.id ? "Laai..." : text.image_url ? "Vervang Prent" : "Laai Prent Op"}
          </button>
        </div>
      )}
      
      {/* Audio Upload/Record for Listening & Spelling */}
      {(text.text_type === "listening" || text.text_type === "spelling") && (
        <div className="mt-3 pt-3 border-t ml-6">
          {text.audio_url ? (
            <div className="mb-2">
              <audio controls src={`${API}${text.audio_url}`} className="w-full h-8" />
            </div>
          ) : (
            <p className="text-xs text-yellow-600 mb-2">⚠️ Geen oudio - laai op of neem op</p>
          )}
          
          <div className="flex flex-wrap gap-2">
            <input
              type="file"
              accept="audio/*"
              id={`audio-upload-${text.id}`}
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleAudioUpload(text.id, e.target.files[0])}
            />
            <button
              onClick={() => document.getElementById(`audio-upload-${text.id}`)?.click()}
              className="flex items-center gap-1 text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-colors"
              disabled={uploadingAudio === text.id || isRecording}
              data-testid={`upload-audio-folder-${text.id}`}
            >
              <Upload className="w-3 h-3" />
              {uploadingAudio === text.id ? "Laai..." : "Laai Op"}
            </button>
            
            {isRecording && recordingTextId === text.id ? (
              <button
                onClick={stopRecording}
                className="flex items-center gap-1 text-xs px-2 py-1 bg-accent-500 text-white rounded hover:bg-accent-600 transition-colors animate-pulse"
                data-testid={`stop-rec-folder-${text.id}`}
              >
                <Pause className="w-3 h-3" />
                Stop
              </button>
            ) : (
              <button
                onClick={() => startRecording(text.id)}
                className="flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                disabled={isRecording}
                data-testid={`start-rec-folder-${text.id}`}
              >
                <Mic className="w-3 h-3" />
                Neem Op
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Language Tabs - Separate AF and EN texts */}
      <Card testId="language-tabs">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold">Tekste / Texts</h3>
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <span>🇿🇦 {texts.filter(t => (t.language || "af") === "af").length}</span>
            <span>|</span>
            <span>🇬🇧 {texts.filter(t => t.language === "en").length}</span>
          </div>
        </div>
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => {
              setSelectedLanguage("af");
              setNewText(prev => ({...prev, language: "af"}));
            }}
            className={`flex-1 py-3 rounded-xl font-semibold transition-all ${
              selectedLanguage === "af" 
                ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg" 
                : "bg-slate-100 text-text-secondary hover:bg-slate-200"
            }`}
            data-testid="texts-tab-afrikaans"
          >
            🇿🇦 Afrikaans ({texts.filter(t => (t.language || "af") === "af").length})
          </button>
          <button
            onClick={() => {
              setSelectedLanguage("en");
              setNewText(prev => ({...prev, language: "en"}));
            }}
            className={`flex-1 py-3 rounded-xl font-semibold transition-all ${
              selectedLanguage === "en" 
                ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg" 
                : "bg-slate-100 text-text-secondary hover:bg-slate-200"
            }`}
            data-testid="texts-tab-english"
          >
            🇬🇧 English ({texts.filter(t => t.language === "en").length})
          </button>
        </div>
        <p className="text-sm text-text-muted">
          {selectedLanguage === "en" 
            ? "Managing English texts. English learners will only see these texts."
            : "Bestuur Afrikaanse tekste. Afrikaanse leerders sal slegs hierdie tekste sien."}
        </p>
      </Card>

      <Card testId="add-text-form">
        <h3 className="font-heading font-bold mb-4">
          {selectedLanguage === "en" ? "Add New Text" : "Voeg Nuwe Teks By"} 
          <span className="ml-2 text-sm font-normal">({selectedLanguage === "en" ? "🇬🇧 English" : "🇿🇦 Afrikaans"})</span>
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label={selectedLanguage === "en" ? "Title" : "Titel"}
            value={newText.title}
            onChange={(e) => setNewText({...newText, title: e.target.value})}
            testId="text-title-input"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-text-secondary mb-2">{selectedLanguage === "en" ? "Grade" : "Graad"}</label>
              <select
                className="input-field"
                value={newText.grade_level}
                onChange={(e) => setNewText({...newText, grade_level: parseInt(e.target.value)})}
                data-testid="text-grade-select"
              >
                {[1,2,3,4,5,6,7,8,9].map(g => <option key={g} value={g}>{selectedLanguage === "en" ? "Grade" : "Graad"} {g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-secondary mb-2">{selectedLanguage === "en" ? "Type" : "Tipe"}</label>
              <select
                className="input-field"
                value={newText.text_type}
                onChange={(e) => setNewText({...newText, text_type: e.target.value})}
                data-testid="text-type-select"
              >
                <option value="comprehension">{selectedLanguage === "en" ? "Comprehension" : "Begripstoets"}</option>
                <option value="reading">{selectedLanguage === "en" ? "Read Aloud" : "Hardoplees"}</option>
                <option value="spelling">Spelling</option>
                <option value="listening">{selectedLanguage === "en" ? "Listening" : "Luistertoets"}</option>
              </select>
            </div>
          </div>
          {/* Term Selector */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-text-secondary mb-2">Term</label>
              <select
                className="input-field"
                value={newText.term}
                onChange={(e) => setNewText({...newText, term: parseInt(e.target.value)})}
                data-testid="text-term-select"
              >
                <option value={1}>Term 1</option>
                <option value={2}>Term 2</option>
                <option value={3}>Term 3</option>
                <option value={4}>Term 4</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-secondary mb-2">{selectedLanguage === "en" ? "Language" : "Taal"}</label>
              <select
                className="input-field"
                value={newText.language || selectedLanguage}
                onChange={(e) => setNewText({...newText, language: e.target.value})}
                data-testid="text-language-select"
              >
                <option value="af">🇿🇦 Afrikaans</option>
                <option value="en">🇬🇧 English</option>
              </select>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-semibold text-text-secondary mb-2">
            {selectedLanguage === "en" ? "Content / Text" : "Inhoud / Teks"}
          </label>
          <textarea
            className="input-field min-h-32"
            value={newText.content}
            onChange={(e) => setNewText({...newText, content: e.target.value})}
            placeholder={selectedLanguage === "en" 
              ? "Type or paste the text here that learners must read..."
              : "Tik of plak die teks hier wat die leerders moet lees..."}
            data-testid="text-content-input"
          />
        </div>

        {/* Questions Section */}
        <div className="mt-6 border-t pt-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold">Vrae ({newText.questions.length})</h4>
            <button
              onClick={() => setShowQuestionForm(!showQuestionForm)}
              className="text-primary-500 text-sm font-medium hover:underline"
              data-testid="add-question-btn"
            >
              + Voeg Vraag By
            </button>
          </div>

          {/* Question Form */}
          {showQuestionForm && (
            <div className="bg-slate-50 rounded-xl p-4 mb-4">
              <Input
                label="Vraag"
                value={currentQuestion.question_text}
                onChange={(e) => setCurrentQuestion({...currentQuestion, question_text: e.target.value})}
                placeholder="bv. Wat is die hond se naam?"
                testId="question-text-input"
              />
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">Tipe</label>
                  <select
                    className="input-field"
                    value={currentQuestion.question_type}
                    onChange={(e) => setCurrentQuestion({...currentQuestion, question_type: e.target.value})}
                    data-testid="question-type-select"
                  >
                    <option value="typed">Tik Antwoord</option>
                    <option value="multiple_choice">Meervoudige Keuse</option>
                  </select>
                </div>
                <Input
                  label="Punte"
                  type="number"
                  value={currentQuestion.points}
                  onChange={(e) => setCurrentQuestion({...currentQuestion, points: parseInt(e.target.value) || 10})}
                  testId="question-points-input"
                />
              </div>

              {currentQuestion.question_type === "multiple_choice" && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-text-secondary mb-2">Opsies (minstens 2)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {currentQuestion.options.map((opt, i) => (
                      <input
                        key={i}
                        type="text"
                        className="input-field"
                        value={opt}
                        onChange={(e) => {
                          const newOpts = [...currentQuestion.options];
                          newOpts[i] = e.target.value;
                          setCurrentQuestion({...currentQuestion, options: newOpts});
                        }}
                        placeholder={`Opsie ${i + 1}`}
                        data-testid={`option-${i}-input`}
                      />
                    ))}
                  </div>
                </div>
              )}

              <Input
                label="Korrekte Antwoord"
                value={currentQuestion.correct_answer}
                onChange={(e) => setCurrentQuestion({...currentQuestion, correct_answer: e.target.value})}
                placeholder="Die presiese korrekte antwoord"
                testId="correct-answer-input"
              />

              {/* Keywords for typed answers */}
              {currentQuestion.question_type === "typed" && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <label className="block text-sm font-semibold text-blue-800 mb-2">
                    🔑 Sleutelwoorde (Opsioneel)
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={currentQuestion.keywords}
                    onChange={(e) => setCurrentQuestion({...currentQuestion, keywords: e.target.value})}
                    placeholder="bv: hond, blaf, hard (skei met kommas)"
                    data-testid="keywords-input"
                  />
                  <p className="text-xs text-blue-600 mt-2">
                    As jy sleutelwoorde invoer, sal die antwoord korrek wees as die leerder hierdie woorde gebruik. 
                    Skei woorde met kommas.
                  </p>
                </div>
              )}

              <div className="flex gap-2 mt-4">
                <Button onClick={addQuestion} testId="save-question-btn">{t('admin.addQuestion')}</Button>
                <button 
                  onClick={() => setShowQuestionForm(false)}
                  className="px-4 py-2 text-text-muted hover:text-text-primary"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Added Questions List */}
          {newText.questions.length > 0 && (
            <div className="space-y-2">
              {newText.questions.map((q, i) => (
                <div key={q.id || i} className="flex items-center justify-between bg-white border rounded-lg p-3">
                  <div className="flex-1">
                    <span className="font-medium">{i + 1}. {q.question_text}</span>
                    <span className="text-xs ml-2 text-text-muted">
                      ({q.question_type === "multiple_choice" ? "Keuse" : "Tik"} | {q.points} punte)
                    </span>
                    {q.keywords && q.keywords.length > 0 && (
                      <div className="text-xs text-blue-600 mt-1">
                        🔑 Sleutelwoorde: {q.keywords.join(', ')}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => removeQuestion(i)}
                    className="p-1 text-accent-500 hover:bg-accent-50 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Note for listening/spelling tests */}
        {(newText.text_type === "listening" || newText.text_type === "spelling") && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>📢 Nota:</strong> Nadat jy die teks gestoor het, kan jy oudio oplaai of opneem. 
              Scroll af na die gestoorde teks en klik op die oudio knoppies.
            </p>
          </div>
        )}
        
        {/* Spelling Word Count Control with Auto-Suggestion */}
        {newText.text_type === "spelling" && (
          <div className="mt-4">
            {(() => {
              const contentWords = newText.content ? newText.content.split(/[,\n]+/).filter(w => w.trim()).length : 0;
              return (
                <>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Aantal Woorde vir Toets
                  </label>
                  {contentWords > 0 && (
                    <div className="flex items-center gap-2 mb-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <span className="text-sm text-green-700">
                        📊 {contentWords} woorde gevind in inhoud.
                      </span>
                      <button
                        type="button"
                        onClick={() => setNewText({...newText, spelling_word_count: contentWords})}
                        className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                      >
                        Gebruik almal ({contentWords})
                      </button>
                      {contentWords > 10 && (
                        <button
                          type="button"
                          onClick={() => setNewText({...newText, spelling_word_count: 10})}
                          className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                        >
                          Gebruik 10
                        </button>
                      )}
                    </div>
                  )}
                  <input
                    type="number"
                    className="input-field"
                    placeholder={contentWords > 0 ? `bv. ${contentWords}` : "bv. 10"}
                    min="1"
                    max="50"
                    value={newText.spelling_word_count || ""}
                    onChange={(e) => setNewText({...newText, spelling_word_count: e.target.value ? parseInt(e.target.value) : null})}
                    data-testid="spelling-word-count-input"
                  />
                  <p className="text-xs text-text-muted mt-1">
                    As jy 'n getal invoer, sal net daardie aantal woorde aan die leerder gewys word.
                  </p>
                </>
              );
            })()}
          </div>
        )}

        <Button onClick={handleCreateText} className="mt-4 w-full" testId="save-text-btn">
          {t('admin.saveText')}
        </Button>
      </Card>

      {/* Bulk Upload Section */}
      <Card testId="bulk-upload-section" className="bg-gradient-to-r from-primary-50 to-secondary-50">
        <h3 className="font-heading font-bold mb-4">📥 {t('admin.bulkImport')}</h3>
        <p className="text-sm text-text-secondary mb-4">
          {t('admin.bulkImportDesc')}
          <br />
          <span className="text-primary-600 font-medium">{i18n.language === 'en' ? 'Note: The template contains a "language" column. Use "af" for Afrikaans and "en" for English.' : 'Let op: Die template bevat \'n "taal" kolom. Gebruik "af" vir Afrikaans en "en" vir Engels.'}</span>
        </p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={downloadTemplate} variant="secondary" testId="download-template-btn">
            <Download className="w-4 h-4 mr-2" />
            {t('admin.downloadTemplate')}
          </Button>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            ref={excelInputRef}
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleExcelUpload(e.target.files[0])}
          />
          <Button 
            onClick={() => excelInputRef.current?.click()} 
            disabled={uploadingExcel}
            testId="upload-excel-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploadingExcel ? t('admin.uploading') : t('admin.uploadExcel')}
          </Button>
        </div>
        <div className="mt-4 text-xs text-text-muted">
          <strong>Kolomme:</strong> titel, tipe, graad, term, <span className="text-primary-600 font-semibold">taal</span>, inhoud, vraag1, antwoord1, sleutelwoorde1, vraag2, ...
        </div>
      </Card>

      {/* Quick Audio Upload Section - Shows texts that need audio */}
      {texts.filter(t => (t.text_type === "listening" || t.text_type === "spelling") && !t.audio_url).length > 0 && (
        <Card className="bg-yellow-50 border-yellow-200" testId="needs-audio-section">
          <h4 className="font-semibold text-yellow-800 mb-3">⚠️ Tekste wat Oudio Benodig</h4>
          <p className="text-sm text-yellow-700 mb-3">Hierdie is vir die algemene instruksie oudio. Vir woord-vir-woord oudio, gebruik die seksie hieronder.</p>
          <div className="space-y-3">
            {texts.filter(t => (t.text_type === "listening" || t.text_type === "spelling") && !t.audio_url).map(text => (
              <div key={text.id} className="bg-white rounded-lg p-3 border">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium">{text.title}</span>
                    <span className="text-xs text-text-muted ml-2">
                      (Graad {text.grade_level} | {textTypeLabels[text.text_type]})
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="audio/*"
                    id={`quick-audio-${text.id}`}
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleAudioUpload(text.id, e.target.files[0])}
                  />
                  <button
                    onClick={() => document.getElementById(`quick-audio-${text.id}`)?.click()}
                    className="flex items-center gap-2 px-3 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600 transition-colors"
                    disabled={uploadingAudio === text.id}
                    data-testid={`quick-upload-${text.id}`}
                  >
                    <Upload className="w-4 h-4" />
                    {uploadingAudio === text.id ? "Laai op..." : "Laai Oudio Op"}
                  </button>
                  
                  {isRecording && recordingTextId === text.id ? (
                    <button
                      onClick={stopRecording}
                      className="flex items-center gap-2 px-3 py-2 bg-accent-500 text-white rounded-lg text-sm hover:bg-accent-600 transition-colors animate-pulse"
                      data-testid={`quick-stop-${text.id}`}
                    >
                      <Pause className="w-4 h-4" />
                      Stop Opname
                    </button>
                  ) : (
                    <button
                      onClick={() => startRecording(text.id)}
                      className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 transition-colors"
                      disabled={isRecording}
                      data-testid={`quick-record-${text.id}`}
                    >
                      <Mic className="w-4 h-4" />
                      Neem Op
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* SPELLING WORD AUDIO UPLOAD - NEW SECTION */}
      {texts.filter(t => t.text_type === "spelling" && t.content).length > 0 && (
        <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200" testId="spelling-word-audio-section">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h4 className="font-semibold text-purple-800 flex items-center gap-2">
                <Volume2 className="w-5 h-5" />
                {i18n.language === 'en' ? 'Spelling Word Audio' : 'Speltoets Woord Oudio'}
              </h4>
              <p className="text-sm text-purple-700">
                {i18n.language === 'en' 
                  ? 'Upload your own audio for spelling test words. Learners will hear your voice instead of AI.'
                  : 'Laai jou eie oudio op vir speltoetse. Leerders sal jou stem hoor in plaas van KI.'}
              </p>
            </div>
            
            {/* BULK UPLOAD ALL BUTTON */}
            <label className="cursor-pointer flex-shrink-0">
              <input
                type="file"
                accept=".zip"
                className="hidden"
                data-testid="bulk-upload-all-spelling"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const formData = new FormData();
                    formData.append("file", file);
                    try {
                      toast.loading(
                        i18n.language === 'en' 
                          ? `Uploading ${file.name} for all spelling texts...` 
                          : `Laai ${file.name} op vir alle speltoetse...`, 
                        { id: 'bulk-upload-all' }
                      );
                      const res = await api.post('/texts/spelling-audio/bulk-all', formData);
                      toast.success(res.data.message, { id: 'bulk-upload-all', duration: 5000 });
                      console.log('Bulk upload all results:', res.data.results);
                      // Refresh texts
                      const textsRes = await api.get("/texts");
                      setTexts(textsRes.data);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Bulk upload misluk', { id: 'bulk-upload-all' });
                    }
                  }
                  e.target.value = '';
                }}
              />
              <span className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl text-sm font-bold hover:from-purple-700 hover:to-blue-700 flex items-center gap-2 cursor-pointer shadow-lg">
                <Upload className="w-5 h-5" />
                {i18n.language === 'en' ? 'Upload ALL Spelling Audio (ZIP)' : 'Laai ALLE Spelling Oudio Op (ZIP)'}
              </span>
            </label>
          </div>
          
          {/* Instructions for ZIP structure */}
          <div className="bg-white/60 rounded-lg p-3 mb-4 text-xs text-purple-800">
            <p className="font-semibold mb-1">
              {i18n.language === 'en' ? 'ZIP Structure:' : 'ZIP Struktuur:'}
            </p>
            <code className="block bg-purple-100 p-2 rounded text-[11px] overflow-x-auto">
              spelling_audios.zip<br/>
              ├── {i18n.language === 'en' ? 'Text Title 1' : 'Teks Titel 1'}/<br/>
              │   ├── 1.mp3 ({i18n.language === 'en' ? 'or' : 'of'} kat.mp3)<br/>
              │   └── 2.mp3<br/>
              └── {i18n.language === 'en' ? 'Text Title 2' : 'Teks Titel 2'}/<br/>
                  └── 1.mp3
            </code>
            <p className="mt-2 text-purple-600">
              {i18n.language === 'en' 
                ? 'Folder names must match your text titles exactly.'
                : 'Gids name moet presies pas by jou teks titels.'}
            </p>
          </div>

          <h5 className="font-semibold text-purple-700 mb-3 text-sm">
            {i18n.language === 'en' ? 'Or upload per text:' : 'Of laai per teks op:'}
          </h5>
          <div className="space-y-4">
            {texts.filter(t => t.text_type === "spelling" && t.content).map(text => {
              // Parse words from content
              const words = text.content.split(/[,\n]+/).filter(w => w.trim()).map(w => 
                w.replace(/^\d+\.\s*/, '').replace(/\*\*/g, '').split(' - ')[0].split(' – ')[0].trim()
              );
              const wordCount = text.spelling_word_count && text.spelling_word_count > 0 
                ? Math.min(text.spelling_word_count, words.length)
                : words.length;
              const wordsToShow = words.slice(0, wordCount);
              const audioCount = (text.word_audio || []).filter(Boolean).length;
              
              return (
                <div key={text.id} className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-semibold">{text.title}</span>
                      <span className="text-xs text-text-muted ml-2">
                        ({i18n.language === 'en' ? 'Grade' : 'Graad'} {text.grade_level} | {wordsToShow.length} {i18n.language === 'en' ? 'words' : 'woorde'})
                      </span>
                      {audioCount > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          {audioCount}/{wordsToShow.length} {i18n.language === 'en' ? 'audio' : 'oudio'}
                        </span>
                      )}
                    </div>
                    {/* Bulk Upload Button for single text */}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".zip"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append("file", file);
                            try {
                              toast.loading(`${i18n.language === 'en' ? 'Uploading' : 'Laai'} ${file.name}...`, { id: 'bulk-upload' });
                              const res = await api.post(`/texts/${text.id}/spelling-word-audio-bulk`, formData);
                              toast.success(res.data.message, { id: 'bulk-upload' });
                              console.log('Bulk upload results:', res.data.results);
                              // Refresh texts
                              const textsRes = await api.get("/texts");
                              setTexts(textsRes.data);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Bulk upload misluk', { id: 'bulk-upload' });
                            }
                          }
                          e.target.value = '';
                        }}
                      />
                      <span className="px-3 py-1.5 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 flex items-center gap-2 cursor-pointer">
                        <Upload className="w-4 h-4" />
                        {i18n.language === 'en' ? 'Upload ZIP' : 'Laai ZIP Op'}
                      </span>
                    </label>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    💡 ZIP lêer: Noem lêers 1.mp3, 2.mp3, 3.mp3... OF {wordsToShow.slice(0, 2).join('.mp3, ')}.mp3...
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {wordsToShow.map((word, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2">
                        <span className="font-bold text-primary-500 w-5 text-sm">{idx + 1}.</span>
                        <span className="flex-1 font-mono text-sm truncate">{word}</span>
                        {text.word_audio?.[idx] ? (
                          <div className="flex items-center gap-1">
                            <audio 
                              src={`${API}${text.word_audio[idx]}`} 
                              className="w-20 h-6"
                              controls
                            />
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="audio/*"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const formData = new FormData();
                                    formData.append("file", file);
                                    formData.append("word_index", idx);
                                    try {
                                      await api.post(`/texts/${text.id}/spelling-word-audio`, formData);
                                      toast.success(`Oudio vir "${word}" vervang!`);
                                      const res = await api.get("/texts");
                                      setTexts(res.data);
                                    } catch (err) {
                                      toast.error(t('toast.couldNotUpload'));
                                    }
                                  }
                                }}
                              />
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs cursor-pointer hover:bg-blue-200">
                                <Upload className="w-3 h-3 inline" />
                              </span>
                            </label>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="audio/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  const formData = new FormData();
                                  formData.append("file", file);
                                  formData.append("word_index", idx);
                                  try {
                                    await api.post(`/texts/${text.id}/spelling-word-audio`, formData);
                                    toast.success(`Oudio vir "${word}" opgelaai!`);
                                    const res = await api.get("/texts");
                                    setTexts(res.data);
                                  } catch (err) {
                                    toast.error(t('toast.couldNotUpload'));
                                  }
                                }
                              }}
                            />
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs cursor-pointer hover:bg-orange-200 flex items-center gap-1">
                              <Volume2 className="w-3 h-3" /> Laai Op
                            </span>
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Export/Import Section */}
      <Card className="bg-slate-50" testId="export-import-section">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-sm">Rugsteun & Herstel</h4>
            <p className="text-xs text-text-muted">Laai af of voer tekste in om data te behou</p>
          </div>
          <div className="flex gap-2">
            <input
              type="file"
              accept=".json"
              className="hidden"
              id="import-texts-file"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                
                try {
                  const content = await file.text();
                  const data = JSON.parse(content);
                  
                  const res = await api.post("/texts/import/bulk", data);
                  toast.success(res.data.message);
                  
                  // Reload texts
                  const textsRes = await api.get("/texts");
                  setTexts(textsRes.data);
                } catch (err) {
                  toast.error(t('toast.couldNotImport') + ": " + (err.response?.data?.detail || err.message));
                }
                e.target.value = "";
              }}
            />
            <button
              onClick={async () => {
                try {
                  const res = await api.get("/texts/export/all");
                  const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `lees-is-duidelik-tekste-${new Date().toISOString().split("T")[0]}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success(`${res.data.total_texts} tekste afgelaai!`);
                } catch (err) {
                  toast.error(t('toast.couldNotExport'));
                }
              }}
              className="px-3 py-1.5 bg-secondary-500 text-white rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-secondary-600"
              data-testid="export-texts-btn"
            >
              <ChevronDown className="w-4 h-4" /> Uitvoer
            </button>
            <button
              onClick={() => document.getElementById("import-texts-file")?.click()}
              className="px-3 py-1.5 bg-primary-500 text-white rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-primary-600"
              data-testid="import-texts-btn"
            >
              <ChevronUp className="w-4 h-4" /> Invoer
            </button>
          </div>
        </div>
      </Card>

      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold">Bestaande Tekste ({texts.length})</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode("folders")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 transition-colors ${
              viewMode === "folders" ? "bg-primary-500 text-white" : "bg-slate-100 text-text-secondary hover:bg-slate-200"
            }`}
            data-testid="folder-view-btn"
          >
            <Folder className="w-4 h-4" /> Vouers
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 transition-colors ${
              viewMode === "list" ? "bg-primary-500 text-white" : "bg-slate-100 text-text-secondary hover:bg-slate-200"
            }`}
            data-testid="list-view-btn"
          >
            <FileText className="w-4 h-4" /> Lys
          </button>
        </div>
      </div>

      {/* Folder View */}
      {viewMode === "folders" && (
        <div className="space-y-3">
          {texts.length === 0 ? (
            <Card><p className="text-text-muted">Geen tekste geskep nie</p></Card>
          ) : (
            [1,2,3,4,5,6,7,8,9].map(grade => {
              const gradeCount = getGradeCount(grade);
              const isGradeExpanded = expandedGrade === grade;
              
              return (
                <Card key={grade} className="!p-0 overflow-hidden" testId={`grade-folder-${grade}`}>
                  <button
                    onClick={() => setExpandedGrade(isGradeExpanded ? null : grade)}
                    className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`expand-grade-${grade}`}
                  >
                    <div className="flex items-center gap-3">
                      {isGradeExpanded ? (
                        <FolderOpen className="w-6 h-6 text-primary-500" />
                      ) : (
                        <Folder className="w-6 h-6 text-slate-400" />
                      )}
                      <span className="font-semibold">Graad {grade}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${gradeCount > 0 ? "bg-primary-100 text-primary-700" : "bg-slate-100 text-slate-500"}`}>
                        {gradeCount} tekste
                      </span>
                    </div>
                    {isGradeExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  
                  {isGradeExpanded && (
                    <div className="border-t bg-slate-50 p-4 space-y-3">
                      {["comprehension", "reading", "spelling", "listening"].map(type => {
                        const typeCount = getTypeCount(grade, type);
                        const isTypeExpanded = expandedType === `${grade}-${type}`;
                        const typeTexts = organizedTexts[grade][type];
                        
                        return (
                          <div key={type} className="bg-white rounded-lg border overflow-hidden">
                            <button
                              onClick={() => setExpandedType(isTypeExpanded ? null : `${grade}-${type}`)}
                              className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
                              data-testid={`expand-type-${grade}-${type}`}
                            >
                              <div className="flex items-center gap-2">
                                {isTypeExpanded ? (
                                  <FolderOpen className="w-5 h-5 text-secondary-500" />
                                ) : (
                                  <Folder className="w-5 h-5 text-slate-300" />
                                )}
                                <span className="font-medium text-sm">{textTypeLabels[type]}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${typeCount > 0 ? "bg-secondary-100 text-secondary-700" : "bg-slate-100 text-slate-500"}`}>
                                  {typeCount}
                                </span>
                              </div>
                              {isTypeExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            
                            {isTypeExpanded && (
                              <div className="border-t p-3 space-y-2 bg-slate-50">
                                {typeTexts.length === 0 ? (
                                  <p className="text-sm text-text-muted text-center py-2">Geen tekste in hierdie kategorie nie</p>
                                ) : (
                                  typeTexts.map(text => renderTextItem(text))
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* List View (Original) */}
      {viewMode === "list" && (
        <div className="space-y-4">
          {texts.length === 0 ? (
            <Card><p className="text-text-muted">Geen tekste geskep nie</p></Card>
          ) : (
            texts.map(text => (
              <Card key={text.id} testId={`text-${text.id}`}>
                {editingText === text.id ? (
                  /* Edit Mode */
                  <div className="space-y-4">
                    <h4 className="font-semibold text-primary-500">Wysig Teks</h4>
                    <Input
                      label="Titel"
                      value={editForm.title}
                      onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-text-secondary mb-2">Graad</label>
                        <select
                          className="input-field"
                          value={editForm.grade_level}
                          onChange={(e) => setEditForm({...editForm, grade_level: parseInt(e.target.value)})}
                        >
                          {[1,2,3,4,5,6,7,8,9].map(g => <option key={g} value={g}>Graad {g}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-text-secondary mb-2">Tipe</label>
                        <select
                          className="input-field"
                          value={editForm.text_type}
                          onChange={(e) => setEditForm({...editForm, text_type: e.target.value})}
                        >
                          <option value="comprehension">Begripstoets</option>
                          <option value="reading">Hardoplees</option>
                          <option value="spelling">Spelling</option>
                          <option value="listening">Luistertoets</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">Inhoud</label>
                      <textarea
                        className="input-field min-h-[150px]"
                        value={editForm.content}
                        onChange={(e) => setEditForm({...editForm, content: e.target.value})}
                      />
                    </div>
                    
                    {/* Spelling Word Count for spelling tests */}
                    {editForm.text_type === "spelling" && (
                      <div>
                        <label className="block text-sm font-semibold text-text-secondary mb-2">
                          Aantal Woorde vir Toets <span className="text-xs text-text-muted">(Opsioneel)</span>
                        </label>
                        <input
                          type="number"
                          className="input-field"
                          placeholder="bv. 10 (leeg = alle woorde)"
                          min="1"
                          max="50"
                          value={editForm.spelling_word_count || ""}
                          onChange={(e) => setEditForm({...editForm, spelling_word_count: e.target.value ? parseInt(e.target.value) : null})}
                          data-testid="edit-spelling-word-count"
                        />
                      </div>
                    )}
                    
                    {/* Spelling Word Audio Upload Section */}
                    {editForm.text_type === "spelling" && editForm.content && (
                      <div className="border-t pt-4 mt-4">
                        <h5 className="font-semibold text-primary-500 mb-3">🔊 Woord Oudio Oplaai</h5>
                        <p className="text-sm text-text-muted mb-3">Laai jou eie oudio op vir elke woord (MP3, WAV, M4A)</p>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {(() => {
                            const words = editForm.content.split(/[,\n]+/).filter(w => w.trim()).map(w => 
                              w.replace(/^\d+\.\s*/, '').replace(/\*\*/g, '').split(' - ')[0].split(' – ')[0].trim()
                            );
                            const wordCount = editForm.spelling_word_count && editForm.spelling_word_count > 0 
                              ? Math.min(editForm.spelling_word_count, words.length)
                              : words.length;
                            const wordsToShow = words.slice(0, wordCount);
                            
                            return wordsToShow.map((word, idx) => (
                              <div key={idx} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                                <span className="font-bold text-primary-500 w-6">{idx + 1}.</span>
                                <span className="flex-1 font-mono font-semibold">{word}</span>
                                {editText?.word_audio?.[idx] ? (
                                  <div className="flex items-center gap-2">
                                    <audio src={`${API}${editText.word_audio[idx]}`} controls className="h-8 w-32" />
                                    <Check className="w-4 h-4 text-green-500" />
                                    <label className="cursor-pointer">
                                      <input
                                        type="file"
                                        accept="audio/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                          const file = e.target.files?.[0];
                                          if (file) {
                                            const formData = new FormData();
                                            formData.append("file", file);
                                            formData.append("word_index", idx);
                                            try {
                                              await api.post(`/texts/${editText.id}/spelling-word-audio`, formData);
                                              toast.success(`Oudio vir "${word}" vervang!`);
                                              const res = await api.get("/texts");
                                              setTexts(res.data);
                                              // Update editText to show new audio
                                              const updated = res.data.find(t => t.id === editText.id);
                                              if (updated) setEditText(updated);
                                            } catch (err) {
                                              toast.error(t('toast.couldNotUpload'));
                                            }
                                          }
                                        }}
                                      />
                                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs cursor-pointer hover:bg-blue-200 flex items-center gap-1">
                                        <Upload className="w-3 h-3" /> Vervang
                                      </span>
                                    </label>
                                  </div>
                                ) : (
                                  <label className="cursor-pointer">
                                    <input
                                      type="file"
                                      accept="audio/*"
                                      className="hidden"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          const formData = new FormData();
                                          formData.append("file", file);
                                          formData.append("word_index", idx);
                                          try {
                                            await api.post(`/texts/${editText.id}/spelling-word-audio`, formData);
                                            toast.success(`Oudio vir "${word}" opgelaai!`);
                                            const res = await api.get("/texts");
                                            setTexts(res.data);
                                            // Update editText to show new audio
                                            const updated = res.data.find(t => t.id === editText.id);
                                            if (updated) setEditText(updated);
                                          } catch (err) {
                                            toast.error(t('toast.couldNotUpload'));
                                          }
                                        }
                                      }}
                                    />
                                    <span className="px-3 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm cursor-pointer hover:bg-orange-200 flex items-center gap-2">
                                      <Volume2 className="w-4 h-4" /> Laai Oudio Op
                                    </span>
                                  </label>
                                )}
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    )}
                    
                    {/* Questions Edit Section */}
                    {editForm.questions && editForm.questions.length > 0 && (
                      <div className="border-t pt-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-semibold text-primary-500">Vrae ({editForm.questions.length})</h5>
                        </div>
                        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                          {editForm.questions.map((q, idx) => (
                            <div key={q.id || idx} className="bg-slate-50 rounded-lg p-3">
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <span className="bg-primary-500 text-white text-xs px-2 py-1 rounded-full">{idx + 1}</span>
                                <button
                                  onClick={() => {
                                    const newQuestions = editForm.questions.filter((_, i) => i !== idx);
                                    setEditForm({...editForm, questions: newQuestions});
                                  }}
                                  className="text-red-500 hover:bg-red-50 p-1 rounded"
                                  title="Verwyder vraag"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  className="input-field text-sm"
                                  placeholder="Vraag"
                                  value={q.question_text || ""}
                                  onChange={(e) => {
                                    const newQuestions = [...editForm.questions];
                                    newQuestions[idx] = {...newQuestions[idx], question_text: e.target.value};
                                    setEditForm({...editForm, questions: newQuestions});
                                  }}
                                />
                                <div className="grid grid-cols-2 gap-2">
                                  <select
                                    className="input-field text-sm"
                                    value={q.question_type || "typed"}
                                    onChange={(e) => {
                                      const newQuestions = [...editForm.questions];
                                      newQuestions[idx] = {...newQuestions[idx], question_type: e.target.value};
                                      setEditForm({...editForm, questions: newQuestions});
                                    }}
                                  >
                                    <option value="typed">Tik Antwoord</option>
                                    <option value="multiple_choice">Meervoudige Keuse</option>
                                  </select>
                                  <input
                                    type="number"
                                    className="input-field text-sm"
                                    placeholder="Punte"
                                    value={q.points || 1}
                                    onChange={(e) => {
                                      const newQuestions = [...editForm.questions];
                                      newQuestions[idx] = {...newQuestions[idx], points: parseInt(e.target.value) || 1};
                                      setEditForm({...editForm, questions: newQuestions});
                                    }}
                                  />
                                </div>
                                <input
                                  type="text"
                                  className="input-field text-sm"
                                  placeholder="Korrekte Antwoord"
                                  value={q.correct_answer || ""}
                                  onChange={(e) => {
                                    const newQuestions = [...editForm.questions];
                                    newQuestions[idx] = {...newQuestions[idx], correct_answer: e.target.value};
                                    setEditForm({...editForm, questions: newQuestions});
                                  }}
                                />
                                <div>
                                  <label className="text-xs text-text-muted">Sleutelwoorde (komma-geskeide, opsioneel)</label>
                                  <input
                                    type="text"
                                    className="input-field text-sm"
                                    placeholder="bv. graan, meel, witbrood"
                                    value={(q.keywords || []).join(", ")}
                                    onChange={(e) => {
                                      const newQuestions = [...editForm.questions];
                                      newQuestions[idx] = {...newQuestions[idx], keywords: e.target.value.split(",").map(k => k.trim()).filter(k => k)};
                                      setEditForm({...editForm, questions: newQuestions});
                                    }}
                                  />
                                </div>
                                {q.question_type === "multiple_choice" && (
                                  <div>
                                    <label className="text-xs text-text-muted">Opsies (komma-geskeide)</label>
                                    <input
                                      type="text"
                                      className="input-field text-sm"
                                      placeholder="Opsie 1, Opsie 2, Opsie 3, Opsie 4"
                                      value={(q.options || []).join(", ")}
                                      onChange={(e) => {
                                        const newQuestions = [...editForm.questions];
                                        newQuestions[idx] = {...newQuestions[idx], options: e.target.value.split(",").map(o => o.trim())};
                                        setEditForm({...editForm, questions: newQuestions});
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Add New Question */}
                        <button
                          onClick={() => {
                            const newQuestion = {
                              id: `new-${Date.now()}`,
                              question_text: "",
                              question_type: "typed",
                              options: [],
                              correct_answer: "",
                              points: 1,
                              order: editForm.questions.length + 1
                            };
                            setEditForm({...editForm, questions: [...editForm.questions, newQuestion]});
                          }}
                          className="mt-3 w-full py-2 border-2 border-dashed border-primary-300 text-primary-500 rounded-lg hover:bg-primary-50 text-sm font-medium"
                        >
                          + Voeg Nuwe Vraag By
                        </button>
                      </div>
                    )}
                    
                    {/* Add questions section if text has no questions */}
                    {(!editForm.questions || editForm.questions.length === 0) && (
                      <div className="border-t pt-4 mt-4">
                        <button
                          onClick={() => {
                            const newQuestion = {
                              id: `new-${Date.now()}`,
                              question_text: "",
                              question_type: "typed",
                              options: [],
                              correct_answer: "",
                              points: 1,
                              order: 1
                            };
                            setEditForm({...editForm, questions: [newQuestion]});
                          }}
                          className="w-full py-3 border-2 border-dashed border-primary-300 text-primary-500 rounded-lg hover:bg-primary-50 text-sm font-medium"
                        >
                          + Voeg Vrae By Hierdie Teks
                        </button>
                      </div>
                    )}
                    
                    <div className="flex gap-3">
                      <Button onClick={handleSaveEdit} testId={`save-edit-${text.id}`}>
                        Stoor Wysigings
                      </Button>
                      <Button variant="secondary" onClick={handleCancelEdit} testId={`cancel-edit-${text.id}`}>
                        Kanselleer
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{text.title}</h4>
                        {text.audio_url && <Volume2 className="w-4 h-4 text-primary-500" title="Het oudio" />}
                        {text.image_url && <Image className="w-4 h-4 text-blue-500" title="Het prent" />}
                      </div>
                      <p className="text-sm text-text-muted">
                        Graad {text.grade_level} | {textTypeLabels[text.text_type] || text.text_type} | 
                        {text.is_ai_generated ? " AI" : " Handmatig"}
                        {text.questions?.length > 0 && ` | ${text.questions.length} vrae`}
                        {text.image_url && " | 🖼️ Prent"}
                      </p>
                      
                      <button
                        onClick={() => setExpandedText(expandedText === text.id ? null : text.id)}
                        className="text-sm text-primary-500 mt-1 flex items-center gap-1"
                      >
                        {expandedText === text.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {expandedText === text.id ? "Versteek" : "Wys meer"}
                      </button>
                      
                      {expandedText === text.id && (
                        <div className="mt-3 bg-slate-50 rounded-lg p-3">
                          <p className="text-sm whitespace-pre-wrap">{text.content}</p>
                          
                          {text.questions?.length > 0 && (
                            <div className="mt-3 border-t pt-3">
                              <p className="text-sm font-semibold mb-2">Vrae:</p>
                              {text.questions.map((q, i) => (
                                <div key={q.id || i} className="text-sm mb-2">
                                  <span className="font-medium">{i + 1}. {q.question_text}</span>
                                  <span className="text-text-muted ml-2">(Antw: {q.correct_answer})</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Image Upload for Grade 1-3 in List View */}
                          {text.grade_level <= 3 && (
                            <div className="mt-3 border-t pt-3">
                              <p className="text-sm font-semibold mb-2">Prent (Graad 1-3):</p>
                              {text.image_url ? (
                                <div className="mb-2">
                                  <img src={`${API}${text.image_url}`} alt={text.title} className="max-w-[200px] h-auto rounded border" />
                                </div>
                              ) : (
                                <p className="text-xs text-blue-600 mb-2">💡 Voeg 'n prent by vir jonger leerders</p>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                id={`image-upload-list-${text.id}`}
                                className="hidden"
                                onChange={(e) => e.target.files?.[0] && handleImageUpload(text.id, e.target.files[0])}
                              />
                              <button
                                onClick={() => document.getElementById(`image-upload-list-${text.id}`)?.click()}
                                className="flex items-center gap-1 text-sm px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                disabled={uploadingImage === text.id}
                                data-testid={`upload-image-list-${text.id}`}
                              >
                                <Image className="w-4 h-4" />
                                {uploadingImage === text.id ? "Laai..." : text.image_url ? "Vervang Prent" : "Laai Prent Op"}
                              </button>
                            </div>
                          )}

                          {/* Audio Upload & Recording for Listening/Spelling */}
                          {(text.text_type === "listening" || text.text_type === "spelling") && (
                            <div className="mt-3 border-t pt-3">
                              <p className="text-sm font-semibold mb-2">Oudio Opname:</p>
                              {text.audio_url ? (
                                <audio controls src={`${API}${text.audio_url}`} className="w-full" />
                              ) : (
                                <p className="text-sm text-text-muted">Geen oudio opgelaai nie</p>
                              )}
                              
                              <div className="flex flex-wrap gap-2 mt-3">
                                {/* Upload Audio File */}
                                <input
                                  type="file"
                                  accept="audio/*"
                                  ref={audioInputRef}
                                  className="hidden"
                                  onChange={(e) => e.target.files?.[0] && handleAudioUpload(text.id, e.target.files[0])}
                                />
                                <button
                                  onClick={() => audioInputRef.current?.click()}
                                  className="flex items-center gap-2 text-sm px-3 py-2 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
                                  disabled={uploadingAudio === text.id || isRecording}
                                  data-testid={`upload-audio-${text.id}`}
                                >
                                  <Upload className="w-4 h-4" />
                                  {uploadingAudio === text.id ? "Laai op..." : "Laai Lêer Op"}
                                </button>
                                
                                {/* Record Audio */}
                                {isRecording && recordingTextId === text.id ? (
                                  <button
                                    onClick={stopRecording}
                                    className="flex items-center gap-2 text-sm px-3 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors animate-pulse"
                                    data-testid={`stop-recording-${text.id}`}
                                  >
                                    <Pause className="w-4 h-4" />
                                    Stop Opname
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => startRecording(text.id)}
                                    className="flex items-center gap-2 text-sm px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                                    disabled={isRecording}
                                    data-testid={`start-recording-${text.id}`}
                                  >
                                    <Mic className="w-4 h-4" />
                                    Neem Op
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleStartEdit(text)}
                        className="p-2 text-primary-500 hover:bg-primary-50 rounded-lg"
                        data-testid={`edit-text-${text.id}`}
                        title="Wysig teks"
                      >
                        <Edit3 className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handleDeleteText(text.id)}
                        className="p-2 text-accent-500 hover:bg-accent-50 rounded-lg"
                        data-testid={`delete-text-${text.id}`}
                        title="Verwyder teks"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Learners Tab Component with detailed view and messaging
const LearnersTab = ({ learners, setLearners }) => {
  const { t, i18n } = useTranslation();
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [learnerDetail, setLearnerDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messageType, setMessageType] = useState("progress_report");
  const [customMessage, setCustomMessage] = useState("");
  const [generatedMessage, setGeneratedMessage] = useState(null);
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState({});
  const [exerciseHistory, setExerciseHistory] = useState([]);
  const [showExerciseHistory, setShowExerciseHistory] = useState(false);
  // Password reset state
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);

  const loadLearnerDetail = async (learnerId) => {
    setLoading(true);
    try {
      const res = await api.get(`/learners/${learnerId}`);
      setLearnerDetail(res.data);
      setContactForm({
        parent_email: res.data.learner.parent_email || "",
        parent_whatsapp: res.data.learner.parent_whatsapp || "",
        parent_phone: res.data.learner.parent_phone || ""
      });
      // Load exercise history
      try {
        const historyRes = await api.get(`/admin/learners/${learnerId}/exercises`);
        setExerciseHistory(historyRes.data.exercises || []);
      } catch (err) {
        console.log("Could not load exercise history");
        setExerciseHistory([]);
      }
    } catch (err) {
      toast.error(t('toast.couldNotLoadLearnerInfo'));
    }
    setLoading(false);
  };

  const handleSelectLearner = (learner) => {
    setSelectedLearner(learner);
    loadLearnerDetail(learner.learner.id);
    setGeneratedMessage(null);
  };

  // Suspend/Unsuspend learner
  const toggleSuspension = async (learnerId, currentlySuspended) => {
    try {
      await api.post("/admin/learner/suspend", {
        learner_id: learnerId,
        suspended: !currentlySuspended,
        reason: currentlySuspended ? null : "Betaling uitstaande"
      });
      toast.success(currentlySuspended 
        ? (i18n.language === 'en' ? "Learner activated" : "Leerder geaktiveer")
        : (i18n.language === 'en' ? "Learner suspended" : "Leerder gesuspendeer"));
      // Refresh learner data
      const res = await api.get("/progress/all");
      setLearners(res.data);
      if (learnerDetail) {
        loadLearnerDetail(learnerId);
      }
    } catch (err) {
      toast.error(t('toast.couldNotSave'));
    }
  };

  // Delete learner permanently
  const deleteLearner = async (learnerId, learnerName) => {
    const confirmMsg = i18n.language === 'en' 
      ? `Are you sure you want to permanently delete ${learnerName}? All data will be lost!`
      : `Is jy seker jy wil ${learnerName} permanent uitvee? Alle data sal verlore gaan!`;
    if (!window.confirm(confirmMsg)) {
      return;
    }
    try {
      await api.delete(`/admin/learners/${learnerId}`);
      toast.success(t('toast.learnerDeleted'));
      // Refresh learner data
      const res = await api.get("/progress/all");
      setLearners(res.data);
      // Clear learner detail if viewing deleted learner
      if (learnerDetail?.learner?.id === learnerId) {
        setLearnerDetail(null);
      }
    } catch (err) {
      toast.error(t('toast.couldNotDelete'));
    }
  };

  const generateNotification = async () => {
    try {
      const res = await api.post("/notifications/generate", {
        learner_id: learnerDetail.learner.id,
        message_type: messageType,
        custom_message: messageType === "custom" ? customMessage : null
      });
      setGeneratedMessage(res.data);
      toast.success(i18n.language === 'en' ? "Message generated!" : "Boodskap gegenereer!");
    } catch (err) {
      toast.error(t('toast.couldNotGenerate'));
    }
  };

  const copyMessage = () => {
    if (generatedMessage?.message) {
      navigator.clipboard.writeText(generatedMessage.message);
      toast.success(t('toast.copiedToClipboard'));
    }
  };

  const saveContact = async () => {
    try {
      await api.put(`/learners/${learnerDetail.learner.id}`, contactForm);
      toast.success(t('toast.saved'));
      setEditingContact(false);
      loadLearnerDetail(learnerDetail.learner.id);
    } catch (err) {
      toast.error(t('toast.couldNotSave'));
    }
  };

  const generateParentLink = async () => {
    try {
      const res = await api.post("/parent/generate-link", {
        learner_id: learnerDetail.learner.id
      });
      const parentUrl = `${window.location.origin}/parent/${res.data.token}`;
      navigator.clipboard.writeText(parentUrl);
      toast.success(t('toast.copiedToClipboard'));
    } catch (err) {
      toast.error(t('toast.couldNotSave'));
    }
  };

  // Reset learner password
  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error(i18n.language === 'en' ? "Password must be at least 4 characters" : "Wagwoord moet ten minste 4 karakters wees");
      return;
    }
    setResettingPassword(true);
    try {
      const res = await api.post(`/admin/learners/${learnerDetail.learner.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(t('toast.passwordChanged'));
      setResettingPassword(false);
      setNewPassword("");
    } catch (err) {
      toast.error(t('toast.couldNotSave'));
    }
    setResettingPassword(false);
  };

  if (selectedLearner && learnerDetail) {
    return (
      <div className="space-y-6">
        <button 
          onClick={() => { setSelectedLearner(null); setLearnerDetail(null); }}
          className="flex items-center gap-2 text-text-muted hover:text-text-primary"
        >
          <ArrowLeft className="w-4 h-4" /> Terug na lys
        </button>

        {loading ? (
          <Card><p className="text-center">Laai...</p></Card>
        ) : (
          <>
            {/* Learner Info Card */}
            <Card testId="learner-detail-card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                    {learnerDetail.learner.name} {learnerDetail.learner.surname}
                    <span className="text-lg" title={learnerDetail.learner.preferred_language === "en" ? "English" : "Afrikaans"}>
                      {learnerDetail.learner.preferred_language === "en" ? "🇬🇧" : "🇿🇦"}
                    </span>
                  </h2>
                  <p className="text-text-secondary">
                    Graad {learnerDetail.learner.grade} | 
                    Leesvlak: Graad {learnerDetail.learner.current_reading_level} | 
                    Taal: {learnerDetail.learner.preferred_language === "en" ? "English" : "Afrikaans"}
                    {learnerDetail.learner.school_name && ` | ${learnerDetail.learner.school_name}`}
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  learnerDetail.subscription?.active 
                    ? "bg-primary-100 text-primary-700" 
                    : "bg-accent-100 text-accent-700"
                }`}>
                  {learnerDetail.subscription?.active ? "Aktief" : "Onaktief"}
                </div>
              </div>

              {/* Contact Details */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">{i18n.language === 'en' ? 'Contact Details' : 'Kontakbesonderhede'}</h3>
                  <button 
                    onClick={() => setEditingContact(!editingContact)}
                    className="text-primary-500 text-sm"
                  >
                    {editingContact ? t('cancel') : t('admin.editContact')}
                  </button>
                </div>

                {editingContact ? (
                  <div className="space-y-3">
                    <Input
                      label={t('auth.parentEmail')}
                      type="email"
                      value={contactForm.parent_email}
                      onChange={(e) => setContactForm({...contactForm, parent_email: e.target.value})}
                      testId="edit-parent-email"
                    />
                    <Input
                      label={i18n.language === 'en' ? 'WhatsApp Number' : 'WhatsApp Nommer'}
                      value={contactForm.parent_whatsapp}
                      onChange={(e) => setContactForm({...contactForm, parent_whatsapp: e.target.value})}
                      placeholder={i18n.language === 'en' ? 'e.g. 0821234567' : 'bv. 0821234567'}
                      testId="edit-parent-whatsapp"
                    />
                    <Input
                      label={i18n.language === 'en' ? 'Phone Number' : 'Telefoonnommer'}
                      value={contactForm.parent_phone}
                      onChange={(e) => setContactForm({...contactForm, parent_phone: e.target.value})}
                      testId="edit-parent-phone"
                    />
                    <Button onClick={saveContact} testId="save-contact-btn">
                      {t('save')}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-text-muted" />
                      <span>{learnerDetail.learner.parent_email || (i18n.language === 'en' ? 'Not provided' : 'Nie verskaf nie')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-text-muted" />
                      <span>{learnerDetail.learner.parent_whatsapp || "Nie verskaf nie"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-text-muted" />
                      <span>{learnerDetail.learner.parent_phone || "Nie verskaf nie"}</span>
                    </div>
                  </div>
                )}
                
                {/* Login Details Section - Username and Password Reset */}
                <div className="mt-4 pt-4 border-t">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {i18n.language === 'en' ? 'Login Details' : 'Aanmeld Besonderhede'}
                  </h3>
                  <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                    {/* Username */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-text-muted uppercase tracking-wide">
                          {i18n.language === 'en' ? 'Username' : 'Gebruikersnaam'}
                        </p>
                        <p className="font-mono text-lg font-semibold text-primary-700 select-all">
                          {learnerDetail.learner.username}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(learnerDetail.learner.username);
                          toast.success(i18n.language === 'en' ? 'Username copied!' : 'Gebruikersnaam gekopieer!');
                        }}
                        className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                        title={i18n.language === 'en' ? 'Copy username' : 'Kopieer gebruikersnaam'}
                      >
                        <Copy className="w-4 h-4 text-slate-500" />
                      </button>
                    </div>
                    
                    {/* Password Reset */}
                    <div className="pt-3 border-t border-slate-200">
                      <p className="text-xs text-text-muted uppercase tracking-wide mb-2">
                        {i18n.language === 'en' ? 'Password' : 'Wagwoord'}
                      </p>
                      <p className="text-sm text-slate-500 mb-3">
                        {i18n.language === 'en' 
                          ? 'Passwords are encrypted and cannot be viewed. You can reset it below.'
                          : 'Wagwoorde is geënkripteer en kan nie gesien word nie. Jy kan dit hieronder herstel.'}
                      </p>
                      
                      {!resettingPassword ? (
                        <button
                          onClick={() => setResettingPassword(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors text-sm font-medium"
                          data-testid="reset-password-btn"
                        >
                          <Edit3 className="w-4 h-4" />
                          {i18n.language === 'en' ? 'Reset Password' : 'Herstel Wagwoord'}
                        </button>
                      ) : (
                        <div className="space-y-3 bg-white p-3 rounded-lg border border-amber-200">
                          <Input
                            label={i18n.language === 'en' ? 'New Password' : 'Nuwe Wagwoord'}
                            type="text"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder={i18n.language === 'en' ? 'Enter new password' : 'Voer nuwe wagwoord in'}
                            testId="new-password-input"
                          />
                          <div className="flex gap-2">
                            <Button 
                              onClick={resetPassword}
                              disabled={!newPassword || newPassword.length < 4}
                              testId="confirm-reset-password-btn"
                            >
                              {i18n.language === 'en' ? 'Save New Password' : 'Stoor Nuwe Wagwoord'}
                            </Button>
                            <button
                              onClick={() => {
                                setResettingPassword(false);
                                setNewPassword('');
                              }}
                              className="px-4 py-2 text-slate-500 hover:text-slate-700"
                            >
                              {t('cancel')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Payment Activation Section - Show based on payment status */}
                <div className="mt-4 pt-4 border-t">
                  {!learnerDetail.learner.invitation_code && (
                    <>
                      {(learnerDetail.subscription?.status === "pending_payment" || learnerDetail.subscription?.status === "monthly_expired" || (!learnerDetail.subscription?.active && !learnerDetail.learner.subscription_active)) ? (
                        <>
                          <div className={`${learnerDetail.subscription?.status === "monthly_expired" ? "bg-orange-50" : "bg-amber-50"} rounded-xl p-3 mb-3`}>
                            <p className={`${learnerDetail.subscription?.status === "monthly_expired" ? "text-orange-800" : "text-amber-800"} text-sm font-medium flex items-center gap-2`}>
                              <Clock className="w-4 h-4" />
                              {learnerDetail.subscription?.status === "monthly_expired" 
                                ? (i18n.language === 'en' ? "Monthly Subscription Expired - Renew Now" : "Maandelikse Subskripsie Verval - Hernu Nou")
                                : t('admin.waitingPayment')
                              }
                            </p>
                            <p className={`${learnerDetail.subscription?.status === "monthly_expired" ? "text-orange-600" : "text-amber-600"} text-xs mt-1`}>
                              {learnerDetail.subscription?.status === "monthly_expired"
                                ? t('admin.monthlyExpiredMsg')
                                : (i18n.language === 'en' ? "This learner registered without an invitation code and is waiting for admin payment confirmation." : "Hierdie leerder het geregistreer sonder 'n uitnodigingskode en wag vir admin bevestiging van betaling.")
                              }
                            </p>
                            {/* Show the subscription type the learner chose */}
                            <div className={`mt-2 p-2 bg-white rounded-lg border ${learnerDetail.subscription?.status === "monthly_expired" ? "border-orange-200" : "border-amber-200"}`}>
                              <p className="text-xs text-amber-700 font-medium">
                                {learnerDetail.subscription?.status === "monthly_expired" 
                                  ? (i18n.language === 'en' ? "Subscription type: " : "Subskripsie tipe: ")
                                  : (i18n.language === 'en' ? "Chosen payment option: " : "Gekose betaling opsie: ")
                                }
                                <span className={`font-bold ${learnerDetail.learner.subscription_type === 'monthly' ? 'text-secondary-600' : 'text-primary-600'}`}>
                                  {learnerDetail.learner.subscription_type === 'monthly' 
                                    ? (i18n.language === 'en' ? 'Monthly (R100/month)' : 'Maandeliks (R100/maand)') 
                                    : (i18n.language === 'en' ? 'Once-off (R399)' : 'Eenmalig (R399)')
                                  }
                                </span>
                              </p>
                              {/* Show activation count if renewal */}
                              {learnerDetail.learner.activation_count > 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {i18n.language === 'en' ? 'Previous activations:' : 'Vorige aktiverings:'} {learnerDetail.learner.activation_count}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const subType = learnerDetail.learner.subscription_type || 'once_off';
                                const res = await api.post('/admin/learner/activate-payment', {
                                  learner_id: learnerDetail.learner.id,
                                  subscription_type: subType,
                                  duration_months: subType === 'once_off' ? 12 : 1
                                });
                                toast.success(res.data.message || (i18n.language === 'en' ? "Account activated!" : "Rekening geaktiveer!"));
                                loadLearnerDetail(learnerDetail.learner.id);
                              } catch (err) {
                                toast.error(i18n.language === 'en' ? "Could not activate account" : "Kon nie rekening aktiveer nie");
                              }
                            }}
                            className={`w-full flex items-center justify-center gap-2 p-3 rounded-xl transition-colors font-medium ${
                              learnerDetail.learner.subscription_type === 'monthly' 
                                ? 'bg-secondary-500 text-white hover:bg-secondary-600' 
                                : 'bg-green-500 text-white hover:bg-green-600'
                            }`}
                            data-testid="activate-payment-btn"
                          >
                            <Check className="w-5 h-5" />
                            {learnerDetail.subscription?.status === "monthly_expired"
                              ? t('admin.renewMonthly')
                              : (learnerDetail.learner.subscription_type === 'monthly' 
                                  ? t('admin.activateMonthly') 
                                  : t('admin.activateLifetime'))
                            }
                          </button>
                        </>
                      ) : (
                        <div className="flex gap-2">
                          <div className="flex-1 bg-green-50 rounded-xl p-3">
                            <p className="text-green-700 text-sm font-medium flex items-center gap-2">
                              <Check className="w-4 h-4" />
                              {i18n.language === 'en' ? 'Account Active' : 'Rekening Aktief'}
                            </p>
                            <p className="text-green-600 text-xs mt-1">
                              {learnerDetail.subscription?.type === "lifetime" 
                                ? (i18n.language === 'en' ? "Lifetime (Once-off payment)" : "Lewenslank (Eenmalige betaling)") : 
                               learnerDetail.subscription?.type === "monthly" ? (
                                 <>
                                   <span className="text-secondary-600 font-medium">{i18n.language === 'en' ? 'Monthly' : 'Maandeliks'}</span> - {learnerDetail.subscription?.days_left} {i18n.language === 'en' ? 'days left' : 'dae oor'}
                                   {learnerDetail.subscription?.days_left <= 7 && (
                                     <span className="text-amber-600 ml-1">({i18n.language === 'en' ? 'Expires soon!' : 'Verval binnekort!'})</span>
                                   )}
                                 </>
                               ) :
                               learnerDetail.subscription?.type === "school" 
                                 ? (i18n.language === 'en' ? "School Subscription" : "Skool Subskripsie") 
                                 : (i18n.language === 'en' ? "Active" : "Aktief")}
                            </p>
                          </div>
                          <button
                            onClick={async () => {
                              if (!window.confirm(i18n.language === 'en' ? "Are you sure you want to deactivate this account?" : "Is jy seker jy wil hierdie rekening deaktiveer?")) return;
                              try {
                                await api.post('/admin/learner/deactivate-payment', {
                                  learner_id: learnerDetail.learner.id
                                });
                                toast.success(i18n.language === 'en' ? "Account deactivated" : "Rekening gedeaktiveer");
                                loadLearnerDetail(learnerDetail.learner.id);
                              } catch (err) {
                                toast.error(i18n.language === 'en' ? "Could not deactivate account" : "Kon nie rekening deaktiveer nie");
                              }
                            }}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-colors text-sm font-medium"
                            data-testid="deactivate-payment-btn"
                          >
                            {i18n.language === 'en' ? 'Deactivate' : 'Deaktiveer'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  {learnerDetail.learner.invitation_code && (
                    <div className="bg-blue-50 rounded-xl p-3">
                      <p className="text-blue-700 text-sm font-medium flex items-center gap-2">
                        <Star className="w-4 h-4" />
                        {i18n.language === 'en' ? 'Registered with Invitation Code' : 'Geregistreer met Uitnodigingskode'}
                      </p>
                      <p className="text-blue-600 text-xs mt-1">
                        Kode: {learnerDetail.learner.invitation_code} • 
                        {learnerDetail.subscription?.type === "trial" ? ` Proeftyd: ${learnerDetail.subscription?.days_left} dae oor` : " Proeftyd verstreke"}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Parent Portal Link Button */}
                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={generateParentLink}
                    className="w-full flex items-center justify-center gap-2 p-3 bg-secondary-100 text-secondary-700 rounded-xl hover:bg-secondary-200 transition-colors font-medium"
                    data-testid="generate-parent-link-btn"
                  >
                    <Users className="w-5 h-5" />
                    Skep Ouer Portaal Skakel
                  </button>
                  <p className="text-xs text-text-muted mt-2 text-center">
                    Stuur hierdie skakel aan die ouer sodat hulle hul kind se vordering kan sien en betaal.
                  </p>
                </div>
                
                {/* Download Reading Progress Report PDF */}
                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={async () => {
                      try {
                        toast.loading(i18n.language === 'en' ? "Generating PDF..." : "PDF word gegenereer...");
                        // Pass language parameter to get language-specific PDF
                        const lang = learnerDetail.learner.preferred_language || i18n.language || "af";
                        const response = await api.get(`/reading-test/report/${learnerDetail.learner.id}/pdf?lang=${lang}`, {
                          responseType: 'blob'
                        });
                        toast.dismiss();
                        
                        // Create download link
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        const filename = lang === 'en' 
                          ? `ReadingProgressReport_${learnerDetail.learner.name}_${learnerDetail.learner.surname}.pdf`
                          : `Leesvorderingverslag_${learnerDetail.learner.name}_${learnerDetail.learner.surname}.pdf`;
                        link.setAttribute('download', filename);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        
                        toast.success(i18n.language === 'en' ? "Report downloaded!" : "Verslag afgelaai!");
                      } catch (err) {
                        toast.dismiss();
                        toast.error(i18n.language === 'en' ? "Could not download report" : "Kon nie verslag aflaai nie");
                        console.error(err);
                      }
                    }}
                    className="w-full flex items-center justify-center gap-2 p-3 bg-purple-100 text-purple-700 rounded-xl hover:bg-purple-200 transition-colors font-medium"
                    data-testid="download-reading-report-btn"
                  >
                    <FileText className="w-5 h-5" />
                    {t('admin.downloadReport')}
                  </button>
                  <p className="text-xs text-text-muted mt-2 text-center">
                    {t('admin.downloadReportDesc')}
                  </p>
                </div>
                
                {/* Reset Password Section */}
                <div className="mt-4 pt-4 border-t">
                  {!showResetPassword ? (
                    <button
                      onClick={() => setShowResetPassword(true)}
                      className="w-full flex items-center justify-center gap-2 p-3 bg-amber-100 text-amber-700 rounded-xl hover:bg-amber-200 transition-colors font-medium"
                      data-testid="show-reset-password-btn"
                    >
                      <RefreshCw className="w-5 h-5" />
                      Reset Wagwoord
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Nuwe Wagwoord Stel
                      </h4>
                      <Input
                        type="text"
                        label="Nuwe Wagwoord"
                        placeholder="Min 4 karakters"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        testId="new-password-input"
                      />
                      <div className="flex gap-2">
                        <Button 
                          onClick={resetPassword} 
                          disabled={resettingPassword || newPassword.length < 4}
                          testId="confirm-reset-password-btn"
                        >
                          {resettingPassword ? "Besig..." : "Bevestig"}
                        </Button>
                        <button
                          onClick={() => { setShowResetPassword(false); setNewPassword(""); }}
                          className="px-4 py-2 text-sm text-text-muted hover:text-text-primary"
                          data-testid="cancel-reset-password-btn"
                        >
                          Kanselleer
                        </button>
                      </div>
                      <p className="text-xs text-amber-600">
                        Die leerder sal die nuwe wagwoord gebruik om aan te meld.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Stats Card */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="text-center">
                <div className="text-3xl font-bold text-primary-500">{learnerDetail.stats.exercises_completed}</div>
                <div className="text-sm text-text-muted">Oefeninge</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-primary-500">{learnerDetail.stats.average_score}%</div>
                <div className="text-sm text-text-muted">Gemiddeld</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-primary-500">{learnerDetail.stats.reading_wpm}</div>
                <div className="text-sm text-text-muted">WPM</div>
              </Card>
            </div>

            {/* Strongest Point Card */}
            {learnerDetail.strongest_point?.strongest_type && (
              <Card testId="strongest-point-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <Star className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Sterkste Punt</h3>
                    <p className="text-sm text-text-muted">Gebaseer op oefeningtellings</p>
                  </div>
                </div>
                
                {/* Highlight strongest */}
                <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">⭐</span>
                      <span className="text-lg font-bold text-amber-700">{learnerDetail.strongest_point.strongest_label}</span>
                    </div>
                    <div className="text-2xl font-bold text-amber-600">
                      {learnerDetail.strongest_point.strongest_score}%
                    </div>
                  </div>
                </div>
                
                {/* All areas breakdown */}
                {Object.keys(learnerDetail.strongest_point.type_stats || {}).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-text-secondary">Alle Areas:</p>
                    {Object.entries(learnerDetail.strongest_point.type_stats)
                      .sort(([,a], [,b]) => b.average_score - a.average_score)
                      .map(([type, stats]) => (
                        <div key={type} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            {type === learnerDetail.strongest_point.strongest_type && (
                              <span className="text-amber-500">🌟</span>
                            )}
                            <span className="font-medium">{stats.label}</span>
                            <span className="text-xs text-text-muted">({stats.count} oefeninge)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-primary-500 h-2 rounded-full transition-all"
                                style={{ width: `${stats.average_score}%` }}
                              />
                            </div>
                            <span className="font-semibold text-sm w-12 text-right">{stats.average_score}%</span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </Card>
            )}

            {/* Reading Level Report */}
            <Card testId="reading-report-card">
              <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                <FileBarChart className="w-5 h-5" /> Leesvlak Verslag
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                Genereer 'n leesvlak verslag om met die skool te deel.
              </p>
              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-muted">Begin Vlak:</span>
                    <span className="font-semibold ml-2">Graad {learnerDetail.learner.initial_reading_level || learnerDetail.learner.grade}</span>
                  </div>
                  <div>
                    <span className="text-text-muted">Huidige Vlak:</span>
                    <span className="font-semibold ml-2">Graad {learnerDetail.learner.current_reading_level}</span>
                  </div>
                  <div>
                    <span className="text-text-muted">Begin WPM:</span>
                    <span className="font-semibold ml-2">{learnerDetail.learner.initial_wpm || 0}</span>
                  </div>
                  <div>
                    <span className="text-text-muted">Huidige WPM:</span>
                    <span className="font-semibold ml-2">{learnerDetail.learner.current_wpm || learnerDetail.learner.initial_wpm || 0}</span>
                  </div>
                </div>
              </div>
              <Button 
                onClick={async () => {
                  try {
                    const res = await api.get(`/reading-test/report/${learnerDetail.learner.id}`);
                    const report = res.data;
                    
                    // Format report as shareable text
                    const reportText = `📊 LEESVLAK VERSLAG - ${report.learner.name} ${report.learner.surname}
━━━━━━━━━━━━━━━━━━━━━━━━
📚 Skool: ${report.learner.school_name || "Nie gespesifiseer"}
📅 Graad: ${report.learner.grade}

📈 VORDERING:
• Begin Vlak: Graad ${report.reading_progress.initial_level}
• Huidige Vlak: Graad ${report.reading_progress.current_level}
• ${report.reading_progress.improvement_summary}

⏱️ LEESSPOED:
• Begin WPM: ${report.reading_progress.initial_wpm}
• Huidige WPM: ${report.reading_progress.current_wpm}
• Verbetering: ${report.reading_progress.wpm_improvement > 0 ? '+' : ''}${report.reading_progress.wpm_improvement} WPM

📝 Toetse Voltooi: ${report.tests_completed}
━━━━━━━━━━━━━━━━━━━━━━━━
Gegenereer: ${new Date(report.report_generated_at).toLocaleDateString("af-ZA")}
Lees is Duidelik`;

                    navigator.clipboard.writeText(reportText);
                    toast.success("Verslag gekopieer! Deel met die skool.");
                  } catch (err) {
                    toast.error("Kon nie verslag genereer nie");
                  }
                }}
                className="w-full"
                testId="generate-reading-report-btn"
              >
                <FileBarChart className="w-4 h-4 mr-2" /> Genereer & Kopieer Verslag
              </Button>
            </Card>

            {/* Message Generator */}
            <Card testId="message-generator">
              <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5" /> Stuur Kennisgewing
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                Genereer 'n boodskap om via WhatsApp te stuur (kopieer en stuur self).
              </p>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-text-secondary mb-2">Boodskap Tipe</label>
                <select
                  className="input-field"
                  value={messageType}
                  onChange={(e) => setMessageType(e.target.value)}
                  data-testid="message-type-select"
                >
                  <option value="progress_report">Vorderingsverslag</option>
                  <option value="billing">Subskripsie Herinnering</option>
                  <option value="custom">Persoonlike Boodskap</option>
                </select>
              </div>

              {messageType === "custom" && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-text-secondary mb-2">Jou Boodskap</label>
                  <textarea
                    className="input-field min-h-[100px]"
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Tik jou boodskap hier..."
                    data-testid="custom-message-input"
                  />
                </div>
              )}

              <Button onClick={generateNotification} className="w-full" testId="generate-message-btn">
                Genereer Boodskap
              </Button>

              {generatedMessage && (
                <div className="mt-4 p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">Gegenereerde Boodskap:</span>
                    <button
                      onClick={copyMessage}
                      className="flex items-center gap-1 text-primary-500 hover:bg-primary-50 px-2 py-1 rounded"
                      data-testid="copy-message-btn"
                    >
                      <ClipboardCopy className="w-4 h-4" /> Kopieer
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap text-sm bg-white p-3 rounded border">
                    {generatedMessage.message}
                  </pre>
                  {generatedMessage.parent_whatsapp && (
                    <p className="text-sm text-text-muted mt-2">
                      WhatsApp: {generatedMessage.parent_whatsapp}
                    </p>
                  )}
                </div>
              )}
            </Card>

            {/* Recent Results */}
            <Card>
              <h3 className="font-heading font-bold mb-4">Onlangse Resultate / Recent Results</h3>
              {learnerDetail.recent_results?.length === 0 ? (
                <p className="text-text-muted">Geen resultate nog nie / No results yet</p>
              ) : (
                <div className="space-y-2">
                  {learnerDetail.recent_results?.slice(0, 10).map((result, idx) => (
                    <div key={result.id || idx} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <span className="font-medium">{result.exercise_type}</span>
                        <span className="text-sm text-text-muted ml-2">
                          {new Date(result.created_at).toLocaleDateString("af-ZA")}
                        </span>
                      </div>
                      <div className={`px-2 py-1 rounded text-sm font-medium ${
                        result.score >= 70 ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"
                      }`}>
                        {result.score}%
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Completed Exercises Detail */}
            <Card testId="completed-exercises-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold flex items-center gap-2">
                  <Check className="w-5 h-5 text-primary-500" /> 
                  Completed Exercises / Voltooide Oefeninge
                </h3>
                <button 
                  onClick={() => setShowExerciseHistory(!showExerciseHistory)}
                  className="text-primary-500 text-sm hover:underline"
                >
                  {showExerciseHistory ? "Hide / Verberg" : "Show All / Wys Alles"} ({exerciseHistory.length})
                </button>
              </div>
              
              {/* Summary by Type */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {(() => {
                  const typeCounts = exerciseHistory.reduce((acc, ex) => {
                    const type = ex.exercise_type || ex.type || "Unknown";
                    acc[type] = (acc[type] || 0) + 1;
                    return acc;
                  }, {});
                  return Object.entries(typeCounts).map(([type, count]) => (
                    <div key={type} className="bg-slate-50 rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold text-primary-600">{count}</div>
                      <div className="text-xs text-text-muted capitalize">{type}</div>
                    </div>
                  ));
                })()}
              </div>

              {/* Detailed Exercise History */}
              {showExerciseHistory && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="font-semibold mb-3 text-sm">Full History / Volledige Geskiedenis</h4>
                  {exerciseHistory.length === 0 ? (
                    <p className="text-text-muted text-sm">No exercises completed yet / Geen oefeninge nog voltooi nie</p>
                  ) : (
                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {exerciseHistory.map((ex, idx) => (
                        <div key={ex.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${ex.score >= 70 ? 'bg-green-500' : 'bg-orange-500'}`} />
                              <span className="font-medium text-sm">{ex.exercise_type || ex.type}</span>
                              {ex.text_title && <span className="text-xs text-text-muted">- {ex.text_title}</span>}
                            </div>
                            <div className="text-xs text-text-muted mt-1">
                              {new Date(ex.created_at || ex.completed_at).toLocaleDateString("af-ZA", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                              {ex.grade_level && <span className="ml-2">• Grade / Graad {ex.grade_level}</span>}
                              {ex.time_spent && <span className="ml-2">• {Math.round(ex.time_spent / 60)} min</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {ex.score !== undefined && (
                              <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                                ex.score >= 70 ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
                              }`}>
                                {ex.score}%
                              </div>
                            )}
                            {ex.wpm && (
                              <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                                {ex.wpm} WPM
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    );
  }

  // Learner List View
  return (
    <div className="space-y-4">
      <h2 className="font-heading text-lg font-bold">Alle Leerders</h2>
      <p className="text-text-secondary text-sm">Klik op 'n leerder om hul volledige profiel, vordering en boodskappe te sien.</p>
      {learners.length === 0 ? (
        <Card><p className="text-text-muted">Geen leerders geregistreer nie</p></Card>
      ) : (
        learners.map(l => (
          <Card 
            key={l.learner.id} 
            testId={`learner-${l.learner.id}`}
            onClick={() => handleSelectLearner(l)}
            className={`cursor-pointer hover:border-primary-500 ${l.learner.suspended ? 'border-red-300 bg-red-50' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{l.learner.name} {l.learner.surname}</h3>
                  {/* Language flag */}
                  <span className="text-sm" title={l.learner.preferred_language === "en" ? "English" : "Afrikaans"}>
                    {l.learner.preferred_language === "en" ? "🇬🇧" : "🇿🇦"}
                  </span>
                  {l.learner.suspended && (
                    <span className="px-2 py-0.5 text-xs bg-red-200 text-red-800 rounded-full">Gesuspendeer</span>
                  )}
                </div>
                <p className="text-sm text-text-muted">
                  <span className="font-mono text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">@{l.learner.username}</span>
                  {' '} | Graad {l.learner.current_reading_level || l.learner.grade} | 
                  {l.exercises_completed} oefeninge | 
                  {l.average_score}% gemiddeld
                  {l.learner.school_name && ` | ${l.learner.school_name}`}
                </p>
                {/* Strongest Point Badge */}
                {l.strongest_point?.strongest_type && (
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-amber-500 text-xs">⭐</span>
                    <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                      {l.strongest_point.strongest_label}: {l.strongest_point.strongest_score}%
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Delete Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteLearner(l.learner.id, `${l.learner.name} ${l.learner.surname}`);
                  }}
                  className="px-3 py-1 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600"
                  data-testid={`delete-learner-${l.learner.id}`}
                  title={i18n.language === 'en' ? "Delete learner" : "Vee leerder uit"}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
                {/* Suspend/Activate Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSuspension(l.learner.id, l.learner.suspended);
                  }}
                  className={`px-3 py-1 rounded-lg text-xs font-medium ${
                    l.learner.suspended 
                      ? "bg-green-100 text-green-700 hover:bg-green-200" 
                      : "bg-red-100 text-red-700 hover:bg-red-200"
                  }`}
                  data-testid={`toggle-suspend-${l.learner.id}`}
                >
                  {l.learner.suspended ? t('admin.activate') : t('admin.suspend')}
                </button>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  l.subscription?.active 
                    ? l.subscription?.type === "monthly" && l.subscription?.days_left <= 7
                      ? "bg-amber-100 text-amber-700"
                      : "bg-primary-100 text-primary-700" 
                    : l.subscription?.status === "pending_payment" || l.subscription?.status === "monthly_expired"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-text-muted"
                }`}>
                  {l.subscription?.type === "trial" && `${i18n.language === 'en' ? 'Trial' : 'Proef'} (${l.subscription?.days_left}d)`}
                  {l.subscription?.type === "monthly" && (
                    l.subscription?.days_left <= 7 
                      ? `⚠️ ${l.subscription?.days_left}d ${i18n.language === 'en' ? 'left' : 'oor'}` 
                      : `${i18n.language === 'en' ? 'Monthly' : 'Maandeliks'} (${l.subscription?.days_left}d)`
                  )}
                  {l.subscription?.type === "lifetime" && (i18n.language === 'en' ? "Lifetime" : "Lewenslank")}
                  {l.subscription?.type === "school" && (i18n.language === 'en' ? "School" : "Skool")}
                  {!l.subscription?.active && l.subscription?.status === "monthly_expired" && (i18n.language === 'en' ? "🔄 Monthly Expired" : "🔄 Maand. Verval")}
                  {!l.subscription?.active && l.subscription?.status === "pending_payment" && (
                    l.learner.subscription_type === "monthly" 
                      ? (i18n.language === 'en' ? "⏳ Monthly" : "⏳ Maand.") 
                      : (i18n.language === 'en' ? "⏳ Once-off" : "⏳ Eenm.")
                  )}
                  {!l.subscription?.active && l.subscription?.status !== "pending_payment" && l.subscription?.status !== "monthly_expired" && l.subscription?.status !== "trial" && (i18n.language === 'en' ? "Inactive" : "Onaktief")}
                </div>
                <ChevronRight className="w-5 h-5 text-text-muted" />
              </div>
            </div>
          </Card>
        ))
      )}
    </div>
  );
};

// Consent Dashboard Component
const ConsentDashboard = ({ schools }) => {
  const [consentData, setConsentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolDetail, setSchoolDetail] = useState(null);

  useEffect(() => {
    loadConsentDashboard();
  }, []);

  const loadConsentDashboard = async () => {
    try {
      const res = await api.get("/admin/consent-dashboard");
      setConsentData(res.data);
    } catch (err) {
      console.error("Could not load consent dashboard:", err);
    }
    setLoading(false);
  };

  const loadSchoolDetail = async (schoolId) => {
    try {
      const res = await api.get(`/schools/${schoolId}/consent-status`);
      setSchoolDetail(res.data);
      setSelectedSchool(schoolId);
    } catch (err) {
      toast.error("Kon nie skool besonderhede laai nie");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!consentData || consentData.total_schools === 0) {
    return (
      <div className="text-center py-6 text-text-muted">
        <p>Geen skole met leerders gevind nie</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Overall Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-emerald-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-emerald-600">{consentData.total_consents_submitted}</div>
          <div className="text-xs text-emerald-700">Ingedien</div>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{consentData.total_consents_pending}</div>
          <div className="text-xs text-amber-700">Uitstaande</div>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{consentData.total_learners}</div>
          <div className="text-xs text-blue-700">Totaal Leerders</div>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">{consentData.overall_completion}%</div>
          <div className="text-xs text-purple-700">Voltooi</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-slate-100 rounded-full h-4 overflow-hidden">
        <div 
          className="bg-gradient-to-r from-emerald-500 to-primary-500 h-full transition-all duration-500"
          style={{ width: `${consentData.overall_completion}%` }}
        />
      </div>

      {/* School List */}
      <div className="space-y-2 mt-4">
        <p className="text-sm font-semibold">Per Skool:</p>
        {consentData.schools.map((school) => (
          <div key={school.school_id} className="border rounded-lg overflow-hidden">
            <button
              onClick={() => loadSchoolDetail(school.school_id)}
              className="w-full p-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
              data-testid={`consent-school-${school.school_id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  school.completion_percentage === 100 ? 'bg-emerald-100 text-emerald-600' :
                  school.completion_percentage >= 50 ? 'bg-amber-100 text-amber-600' :
                  'bg-red-100 text-red-600'
                }`}>
                  {school.completion_percentage}%
                </div>
                <div className="text-left">
                  <p className="font-medium">{school.school_name}</p>
                  <p className="text-xs text-text-muted">
                    {school.consents_submitted}/{school.total_learners} ingedien
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-text-muted" />
            </button>
            
            {/* Expanded Detail */}
            {selectedSchool === school.school_id && schoolDetail && (
              <div className="border-t bg-slate-50 p-3">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-sm font-semibold">Leerders:</p>
                  <button 
                    onClick={() => setSelectedSchool(null)}
                    className="text-xs text-text-muted hover:text-text-primary"
                  >
                    Sluit
                  </button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {schoolDetail.learners.map((learner) => (
                    <div 
                      key={learner.learner_id}
                      className={`p-2 rounded-lg text-sm flex items-center justify-between ${
                        learner.status === 'submitted' ? 'bg-emerald-100' : 'bg-amber-100'
                      }`}
                    >
                      <div>
                        <span className="font-medium">{learner.name}</span>
                        <span className="text-text-muted ml-2">Gr {learner.grade}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {learner.status === 'submitted' ? (
                          <>
                            <span className="text-xs text-emerald-700">{learner.parent_name}</span>
                            <Check className="w-4 h-4 text-emerald-600" />
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-amber-700">Wag</span>
                            <Clock className="w-4 h-4 text-amber-600" />
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Refresh Button */}
      <button 
        onClick={loadConsentDashboard}
        className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-2"
      >
        <RefreshCw className="w-4 h-4" /> Herlaai data
      </button>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("overview");
  const [learners, setLearners] = useState([]);
  const [texts, setTexts] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolCodes, setSchoolCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // Note: newText state is defined earlier in TextsManagement component
  const [generateForm, setGenerateForm] = useState({ grade_level: 1, text_type: "comprehension", topic: "", language: "af" });
  const [inviteNote, setInviteNote] = useState("");
  const [schoolCodeForm, setSchoolCodeForm] = useState({ school_id: "", max_uses: 100, note: "" });
  const [newSchoolForm, setNewSchoolForm] = useState({ school_name: "", contact_person: "", contact_email: "", contact_whatsapp: "", max_learners: 100 });
  const [editingSchool, setEditingSchool] = useState(null);
  const [editSchoolForm, setEditSchoolForm] = useState({});
  const [bulkUploadSchoolId, setBulkUploadSchoolId] = useState("");
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadResults, setBulkUploadResults] = useState(null);
  const [woordbouList, setWoordbouList] = useState([]);
  const [klanktoetsList, setKlanktoetsList] = useState([]);
  const [alphabetItems, setAlphabetItems] = useState([]);
  const [alphabetLanguage, setAlphabetLanguage] = useState("af");
  const [newWoordbou, setNewWoordbou] = useState({ title: "", grade_level: 1, term: 1, target_word: "", available_letters: "", language: "af" });
  const [newKlanktoets, setNewKlanktoets] = useState({ title: "", grade_level: 1, term: 1, test_type: "audio_to_text", items: [], language: "af" });
  const [editingKlanktoets, setEditingKlanktoets] = useState(null); // For editing existing klanktoets
  const [siteSettings, setSiteSettings] = useState({ logo_url: null, about_title: "Lees is Duidelik", about_text: "", contact_email: "", contact_phone: "" });
  const [ttsDemo, setTtsDemo] = useState({ text: "Hallo! Hierdie is 'n toets van die Afrikaanse teks-na-spraak funksie. Luister hoe dit klink.", voice: "nova", audioUrl: null, loading: false });
  const [ttsVoices, setTtsVoices] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });
  const [pendingPayments, setPendingPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [tutoringRequests, setTutoringRequests] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [exerciseInstructions, setExerciseInstructions] = useState({
    // Afrikaans instructions
    comprehension: "Lees die teks aandagtig deur en beantwoord dan die vrae.",
    reading: "Lees die teks hardop so duidelik en akkuraat as moontlik.",
    reading_level_test: `a) Dis belangrik dat jy wel lees sodat ons jou korrekte leesvlak bepaal.
b) Lees die teks hardop as jy alleen is, maar as jy in 'n klas is lees dit dan stil.
c) Indien jy nie saamstem met jou eerste probeerslag, doen dit liefs oor.
Geniet die toets.`,
    reading_level_encouragement: `🎉 Baie geluk met jou leesvlaktoets!

Jy het wonderlik gedoen. Onthou: elke dag wat jy lees, word jy 'n beter leser. 

Hou aan oefen en jy sal sien hoe jou vaardighede groei! 📚⭐`,
    spelling: `a) Volg asseblief die instruksie noukeurig.
b) Elke woord sal 3 keer gelees word.
c) Nadat die woord gelees is, tik die woord in die korrekte spasie.
d) Aan die einde van die toets, sal elke woord 1 keer gelees word sodat jy deur jou antwoorde kan gaan voordat jy dit indien.`,
    listening: "Luister aandagtig na die oudio en beantwoord die vrae.",
    // English instructions
    comprehension_en: "Read the text carefully and then answer the questions.",
    reading_en: "Read the text aloud as clearly and accurately as possible.",
    reading_level_test_en: `a) It's important that you read so we can determine your correct reading level.
b) Read the text aloud if you're alone, but if you're in a class, read silently.
c) If you don't agree with your first attempt, please try again.
Enjoy the test.`,
    reading_level_encouragement_en: `🎉 Congratulations on completing your reading level test!

You did wonderfully. Remember: every day you read, you become a better reader.

Keep practicing and you'll see your skills grow! 📚⭐`,
    spelling_en: `a) Please follow the instructions carefully.
b) Each word will be read 3 times.
c) After the word is read, type the word in the correct space.
d) At the end of the test, each word will be read once so you can review your answers before submitting.`,
    listening_en: "Listen carefully to the audio and answer the questions.",
    // Audio for reading test completion
    reading_level_audio_url: null
  });
  const [bankDetails, setBankDetails] = useState({
    bank_name: "FNB (First National Bank)",
    account_holder: "Lees is Duidelik",
    account_number: "62123456789",
    branch_code: "250655",
    account_type: "Tjek/Cheque",
    reference_instructions: "Gebruik jou gebruikersnaam as verwysing"
  });
  const logoInputRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get("/progress/all"),
      api.get("/texts"),
      api.get("/invitations"),
      api.get("/schools").catch(() => ({ data: [] })),
      api.get("/school-codes").catch(() => ({ data: [] })),
      api.get("/settings").catch(() => ({ data: {} })),
      api.get("/payments/eft/pending").catch(() => ({ data: { payments: [] } })),
      api.get("/payments/eft/all").catch(() => ({ data: { payments: [] } })),
      api.get("/payments/bank-details").catch(() => ({ data: bankDetails })),
      api.get("/admin/tutoring/requests").catch(() => ({ data: { requests: [] } })),
      api.get("/exercise-instructions").catch(() => ({ data: exerciseInstructions })),
      api.get("/woordbou").catch(() => ({ data: { exercises: [] } })),
      api.get("/klanktoets").catch(() => ({ data: { exercises: [] } }))
    ])
    .then(([learnersRes, textsRes, invitesRes, schoolsRes, schoolCodesRes, settingsRes, pendingRes, allPaymentsRes, bankRes, tutoringRes, instructionsRes, woordbouRes, klanktoetsRes]) => {
      setLearners(learnersRes.data);
      setTexts(textsRes.data);
      setInvitations(invitesRes.data);
      setSchools(schoolsRes.data);
      setSchoolCodes(schoolCodesRes.data);
      if (settingsRes.data) setSiteSettings(prev => ({ ...prev, ...settingsRes.data }));
      setPendingPayments(pendingRes.data.payments || []);
      setAllPayments(allPaymentsRes.data.payments || []);
      if (bankRes.data) setBankDetails(prev => ({ ...prev, ...bankRes.data }));
      setTutoringRequests(tutoringRes.data.requests || []);
      if (instructionsRes.data) setExerciseInstructions(prev => ({ ...prev, ...instructionsRes.data }));
      setWoordbouList(woordbouRes.data.exercises || []);
      setKlanktoetsList(klanktoetsRes.data.exercises || []);
      // Fetch TTS voices
      api.get("/tts/voices").then(res => setTtsVoices(res.data.voices || [])).catch(() => {});
      // Fetch alphabet items
      api.get("/alphabet").then(res => setAlphabetItems(res.data || [])).catch(() => {});
    })
    .catch(err => {
      console.error("Error loading admin data:", err);
      toast.error("Kon nie data laai nie. Probeer weer.");
    })
    .finally(() => setLoading(false));
  }, []);

  const handleGenerateText = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/texts/generate", generateForm);
      toast.success(t('toast.textGenerated'));
      const textsRes = await api.get("/texts");
      setTexts(textsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.couldNotGenerate'));
    }
    setGenerating(false);
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/settings", siteSettings);
      toast.success(t('toast.settingsSaved'));
    } catch (err) {
      toast.error(t('toast.couldNotSave'));
    }
    setSavingSettings(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post("/settings/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setSiteSettings(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success(t('toast.imageUploaded'));
    } catch (err) {
      toast.error(t('toast.couldNotUpload'));
    }
  };

  const handleGenerateInvitation = async () => {
    try {
      const res = await api.post("/invitations/generate", { note: inviteNote });
      toast.success(t('toast.invitationSent') + `: ${res.data.code}`);
      setInviteNote("");
      const invitesRes = await api.get("/invitations");
      setInvitations(invitesRes.data);
    } catch (err) {
      toast.error(t('toast.couldNotSave'));
    }
  };

  const handleDeleteInvitation = async (code) => {
    try {
      await api.delete(`/invitations/${code}`);
      setInvitations(invitations.filter(i => i.code !== code));
      toast.success(t('toast.deleted'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.couldNotDelete'));
    }
  };

  const copyInviteLink = (code) => {
    const link = `${window.location.origin}/register?code=${code}`;
    navigator.clipboard.writeText(link);
    toast.success(t('toast.copiedToClipboard'));
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('loading')}</div>;
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <h1 className="font-heading text-xl font-bold">Admin Dashboard</h1>
            <button onClick={onLogout} className="p-2 text-text-muted hover:text-accent-500" data-testid="admin-logout">
              <LogOut className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex gap-1 overflow-x-auto">
              {[
                { id: "overview", icon: Home, label: "Oorsig" },
                { id: "analytics", icon: TrendingUp, label: "Analytics" },
                { id: "payments", icon: CreditCard, label: "EFT Betalings" },
                { id: "schools", icon: Building2, label: "Skole" },
                { id: "invitations", icon: User, label: "Uitnodigings" },
                { id: "learners", icon: Users, label: "Leerders" },
                { id: "tutoring", icon: Phone, label: "Tutoring" },
                { id: "texts", icon: FileText, label: "Tekste" },
                { id: "woordbou", icon: BookOpen, label: "Woordbou" },
                { id: "klanktoets", icon: Volume2, label: "Klanktoets" },
                { id: "alfabet", icon: BookOpen, label: "Alfabet" },
                { id: "generate", icon: Star, label: "Genereer" },
                { id: "audio", icon: Mic, label: "Oudio Instruksies" },
                { id: "settings", icon: Settings, label: "Instellings" },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id 
                      ? "border-primary-500 text-primary-500" 
                      : "border-transparent text-text-muted hover:text-text-primary"
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="grid md:grid-cols-4 gap-6">
              <Card testId="total-learners-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{learners.length}</div>
                    <div className="text-text-muted">Totale Leerders</div>
                  </div>
                </div>
              </Card>
              <Card testId="total-texts-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-secondary-500 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{texts.length}</div>
                    <div className="text-text-muted">Totale Tekste</div>
                  </div>
                </div>
              </Card>
              <Card testId="invitations-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{invitations.filter(i => !i.used).length}</div>
                    <div className="text-text-muted">Oop Uitnodigings</div>
                  </div>
                </div>
              </Card>
              <Card testId="active-subs-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent-500 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {learners.filter(l => l.subscription?.active).length}
                    </div>
                    <div className="text-text-muted">Aktiewe Subskripsies</div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === "analytics" && (
            <div className="space-y-6">
              <Card>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-bold text-xl flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-primary-500" />
                    {i18n.language === 'en' ? 'Visitor & Registration Analytics' : 'Besoeker & Registrasie Analytics'}
                  </h3>
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      setLoadingAnalytics(true);
                      try {
                        const res = await api.get("/analytics/summary");
                        setAnalyticsData(res.data);
                      } catch (err) {
                        toast.error(i18n.language === 'en' ? "Could not load analytics" : "Kon nie analytics laai nie");
                      }
                      setLoadingAnalytics(false);
                    }}
                    disabled={loadingAnalytics}
                  >
                    {loadingAnalytics ? t('admin.loadingText') : t('admin.refreshData')}
                  </Button>
                </div>

                {!analyticsData ? (
                  <div className="text-center py-12">
                    <TrendingUp className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-text-muted mb-4">{i18n.language === 'en' ? 'Click "Refresh Data" to see analytics' : 'Klik "Verfris Data" om analytics te sien'}</p>
                    <Button
                      onClick={async () => {
                        setLoadingAnalytics(true);
                        try {
                          const res = await api.get("/analytics/summary");
                          setAnalyticsData(res.data);
                        } catch (err) {
                          toast.error(i18n.language === 'en' ? "Could not load analytics" : "Kon nie analytics laai nie");
                        }
                        setLoadingAnalytics(false);
                      }}
                    >
                      {t('admin.loadAnalytics')}
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Stats Overview */}
                    <div className="grid md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-blue-50 rounded-xl p-4">
                        <p className="text-blue-600 text-sm font-medium">{i18n.language === 'en' ? 'Page Visits (Today)' : 'Bladsy Besoeke (Vandag)'}</p>
                        <p className="text-3xl font-bold text-blue-700">{analyticsData.page_visits?.today || 0}</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4">
                        <p className="text-green-600 text-sm font-medium">{i18n.language === 'en' ? 'Registrations (Today)' : 'Registrasies (Vandag)'}</p>
                        <p className="text-3xl font-bold text-green-700">{analyticsData.registration_attempts?.today || 0}</p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-4">
                        <p className="text-purple-600 text-sm font-medium">Suksesvol (Totaal)</p>
                        <p className="text-3xl font-bold text-purple-700">{analyticsData.registration_attempts?.successful || 0}</p>
                      </div>
                      <div className="bg-red-50 rounded-xl p-4">
                        <p className="text-red-600 text-sm font-medium">Misluk (Totaal)</p>
                        <p className="text-3xl font-bold text-red-700">{analyticsData.registration_attempts?.failed || 0}</p>
                      </div>
                    </div>

                    {/* Recent Registration Attempts */}
                    <div className="mb-6">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Onlangse Registrasie Pogings
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-100">
                              <th className="text-left p-3 rounded-l-lg">Naam</th>
                              <th className="text-left p-3">Gebruikersnaam</th>
                              <th className="text-left p-3">Graad</th>
                              <th className="text-left p-3">WhatsApp</th>
                              <th className="text-left p-3">Kode?</th>
                              <th className="text-left p-3">Status</th>
                              <th className="text-left p-3 rounded-r-lg">Datum/Tyd</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analyticsData.registration_attempts?.recent?.length > 0 ? (
                              analyticsData.registration_attempts.recent.map((attempt, idx) => (
                                <tr key={attempt.id || attempt.username || `reg-${idx}`} className="border-b hover:bg-slate-50">
                                  <td className="p-3 font-medium">{attempt.name || "-"}</td>
                                  <td className="p-3">{attempt.username || "-"}</td>
                                  <td className="p-3">{attempt.grade || "-"}</td>
                                  <td className="p-3">{attempt.whatsapp || "-"}</td>
                                  <td className="p-3">
                                    {attempt.has_invitation_code ? (
                                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Ja</span>
                                    ) : (
                                      <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs">Nee</span>
                                    )}
                                  </td>
                                  <td className="p-3">
                                    {attempt.success ? (
                                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1 w-fit">
                                        <Check className="w-3 h-3" /> Sukses
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs" title={attempt.error_reason}>
                                        Misluk
                                      </span>
                                    )}
                                  </td>
                                  <td className="p-3 text-text-muted text-xs">
                                    {attempt.timestamp ? new Date(attempt.timestamp).toLocaleString('af-ZA') : "-"}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" className="p-6 text-center text-text-muted">Geen registrasie pogings nog nie</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Recent Page Visits */}
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Eye className="w-5 h-5" />
                        Onlangse Bladsy Besoeke (Registrasie Bladsy)
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-100">
                              <th className="text-left p-3 rounded-l-lg">Bladsy</th>
                              <th className="text-left p-3">Toestel/Browser</th>
                              <th className="text-left p-3">Verwysing</th>
                              <th className="text-left p-3 rounded-r-lg">Datum/Tyd</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analyticsData.page_visits?.recent?.length > 0 ? (
                              analyticsData.page_visits.recent.map((visit, idx) => (
                                <tr key={visit.id || visit.timestamp || `visit-${idx}`} className="border-b hover:bg-slate-50">
                                  <td className="p-3">{visit.page || "-"}</td>
                                  <td className="p-3 text-xs text-text-muted max-w-xs truncate" title={visit.user_agent}>
                                    {visit.user_agent?.substring(0, 50) || "-"}...
                                  </td>
                                  <td className="p-3 text-xs">{visit.referrer || "direct"}</td>
                                  <td className="p-3 text-text-muted text-xs">
                                    {visit.timestamp ? new Date(visit.timestamp).toLocaleString('af-ZA') : "-"}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4" className="p-6 text-center text-text-muted">Geen bladsy besoeke nog nie</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </Card>
            </div>
          )}

          {/* EFT Payments Tab */}
          {activeTab === "payments" && (
            <div className="space-y-6">
              {/* Pending Payments Alert */}
              {pendingPayments.length > 0 && (
                <Card className="border-yellow-500 bg-yellow-50" testId="pending-payments-alert">
                  <div className="flex items-center gap-3">
                    <Bell className="w-6 h-6 text-yellow-600" />
                    <div>
                      <p className="font-bold text-yellow-800">{pendingPayments.length} Waggende Betalings</p>
                      <p className="text-sm text-yellow-700">Betalings wat bevestiging benodig</p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Bank Details Management */}
              <Card testId="bank-details-admin">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Bankbesonderhede (vir Leerders om te sien)
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Bank Naam"
                    value={bankDetails.bank_name}
                    onChange={(e) => setBankDetails({...bankDetails, bank_name: e.target.value})}
                    testId="bank-name-input"
                  />
                  <Input
                    label="Rekeninghouer"
                    value={bankDetails.account_holder}
                    onChange={(e) => setBankDetails({...bankDetails, account_holder: e.target.value})}
                    testId="account-holder-input"
                  />
                  <Input
                    label="Rekeningnommer"
                    value={bankDetails.account_number}
                    onChange={(e) => setBankDetails({...bankDetails, account_number: e.target.value})}
                    testId="account-number-input"
                  />
                  <Input
                    label="Takkode"
                    value={bankDetails.branch_code}
                    onChange={(e) => setBankDetails({...bankDetails, branch_code: e.target.value})}
                    testId="branch-code-input"
                  />
                  <Input
                    label="Tipe Rekening"
                    value={bankDetails.account_type}
                    onChange={(e) => setBankDetails({...bankDetails, account_type: e.target.value})}
                    testId="account-type-input"
                  />
                  <Input
                    label="Verwysing Instruksies"
                    value={bankDetails.reference_instructions}
                    onChange={(e) => setBankDetails({...bankDetails, reference_instructions: e.target.value})}
                    testId="reference-instructions-input"
                  />
                </div>
                <Button 
                  onClick={async () => {
                    try {
                      await api.put("/payments/bank-details", bankDetails);
                      toast.success(t('toast.saved'));
                    } catch (err) {
                      toast.error(t('toast.couldNotSave'));
                    }
                  }}
                  className="mt-4"
                  testId="save-bank-details"
                >
                  {i18n.language === 'en' ? 'Save Bank Details' : 'Stoor Bankbesonderhede'}
                </Button>
              </Card>

              {/* Pending Payments */}
              <Card testId="pending-payments-list">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                  <Timer className="w-5 h-5 text-yellow-600" />
                  {i18n.language === 'en' ? 'Pending Payments' : 'Waggende Betalings'} ({pendingPayments.length})
                </h3>
                {pendingPayments.length === 0 ? (
                  <p className="text-text-muted text-center py-8">{i18n.language === 'en' ? 'No pending payments' : 'Geen waggende betalings nie'}</p>
                ) : (
                  <div className="space-y-3">
                    {pendingPayments.map((payment) => (
                      <div key={payment.id} className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <p className="font-bold">{payment.user_name || (i18n.language === 'en' ? 'Unknown' : 'Onbekend')}</p>
                            <p className="text-sm text-text-muted">{i18n.language === 'en' ? 'Username' : 'Gebruikersnaam'}: {payment.username}</p>
                            <p className="text-sm">{i18n.language === 'en' ? 'Package' : 'Pakket'}: <strong>{payment.package_name}</strong></p>
                            <p className="text-sm">{i18n.language === 'en' ? 'Amount paid' : 'Bedrag betaal'}: <strong>R{payment.amount_paid}</strong> ({i18n.language === 'en' ? 'Expected' : 'Verwag'}: R{payment.expected_amount})</p>
                            <p className="text-sm">{i18n.language === 'en' ? 'Reference' : 'Verwysing'}: <strong>{payment.reference_used}</strong></p>
                            <p className="text-sm">{i18n.language === 'en' ? 'Date' : 'Datum'}: {payment.payment_date}</p>
                            {payment.proof_description && (
                              <p className="text-sm text-text-muted mt-1">{i18n.language === 'en' ? 'Note' : 'Nota'}: {payment.proof_description}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={async () => {
                                try {
                                  await api.post("/payments/eft/confirm", { payment_id: payment.id, confirmed: true });
                                  toast.success(t('toast.success'));
                                  const [pendingRes, allRes] = await Promise.all([
                                    api.get("/payments/eft/pending"),
                                    api.get("/payments/eft/all")
                                  ]);
                                  setPendingPayments(pendingRes.data.payments || []);
                                  setAllPayments(allRes.data.payments || []);
                                } catch (err) {
                                  toast.error(t('toast.couldNotSave'));
                                }
                              }}
                              className="bg-green-500 hover:bg-green-600"
                              testId={`confirm-payment-${payment.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" /> {i18n.language === 'en' ? 'Confirm' : 'Bevestig'}
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={async () => {
                                const reason = prompt(i18n.language === 'en' ? "Reason for rejection (optional):" : "Rede vir afkeur (opsioneel):");
                                try {
                                  await api.post("/payments/eft/confirm", { 
                                    payment_id: payment.id, 
                                    confirmed: false,
                                    admin_notes: reason 
                                  });
                                  toast.success(t('toast.success'));
                                  const [pendingRes, allRes] = await Promise.all([
                                    api.get("/payments/eft/pending"),
                                    api.get("/payments/eft/all")
                                  ]);
                                  setPendingPayments(pendingRes.data.payments || []);
                                  setAllPayments(allRes.data.payments || []);
                                } catch (err) {
                                  toast.error(t('toast.couldNotSave'));
                                }
                              }}
                              className="!text-red-600 !border-red-300"
                              testId={`reject-payment-${payment.id}`}
                            >
                              <X className="w-4 h-4 mr-1" /> Keur Af
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* All Payments History */}
              <Card testId="all-payments-list">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                  <FileBarChart className="w-5 h-5" />
                  Alle Betalings ({allPayments.length})
                </h3>
                {allPayments.length === 0 ? (
                  <p className="text-text-muted text-center py-8">Geen betalings nog nie</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2">Leerder</th>
                          <th className="text-left py-2 px-2">Pakket</th>
                          <th className="text-left py-2 px-2">Bedrag</th>
                          <th className="text-left py-2 px-2">Verwysing</th>
                          <th className="text-left py-2 px-2">Datum</th>
                          <th className="text-left py-2 px-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allPayments.slice(0, 50).map((payment) => (
                          <tr key={payment.id} className="border-b hover:bg-slate-50">
                            <td className="py-2 px-2">
                              <div className="font-medium">{payment.user_name}</div>
                              <div className="text-xs text-text-muted">{payment.username}</div>
                            </td>
                            <td className="py-2 px-2">{payment.package_id === "once_off" ? "Eenmalig" : "Maandeliks"}</td>
                            <td className="py-2 px-2">R{payment.amount_paid}</td>
                            <td className="py-2 px-2 font-mono text-xs">{payment.reference_used}</td>
                            <td className="py-2 px-2">{new Date(payment.created_at).toLocaleDateString("af-ZA")}</td>
                            <td className="py-2 px-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                payment.payment_status === "paid" ? "bg-green-100 text-green-700" :
                                payment.payment_status === "pending" ? "bg-yellow-100 text-yellow-700" :
                                "bg-red-100 text-red-700"
                              }`}>
                                {payment.payment_status === "paid" ? "Bevestig" : 
                                 payment.payment_status === "pending" ? "Wag" : "Afgekeur"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Schools Tab */}
          {activeTab === "schools" && (
            <div className="space-y-6">
              <Card testId="schools-info">
                <h3 className="font-heading font-bold mb-4">Skole Bestuur</h3>
                <p className="text-text-secondary mb-4">
                  Hier kan jy skool registrasies sien en skoolkodes skep wat veelvuldige leerders kan gebruik om te registreer.
                </p>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm"><strong>Gelyktydige Toegang:</strong> Die platform kan honderde leerders gelyktydig hanteer. Elke skool kry 'n unieke kode wat hul leerders kan deel.</p>
                </div>
              </Card>

              {/* Manual School Creation */}
              <Card testId="add-school-manually">
                <h3 className="font-heading font-bold mb-4">Voeg Skool By & Genereer Kode</h3>
                <p className="text-sm text-text-muted mb-4">
                  Voeg 'n nuwe skool by en genereer outomaties 'n permanente skoolkode wat leerders kan gebruik om te registreer.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Skool Naam"
                    value={newSchoolForm?.school_name || ""}
                    onChange={(e) => setNewSchoolForm({...newSchoolForm, school_name: e.target.value})}
                    placeholder="bv. Laerskool Pretoria-Oos"
                    testId="new-school-name"
                  />
                  <Input
                    label="Kontak Persoon"
                    value={newSchoolForm?.contact_person || ""}
                    onChange={(e) => setNewSchoolForm({...newSchoolForm, contact_person: e.target.value})}
                    placeholder="bv. Mnr. Van der Merwe"
                    testId="new-school-contact"
                  />
                  <Input
                    label="Kontak E-pos"
                    type="email"
                    value={newSchoolForm?.contact_email || ""}
                    onChange={(e) => setNewSchoolForm({...newSchoolForm, contact_email: e.target.value})}
                    placeholder="bv. admin@skool.co.za"
                    testId="new-school-email"
                  />
                  <Input
                    label="Kontak WhatsApp"
                    value={newSchoolForm?.contact_whatsapp || ""}
                    onChange={(e) => setNewSchoolForm({...newSchoolForm, contact_whatsapp: e.target.value})}
                    placeholder="bv. 0821234567"
                    testId="new-school-whatsapp"
                  />
                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">Maksimum Leerders</label>
                    <select
                      className="input-field"
                      value={newSchoolForm?.max_learners || 100}
                      onChange={(e) => setNewSchoolForm({...newSchoolForm, max_learners: parseInt(e.target.value)})}
                      data-testid="new-school-max-learners"
                    >
                      <option value={50}>50 leerders</option>
                      <option value={100}>100 leerders</option>
                      <option value={200}>200 leerders</option>
                      <option value={500}>500 leerders</option>
                      <option value={1000}>1000+ leerders</option>
                    </select>
                  </div>
                </div>
                <Button 
                  onClick={async () => {
                    if (!newSchoolForm?.school_name) {
                      toast.error("Voer die skool naam in");
                      return;
                    }
                    try {
                      const res = await api.post("/schools/create-with-code", newSchoolForm);
                      toast.success(`Skool bygevoeg! Kode: ${res.data.school_code}`);
                      setNewSchoolForm({ school_name: "", contact_person: "", contact_email: "", contact_whatsapp: "", max_learners: 100 });
                      // Refresh schools and codes
                      const schoolsRes = await api.get("/schools");
                      setSchools(schoolsRes.data);
                      const codesRes = await api.get("/school-codes");
                      setSchoolCodes(codesRes.data);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Kon nie skool byvoeg nie");
                    }
                  }}
                  className="mt-4 w-full"
                  testId="create-school-btn"
                >
                  Skep Skool & Genereer Kode
                </Button>
              </Card>

              {/* Bulk Learner Upload Section */}
              <Card testId="bulk-learner-upload">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Upload className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Bulk Leerder Oplaai</h3>
                    <p className="text-sm text-text-muted">Laai 'n Excel lêer op om veelvuldige leerders vir 'n skool te registreer</p>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Stap 1:</strong> Laai die Excel template af<br/>
                    <strong>Stap 2:</strong> Vul die leerder besonderhede in<br/>
                    <strong>Stap 3:</strong> Kies die skool en laai die lêer op
                  </p>
                </div>
                
                <div className="flex flex-wrap gap-3 mb-4">
                  <Button
                    onClick={async () => {
                      try {
                        const response = await api.get("/schools/learners/template", { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'leerders_template.xlsx');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success("Template afgelaai!");
                      } catch (err) {
                        toast.error("Kon nie template aflaai nie");
                      }
                    }}
                    variant="outline"
                    testId="download-learner-template"
                  >
                    <Download className="w-4 h-4 mr-2" /> Laai Excel Template Af
                  </Button>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">Kies Skool</label>
                    <select
                      className="input-field"
                      value={bulkUploadSchoolId}
                      onChange={(e) => setBulkUploadSchoolId(e.target.value)}
                      data-testid="bulk-upload-school-select"
                    >
                      <option value="">Kies 'n skool...</option>
                      {schools.filter(s => s.school_code).map(s => (
                        <option key={s.id} value={s.id}>{s.school_name} ({s.school_code})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">Excel Lêer</label>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setBulkUploadFile(e.target.files?.[0] || null)}
                      className="input-field"
                      data-testid="bulk-upload-file"
                    />
                  </div>
                </div>

                <Button
                  onClick={async () => {
                    if (!bulkUploadSchoolId) {
                      toast.error("Kies eers 'n skool");
                      return;
                    }
                    if (!bulkUploadFile) {
                      toast.error("Kies eers 'n Excel lêer");
                      return;
                    }
                    setBulkUploadLoading(true);
                    setBulkUploadResults(null);
                    try {
                      const formData = new FormData();
                      formData.append("file", bulkUploadFile);
                      const res = await api.post(`/schools/${bulkUploadSchoolId}/bulk-upload-learners`, formData, {
                        headers: { "Content-Type": "multipart/form-data" }
                      });
                      toast.success(res.data.message);
                      setBulkUploadResults(res.data.results);
                      setBulkUploadFile(null);
                      // Refresh data
                      const [schoolsRes, codesRes] = await Promise.all([api.get("/schools"), api.get("/school-codes")]);
                      setSchools(schoolsRes.data);
                      setSchoolCodes(codesRes.data);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Kon nie leerders oplaai nie");
                    }
                    setBulkUploadLoading(false);
                  }}
                  disabled={bulkUploadLoading || !bulkUploadSchoolId || !bulkUploadFile}
                  testId="bulk-upload-btn"
                >
                  {bulkUploadLoading ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Besig om op te laai...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Laai Leerders Op</>
                  )}
                </Button>

                {/* Upload Results */}
                {bulkUploadResults && (
                  <div className="mt-4 space-y-3">
                    {bulkUploadResults.success?.length > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="font-semibold text-green-800 mb-2">✅ Suksesvol: {bulkUploadResults.success.length} leerders</p>
                        <div className="max-h-32 overflow-y-auto text-sm text-green-700">
                          {bulkUploadResults.success.map((s, i) => (
                            <div key={i}>{s.name} (@{s.username})</div>
                          ))}
                        </div>
                      </div>
                    )}
                    {bulkUploadResults.failed?.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="font-semibold text-red-800 mb-2">❌ Misluk: {bulkUploadResults.failed.length}</p>
                        <div className="max-h-32 overflow-y-auto text-sm text-red-700">
                          {bulkUploadResults.failed.map((f, i) => (
                            <div key={i}>Ry {f.row}: {f.name} - {f.error}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>

              {/* Parental Consent Form Section */}
              <Card testId="consent-form-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Ouerlike Toestemmingsvorms</h3>
                    <p className="text-sm text-text-muted">Laai drukbare PDF vorms af vir ouer toestemming</p>
                  </div>
                </div>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-purple-800">
                    <strong>Proses:</strong><br/>
                    1. Laai die toestemmingsvorm af vir 'n spesifieke skool<br/>
                    2. Druk die vorms uit en gee dit aan die skool<br/>
                    3. Ouers vul dit in, teken en gee terug aan die skool<br/>
                    4. Die skool stuur 'n e-pos aan admin wanneer alle vorms ontvang is
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={async () => {
                      try {
                        const response = await api.get("/schools/consent-form/blank", { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'Toestemmingsvorm_Leeg.pdf');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success("Blanko toestemmingsvorm afgelaai!");
                      } catch (err) {
                        toast.error("Kon nie vorm aflaai nie");
                      }
                    }}
                    variant="outline"
                    testId="download-blank-consent"
                  >
                    <Download className="w-4 h-4 mr-2" /> Blanko Vorm (PDF)
                  </Button>
                </div>

                {/* School-specific consent forms */}
                {schools.filter(s => s.school_code).length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-semibold mb-3">Skool-spesifieke Vorms:</p>
                    <div className="flex flex-wrap gap-2">
                      {schools.filter(s => s.school_code).map(school => (
                        <button
                          key={school.id}
                          onClick={async () => {
                            try {
                              const response = await api.get(`/schools/${school.id}/consent-form`, { responseType: 'blob' });
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `Toestemmingsvorm_${school.school_name.replace(/\s+/g, '_')}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              toast.success(`Toestemmingsvorm vir ${school.school_name} afgelaai!`);
                            } catch (err) {
                              toast.error("Kon nie vorm aflaai nie");
                            }
                          }}
                          className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 flex items-center gap-2"
                          data-testid={`consent-form-${school.id}`}
                        >
                          <FileText className="w-4 h-4" /> {school.school_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              {/* Consent Dashboard */}
              <Card testId="consent-dashboard">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Toestemming Dashboard</h3>
                    <p className="text-sm text-text-muted">Sien hoeveel ouers al digitale toestemming gegee het</p>
                  </div>
                </div>
                
                <ConsentDashboard schools={schools} />
              </Card>

              {/* Active School Codes */}
              <h3 className="font-heading font-bold">Aktiewe Skoolkodes</h3>
              <div className="space-y-3">
                {schoolCodes.length === 0 ? (
                  <Card><p className="text-text-muted">Geen skoolkodes geskep nie. Voeg 'n skool hierbo by om 'n kode te genereer.</p></Card>
                ) : (
                  schoolCodes.map(code => (
                    <Card key={code.code} testId={`school-code-${code.code}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="bg-primary-100 text-primary-700 px-3 py-1 rounded-lg font-mono font-bold text-lg">
                              {code.code}
                            </div>
                            <span className="font-semibold">{code.school_name}</span>
                          </div>
                          <p className="text-sm text-text-muted mt-1">
                            Gebruik: {code.uses_count || 0} / {code.max_uses} leerders
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const link = `${window.location.origin}/register?code=${code.code}`;
                              navigator.clipboard.writeText(link);
                              toast.success("Registrasie skakel gekopieer!");
                            }}
                            className="p-2 text-primary-500 hover:bg-primary-50 rounded-lg"
                            title="Kopieer skakel"
                            data-testid={`copy-school-code-${code.code}`}
                          >
                            <Copy className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(code.code);
                              toast.success(`Kode gekopieer: ${code.code}`);
                            }}
                            className="px-3 py-2 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium hover:bg-primary-100"
                            data-testid={`copy-code-only-${code.code}`}
                          >
                            Kopieer Kode
                          </button>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>

              {/* School Code Generator for existing schools without codes */}
              {schools.filter(s => s.status === "approved" && !s.school_code).length > 0 && (
                <Card testId="generate-school-code-form">
                  <h3 className="font-heading font-bold mb-4">Skep Skoolkode</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-text-secondary mb-2">Kies Skool</label>
                      <select
                        className="input-field"
                        value={schoolCodeForm.school_id}
                        onChange={(e) => setSchoolCodeForm({...schoolCodeForm, school_id: e.target.value})}
                        data-testid="school-select"
                      >
                        <option value="">Kies 'n skool...</option>
                        {schools.filter(s => s.status === "pending" || !s.school_code).map(s => (
                          <option key={s.id} value={s.id}>{s.school_name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-text-secondary mb-2">Maksimum Leerders</label>
                      <select
                        className="input-field"
                        value={schoolCodeForm.max_uses}
                        onChange={(e) => setSchoolCodeForm({...schoolCodeForm, max_uses: parseInt(e.target.value)})}
                        data-testid="max-uses-select"
                      >
                        <option value={50}>50 leerders</option>
                        <option value={100}>100 leerders</option>
                        <option value={200}>200 leerders</option>
                        <option value={500}>500 leerders</option>
                        <option value={1000}>1000 leerders</option>
                      </select>
                    </div>
                  </div>
                  <Button 
                    onClick={async () => {
                      if (!schoolCodeForm.school_id) {
                        toast.error("Kies eers 'n skool");
                        return;
                      }
                      try {
                        const res = await api.post(`/schools/${schoolCodeForm.school_id}/generate-code`, schoolCodeForm);
                        toast.success(`Skoolkode geskep: ${res.data.code}`);
                        const [schoolsRes, codesRes] = await Promise.all([api.get("/schools"), api.get("/school-codes")]);
                        setSchools(schoolsRes.data);
                        setSchoolCodes(codesRes.data);
                      } catch (err) {
                        toast.error("Kon nie kode skep nie");
                      }
                    }}
                    className="mt-2" 
                    testId="generate-school-code-btn"
                  >
                    Skep Skoolkode
                  </Button>
                </Card>
              )}

              {/* Pending School Registrations */}
              <h3 className="font-heading font-bold mt-8">Skool Registrasies</h3>
              <div className="space-y-4">
                {schools.length === 0 ? (
                  <Card><p className="text-text-muted">Geen skool registrasies ontvang nie</p></Card>
                ) : (
                  schools.map(school => (
                    <Card key={school.id} testId={`school-${school.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <Building2 className="w-5 h-5 text-primary-500" />
                            <h4 className="font-semibold">{school.school_name}</h4>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              school.status === "approved" ? "bg-primary-100 text-primary-700" :
                              school.status === "rejected" ? "bg-accent-100 text-accent-700" :
                              "bg-secondary-100 text-secondary-700"
                            }`}>
                              {school.status === "approved" ? "Goedgekeur" : school.status === "rejected" ? "Afgekeur" : "Hangende"}
                            </span>
                            {school.payment_status === "paid" && (
                              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                                Betaal
                              </span>
                            )}
                            <button
                              onClick={() => {
                                setEditingSchool(school);
                                setEditSchoolForm({
                                  school_name: school.school_name || "",
                                  contact_person: school.contact_person || "",
                                  contact_email: school.contact_email || "",
                                  contact_whatsapp: school.contact_whatsapp || "",
                                  contact_phone: school.contact_phone || "",
                                  principal_contact: school.principal_contact || "",
                                  school_email: school.school_email || "",
                                  learner_count: school.learner_count || 10,
                                  status: school.status || "pending"
                                });
                              }}
                              className="ml-auto p-1.5 text-primary-500 hover:bg-primary-50 rounded-lg"
                              title="Wysig Skool"
                              data-testid={`edit-school-${school.id}`}
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="mt-2 text-sm text-text-secondary grid grid-cols-2 gap-2">
                            <p><strong>Kontak:</strong> {school.contact_person}</p>
                            <p><strong>E-pos:</strong> {school.contact_email}</p>
                            <p><strong>WhatsApp:</strong> {school.contact_whatsapp}</p>
                            <p><strong>Leerders:</strong> {school.learner_count}+</p>
                            {school.principal_contact && <p><strong>Hoof:</strong> {school.principal_contact}</p>}
                            {school.school_code && <p><strong>Kode:</strong> {school.school_code}</p>}
                          </div>
                          
                          {/* Package Settings with Per-Learner Calculator */}
                          {school.status === "approved" && (
                            <div className="mt-4 pt-4 border-t">
                              <div className="flex items-center gap-2 mb-3">
                                <Package className="w-4 h-4 text-primary-500" />
                                <span className="font-semibold text-sm">Pakket Instellings & Sakrekenaar</span>
                              </div>
                              
                              {/* Per Learner Calculator */}
                              <div className="bg-primary-50 rounded-xl p-4 mb-4">
                                <p className="text-sm font-semibold text-primary-700 mb-3">Per Leerder Sakrekenaar</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  <div>
                                    <label className="block text-xs text-text-muted mb-1">Aantal Leerders</label>
                                    <input
                                      type="number"
                                      className="input-field !py-2"
                                      defaultValue={school.learner_count || 100}
                                      id={`learner-count-${school.id}`}
                                      onChange={(e) => {
                                        const count = parseInt(e.target.value) || 0;
                                        const monthlyRate = parseFloat(document.getElementById(`monthly-rate-${school.id}`)?.value) || 10;
                                        const onceOffRate = parseFloat(document.getElementById(`once-off-rate-${school.id}`)?.value) || 50;
                                        document.getElementById(`monthly-total-${school.id}`).textContent = `R${(count * monthlyRate).toLocaleString()}`;
                                        document.getElementById(`once-off-total-${school.id}`).textContent = `R${(count * onceOffRate).toLocaleString()}`;
                                      }}
                                      data-testid={`learner-count-${school.id}`}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-text-muted mb-1">Maandeliks per Leerder</label>
                                    <div className="flex items-center gap-1">
                                      <span className="text-text-muted">R</span>
                                      <input
                                        type="number"
                                        className="input-field !py-2"
                                        defaultValue={school.monthly_per_learner || 10}
                                        id={`monthly-rate-${school.id}`}
                                        onChange={(e) => {
                                          const rate = parseFloat(e.target.value) || 0;
                                          const count = parseInt(document.getElementById(`learner-count-${school.id}`)?.value) || 0;
                                          document.getElementById(`monthly-total-${school.id}`).textContent = `R${(count * rate).toLocaleString()}`;
                                        }}
                                        data-testid={`monthly-rate-${school.id}`}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-xs text-text-muted mb-1">Eenmalig per Leerder</label>
                                    <div className="flex items-center gap-1">
                                      <span className="text-text-muted">R</span>
                                      <input
                                        type="number"
                                        className="input-field !py-2"
                                        defaultValue={school.once_off_per_learner || 50}
                                        id={`once-off-rate-${school.id}`}
                                        onChange={(e) => {
                                          const rate = parseFloat(e.target.value) || 0;
                                          const count = parseInt(document.getElementById(`learner-count-${school.id}`)?.value) || 0;
                                          document.getElementById(`once-off-total-${school.id}`).textContent = `R${(count * rate).toLocaleString()}`;
                                        }}
                                        data-testid={`once-off-rate-${school.id}`}
                                      />
                                    </div>
                                  </div>
                                  <div className="flex flex-col justify-end">
                                    <label className="block text-xs text-text-muted mb-1">Bereken Totaal</label>
                                    <div className="text-sm space-y-1">
                                      <div className="flex justify-between bg-white rounded px-2 py-1">
                                        <span>Maandeliks:</span>
                                        <span className="font-bold text-primary-600" id={`monthly-total-${school.id}`}>
                                          R{((school.learner_count || 100) * (school.monthly_per_learner || 10)).toLocaleString()}
                                        </span>
                                      </div>
                                      <div className="flex justify-between bg-white rounded px-2 py-1">
                                        <span>Eenmalig:</span>
                                        <span className="font-bold text-secondary-600" id={`once-off-total-${school.id}`}>
                                          R{((school.learner_count || 100) * (school.once_off_per_learner || 50)).toLocaleString()}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Final Price & Billing */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="bg-slate-50 rounded-lg p-3">
                                  <label className="block text-xs text-text-muted mb-1">Finale Maandelikse Prys</label>
                                  <div className="flex items-center gap-1">
                                    <span className="text-text-muted">R</span>
                                    <input
                                      type="number"
                                      className="input-field !py-1 !px-2 w-24"
                                      defaultValue={school.monthly_price || ((school.learner_count || 100) * 10)}
                                      onChange={(e) => {
                                        const price = parseFloat(e.target.value);
                                        api.put(`/schools/${school.id}/package`, {
                                          package_type: "standard",
                                          monthly_price: price,
                                          once_off_price: school.once_off_price || 399
                                        }).catch(console.error);
                                      }}
                                      data-testid={`school-monthly-price-${school.id}`}
                                    />
                                  </div>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-3">
                                  <label className="block text-xs text-text-muted mb-1">Finale Eenmalige Prys</label>
                                  <div className="flex items-center gap-1">
                                    <span className="text-text-muted">R</span>
                                    <input
                                      type="number"
                                      className="input-field !py-1 !px-2 w-24"
                                      defaultValue={school.once_off_price || ((school.learner_count || 100) * 50)}
                                      onChange={(e) => {
                                        const price = parseFloat(e.target.value);
                                        api.put(`/schools/${school.id}/package`, {
                                          package_type: "standard",
                                          monthly_price: school.monthly_price || 100,
                                          once_off_price: price
                                        }).catch(console.error);
                                      }}
                                      data-testid={`school-once-off-price-${school.id}`}
                                    />
                                  </div>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-3">
                                  <label className="block text-xs text-text-muted mb-1">Fakturering</label>
                                  <select
                                    className="input-field !py-1 !px-2"
                                    defaultValue={school.billing_cycle || "monthly"}
                                    onChange={(e) => {
                                      api.put(`/schools/${school.id}/package`, {
                                        package_type: "standard",
                                        monthly_price: school.monthly_price || 100,
                                        once_off_price: school.once_off_price || 399,
                                        billing_cycle: e.target.value
                                      }).catch(console.error);
                                    }}
                                    data-testid={`school-billing-cycle-${school.id}`}
                                  >
                                    <option value="monthly">Maandeliks</option>
                                    <option value="once_off">Eenmalig</option>
                                  </select>
                                </div>
                                <div className="flex items-end">
                                  <button
                                    onClick={async () => {
                                      try {
                                        await api.post(`/schools/${school.id}/confirm-payment`);
                                        toast.success("Betaling bevestig! Leerders kan nou aanhou.");
                                        const res = await api.get("/schools");
                                        setSchools(res.data);
                                      } catch (err) {
                                        toast.error("Kon nie betaling bevestig nie");
                                      }
                                    }}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
                                      school.payment_status === "paid"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-primary-500 text-white hover:bg-primary-600"
                                    }`}
                                    data-testid={`confirm-payment-${school.id}`}
                                  >
                                    <DollarSign className="w-4 h-4" />
                                    {school.payment_status === "paid" ? "Betaal ✓" : "Bevestig Betaling"}
                                  </button>
                                </div>
                              </div>
                              {school.payment_valid_until && (
                                <p className="text-xs text-text-muted mt-2">
                                  Geldig tot: {new Date(school.payment_valid_until).toLocaleDateString("af-ZA")}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>

              {/* School Edit Modal */}
              {editingSchool && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="font-heading text-xl font-bold">Wysig Skool</h3>
                      <button
                        onClick={() => setEditingSchool(null)}
                        className="p-2 hover:bg-slate-100 rounded-lg"
                        data-testid="close-edit-school"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      <Input
                        label="Skool Naam"
                        value={editSchoolForm.school_name || ""}
                        onChange={(e) => setEditSchoolForm({...editSchoolForm, school_name: e.target.value})}
                        testId="edit-school-name"
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="Kontak Persoon"
                          value={editSchoolForm.contact_person || ""}
                          onChange={(e) => setEditSchoolForm({...editSchoolForm, contact_person: e.target.value})}
                          testId="edit-contact-person"
                        />
                        <Input
                          label="Kontak E-pos"
                          type="email"
                          value={editSchoolForm.contact_email || ""}
                          onChange={(e) => setEditSchoolForm({...editSchoolForm, contact_email: e.target.value})}
                          testId="edit-contact-email"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="WhatsApp Nommer"
                          value={editSchoolForm.contact_whatsapp || ""}
                          onChange={(e) => setEditSchoolForm({...editSchoolForm, contact_whatsapp: e.target.value})}
                          testId="edit-contact-whatsapp"
                        />
                        <Input
                          label="Telefoon Nommer"
                          value={editSchoolForm.contact_phone || ""}
                          onChange={(e) => setEditSchoolForm({...editSchoolForm, contact_phone: e.target.value})}
                          testId="edit-contact-phone"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="Skoolhoof Kontak"
                          value={editSchoolForm.principal_contact || ""}
                          onChange={(e) => setEditSchoolForm({...editSchoolForm, principal_contact: e.target.value})}
                          testId="edit-principal-contact"
                        />
                        <Input
                          label="Skool E-pos"
                          type="email"
                          value={editSchoolForm.school_email || ""}
                          onChange={(e) => setEditSchoolForm({...editSchoolForm, school_email: e.target.value})}
                          testId="edit-school-email"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-text-secondary mb-2">Aantal Leerders</label>
                          <input
                            type="number"
                            className="input-field"
                            value={editSchoolForm.learner_count || ""}
                            onChange={(e) => setEditSchoolForm({...editSchoolForm, learner_count: parseInt(e.target.value) || 0})}
                            data-testid="edit-learner-count"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-text-secondary mb-2">Status</label>
                          <select
                            className="input-field"
                            value={editSchoolForm.status || "pending"}
                            onChange={(e) => setEditSchoolForm({...editSchoolForm, status: e.target.value})}
                            data-testid="edit-school-status"
                          >
                            <option value="pending">Hangende</option>
                            <option value="approved">Goedgekeur</option>
                            <option value="rejected">Afgekeur</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <Button
                        variant="secondary"
                        onClick={() => setEditingSchool(null)}
                        testId="cancel-edit-school"
                      >
                        Kanselleer
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            await api.put(`/schools/${editingSchool.id}`, editSchoolForm);
                            toast.success("Skool suksesvol opgedateer!");
                            const res = await api.get("/schools");
                            setSchools(res.data);
                            setEditingSchool(null);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Kon nie skool opdateer nie");
                          }
                        }}
                        testId="save-edit-school"
                      >
                        Stoor Veranderinge
                      </Button>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* Invitations Tab */}
          {activeTab === "invitations" && (
            <div className="space-y-6">
              <Card testId="generate-invite-form">
                <h3 className="font-heading font-bold mb-4">Skep Nuwe Uitnodigingskode</h3>
                <p className="text-text-secondary mb-4">
                  Skep 'n kode en stuur dit na ouers/leerders om te registreer.
                </p>
                <div className="flex gap-4">
                  <Input
                    label="Nota (opsioneel)"
                    value={inviteNote}
                    onChange={(e) => setInviteNote(e.target.value)}
                    placeholder="bv. Vir Johan se kind"
                    className="flex-1"
                    testId="invite-note-input"
                  />
                </div>
                <Button onClick={handleGenerateInvitation} className="mt-4" testId="generate-invite-btn">
                  Skep Uitnodigingskode
                </Button>
              </Card>

              <h3 className="font-heading font-bold">Alle Uitnodigingskodes</h3>
              <div className="space-y-4">
                {invitations.length === 0 ? (
                  <Card><p className="text-text-muted">Geen uitnodigingskodes geskep nie</p></Card>
                ) : (
                  invitations.map(inv => (
                    <Card key={inv.code} testId={`invite-${inv.code}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-lg font-bold bg-slate-100 px-3 py-1 rounded">{inv.code}</span>
                            {inv.used ? (
                              <span className="text-xs bg-slate-200 text-text-muted px-2 py-1 rounded-full">Gebruik</span>
                            ) : (
                              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full">Beskikbaar</span>
                            )}
                          </div>
                          {inv.note && <p className="text-sm text-text-muted mt-1">{inv.note}</p>}
                          {inv.used && inv.used_by && (
                            <p className="text-sm text-text-muted mt-1">Gebruik deur leerder</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {!inv.used && (
                            <>
                              <button
                                onClick={() => copyInviteLink(inv.code)}
                                className="p-2 text-primary-500 hover:bg-primary-50 rounded-lg"
                                title="Kopieer skakel"
                                data-testid={`copy-invite-${inv.code}`}
                              >
                                <Copy className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteInvitation(inv.code)}
                                className="p-2 text-accent-500 hover:bg-accent-50 rounded-lg"
                                data-testid={`delete-invite-${inv.code}`}
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Learners Tab */}
          {activeTab === "learners" && (
            <LearnersTab learners={learners} setLearners={setLearners} />
          )}

          {/* Tutoring Requests Tab */}
          {activeTab === "tutoring" && (
            <div className="space-y-6">
              <Card testId="tutoring-requests">
                <h3 className="font-heading font-bold mb-4">Online Tutoring Aanvrae (R100/week)</h3>
                {tutoringRequests.length === 0 ? (
                  <p className="text-text-muted">Geen aanvrae nog ontvang nie</p>
                ) : (
                  <div className="space-y-4">
                    {tutoringRequests.map(req => (
                      <div key={req.id} className={`p-4 rounded-xl border ${
                        req.status === 'pending' ? 'border-yellow-300 bg-yellow-50' :
                        req.status === 'contacted' ? 'border-blue-300 bg-blue-50' :
                        req.status === 'confirmed' ? 'border-green-300 bg-green-50' :
                        'border-gray-300 bg-gray-50'
                      }`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold">{req.requester_name}</h4>
                            <p className="text-sm text-text-muted">{req.requester_type === 'learner' ? 'Leerder' : req.requester_type === 'parent' ? 'Ouer' : 'Skool'}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            req.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                            req.status === 'contacted' ? 'bg-blue-200 text-blue-800' :
                            req.status === 'confirmed' ? 'bg-green-200 text-green-800' :
                            'bg-gray-200 text-gray-800'
                          }`}>
                            {req.status === 'pending' ? 'Wag' : req.status === 'contacted' ? 'Gekontak' : req.status === 'confirmed' ? 'Bevestig' : 'Gekanselleer'}
                          </span>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-text-muted">WhatsApp:</span>
                            <a href={`https://wa.me/${req.whatsapp?.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="ml-2 text-green-600 hover:underline">
                              {req.whatsapp}
                            </a>
                          </div>
                          <div>
                            <span className="text-text-muted">Dae:</span> {req.preferred_days?.join(', ') || '-'}
                          </div>
                          <div>
                            <span className="text-text-muted">Tye:</span> {req.preferred_times?.join(', ') || '-'}
                          </div>
                          <div>
                            <span className="text-text-muted">Datum:</span> {new Date(req.created_at).toLocaleDateString('af-ZA')}
                          </div>
                        </div>
                        {req.notes && <p className="mt-2 text-sm italic">"{req.notes}"</p>}
                        <div className="mt-3 flex gap-2">
                          <select 
                            className="input-field text-sm py-1"
                            value={req.status}
                            onChange={async (e) => {
                              try {
                                await api.put(`/admin/tutoring/request/${req.id}?status=${e.target.value}`);
                                const res = await api.get("/admin/tutoring/requests");
                                setTutoringRequests(res.data.requests || []);
                                toast.success("Status opgedateer");
                              } catch (err) {
                                toast.error(t('toast.couldNotSave'));
                              }
                            }}
                          >
                            <option value="pending">Wag</option>
                            <option value="contacted">Gekontak</option>
                            <option value="confirmed">Bevestig</option>
                            <option value="cancelled">Gekanselleer</option>
                          </select>
                          <a 
                            href={`https://wa.me/${req.whatsapp?.replace(/\D/g, '')}?text=Hallo ${req.requester_name}, dankie vir jou aanvraag vir online tutoring by Lees is Duidelik!`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600"
                          >
                            <Phone className="w-4 h-4" /> WhatsApp
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Texts Tab */}
          {activeTab === "texts" && (
            <TextsTab texts={texts} setTexts={setTexts} />
          )}

          {/* Woordbou Tab - Word Building for Grade 1-3 */}
          {activeTab === "woordbou" && (
            <div className="space-y-6">
              <Card testId="woordbou-info">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Woordbou (Graad 1-6)</h3>
                    <p className="text-sm text-text-muted">Leerders bou woorde deur letters te sleep</p>
                  </div>
                </div>
                <p className="text-text-secondary">
                  Laai 'n woordkaart prent op en spesifiseer watter letters beskikbaar moet wees. 
                  Leerders sal die letters een-vir-een sleep om die woord te bou.
                </p>
                <p className="text-xs text-text-muted mt-2">
                  <strong>Ondersteunde formate:</strong> Prente: PNG, JPG, JPEG, GIF, WEBP (max 5MB)
                </p>
              </Card>

              {/* Create Woordbou */}
              <Card testId="create-woordbou">
                <h3 className="font-heading font-bold mb-4">Skep Nuwe Woordbou Oefening</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Title / Titel"
                    value={newWoordbou.title}
                    onChange={(e) => setNewWoordbou({...newWoordbou, title: e.target.value})}
                    placeholder="e.g. Build the word: CAT / bv. Bou die woord: KAT"
                    testId="woordbou-title"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">Grade / Graad</label>
                      <select
                        className="input-field"
                        value={newWoordbou.grade_level}
                        onChange={(e) => setNewWoordbou({...newWoordbou, grade_level: parseInt(e.target.value)})}
                        data-testid="woordbou-grade"
                      >
                        <option value={1}>Graad 1</option>
                        <option value={2}>Graad 2</option>
                        <option value={3}>Graad 3</option>
                        <option value={4}>Graad 4</option>
                        <option value={5}>Graad 5</option>
                        <option value={6}>Graad 6</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">Term / Kwartaal</label>
                      <select
                        className="input-field"
                        value={newWoordbou.term}
                        onChange={(e) => setNewWoordbou({...newWoordbou, term: parseInt(e.target.value)})}
                        data-testid="woordbou-term"
                      >
                        {[1,2,3,4].map(t => <option key={t} value={t}>Kwartaal {t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">Language / Taal</label>
                      <select
                        className="input-field"
                        value={newWoordbou.language || "af"}
                        onChange={(e) => setNewWoordbou({...newWoordbou, language: e.target.value})}
                        data-testid="woordbou-language"
                      >
                        <option value="af">🇿🇦 Afrikaans</option>
                        <option value="en">🇬🇧 English</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Words / Woorde (semicolon-separated for multiple / kommapunt-geskeide vir meerdere)
                    </label>
                    <textarea
                      className="input-field min-h-[60px]"
                      value={newWoordbou.target_word}
                      onChange={(e) => setNewWoordbou({...newWoordbou, target_word: e.target.value.toLowerCase()})}
                      placeholder="Single: kat&#10;Multiple: kat; hond; vis"
                      data-testid="woordbou-target"
                    />
                    <p className="text-xs text-text-muted mt-1">
                      Gebruik <strong>;</strong> om woorde te skei vir veelvuldige woorde per oefening
                    </p>
                  </div>
                  <Input
                    label="Available Letters / Beskikbare Letters (comma-separated / komma-geskeide)"
                    value={newWoordbou.available_letters}
                    onChange={(e) => setNewWoordbou({...newWoordbou, available_letters: e.target.value.toLowerCase()})}
                    placeholder="e.g. k, a, t, h, o, n, d, v, i, s"
                    testId="woordbou-letters"
                  />
                </div>
                
                {/* OCR Extraction */}
                <div className="bg-primary-50 rounded-xl p-4 mt-4">
                  <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary-500" />
                    Outomatiese Letter-Ekstrasie van Prent
                  </p>
                  <p className="text-xs text-text-muted mb-3">
                    Laai 'n woordkaart prent op en ons sal die letters outomaties uittrek.
                  </p>
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-white border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-100">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          toast.info("Ekstraheer letters...");
                          const formData = new FormData();
                          formData.append("file", file);
                          try {
                            const res = await api.post("/ocr/extract-letters", formData);
                            const letters = res.data.letters.join(", ");
                            setNewWoordbou({...newWoordbou, available_letters: letters});
                            toast.success(res.data.message);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Kon nie letters ekstraheer nie");
                          }
                        }
                      }}
                      data-testid="ocr-extract-input"
                    />
                    <Image className="w-4 h-4" />
                    Laai Prent Op vir OCR
                  </label>
                </div>
                
                <p className="text-xs text-text-muted mt-2">
                  Voeg ekstra letters by om dit moeiliker te maak. Die leerder moet die korrekte letters kies en sleep om die woord te bou.
                </p>
                <Button
                  onClick={async () => {
                    if (!newWoordbou.title || !newWoordbou.target_word || !newWoordbou.available_letters) {
                      toast.error("Vul alle velde in");
                      return;
                    }
                    try {
                      const lettersArray = newWoordbou.available_letters.split(",").map(l => l.trim()).filter(l => l);
                      // Parse words - support semicolon separated for multiple words
                      const wordsArray = newWoordbou.target_word.split(";").map(w => w.trim()).filter(w => w);
                      const res = await api.post("/woordbou", {
                        ...newWoordbou,
                        target_word: wordsArray[0],  // For backward compatibility
                        words: wordsArray,  // Multiple words
                        available_letters: lettersArray
                      });
                      toast.success(`Woordbou oefening geskep met ${wordsArray.length} woord(e)!`);
                      setNewWoordbou({ title: "", grade_level: 1, term: 1, target_word: "", available_letters: "", language: "af" });
                      const listRes = await api.get("/woordbou");
                      setWoordbouList(listRes.data.exercises || []);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Kon nie skep nie");
                    }
                  }}
                  className="mt-4"
                  testId="create-woordbou-btn"
                >
                  Skep Woordbou Oefening
                </Button>
              </Card>

              {/* List of Woordbou exercises */}
              <h3 className="font-heading font-bold">Bestaande Woordbou Oefeninge</h3>
              <div className="space-y-3">
                {woordbouList.length === 0 ? (
                  <Card><p className="text-text-muted">Geen woordbou oefeninge geskep nie</p></Card>
                ) : (
                  woordbouList.map(wb => (
                    <Card key={wb.id} testId={`woordbou-${wb.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">📚</span>
                            <div>
                              <h4 className="font-semibold flex items-center gap-2">
                                {wb.title}
                                {wb.words?.length > 1 && (
                                  <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                                    {wb.words.length} woorde
                                  </span>
                                )}
                                <span className="text-sm" title={wb.language === "en" ? "English" : "Afrikaans"}>
                                  {wb.language === "en" ? "🇬🇧" : "🇿🇦"}
                                </span>
                              </h4>
                              <p className="text-sm text-text-muted">
                                Graad {wb.grade_level} | Kwartaal {wb.term} | 
                                {wb.words?.length > 1 
                                  ? ` Woorde: ${wb.words.join(", ")}`
                                  : ` Woord: ${wb.target_word}`
                                }
                              </p>
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {wb.available_letters?.map((letter, i) => (
                              <span key={i} className="bg-primary-100 text-primary-700 px-3 py-1 rounded-lg font-mono font-bold">
                                {letter}
                              </span>
                            ))}
                          </div>
                          {wb.image_url && (
                            <div className="mt-3 relative group">
                              <img src={`${API}${wb.image_url}`} alt="Woordkaart" className="w-32 h-32 object-cover rounded-lg border" />
                              {/* Overlay with replace button on hover */}
                              <div className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        const formData = new FormData();
                                        formData.append("file", file);
                                        try {
                                          await api.post(`/woordbou/${wb.id}/image`, formData);
                                          toast.success(t('toast.imageUploaded'));
                                          localStorage.setItem('lastUploadTime', new Date().toISOString());
                                          const res = await api.get("/woordbou");
                                          setWoordbouList(res.data.exercises || []);
                                        } catch (err) {
                                          toast.error(t('toast.couldNotUpload'));
                                        }
                                      }
                                    }}
                                  />
                                  <span className="px-3 py-2 bg-white text-slate-700 rounded-lg text-sm font-medium cursor-pointer hover:bg-slate-100 flex items-center gap-2">
                                    <Upload className="w-4 h-4" /> Vervang
                                  </span>
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          {/* Upload or AI generate image buttons when no image exists */}
                          {!wb.image_url && (
                            <div className="flex gap-2">
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const formData = new FormData();
                                      formData.append("file", file);
                                      try {
                                        await api.post(`/woordbou/${wb.id}/image`, formData);
                                        toast.success(t('toast.imageUploaded'));
                                        localStorage.setItem('lastUploadTime', new Date().toISOString());
                                        const res = await api.get("/woordbou");
                                        setWoordbouList(res.data.exercises || []);
                                      } catch (err) {
                                        toast.error(t('toast.couldNotUpload'));
                                      }
                                    }
                                  }}
                                />
                                <span className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium cursor-pointer hover:bg-blue-600 flex items-center gap-2">
                                  <Upload className="w-4 h-4" />
                                  Laai Op
                                </span>
                              </label>
                              <button
                                onClick={async () => {
                                  try {
                                    toast.info(`Genereer KI prent...`);
                                    const res = await api.post(`/woordbou/${wb.id}/generate-ai-image`);
                                    toast.success(res.data.message);
                                    const updated = await api.get("/woordbou");
                                    setWoordbouList(updated.data.exercises || []);
                                  } catch (err) {
                                    toast.error(`Kon nie prent genereer nie: ${err.response?.data?.detail || err.message}`);
                                  }
                                }}
                                className="px-3 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium cursor-pointer hover:bg-purple-600 flex items-center gap-2"
                              >
                                <Sparkles className="w-4 h-4" />
                                KI Prent
                              </button>
                            </div>
                          )}
                          {/* Image management buttons when image exists */}
                          {wb.image_url && (
                            <div className="flex gap-2">
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const formData = new FormData();
                                      formData.append("file", file);
                                      try {
                                        await api.post(`/woordbou/${wb.id}/image`, formData);
                                        toast.success("Prent vervang!");
                                        localStorage.setItem('lastUploadTime', new Date().toISOString());
                                        const res = await api.get("/woordbou");
                                        setWoordbouList(res.data.exercises || []);
                                      } catch (err) {
                                        toast.error(t('toast.couldNotUpload'));
                                      }
                                    }
                                  }}
                                />
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs cursor-pointer hover:bg-blue-200 flex items-center gap-1">
                                  <Upload className="w-3 h-3" /> Vervang
                                </span>
                              </label>
                              <button
                                onClick={async () => {
                                  try {
                                    toast.info(`Genereer nuwe KI prent...`);
                                    const res = await api.post(`/woordbou/${wb.id}/generate-ai-image`);
                                    toast.success(res.data.message);
                                    const updated = await api.get("/woordbou");
                                    setWoordbouList(updated.data.exercises || []);
                                  } catch (err) {
                                    toast.error(`Kon nie prent genereer nie: ${err.response?.data?.detail || err.message}`);
                                  }
                                }}
                                className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs cursor-pointer hover:bg-purple-200 flex items-center gap-1"
                              >
                                <Sparkles className="w-3 h-3" /> KI
                              </button>
                              <button
                                onClick={async () => {
                                  if (window.confirm("Wil jy die prent uitvee?")) {
                                    try {
                                      await api.delete(`/woordbou/${wb.id}/image`);
                                      toast.success("Prent uitgevee");
                                      const res = await api.get("/woordbou");
                                      setWoordbouList(res.data.exercises || []);
                                    } catch (err) {
                                      toast.error(t('toast.couldNotDelete'));
                                    }
                                  }
                                }}
                                className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs cursor-pointer hover:bg-red-200 flex items-center gap-1"
                              >
                                <Trash2 className="w-3 h-3" /> Vee
                              </button>
                            </div>
                          )}
                          <button
                            onClick={async () => {
                              if (window.confirm("Is jy seker jy wil hierdie oefening verwyder?")) {
                                try {
                                  await api.delete(`/woordbou/${wb.id}`);
                                  toast.success("Oefening verwyder");
                                  const res = await api.get("/woordbou");
                                  setWoordbouList(res.data.exercises || []);
                                } catch (err) {
                                  toast.error(t('toast.couldNotDelete'));
                                }
                              }
                            }}
                            className="px-3 py-2 text-accent-500 hover:bg-accent-50 rounded-lg text-sm"
                            data-testid={`delete-woordbou-${wb.id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Klanktoets Tab - Sound/Spelling Test for Grade 1-6 */}
          {activeTab === "klanktoets" && (
            <div className="space-y-6">
              <Card testId="klanktoets-info">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center">
                    <Volume2 className="w-6 h-6 text-secondary-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Klanktoets / Sound Test (Graad 1-6)</h3>
                    <p className="text-sm text-text-muted">Leerders sien woorde en neem hulself op</p>
                  </div>
                </div>
                <p className="text-text-secondary">
                  Skep toetse waar leerders die woord op die skerm sien, en hulself opneem terwyl hulle dit sê. 
                  <strong> KI analiseer hul uitspraak outomaties.</strong> Jy kan ook prente byvoeg vir ekstra hulp.
                </p>
              </Card>

              {/* Create Klanktoets */}
              <Card testId="create-klanktoets">
                <h3 className="font-heading font-bold mb-4">Skep Nuwe Klanktoets</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Titel"
                    value={newKlanktoets.title}
                    onChange={(e) => setNewKlanktoets({...newKlanktoets, title: e.target.value})}
                    placeholder="bv. Klanktoets - Week 1"
                    testId="klanktoets-title"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">Graad</label>
                      <select
                        className="input-field"
                        value={newKlanktoets.grade_level}
                        onChange={(e) => setNewKlanktoets({...newKlanktoets, grade_level: parseInt(e.target.value)})}
                        data-testid="klanktoets-grade"
                      >
                        <option value={1}>Graad 1</option>
                        <option value={2}>Graad 2</option>
                        <option value={3}>Graad 3</option>
                        <option value={4}>Graad 4</option>
                        <option value={5}>Graad 5</option>
                        <option value={6}>Graad 6</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">Kwartaal</label>
                      <select
                        className="input-field"
                        value={newKlanktoets.term}
                        onChange={(e) => setNewKlanktoets({...newKlanktoets, term: parseInt(e.target.value)})}
                        data-testid="klanktoets-term"
                      >
                        {[1,2,3,4].map(t => <option key={t} value={t}>Kwartaal {t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">Taal</label>
                      <select
                        className="input-field"
                        value={newKlanktoets.language || "af"}
                        onChange={(e) => setNewKlanktoets({...newKlanktoets, language: e.target.value})}
                        data-testid="klanktoets-language"
                      >
                        <option value="af">🇿🇦 Afrikaans</option>
                        <option value="en">🇬🇧 English</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Items to add */}
                <div className="mt-4 border-t pt-4">
                  <p className="text-sm font-semibold mb-3">Voeg Items By (elkeen = 1 punt)</p>
                  <div className="space-y-2">
                    {newKlanktoets.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                        <span className="font-bold text-primary-500">{idx + 1}.</span>
                        <span className="flex-1">Antwoord: <strong>{item.correct_answer}</strong></span>
                        <button
                          onClick={() => {
                            const newItems = newKlanktoets.items.filter((_, i) => i !== idx);
                            setNewKlanktoets({...newKlanktoets, items: newItems});
                          }}
                          className="text-accent-500 hover:bg-accent-50 p-1 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 mt-3">
                    <input
                      type="text"
                      className="input-field flex-1"
                      placeholder="Tik korrekte antwoord (bv. kat)"
                      id="new-klank-item"
                      data-testid="klanktoets-new-item"
                    />
                    <Button
                      variant="secondary"
                      onClick={() => {
                        const input = document.getElementById("new-klank-item");
                        const answer = input?.value?.trim();
                        if (answer) {
                          setNewKlanktoets({
                            ...newKlanktoets,
                            items: [...newKlanktoets.items, { correct_answer: answer.toLowerCase() }]
                          });
                          input.value = "";
                        }
                      }}
                      testId="add-klanktoets-item"
                    >
                      + Voeg By
                    </Button>
                  </div>
                </div>

                <Button
                  onClick={async () => {
                    if (!newKlanktoets.title || newKlanktoets.items.length === 0) {
                      toast.error(t('klanktoets.addTitleAndItem'));
                      return;
                    }
                    try {
                      const res = await api.post("/klanktoets", newKlanktoets);
                      toast.success(t('klanktoets.created'));
                      setNewKlanktoets({ title: "", grade_level: 1, term: 1, test_type: "audio_to_text", items: [] });
                      const listRes = await api.get("/klanktoets");
                      setKlanktoetsList(listRes.data.exercises || []);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || t('toast.couldNotCreate'));
                    }
                  }}
                  className="mt-4"
                  disabled={newKlanktoets.items.length === 0}
                  testId="create-klanktoets-btn"
                >
                  {t('klanktoets.create')} ({newKlanktoets.items.length} items)
                </Button>
              </Card>

              {/* List of Klanktoets exercises */}
              <h3 className="font-heading font-bold">{t('klanktoets.existingTests') || 'Bestaande Klanktoetse'}</h3>
              <div className="space-y-4">
                {klanktoetsList.length === 0 ? (
                  <Card><p className="text-text-muted">{t('klanktoets.noTestsCreated') || 'Geen klanktoetse geskep nie'}</p></Card>
                ) : (
                  klanktoetsList.map(kt => (
                    <Card key={kt.id} testId={`klanktoets-${kt.id}`}>
                      {/* Edit mode for this klanktoets */}
                      {editingKlanktoets?.id === kt.id ? (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-heading font-bold">{t('edit')} Klanktoets</h4>
                            <button
                              onClick={() => setEditingKlanktoets(null)}
                              className="p-2 text-text-muted hover:bg-slate-100 rounded-lg"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <Input
                            label="Titel"
                            value={editingKlanktoets.title}
                            onChange={(e) => setEditingKlanktoets({...editingKlanktoets, title: e.target.value})}
                            testId="edit-klanktoets-title"
                          />
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-sm font-semibold text-text-secondary mb-2">Graad</label>
                              <select
                                className="input-field"
                                value={editingKlanktoets.grade_level}
                                onChange={(e) => setEditingKlanktoets({...editingKlanktoets, grade_level: parseInt(e.target.value)})}
                              >
                                {[1,2,3,4,5,6].map(g => <option key={g} value={g}>Graad {g}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-text-secondary mb-2">Kwartaal</label>
                              <select
                                className="input-field"
                                value={editingKlanktoets.term}
                                onChange={(e) => setEditingKlanktoets({...editingKlanktoets, term: parseInt(e.target.value)})}
                              >
                                {[1,2,3,4].map(t => <option key={t} value={t}>Kwartaal {t}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-text-secondary mb-2">Taal</label>
                              <select
                                className="input-field"
                                value={editingKlanktoets.language || "af"}
                                onChange={(e) => setEditingKlanktoets({...editingKlanktoets, language: e.target.value})}
                              >
                                <option value="af">🇿🇦 Afrikaans</option>
                                <option value="en">🇬🇧 English</option>
                              </select>
                            </div>
                          </div>
                          
                          {/* Edit items */}
                          <div className="border-t pt-4">
                            <p className="text-sm font-semibold mb-3">{t('klanktoets.editItems') || 'Wysig Woorde'}</p>
                            <div className="space-y-2">
                              {editingKlanktoets.items?.map((item, idx) => (
                                <div key={item.id || idx} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                                  <span className="font-bold text-primary-500 w-6">{idx + 1}.</span>
                                  <input
                                    type="text"
                                    className="input-field flex-1"
                                    value={item.correct_answer}
                                    onChange={(e) => {
                                      const newItems = [...editingKlanktoets.items];
                                      newItems[idx] = {...newItems[idx], correct_answer: e.target.value.toLowerCase()};
                                      setEditingKlanktoets({...editingKlanktoets, items: newItems});
                                    }}
                                  />
                                  <button
                                    onClick={() => {
                                      const newItems = editingKlanktoets.items.filter((_, i) => i !== idx);
                                      setEditingKlanktoets({...editingKlanktoets, items: newItems});
                                    }}
                                    className="text-accent-500 hover:bg-accent-50 p-2 rounded"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            {/* Add new item */}
                            <div className="flex gap-3 mt-3">
                              <input
                                type="text"
                                className="input-field flex-1"
                                placeholder={t('klanktoets.addNewWord') || "Voeg nuwe woord by (bv. boom)"}
                                id={`edit-klank-new-item-${kt.id}`}
                              />
                              <Button
                                variant="secondary"
                                onClick={() => {
                                  const input = document.getElementById(`edit-klank-new-item-${kt.id}`);
                                  const answer = input?.value?.trim();
                                  if (answer) {
                                    setEditingKlanktoets({
                                      ...editingKlanktoets,
                                      items: [...(editingKlanktoets.items || []), { 
                                        id: `new-${Date.now()}`,
                                        correct_answer: answer.toLowerCase() 
                                      }]
                                    });
                                    input.value = "";
                                  }
                                }}
                              >
                                + {t('klanktoets.addWord') || 'Voeg By'}
                              </Button>
                            </div>
                          </div>
                          
                          {/* Save/Cancel buttons */}
                          <div className="flex gap-3 pt-4 border-t">
                            <Button
                              onClick={async () => {
                                try {
                                  await api.put(`/klanktoets/${kt.id}`, {
                                    title: editingKlanktoets.title,
                                    grade_level: editingKlanktoets.grade_level,
                                    term: editingKlanktoets.term,
                                    test_type: editingKlanktoets.test_type || "audio_to_text",
                                    items: editingKlanktoets.items.map(item => ({
                                      correct_answer: item.correct_answer,
                                      audio_url: item.audio_url,
                                      image_url: item.image_url
                                    })),
                                    language: editingKlanktoets.language
                                  });
                                  toast.success(t('toast.saved'));
                                  setEditingKlanktoets(null);
                                  const res = await api.get("/klanktoets");
                                  setKlanktoetsList(res.data.exercises || []);
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || t('toast.couldNotSave'));
                                }
                              }}
                              testId="save-klanktoets-edit"
                            >
                              {t('save')}
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => setEditingKlanktoets(null)}
                            >
                              {t('cancel')}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">🎤</span>
                            <div>
                              <h4 className="font-semibold">{kt.title}</h4>
                              <p className="text-sm text-text-muted">
                                Graad {kt.grade_level} | Kwartaal {kt.term} | 
                                {kt.items?.length || 0} {t('klanktoets.words') || 'woorde'}
                                {kt.language && <span className="ml-2">{kt.language === "en" ? "🇬🇧" : "🇿🇦"}</span>}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Edit button */}
                          <button
                            onClick={() => setEditingKlanktoets({
                              id: kt.id,
                              title: kt.title,
                              grade_level: kt.grade_level,
                              term: kt.term,
                              test_type: kt.test_type || "audio_to_text",
                              items: kt.items?.map(item => ({...item})) || [],
                              language: kt.language || "af"
                            })}
                            className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                            data-testid={`edit-klanktoets-${kt.id}`}
                            title={t('edit')}
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          {/* Delete button */}
                          <button
                            onClick={async () => {
                              if (window.confirm(t('klanktoets.confirmDelete') || "Is jy seker jy wil hierdie toets verwyder?")) {
                                try {
                                  await api.delete(`/klanktoets/${kt.id}`);
                                  toast.success(t('toast.deleted'));
                                  const res = await api.get("/klanktoets");
                                  setKlanktoetsList(res.data.exercises || []);
                                } catch (err) {
                                  toast.error(t('toast.couldNotDelete'));
                                }
                              }
                            }}
                            className="p-2 text-accent-500 hover:bg-accent-50 rounded-lg"
                            data-testid={`delete-klanktoets-${kt.id}`}
                            title={t('delete')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Items with upload options */}
                      <div className="space-y-2 border-t pt-4">
                        <p className="text-sm font-semibold mb-2">Items - Laai prente op of genereer KI prente:</p>
                        {kt.items?.map((item, idx) => (
                          <div key={item.id || idx} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                            <span className="font-bold text-primary-500 w-6">{idx + 1}.</span>
                            <span className="flex-1 font-mono text-lg font-semibold">{item.correct_answer}</span>
                            {/* Always show image upload option */}
                            {item.image_url ? (
                              <div className="flex items-center gap-2 group relative">
                                <img src={`${API}${item.image_url}`} alt={item.correct_answer} className="w-16 h-16 object-cover rounded-lg border-2 border-green-200" />
                                <Check className="w-4 h-4 text-green-500" />
                                {/* Replace and AI generate buttons */}
                                <div className="flex flex-col gap-1">
                                  <label className="cursor-pointer">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          const formData = new FormData();
                                          formData.append("file", file);
                                          formData.append("item_index", idx);
                                          try {
                                            await api.post(`/klanktoets/${kt.id}/image`, formData);
                                            toast.success(`Prent vir "${item.correct_answer}" vervang!`);
                                            localStorage.setItem('lastUploadTime', new Date().toISOString());
                                            const res = await api.get("/klanktoets");
                                            setKlanktoetsList(res.data.exercises || []);
                                          } catch (err) {
                                            toast.error(t('toast.couldNotUpload'));
                                          }
                                        }
                                      }}
                                    />
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs cursor-pointer hover:bg-blue-200 flex items-center gap-1">
                                      <Upload className="w-3 h-3" /> Vervang
                                    </span>
                                  </label>
                                  <button
                                    onClick={async () => {
                                      try {
                                        toast.info(`Genereer KI prent vir "${item.correct_answer}"...`);
                                        const res = await api.post(`/klanktoets/${kt.id}/generate-ai-image?item_index=${idx}`);
                                        toast.success(res.data.message);
                                        const updated = await api.get("/klanktoets");
                                        setKlanktoetsList(updated.data.exercises || []);
                                      } catch (err) {
                                        toast.error(`Kon nie prent genereer nie: ${err.response?.data?.detail || err.message}`);
                                      }
                                    }}
                                    className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs cursor-pointer hover:bg-purple-200 flex items-center gap-1"
                                  >
                                    <Sparkles className="w-3 h-3" /> KI Prent
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        const formData = new FormData();
                                        formData.append("file", file);
                                        formData.append("item_index", idx);
                                        try {
                                          await api.post(`/klanktoets/${kt.id}/image`, formData);
                                          toast.success(`Prent vir "${item.correct_answer}" opgelaai!`);
                                          localStorage.setItem('lastUploadTime', new Date().toISOString());
                                          const res = await api.get("/klanktoets");
                                          setKlanktoetsList(res.data.exercises || []);
                                        } catch (err) {
                                          toast.error(t('toast.couldNotUpload'));
                                        }
                                      }
                                    }}
                                  />
                                  <span className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm cursor-pointer hover:bg-blue-200 flex items-center gap-2">
                                    <Upload className="w-4 h-4" /> Laai Op
                                  </span>
                                </label>
                                <button
                                  onClick={async () => {
                                    try {
                                      toast.info(`Genereer KI prent vir "${item.correct_answer}"...`);
                                      const res = await api.post(`/klanktoets/${kt.id}/generate-ai-image?item_index=${idx}`);
                                      toast.success(res.data.message);
                                      const updated = await api.get("/klanktoets");
                                      setKlanktoetsList(updated.data.exercises || []);
                                    } catch (err) {
                                      toast.error(`Kon nie prent genereer nie: ${err.response?.data?.detail || err.message}`);
                                    }
                                  }}
                                  className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm cursor-pointer hover:bg-purple-200 flex items-center gap-2"
                                >
                                  <Sparkles className="w-4 h-4" /> KI Prent
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                        </>
                      )}
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Alfabet Tab - Manage alphabet images and audio for Grade 1-3 */}
          {activeTab === "alfabet" && (
            <div className="space-y-6">
              <Card testId="alfabet-info">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Alfabet Helper (Graad 1-3)</h3>
                    <p className="text-sm text-text-muted">Laai prente en klanke op vir elke letter</p>
                  </div>
                </div>
                <p className="text-text-secondary">
                  Leerders in Graad 1-3 kan die alfabet leer deur op letters te klik.
                  Laai 'n prent en klank op vir elke letter (bv. "A is vir Appel").
                </p>
                <p className="text-xs text-text-muted mt-2">
                  <strong>Ondersteunde formate:</strong> Prente: PNG, JPG, JPEG, GIF, WEBP (max 5MB) | Klanke: MP3, WAV, OGG (max 10MB)
                </p>
              </Card>

              {/* Language Selector for Alphabet */}
              <Card>
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold">Kies Taal / Select Language</h3>
                  <select
                    className="input-field w-48"
                    value={alphabetLanguage}
                    onChange={async (e) => {
                      const lang = e.target.value;
                      setAlphabetLanguage(lang);
                      try {
                        const res = await api.get(`/alphabet?language=${lang}`);
                        setAlphabetItems(res.data || []);
                      } catch (err) {
                        console.error("Error loading alphabet:", err);
                      }
                    }}
                    data-testid="alphabet-language-selector"
                  >
                    <option value="af">🇿🇦 Afrikaans</option>
                    <option value="en">🇬🇧 English</option>
                  </select>
                </div>
              </Card>

              {/* Alphabet Grid Management */}
              <Card testId="alfabet-management">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold">
                    Alfabet Bestuur ({alphabetLanguage === "en" ? "English" : "Afrikaans"})
                  </h3>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Genereer KI prente vir alle letters sonder prente? Dit kan 'n paar minute neem.")) return;
                      try {
                        toast.info("Genereer KI prente... Dit kan 'n rukkie neem.");
                        const res = await api.post(`/alphabet/generate-ai-images?language=${alphabetLanguage}`);
                        toast.success(res.data.message);
                        // Reload alphabet
                        const updated = await api.get(`/alphabet?language=${alphabetLanguage}`);
                        setAlphabetItems(updated.data);
                      } catch (err) {
                        toast.error("Kon nie KI prente genereer nie");
                      }
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm font-medium"
                    data-testid="generate-ai-images-btn"
                  >
                    <Sparkles className="w-4 h-4" />
                    Genereer KI Prente
                  </button>
                </div>
                <p className="text-sm text-text-muted mb-4">
                  Laai jou eie <strong>oudio</strong> op, of klik "Genereer KI Prente" om outomaties prente te skep vir elke letter.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {alphabetItems.map((item) => (
                    <div key={item.letter} className="border rounded-xl p-3 text-center">
                      <div className="text-4xl font-bold text-pink-600 mb-2">{item.letter}</div>
                      <p className="text-sm font-medium mb-2 text-text-secondary">{item.word}</p>
                      
                      {/* Image preview */}
                      <div className="mb-2">
                        {item.image_url ? (
                          <div className="relative">
                            <img 
                              src={`${API}${item.image_url}`} 
                              alt={item.word}
                              className="w-16 h-16 object-cover rounded mx-auto"
                            />
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </span>
                            {/* Replace/Delete image buttons */}
                            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const formData = new FormData();
                                      formData.append("file", file);
                                      formData.append("language", alphabetLanguage);
                                      try {
                                        await api.post(`/alphabet/${item.letter}/upload-image`, formData);
                                        toast.success(`Prent vir ${item.letter} opgelaai!`);
                                        const res = await api.get(`/alphabet?language=${alphabetLanguage}`);
                                        setAlphabetItems(res.data);
                                      } catch (err) {
                                        toast.error(`Kon nie prent oplaai nie`);
                                      }
                                    }
                                  }}
                                />
                                <span className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded hover:bg-blue-600" title="Vervang prent">
                                  <Upload className="w-3 h-3 inline" />
                                </span>
                              </label>
                              <button
                                onClick={async () => {
                                  try {
                                    toast.info(`Genereer nuwe KI prent vir ${item.letter}...`);
                                    const res = await api.post(`/alphabet/${item.letter}/generate-ai-image?language=${alphabetLanguage}`);
                                    toast.success(res.data.message);
                                    const updated = await api.get(`/alphabet?language=${alphabetLanguage}`);
                                    setAlphabetItems(updated.data);
                                  } catch (err) {
                                    toast.error(`Kon nie prent genereer nie`);
                                  }
                                }}
                                className="bg-purple-500 text-white text-xs px-1 py-0.5 rounded hover:bg-purple-600"
                                title="Genereer nuwe KI prent"
                              >
                                <Sparkles className="w-3 h-3 inline" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            {/* Upload own image */}
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const formData = new FormData();
                                    formData.append("file", file);
                                    formData.append("language", alphabetLanguage);
                                    try {
                                      await api.post(`/alphabet/${item.letter}/upload-image`, formData);
                                      toast.success(`Prent vir ${item.letter} opgelaai!`);
                                      const res = await api.get(`/alphabet?language=${alphabetLanguage}`);
                                      setAlphabetItems(res.data);
                                    } catch (err) {
                                      toast.error(`Kon nie prent oplaai nie`);
                                    }
                                  }
                                }}
                              />
                              <span className="w-14 h-10 bg-blue-100 hover:bg-blue-200 rounded flex flex-col items-center justify-center cursor-pointer transition-colors" title="Laai eie prent op">
                                <Upload className="w-4 h-4 text-blue-600" />
                                <span className="text-[10px] text-blue-600">Laai Op</span>
                              </span>
                            </label>
                            {/* Generate AI image */}
                            <button
                              onClick={async () => {
                                try {
                                  toast.info(`Genereer prent vir ${item.letter}...`);
                                  const res = await api.post(`/alphabet/${item.letter}/generate-ai-image?language=${alphabetLanguage}`);
                                  toast.success(res.data.message);
                                  const updated = await api.get(`/alphabet?language=${alphabetLanguage}`);
                                  setAlphabetItems(updated.data);
                                } catch (err) {
                                  toast.error(`Kon nie prent genereer nie: ${err.response?.data?.detail || err.message}`);
                                }
                              }}
                              className="w-14 h-10 bg-purple-100 hover:bg-purple-200 rounded flex flex-col items-center justify-center cursor-pointer transition-colors"
                              title="Klik om KI prent te genereer"
                            >
                              <Sparkles className="w-4 h-4 text-purple-600" />
                              <span className="text-[10px] text-purple-600">KI Prent</span>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Audio upload button only */}
                      <div className="space-y-1">
                        <label className="block cursor-pointer">
                          <input
                            type="file"
                            accept="audio/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const formData = new FormData();
                                formData.append("file", file);
                                formData.append("language", alphabetLanguage);
                                try {
                                  await api.post(`/alphabet/${item.letter}/upload-audio`, formData);
                                  toast.success(`Klank vir ${item.letter} opgelaai!`);
                                  const res = await api.get(`/alphabet?language=${alphabetLanguage}`);
                                  setAlphabetItems(res.data);
                                } catch (err) {
                                  toast.error(t('toast.couldNotUpload'));
                                }
                              }
                            }}
                          />
                          <span className={`block px-2 py-1 rounded text-xs cursor-pointer ${
                            item.audio_url 
                              ? "bg-green-100 text-green-700 hover:bg-green-200"
                              : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                          }`}>
                            {item.audio_url ? "✓ Klank" : "🎤 Laai Klank Op"}
                          </span>
                        </label>
                        {/* Delete audio button */}
                        {item.audio_url && (
                          <button
                            onClick={async () => {
                              if (window.confirm(`Wil jy die klank vir ${item.letter} uitvee?`)) {
                                try {
                                  await api.delete(`/alphabet/${item.letter}/media?language=${alphabetLanguage}`);
                                  toast.success(`Klank vir ${item.letter} uitgevee`);
                                  const res = await api.get(`/alphabet?language=${alphabetLanguage}`);
                                  setAlphabetItems(res.data);
                                } catch (err) {
                                  toast.error(t('toast.couldNotDelete'));
                                }
                              }
                            }}
                            className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                          >
                            Vee Klank Uit
                          </button>
                        )}
                      </div>
                      
                      {/* Edit word */}
                      <input
                        type="text"
                        className="mt-2 w-full text-xs p-1 border rounded text-center"
                        defaultValue={item.word}
                        placeholder="Woord"
                        onBlur={async (e) => {
                          const newWord = e.target.value.trim();
                          if (newWord && newWord !== item.word) {
                            try {
                              await api.put(`/alphabet/${item.letter}`, {
                                letter: item.letter,
                                word: newWord,
                                image_url: item.image_url,
                                audio_url: item.audio_url,
                                language: alphabetLanguage
                              });
                              toast.success(`Woord vir ${item.letter} opgedateer!`);
                              const res = await api.get(`/alphabet?language=${alphabetLanguage}`);
                              setAlphabetItems(res.data);
                            } catch (err) {
                              toast.error(t('toast.couldNotSave'));
                            }
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {/* Generate Tab */}
          {activeTab === "generate" && (
            <Card testId="generate-form">
              <h3 className="font-heading font-bold mb-4">Generate Text with AI / Genereer Teks met KI</h3>
              <p className="text-text-secondary mb-6">
                Use AI to automatically create reading material for different levels. / Gebruik KI om outomaties leesmateriaal te skep vir verskillende vlakke.
              </p>
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">Grade / Graad</label>
                  <select
                    className="input-field"
                    value={generateForm.grade_level}
                    onChange={(e) => setGenerateForm({...generateForm, grade_level: parseInt(e.target.value)})}
                    data-testid="generate-grade-select"
                  >
                    {[1,2,3,4,5,6,7,8,9].map(g => <option key={g} value={g}>Graad {g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">Type / Tipe</label>
                  <select
                    className="input-field"
                    value={generateForm.text_type}
                    onChange={(e) => setGenerateForm({...generateForm, text_type: e.target.value})}
                    data-testid="generate-type-select"
                  >
                    <option value="comprehension">Comprehension / Begrip</option>
                    <option value="reading">Reading / Lees</option>
                    <option value="spelling">Spelling</option>
                    <option value="listening">Listening / Luister</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">Language / Taal</label>
                  <select
                    className="input-field"
                    value={generateForm.language || "af"}
                    onChange={(e) => setGenerateForm({...generateForm, language: e.target.value})}
                    data-testid="generate-language-select"
                  >
                    <option value="af">🇿🇦 Afrikaans</option>
                    <option value="en">🇬🇧 English</option>
                  </select>
                </div>
                <Input
                  label="Topic / Onderwerp (optional / opsioneel)"
                  value={generateForm.topic}
                  onChange={(e) => setGenerateForm({...generateForm, topic: e.target.value})}
                  placeholder="e.g. Animals, Sports / bv. Diere, Sport"
                  testId="generate-topic-input"
                />
              </div>
              <Button 
                onClick={handleGenerateText} 
                disabled={generating} 
                className="mt-6"
                testId="generate-text-btn"
              >
                {generating ? "Generating... / Genereer..." : "Generate Text / Genereer Teks"}
              </Button>
            </Card>
          )}

          {/* Audio Instructions Tab - Quick access to upload audio for each exercise */}
          {activeTab === "audio" && (
            <div className="space-y-6">
              <Card testId="audio-instructions-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Mic className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">{t('audio.title') || 'Oudio Instruksies per Oefening'}</h3>
                    <p className="text-sm text-text-muted">{t('audio.subtitle') || 'Laai oudio op of neem op vir elke oefening tipe'}</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Hoe dit werk:</strong> Wanneer 'n leerder op 'n oefening klik, sal hierdie oudio outomaties speel om hulle te vertel wat om te doen.
                  </p>
                </div>

                <div className="grid gap-4">
                  {[
                    { key: "comprehension", label: "Begripstoets", icon: BookOpen, color: "bg-orange-500", desc: "Instruksies vir begripstoets oefeninge" },
                    { key: "reading", label: "Hardoplees", icon: Mic, color: "bg-green-500", desc: "Instruksies vir hardoplees oefeninge" },
                    { key: "spelling", label: "Speltoets", icon: Edit3, color: "bg-purple-500", desc: "Instruksies vir speltoets oefeninge" },
                    { key: "listening", label: "Luistertoets", icon: Headphones, color: "bg-yellow-500", desc: "Instruksies vir luistertoets oefeninge" },
                    { key: "woordbou", label: "Woordbou", icon: BookOpen, color: "bg-emerald-500", desc: "Instruksies vir woordbou oefeninge (Gr 1-6)" },
                    { key: "klanktoets", label: "Klanktoets", icon: Volume2, color: "bg-orange-400", desc: "Instruksies vir klanktoets oefeninge (Gr 1-6)" },
                    { key: "alfabet", label: "Alfabet", icon: BookOpen, color: "bg-pink-500", desc: "Instruksies vir alfabet oefeninge (Gr 1-3)" },
                  ].map((ex) => (
                    <div key={ex.key} className="border rounded-lg p-4 bg-slate-50">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 ${ex.color} rounded-xl flex items-center justify-center`}>
                          <ex.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold">{ex.label}</h4>
                          <p className="text-xs text-text-muted">{ex.desc}</p>
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Afrikaans Audio */}
                        <div className="bg-white rounded-lg p-3 border">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">🇿🇦</span>
                            <span className="font-medium text-sm">Afrikaans</span>
                          </div>
                          {exerciseInstructions[`${ex.key}_audio_url`] ? (
                            <div className="space-y-2">
                              <audio 
                                controls 
                                src={`${API}${exerciseInstructions[`${ex.key}_audio_url`]}`} 
                                className="w-full h-8"
                              />
                              <div className="flex gap-2">
                                <input
                                  type="file"
                                  accept="audio/*"
                                  id={`audio-af-replace-${ex.key}`}
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append("file", file);
                                    formData.append("exercise_type", ex.key);
                                    formData.append("language", "af");
                                    try {
                                      const res = await api.post("/admin/upload-exercise-audio", formData);
                                      setExerciseInstructions({...exerciseInstructions, [`${ex.key}_audio_url`]: res.data.audio_url});
                                      toast.success(t('toast.audioUploaded'));
                                      localStorage.setItem('lastUploadTime', new Date().toISOString());
                                    } catch (err) {
                                      toast.error(t('toast.couldNotUpload'));
                                    }
                                    e.target.value = '';
                                  }}
                                />
                                <Button 
                                  size="sm" 
                                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                                  onClick={() => document.getElementById(`audio-af-replace-${ex.key}`)?.click()}
                                >
                                  <Upload className="w-3 h-3 mr-1" /> Vervang
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="bg-red-500 hover:bg-red-600"
                                  onClick={async () => {
                                    if (window.confirm('Verwyder hierdie oudio?')) {
                                      try {
                                        await api.post("/admin/upload-exercise-audio", new FormData(), {
                                          params: { exercise_type: ex.key, language: "af", remove: true }
                                        });
                                        const newInstructions = {...exerciseInstructions};
                                        delete newInstructions[`${ex.key}_audio_url`];
                                        setExerciseInstructions(newInstructions);
                                        toast.success(t('toast.deleted'));
                                      } catch (err) {
                                        toast.error(t('toast.couldNotDelete'));
                                      }
                                    }
                                  }}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <input
                                type="file"
                                accept="audio/*"
                                id={`audio-af-${ex.key}`}
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const formData = new FormData();
                                  formData.append("file", file);
                                  formData.append("exercise_type", ex.key);
                                  formData.append("language", "af");
                                  try {
                                    const res = await api.post("/admin/upload-exercise-audio", formData);
                                    setExerciseInstructions({...exerciseInstructions, [`${ex.key}_audio_url`]: res.data.audio_url});
                                    toast.success(t('toast.audioUploaded'));
                                    localStorage.setItem('lastUploadTime', new Date().toISOString());
                                  } catch (err) {
                                    toast.error(t('toast.couldNotUpload'));
                                  }
                                  e.target.value = '';
                                }}
                              />
                              <Button 
                                className="w-full bg-green-500 hover:bg-green-600"
                                onClick={() => document.getElementById(`audio-af-${ex.key}`)?.click()}
                              >
                                <Upload className="w-4 h-4 mr-2" /> Laai Oudio Op
                              </Button>
                            </>
                          )}
                        </div>

                        {/* English Audio */}
                        <div className="bg-white rounded-lg p-3 border">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">🇬🇧</span>
                            <span className="font-medium text-sm">English</span>
                          </div>
                          {exerciseInstructions[`${ex.key}_audio_url_en`] ? (
                            <div className="space-y-2">
                              <audio 
                                controls 
                                src={`${API}${exerciseInstructions[`${ex.key}_audio_url_en`]}`} 
                                className="w-full h-8"
                              />
                              <div className="flex gap-2">
                                <input
                                  type="file"
                                  accept="audio/*"
                                  id={`audio-en-replace-${ex.key}`}
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append("file", file);
                                    formData.append("exercise_type", ex.key);
                                    formData.append("language", "en");
                                    try {
                                      const res = await api.post("/admin/upload-exercise-audio", formData);
                                      setExerciseInstructions({...exerciseInstructions, [`${ex.key}_audio_url_en`]: res.data.audio_url});
                                      toast.success(t('toast.audioUploaded'));
                                      localStorage.setItem('lastUploadTime', new Date().toISOString());
                                    } catch (err) {
                                      toast.error(t('toast.couldNotUpload'));
                                    }
                                    e.target.value = '';
                                  }}
                                />
                                <Button 
                                  size="sm" 
                                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                                  onClick={() => document.getElementById(`audio-en-replace-${ex.key}`)?.click()}
                                >
                                  <Upload className="w-3 h-3 mr-1" /> Replace
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="bg-red-500 hover:bg-red-600"
                                  onClick={async () => {
                                    if (window.confirm('Remove this audio?')) {
                                      try {
                                        await api.post("/admin/upload-exercise-audio", new FormData(), {
                                          params: { exercise_type: ex.key, language: "en", remove: true }
                                        });
                                        const newInstructions = {...exerciseInstructions};
                                        delete newInstructions[`${ex.key}_audio_url_en`];
                                        setExerciseInstructions(newInstructions);
                                        toast.success(t('toast.deleted'));
                                      } catch (err) {
                                        toast.error(t('toast.couldNotDelete'));
                                      }
                                    }
                                  }}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <input
                                type="file"
                                accept="audio/*"
                                id={`audio-en-${ex.key}`}
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const formData = new FormData();
                                  formData.append("file", file);
                                  formData.append("exercise_type", ex.key);
                                  formData.append("language", "en");
                                  try {
                                    const res = await api.post("/admin/upload-exercise-audio", formData);
                                    setExerciseInstructions({...exerciseInstructions, [`${ex.key}_audio_url_en`]: res.data.audio_url});
                                    toast.success(t('toast.audioUploaded'));
                                    localStorage.setItem('lastUploadTime', new Date().toISOString());
                                  } catch (err) {
                                    toast.error(t('toast.couldNotUpload'));
                                  }
                                  e.target.value = '';
                                }}
                              />
                              <Button 
                                className="w-full bg-green-500 hover:bg-green-600"
                                onClick={() => document.getElementById(`audio-en-${ex.key}`)?.click()}
                              >
                                <Upload className="w-4 h-4 mr-2" /> Upload Audio
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <div className="space-y-6">
              {/* Database Import/Export Card */}
              <Card testId="settings-import-export">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Database className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">{t('backup.title')}</h3>
                    <p className="text-sm text-text-muted">{t('backup.subtitle')}</p>
                  </div>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-green-800">
                    <strong>💡 {i18n.language === 'en' ? 'Tip:' : 'Tip:'}</strong> {t('backup.tip')}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Download className="w-4 h-4" /> {t('backup.createBackup')}
                    </h4>
                    <p className="text-sm text-text-muted mb-3">{t('backup.downloadFullDesc')}</p>
                    <Button
                      onClick={async () => {
                        try {
                          toast.loading(t('backup.creating'), { id: 'backup' });
                          const response = await api.get('/admin/full-backup', { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `lees-is-duidelik-backup-${new Date().toISOString().split('T')[0]}.zip`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(url);
                          toast.success(t('toast.backupCreated'), { id: 'backup' });
                          // Mark that backup was done
                          localStorage.setItem('lastBackupTime', new Date().toISOString());
                        } catch (err) {
                          toast.error(err.response?.data?.detail || t('toast.failed'), { id: 'backup' });
                        }
                      }}
                      className="w-full bg-blue-500 hover:bg-blue-600"
                      testId="full-backup-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {t('backup.downloadFull')}
                    </Button>
                    
                    {/* Download Audio Only Button */}
                    <Button
                      onClick={async () => {
                        try {
                          toast.loading(t('backup.creating'), { id: 'audio-backup' });
                          const response = await api.get('/admin/download-audio-only', { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `lees-audio-${new Date().toISOString().split('T')[0]}.zip`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(url);
                          toast.success(t('toast.backupCreated'), { id: 'audio-backup' });
                          // Mark that backup was done
                          localStorage.setItem('lastBackupTime', new Date().toISOString());
                        } catch (err) {
                          toast.error(err.response?.data?.detail || t('toast.failed'), { id: 'audio-backup' });
                        }
                      }}
                      className="w-full mt-2 bg-purple-500 hover:bg-purple-600"
                      testId="audio-only-backup-btn"
                    >
                      <Volume2 className="w-4 h-4 mr-2" />
                      {t('backup.downloadAudioOnly')}
                    </Button>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Upload className="w-4 h-4" /> {t('backup.restoreBackup')}
                    </h4>
                    <p className="text-sm text-text-muted mb-3">{t('backup.restoreBackupDesc')}</p>
                    <input
                      type="file"
                      accept=".zip"
                      className="hidden"
                      id="restore-backup-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        if (!window.confirm(t('backup.confirmRestore'))) {
                          e.target.value = '';
                          return;
                        }
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          toast.loading(t('backup.restoring'), { id: 'restore' });
                          const res = await api.post('/admin/restore-backup', formData);
                          const r = res.data.results;
                          toast.success(`${t('backup.restoreComplete')} ${t('backup.textsRestored')}: ${r.texts_imported}, ${t('backup.audioRestored')}: ${r.audio_files_restored}`, { id: 'restore', duration: 5000 });
                          window.location.reload();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || t('toast.failed'), { id: 'restore' });
                        }
                        e.target.value = '';
                      }}
                    />
                    <Button
                      onClick={() => document.getElementById('restore-backup-input')?.click()}
                      className="w-full bg-green-500 hover:bg-green-600"
                      testId="restore-backup-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {t('backup.restoreFull')}
                    </Button>
                    
                    {/* Restore Audio Only */}
                    <input
                      type="file"
                      accept=".zip"
                      className="hidden"
                      id="restore-audio-only-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          toast.loading(t('backup.restoringAudio'), { id: 'restore-audio' });
                          const res = await api.post('/admin/restore-audio-only', formData);
                          toast.success(res.data.message, { id: 'restore-audio', duration: 5000 });
                          window.location.reload();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || t('toast.failed'), { id: 'restore-audio' });
                        }
                        e.target.value = '';
                      }}
                    />
                    <Button
                      onClick={() => document.getElementById('restore-audio-only-input')?.click()}
                      className="w-full mt-2 bg-purple-500 hover:bg-purple-600"
                      testId="restore-audio-only-btn"
                    >
                      <Volume2 className="w-4 h-4 mr-2" />
                      {t('backup.restoreAudioOnly')}
                    </Button>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-text-muted">
                    <strong>{i18n.language === 'en' ? 'Note:' : 'Let op:'}</strong> {t('backup.backupNote')}
                  </p>
                </div>
              </Card>

              {/* Database Diagnostic Card */}
              <Card testId="settings-diagnostic">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">Databasis Diagnose</h3>
                    <p className="text-sm text-text-muted">Kyk hoeveel tekste per graad gestoor is</p>
                  </div>
                </div>
                
                <div id="diagnostic-results" className="mb-4 p-3 bg-slate-50 rounded-lg text-sm font-mono hidden">
                </div>
                
                <div className="flex gap-4 flex-wrap">
                  <Button
                    onClick={async () => {
                      const resultsDiv = document.getElementById('diagnostic-results');
                      resultsDiv.classList.remove('hidden');
                      resultsDiv.innerHTML = 'Laai...';
                      
                      try {
                        let html = '<table class="w-full text-left"><thead><tr><th class="pb-2">Graad</th><th class="pb-2">AF Begrip</th><th class="pb-2">EN Begrip</th><th class="pb-2">AF Lees</th><th class="pb-2">EN Lees</th><th class="pb-2 text-red-600">Geen Taal</th></tr></thead><tbody>';
                        
                        for (let grade = 1; grade <= 9; grade++) {
                          const res = await api.get(`/debug/texts/${grade}`);
                          const d = res.data.texts_by_type_and_language;
                          const noLang = (d.comprehension_no_lang || 0) + (d.reading_no_lang || 0);
                          html += `<tr class="border-t"><td class="py-1 font-bold">Gr ${grade}</td><td>${d.comprehension_af || 0}</td><td>${d.comprehension_en || 0}</td><td>${d.reading_af || 0}</td><td>${d.reading_en || 0}</td><td class="${noLang > 0 ? 'text-red-600 font-bold' : ''}">${noLang}</td></tr>`;
                        }
                        
                        html += '</tbody></table>';
                        html += '<p class="mt-2 text-xs text-text-muted">* "Geen Taal" tekste sal nie laai vir leerders nie. Klik "Fix Tekste" om dit reg te maak.</p>';
                        resultsDiv.innerHTML = DOMPurify.sanitize(html);
                      } catch (err) {
                        resultsDiv.innerHTML = DOMPurify.sanitize(`<span class="text-red-600">Fout: ${err.response?.data?.detail || err.message}</span>`);
                      }
                    }}
                    variant="secondary"
                    testId="run-diagnostic-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Begin Diagnose
                  </Button>
                  
                  <Button
                    onClick={async () => {
                      if (!window.confirm('Dit sal alle tekste sonder \'n taal na Afrikaans stel. Gaan voort?')) return;
                      
                      try {
                        toast.loading('Fixing texts...', { id: 'fix-texts' });
                        const res = await api.post('/admin/fix-texts-language');
                        toast.success(`${res.data.texts_updated} tekste is reggestel!`, { id: 'fix-texts' });
                        // Run diagnostic again
                        document.querySelector('[data-testid="run-diagnostic-btn"]')?.click();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Fout', { id: 'fix-texts' });
                      }
                    }}
                    variant="secondary"
                    className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                    testId="fix-texts-btn"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Fix Tekste (Stel Taal)
                  </Button>
                  
                  <Button
                    onClick={async () => {
                      if (!window.confirm('Dit sal alle databasis URLs regmaak (verwyder dubbele /api/ prefix). Gaan voort?')) return;
                      
                      try {
                        toast.loading('Fix URLs...', { id: 'fix-urls' });
                        const res = await api.post('/admin/fix-database-urls');
                        toast.success(res.data.message, { id: 'fix-urls' });
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Fout', { id: 'fix-urls' });
                      }
                    }}
                    variant="secondary"
                    className="bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
                    testId="fix-urls-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Fix Databasis URLs
                  </Button>
                  
                  <Button
                    onClick={async () => {
                      if (!window.confirm('Dit sal alle gebroke /uploads/ URLs verwyder (lêers wat verlore gegaan het na redeployment). U sal hierdie lêers weer moet oplaai. Gaan voort?')) return;
                      
                      try {
                        toast.loading('Verwyder gebroke URLs...', { id: 'clear-urls' });
                        const res = await api.post('/admin/clear-broken-urls');
                        toast.success(res.data.message, { id: 'clear-urls' });
                        console.log('Cleared URLs:', res.data.cleared);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Fout', { id: 'clear-urls' });
                      }
                    }}
                    variant="secondary"
                    className="bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
                    testId="clear-broken-urls-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Verwyder Gebroke URLs
                  </Button>
                </div>
              </Card>

              <Card testId="settings-logo">
                <h3 className="font-heading font-bold mb-4">Logo</h3>
                <p className="text-text-secondary mb-4">
                  Laai jou logo op wat op die tuisblad vertoon sal word.
                </p>
                <div className="flex items-center gap-6">
                  <div className="w-32 h-32 bg-slate-100 rounded-2xl flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300">
                    {siteSettings.logo_url ? (
                      <img 
                        src={`${API}${siteSettings.logo_url}`} 
                        alt="Logo" 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Upload className="w-8 h-8 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      ref={logoInputRef}
                      onChange={handleLogoUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button 
                      onClick={() => logoInputRef.current?.click()}
                      variant="secondary"
                      testId="upload-logo-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {siteSettings.logo_url ? "Vervang Logo" : "Laai Logo Op"}
                    </Button>
                    <p className="text-sm text-text-muted mt-2">PNG, JPG of SVG. Maksimum 2MB.</p>
                  </div>
                </div>
              </Card>

              {/* TTS Demo Card */}
              <Card testId="settings-tts-demo">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-secondary-100 rounded-xl flex items-center justify-center">
                    <Volume2 className="w-5 h-5 text-secondary-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">AI Afrikaans TTS Demo</h3>
                    <p className="text-sm text-text-muted">Luister hoe AI Afrikaans klink</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">Teks om te lees</label>
                    <textarea
                      className="input-field min-h-[100px]"
                      value={ttsDemo.text}
                      onChange={(e) => setTtsDemo({...ttsDemo, text: e.target.value})}
                      placeholder="Tik Afrikaanse teks hier..."
                      data-testid="tts-demo-text"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">Stem</label>
                    <select
                      className="input-field"
                      value={ttsDemo.voice}
                      onChange={(e) => setTtsDemo({...ttsDemo, voice: e.target.value})}
                      data-testid="tts-demo-voice"
                    >
                      {ttsVoices.map(v => (
                        <option key={v.id} value={v.id}>{v.name} - {v.description}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={async () => {
                        if (!ttsDemo.text.trim()) {
                          toast.error("Voer eers teks in");
                          return;
                        }
                        setTtsDemo({...ttsDemo, loading: true, audioUrl: null});
                        try {
                          const res = await api.post("/tts/demo", {
                            text: ttsDemo.text,
                            voice: ttsDemo.voice
                          });
                          setTtsDemo({...ttsDemo, loading: false, audioUrl: res.data.audio_url});
                          toast.success("TTS audio gegenereer!");
                        } catch (err) {
                          setTtsDemo({...ttsDemo, loading: false});
                          toast.error(err.response?.data?.detail || "TTS fout");
                        }
                      }}
                      disabled={ttsDemo.loading || !ttsDemo.text.trim()}
                      testId="tts-demo-generate"
                    >
                      {ttsDemo.loading ? (
                        <><Loader className="w-4 h-4 mr-2 animate-spin" /> Genereer...</>
                      ) : (
                        <><Volume2 className="w-4 h-4 mr-2" /> Genereer Audio</>
                      )}
                    </Button>
                  </div>

                  {ttsDemo.audioUrl && (
                    <div className="bg-secondary-50 rounded-xl p-4">
                      <p className="text-sm font-semibold mb-2">Luister na die resultaat:</p>
                      <audio 
                        controls 
                        src={`${API}${ttsDemo.audioUrl}`} 
                        className="w-full"
                        data-testid="tts-demo-audio"
                      />
                      <p className="text-xs text-text-muted mt-2">
                        Stem: {ttsVoices.find(v => v.id === ttsDemo.voice)?.name || ttsDemo.voice}
                      </p>
                    </div>
                  )}
                </div>
              </Card>

              <Card testId="settings-about">
                <h3 className="font-heading font-bold mb-4">Oor Ons</h3>
                <p className="text-text-secondary mb-4">
                  Vertel besoekers meer oor jouself en jou platform.
                </p>
                <Input
                  label="Titel"
                  value={siteSettings.about_title}
                  onChange={(e) => setSiteSettings({...siteSettings, about_title: e.target.value})}
                  placeholder="bv. Lees is Duidelik"
                  testId="settings-about-title"
                />
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-text-secondary mb-2">Oor Ons Teks</label>
                  <textarea
                    className="input-field min-h-[150px]"
                    value={siteSettings.about_text}
                    onChange={(e) => setSiteSettings({...siteSettings, about_text: e.target.value})}
                    placeholder="Skryf hier oor jouself, jou missie, en wat jou platform bied..."
                    data-testid="settings-about-text"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Kontak E-pos"
                    type="email"
                    value={siteSettings.contact_email}
                    onChange={(e) => setSiteSettings({...siteSettings, contact_email: e.target.value})}
                    placeholder="bv. info@leesisduidelik.co.za"
                    testId="settings-contact-email"
                  />
                  <Input
                    label="Kontak Telefoon"
                    value={siteSettings.contact_phone}
                    onChange={(e) => setSiteSettings({...siteSettings, contact_phone: e.target.value})}
                    placeholder="bv. 012 345 6789"
                    testId="settings-contact-phone"
                  />
                </div>
              </Card>

              <Button 
                onClick={handleSaveSettings} 
                disabled={savingSettings}
                className="w-full md:w-auto"
                testId="save-settings-btn"
              >
                {savingSettings ? t('admin.savingSettings') : t('admin.saveSettings')}
              </Button>

              {/* Exercise Instructions */}
              <Card className="mt-6" testId="exercise-instructions-card">
                <h3 className="font-heading font-bold mb-4">📋 Exercise Instructions / Oefening Instruksies</h3>
                <p className="text-text-secondary text-sm mb-4">
                  Write instructions that learners will see before starting each exercise type. / 
                  Skryf instruksies wat leerders sal sien voordat hulle elke tipe oefening begin.
                </p>
                
                {/* Comprehension */}
                <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-semibold mb-3">📖 Comprehension Test / Begripstoets</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇿🇦 Afrikaans</label>
                      <RichTextEditor
                        value={exerciseInstructions.comprehension || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, comprehension: e.target.value})}
                        placeholder="Lees die teks aandagtig deur en beantwoord dan die vrae."
                        testId="instruction-comprehension-af"
                        minHeight="80px"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇬🇧 English</label>
                      <RichTextEditor
                        value={exerciseInstructions.comprehension_en || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, comprehension_en: e.target.value})}
                        placeholder="Read the text carefully and then answer the questions."
                        testId="instruction-comprehension-en"
                        minHeight="80px"
                      />
                    </div>
                  </div>
                </div>

                {/* Reading Aloud */}
                <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-semibold mb-3">🎤 Read Aloud / Hardoplees</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇿🇦 Afrikaans</label>
                      <RichTextEditor
                        value={exerciseInstructions.reading || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, reading: e.target.value})}
                        placeholder="Lees die teks hardop so duidelik en akkuraat as moontlik."
                        testId="instruction-reading-af"
                        minHeight="80px"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇬🇧 English</label>
                      <RichTextEditor
                        value={exerciseInstructions.reading_en || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, reading_en: e.target.value})}
                        placeholder="Read the text aloud as clearly and accurately as possible."
                        testId="instruction-reading-en"
                        minHeight="80px"
                      />
                    </div>
                  </div>
                </div>

                {/* Reading Level Test */}
                <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-semibold mb-3">📊 Reading Level Test / Leesvlaktoets</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇿🇦 Afrikaans</label>
                      <RichTextEditor
                        value={exerciseInstructions.reading_level_test || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, reading_level_test: e.target.value})}
                        placeholder="Instruksies vir die leesvlaktoets..."
                        testId="instruction-reading-level-af"
                        minHeight="120px"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇬🇧 English</label>
                      <RichTextEditor
                        value={exerciseInstructions.reading_level_test_en || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, reading_level_test_en: e.target.value})}
                        placeholder="Instructions for the reading level test..."
                        testId="instruction-reading-level-en"
                        minHeight="120px"
                      />
                    </div>
                  </div>
                </div>

                {/* Encouragement Message */}
                <div className="mb-6 p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
                  <h4 className="font-semibold mb-1">🎉 Encouragement Message / Bemoedigende Boodskap</h4>
                  <p className="text-xs text-text-muted mb-3">Shown after learner completes reading level test / Wys nadat leerder die toets voltooi</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇿🇦 Afrikaans</label>
                      <RichTextEditor
                        value={exerciseInstructions.reading_level_encouragement || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, reading_level_encouragement: e.target.value})}
                        placeholder="Baie geluk met jou toets! Jy het wonderlik gedoen..."
                        testId="instruction-encouragement-af"
                        minHeight="120px"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇬🇧 English</label>
                      <RichTextEditor
                        value={exerciseInstructions.reading_level_encouragement_en || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, reading_level_encouragement_en: e.target.value})}
                        placeholder="Congratulations on your test! You did wonderfully..."
                        testId="instruction-encouragement-en"
                        minHeight="120px"
                      />
                    </div>
                  </div>
                </div>

                {/* Audio Upload for Reading Test Completion - Separate tabs for each language */}
                <div className="mb-6 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                  <h4 className="font-semibold mb-1">🔊 Voice Message / Stemboodskap</h4>
                  <p className="text-xs text-text-muted mb-3">Upload audio messages for each language / Laai oudioboodskappe vir elke taal op</p>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Afrikaans Audio */}
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <h5 className="font-semibold text-purple-700 mb-3 flex items-center gap-2">
                        🇿🇦 Afrikaans Oudio
                      </h5>
                      {exerciseInstructions.reading_level_audio_url ? (
                        <div>
                          <audio 
                            controls 
                            src={`${API}${exerciseInstructions.reading_level_audio_url}`}
                            className="w-full mb-2"
                            data-testid="reading-level-audio-af"
                          />
                          <button
                            onClick={() => setExerciseInstructions({...exerciseInstructions, reading_level_audio_url: null})}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Verwyder
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-text-muted mb-2">Geen oudio opgelaai</p>
                      )}
                      <label className="cursor-pointer inline-block mt-2">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append("file", file);
                              formData.append("language", "af");
                              try {
                                const res = await api.post("/admin/upload-reading-audio", formData);
                                setExerciseInstructions({...exerciseInstructions, reading_level_audio_url: res.data.audio_url});
                                toast.success("Afrikaans oudio opgelaai!");
                              } catch (err) {
                                toast.error(t('toast.couldNotUpload'));
                              }
                            }
                          }}
                          data-testid="upload-audio-af"
                        />
                        <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 cursor-pointer">
                          {exerciseInstructions.reading_level_audio_url ? "Vervang" : "Laai Op"}
                        </span>
                      </label>
                    </div>
                    
                    {/* English Audio */}
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <h5 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                        🇬🇧 English Audio
                      </h5>
                      {exerciseInstructions.reading_level_audio_url_en ? (
                        <div>
                          <audio 
                            controls 
                            src={`${API}${exerciseInstructions.reading_level_audio_url_en}`}
                            className="w-full mb-2"
                            data-testid="reading-level-audio-en"
                          />
                          <button
                            onClick={() => setExerciseInstructions({...exerciseInstructions, reading_level_audio_url_en: null})}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-text-muted mb-2">No audio uploaded</p>
                      )}
                      <label className="cursor-pointer inline-block mt-2">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append("file", file);
                              formData.append("language", "en");
                              try {
                                const res = await api.post("/admin/upload-reading-audio", formData);
                                setExerciseInstructions({...exerciseInstructions, reading_level_audio_url_en: res.data.audio_url});
                                toast.success("English audio uploaded!");
                              } catch (err) {
                                toast.error(t('toast.couldNotUpload'));
                              }
                            }
                          }}
                          data-testid="upload-audio-en"
                        />
                        <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 cursor-pointer">
                          {exerciseInstructions.reading_level_audio_url_en ? "Replace" : "Upload"}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Oral Instructions Audio for Grade 1-4 */}
                <div className="mb-6 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                  <h4 className="font-semibold mb-1">🎙️ Oral Instructions / Mondelinge Instruksies (Gr 1-4)</h4>
                  <p className="text-xs text-text-muted mb-4">Upload audio instructions that play automatically for Grade 1-4 learners / Laai oudio instruksies op wat outomaties speel vir Graad 1-4 leerders</p>
                  
                  {/* Exercise type selector */}
                  {[
                    { key: "comprehension", icon: "📖", label: "Comprehension / Begrip" },
                    { key: "reading", icon: "🎤", label: "Read Aloud / Hardoplees" },
                    { key: "spelling", icon: "✏️", label: "Spelling" },
                    { key: "listening", icon: "🎧", label: "Listening / Luister" },
                    { key: "woordbou", icon: "🧩", label: "Woordbou" },
                    { key: "klanktoets", icon: "🔊", label: "Klanktoets" },
                    { key: "alfabet", icon: "🔤", label: "Alfabet Helper" }
                  ].map(ex => (
                    <div key={ex.key} className="mb-4 p-3 bg-white rounded-lg border border-emerald-100">
                      <h5 className="font-medium mb-2 flex items-center gap-2">
                        <span>{ex.icon}</span> {ex.label}
                      </h5>
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Afrikaans Audio */}
                        <div className="flex items-center gap-2">
                          <span className="text-sm">🇿🇦</span>
                          {exerciseInstructions[`${ex.key}_audio_url`] ? (
                            <div className="flex-1 flex items-center gap-2">
                              <audio 
                                controls 
                                src={`${API}${exerciseInstructions[`${ex.key}_audio_url`]}`}
                                className="h-8 flex-1"
                              />
                              <button
                                onClick={() => setExerciseInstructions({...exerciseInstructions, [`${ex.key}_audio_url`]: null})}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >✗</button>
                            </div>
                          ) : (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="audio/*"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const formData = new FormData();
                                    formData.append("file", file);
                                    formData.append("exercise_type", ex.key);
                                    formData.append("language", "af");
                                    try {
                                      const res = await api.post("/admin/upload-exercise-audio", formData);
                                      setExerciseInstructions({...exerciseInstructions, [`${ex.key}_audio_url`]: res.data.audio_url});
                                      toast.success(t('toast.audioUploaded'));
                                      // Track upload time for backup reminder
                                      localStorage.setItem('lastUploadTime', new Date().toISOString());
                                    } catch (err) {
                                      toast.error(t('toast.couldNotUpload'));
                                    }
                                  }
                                }}
                              />
                              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium hover:bg-emerald-200">
                                Upload
                              </span>
                            </label>
                          )}
                        </div>
                        
                        {/* English Audio */}
                        <div className="flex items-center gap-2">
                          <span className="text-sm">🇬🇧</span>
                          {exerciseInstructions[`${ex.key}_audio_url_en`] ? (
                            <div className="flex-1 flex items-center gap-2">
                              <audio 
                                controls 
                                src={`${API}${exerciseInstructions[`${ex.key}_audio_url_en`]}`}
                                className="h-8 flex-1"
                              />
                              <button
                                onClick={() => setExerciseInstructions({...exerciseInstructions, [`${ex.key}_audio_url_en`]: null})}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >✗</button>
                            </div>
                          ) : (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="audio/*"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const formData = new FormData();
                                    formData.append("file", file);
                                    formData.append("exercise_type", ex.key);
                                    formData.append("language", "en");
                                    try {
                                      const res = await api.post("/admin/upload-exercise-audio", formData);
                                      setExerciseInstructions({...exerciseInstructions, [`${ex.key}_audio_url_en`]: res.data.audio_url});
                                      toast.success(t('toast.audioUploaded'));
                                      // Track upload time for backup reminder
                                      localStorage.setItem('lastUploadTime', new Date().toISOString());
                                    } catch (err) {
                                      toast.error(t('toast.couldNotUpload'));
                                    }
                                  }
                                }}
                              />
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200">
                                Upload
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Spelling Test */}
                <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-semibold mb-3">✏️ Spelling Test / Speltoets</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇿🇦 Afrikaans</label>
                      <RichTextEditor
                        value={exerciseInstructions.spelling || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, spelling: e.target.value})}
                        placeholder="Luister na elke woord en skryf dit korrek."
                        testId="instruction-spelling-af"
                        minHeight="120px"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇬🇧 English</label>
                      <RichTextEditor
                        value={exerciseInstructions.spelling_en || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, spelling_en: e.target.value})}
                        placeholder="Listen to each word and write it correctly."
                        testId="instruction-spelling-en"
                        minHeight="120px"
                      />
                    </div>
                  </div>
                </div>

                {/* Listening Test */}
                <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-semibold mb-3">🎧 Listening Test / Luistertoets</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇿🇦 Afrikaans</label>
                      <RichTextEditor
                        value={exerciseInstructions.listening || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, listening: e.target.value})}
                        placeholder="Luister aandagtig na die oudio en beantwoord die vrae."
                        testId="instruction-listening-af"
                        minHeight="80px"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">🇬🇧 English</label>
                      <RichTextEditor
                        value={exerciseInstructions.listening_en || ""}
                        onChange={(e) => setExerciseInstructions({...exerciseInstructions, listening_en: e.target.value})}
                        placeholder="Listen carefully to the audio and answer the questions."
                        testId="instruction-listening-en"
                        minHeight="80px"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={async () => {
                    try {
                      await api.put("/admin/exercise-instructions", exerciseInstructions);
                      toast.success("Instructions saved! / Instruksies gestoor!");
                    } catch (err) {
                      toast.error("Could not save / Kon nie stoor nie");
                    }
                  }}
                  variant="secondary"
                  testId="save-instructions-btn"
                >
                  Save Instructions / Stoor Instruksies
                </Button>
              </Card>

              {/* Reading Level Test Texts */}
              <Card className="mt-6" testId="reading-test-texts-card">
                <h3 className="font-heading font-bold mb-4">📚 Leesvlaktoets Tekste / Reading Level Test Texts</h3>
                <p className="text-text-secondary text-sm mb-4">
                  Wysig die tekste wat leerders moet lees tydens die leesvlaktoets. Elke graad het sy eie teks.
                  <br />
                  <span className="text-primary-600 font-medium">Die tekste word gebruik om leesspoed (woorde per minuut) te meet.</span>
                </p>
                
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(grade => (
                    <details key={grade} className="bg-slate-50 rounded-xl overflow-hidden">
                      <summary className="p-4 cursor-pointer hover:bg-slate-100 font-semibold flex items-center justify-between">
                        <span>Graad {grade} / Grade {grade}</span>
                        <span className="text-xs text-text-muted">
                          {exerciseInstructions.reading_test_texts_af?.[String(grade)]?.split(/\s+/).length || 0} woorde (AF) | {exerciseInstructions.reading_test_texts_en?.[String(grade)]?.split(/\s+/).length || 0} words (EN)
                        </span>
                      </summary>
                      <div className="p-4 border-t bg-white">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-text-secondary mb-2">🇿🇦 Afrikaans</label>
                            <textarea
                              className="input-field min-h-[120px] text-sm"
                              value={exerciseInstructions.reading_test_texts_af?.[String(grade)] || ""}
                              onChange={(e) => {
                                const newTexts = {...(exerciseInstructions.reading_test_texts_af || {})};
                                newTexts[String(grade)] = e.target.value;
                                setExerciseInstructions({...exerciseInstructions, reading_test_texts_af: newTexts});
                              }}
                              placeholder={`Tik Graad ${grade} leesvlaktoets teks hier...`}
                              data-testid={`reading-text-af-grade-${grade}`}
                            />
                            <p className="text-xs text-text-muted mt-1">
                              {exerciseInstructions.reading_test_texts_af?.[String(grade)]?.split(/\s+/).filter(Boolean).length || 0} woorde
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-text-secondary mb-2">🇬🇧 English</label>
                            <textarea
                              className="input-field min-h-[120px] text-sm"
                              value={exerciseInstructions.reading_test_texts_en?.[String(grade)] || ""}
                              onChange={(e) => {
                                const newTexts = {...(exerciseInstructions.reading_test_texts_en || {})};
                                newTexts[String(grade)] = e.target.value;
                                setExerciseInstructions({...exerciseInstructions, reading_test_texts_en: newTexts});
                              }}
                              placeholder={`Type Grade ${grade} reading level test text here...`}
                              data-testid={`reading-text-en-grade-${grade}`}
                            />
                            <p className="text-xs text-text-muted mt-1">
                              {exerciseInstructions.reading_test_texts_en?.[String(grade)]?.split(/\s+/).filter(Boolean).length || 0} words
                            </p>
                          </div>
                        </div>
                      </div>
                    </details>
                  ))}
                </div>

                <Button
                  className="mt-4"
                  onClick={async () => {
                    try {
                      await api.put("/admin/exercise-instructions", exerciseInstructions);
                      toast.success("Leesvlaktoets tekste gestoor!");
                    } catch (err) {
                      toast.error("Kon nie stoor nie");
                    }
                  }}
                  testId="save-reading-texts-btn"
                >
                  Stoor Leesvlaktoets Tekste
                </Button>
              </Card>

              {/* Bulk Import Section */}
              <Card className="mt-6" testId="bulk-import-card">
                <h3 className="font-heading font-bold mb-4">📥 Bulk Invoer (Excel)</h3>
                <p className="text-sm text-text-secondary mb-4">
                  Laai Excel lêers op om verskeie oefeninge tegelyk in te voer. Laai eers die templates af om die korrekte formaat te sien.
                  <br />
                  <span className="text-primary-600 font-medium">Let op: Elke template bevat 'n "language" kolom. Gebruik "af" vir Afrikaans en "en" vir Engels.</span>
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {[
                    { type: "spelling", label: "Speltoetse", color: "purple" },
                    { type: "woordbou", label: "Woordbou", color: "emerald" },
                    { type: "klanktoets", label: "Klanktoets", color: "orange" },
                    { type: "alphabet", label: "Alfabet", color: "pink" }
                  ].map(template => (
                    <button
                      key={template.type}
                      onClick={async () => {
                        try {
                          const res = await api.get(`/bulk-import/templates/${template.type}`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `${template.type}_template.xlsx`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.success(`${template.label} template afgelaai!`);
                        } catch (err) {
                          toast.error("Kon nie template aflaai nie");
                        }
                      }}
                      className={`p-3 border-2 border-dashed border-${template.color}-300 rounded-xl text-center hover:bg-${template.color}-50 transition-colors`}
                      data-testid={`download-template-${template.type}`}
                    >
                      <Download className={`w-6 h-6 mx-auto mb-1 text-${template.color}-500`} />
                      <span className="text-sm font-medium">{template.label}</span>
                      <span className="block text-xs text-text-muted">Template</span>
                    </button>
                  ))}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { type: "spelling", label: "Speltoetse", endpoint: "/bulk-import/spelling" },
                    { type: "woordbou", label: "Woordbou", endpoint: "/bulk-import/woordbou" },
                    { type: "klanktoets", label: "Klanktoets", endpoint: "/bulk-import/klanktoets" },
                    { type: "alphabet", label: "Alfabet", endpoint: "/bulk-import/alphabet" }
                  ].map(importType => (
                    <label key={importType.type} className="cursor-pointer">
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          const formData = new FormData();
                          formData.append("file", file);
                          
                          try {
                            toast.info(`Laai ${importType.label} in...`);
                            const res = await api.post(importType.endpoint, formData);
                            toast.success(res.data.message);
                            if (res.data.errors?.length > 0) {
                              toast.warning(`${res.data.errors.length} foute: ${res.data.errors[0]}`);
                            }
                            // Refresh data
                            if (importType.type === "alphabet") {
                              const alphaRes = await api.get("/alphabet");
                              setAlphabetItems(alphaRes.data || []);
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Kon nie invoer nie");
                          }
                          e.target.value = "";
                        }}
                        data-testid={`import-${importType.type}`}
                      />
                      <div className="p-3 bg-primary-50 border border-primary-200 rounded-xl text-center hover:bg-primary-100 transition-colors">
                        <Upload className="w-6 h-6 mx-auto mb-1 text-primary-500" />
                        <span className="text-sm font-medium text-primary-700">{importType.label}</span>
                        <span className="block text-xs text-primary-500">Invoer</span>
                      </div>
                    </label>
                  ))}
                </div>
              </Card>

              {/* Admin Password Change */}
              <Card className="mt-6" testId="password-change-card">
                <h3 className="font-heading font-bold mb-4">🔐 Verander Admin Wagwoord</h3>
                <div className="space-y-4">
                  <Input
                    label="Huidige Wagwoord"
                    type="password"
                    value={passwordForm?.current || ""}
                    onChange={(e) => setPasswordForm({...passwordForm, current: e.target.value})}
                    testId="current-password-input"
                  />
                  <Input
                    label="Nuwe Wagwoord"
                    type="password"
                    value={passwordForm?.new || ""}
                    onChange={(e) => setPasswordForm({...passwordForm, new: e.target.value})}
                    testId="new-password-input"
                  />
                  <Input
                    label="Bevestig Nuwe Wagwoord"
                    type="password"
                    value={passwordForm?.confirm || ""}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})}
                    testId="confirm-password-input"
                  />
                  <Button 
                    onClick={async () => {
                      if (!passwordForm?.current || !passwordForm?.new || !passwordForm?.confirm) {
                        toast.error("Vul al die velde in");
                        return;
                      }
                      if (passwordForm.new !== passwordForm.confirm) {
                        toast.error("Nuwe wagwoorde stem nie ooreen nie");
                        return;
                      }
                      if (passwordForm.new.length < 6) {
                        toast.error("Wagwoord moet ten minste 6 karakters wees");
                        return;
                      }
                      try {
                        const res = await api.post("/auth/admin/change-password", {
                          current_password: passwordForm.current,
                          new_password: passwordForm.new
                        });
                        toast.success(res.data.message);
                        setPasswordForm({ current: "", new: "", confirm: "" });
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Kon nie wagwoord verander nie");
                      }
                    }}
                    variant="secondary"
                    testId="change-password-btn"
                  >
                    Verander Wagwoord
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </PageTransition>
  );
};

// Payment Success/Cancel Pages
const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
  }, []);

  return (
    <PageTransition>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center max-w-md">
          <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-4">Betaling Suksesvol!</h1>
          <p className="text-text-secondary mb-6">Dankie vir jou ondersteuning. Jy kan nou al die oefeninge geniet!</p>
          <Button onClick={() => navigate("/dashboard")} className="w-full" testId="go-to-dashboard-success">
            Gaan na Dashboard
          </Button>
        </Card>
      </div>
    </PageTransition>
  );
};

const PaymentCancelPage = () => {
  const navigate = useNavigate();

  return (
    <PageTransition>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center max-w-md">
          <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-text-muted" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-4">Betaling Gekanselleer</h1>
          <p className="text-text-secondary mb-6">Geen probleem! Jy kan enige tyd weer probeer.</p>
          <div className="flex gap-4">
            <Button variant="secondary" onClick={() => navigate("/dashboard")} className="flex-1" testId="back-to-dashboard">
              Terug
            </Button>
            <Button onClick={() => navigate("/subscription")} className="flex-1" testId="try-again">
              Probeer Weer
            </Button>
          </div>
        </Card>
      </div>
    </PageTransition>
  );
};

// Parent Portal
const ParentPortal = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [childData, setChildData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otpStep, setOtpStep] = useState("phone"); // phone, otp, verified
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpMessage, setOtpMessage] = useState(null);
  const [error, setError] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Consent form state
  const [consentStatus, setConsentStatus] = useState(null);
  const [showConsentForm, setShowConsentForm] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [consentForm, setConsentForm] = useState({
    parent_name: "",
    parent_relationship: "Ouer",
    parent_phone: "",
    parent_email: "",
    consent_data_storage: true,
    consent_progress_sharing: true,
    consent_audio_recordings: true,
    consent_program_participation: true,
    signature: ""
  });

  useEffect(() => {
    loadChildData();
    loadConsentStatus();
    // Check for payment status
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      toast.success("Betaling suksesvol! Dankie!");
    }
  }, [token]);

  const loadChildData = async () => {
    try {
      const res = await axios.get(`${API}/parent/child-progress/${token}`);
      setChildData(res.data);
      if (res.data.link_verified) {
        setOtpStep("verified");
      }
    } catch (err) {
      setError("Ongeldige skakel. Kontak asseblief die admin.");
    }
    setLoading(false);
  };
  
  const loadConsentStatus = async () => {
    try {
      const res = await axios.get(`${API}/parent/consent-status/${token}`);
      setConsentStatus(res.data);
    } catch (err) {
      console.log("Could not load consent status");
    }
  };
  
  const submitConsent = async () => {
    if (!consentForm.parent_name || !consentForm.parent_phone || !consentForm.signature) {
      toast.error("Vul asseblief alle verpligte velde in");
      return;
    }
    if (!consentForm.consent_program_participation) {
      toast.error("Toestemming vir programdeelname is verpligtend");
      return;
    }
    setConsentSubmitting(true);
    try {
      await axios.post(`${API}/parent/consent/${token}`, consentForm);
      toast.success("Toestemming suksesvol ingedien!");
      setShowConsentForm(false);
      loadConsentStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon nie toestemming indien nie");
    }
    setConsentSubmitting(false);
  };

  const requestOTP = async () => {
    if (!whatsappNumber || whatsappNumber.length < 10) {
      setError("Voer asseblief 'n geldige WhatsApp nommer in");
      return;
    }
    setError("");
    try {
      const res = await axios.post(`${API}/parent/request-otp`, {
        whatsapp_number: whatsappNumber,
        parent_token: token
      });
      setOtpMessage(res.data);
      setOtpStep("otp");
      toast.success("OTP gegenereer! Admin sal dit aan jou stuur.");
    } catch (err) {
      setError(err.response?.data?.detail || "Kon nie OTP genereer nie");
    }
  };

  const verifyOTP = async () => {
    setError("");
    try {
      const res = await axios.post(`${API}/parent/verify-otp`, {
        whatsapp_number: whatsappNumber,
        otp: otp,
        parent_token: token
      });
      toast.success("Welkom! Jy het nou toegang.");
      setOtpStep("verified");
      loadChildData();
    } catch (err) {
      setError(err.response?.data?.detail || "Ongeldige OTP");
    }
  };

  const handleCheckout = async (packageId) => {
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/parent/checkout/${token}`, {
        package_id: packageId,
        origin_url: window.location.origin
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Kon nie betaling begin nie");
    }
    setCheckoutLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-muted">Laai...</p>
        </div>
      </div>
    );
  }

  if (error && !childData) {
    return (
      <PageTransition>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="text-center max-w-md">
            <div className="w-20 h-20 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-accent-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-4">Oeps!</h1>
            <p className="text-text-secondary">{error}</p>
          </Card>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-white shadow-sm p-4">
          <div className="max-w-lg mx-auto">
            <h1 className="font-heading text-xl font-bold text-center text-primary-500">
              Lees is Duidelik
            </h1>
            <p className="text-center text-sm text-text-muted">Ouer Portaal</p>
          </div>
        </div>

        <div className="max-w-lg mx-auto p-4 space-y-6">
          {/* OTP Verification (if not verified) */}
          {otpStep !== "verified" && (
            <Card testId="otp-verification">
              <h2 className="font-heading text-lg font-bold mb-4">Verifieer Jou Toegang</h2>
              
              {otpStep === "phone" && (
                <>
                  <p className="text-text-secondary text-sm mb-4">
                    Voer jou WhatsApp nommer in om 'n eenmalige kode te ontvang.
                  </p>
                  <Input
                    label="WhatsApp Nommer"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    placeholder="bv. 0821234567"
                    testId="parent-whatsapp-input"
                  />
                  {error && <p className="text-accent-500 text-sm mb-4">{error}</p>}
                  <Button onClick={requestOTP} className="w-full" testId="request-otp-btn">
                    Stuur OTP
                  </Button>
                </>
              )}

              {otpStep === "otp" && (
                <>
                  <div className="bg-primary-50 rounded-xl p-4 mb-4">
                    <p className="text-sm text-primary-700">
                      <strong>OTP Kode:</strong> {otpMessage?.otp}
                    </p>
                    <p className="text-xs text-primary-600 mt-1">
                      Die admin sal hierdie kode aan jou WhatsApp stuur.
                    </p>
                  </div>
                  <Input
                    label="Voer OTP Kode In"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="6-syfer kode"
                    testId="otp-input"
                  />
                  {error && <p className="text-accent-500 text-sm mb-4">{error}</p>}
                  <Button onClick={verifyOTP} className="w-full" testId="verify-otp-btn">
                    Verifieer
                  </Button>
                  <button 
                    onClick={() => setOtpStep("phone")} 
                    className="w-full text-center text-sm text-text-muted mt-3"
                  >
                    Terug
                  </button>
                </>
              )}
            </Card>
          )}

          {/* Child Info Card */}
          {childData && (
            <>
              <Card testId="child-info-card">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-primary-500" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold">
                      {childData.learner.name} {childData.learner.surname}
                    </h2>
                    <p className="text-text-secondary">
                      Graad {childData.learner.grade} | 
                      Leesvlak: Graad {childData.learner.current_reading_level}
                    </p>
                    {childData.learner.school_name && (
                      <p className="text-sm text-text-muted">{childData.learner.school_name}</p>
                    )}
                  </div>
                </div>
              </Card>

              {/* Parental Consent Status */}
              <Card testId="consent-status-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${consentStatus?.consent_submitted ? 'bg-primary-100' : 'bg-amber-100'}`}>
                      {consentStatus?.consent_submitted ? (
                        <Check className="w-5 h-5 text-primary-600" />
                      ) : (
                        <FileText className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-heading font-bold">Ouerlike Toestemming</h3>
                      <p className="text-sm text-text-muted">
                        {consentStatus?.consent_submitted 
                          ? `Ingedien op ${new Date(consentStatus.consent_date).toLocaleDateString('af-ZA')}` 
                          : 'Nog nie ingedien nie'}
                      </p>
                    </div>
                  </div>
                  {consentStatus?.consent_submitted ? (
                    <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                      Voltooi ✓
                    </span>
                  ) : (
                    <Button size="sm" onClick={() => setShowConsentForm(true)} testId="open-consent-form-btn">
                      Voltooi Nou
                    </Button>
                  )}
                </div>
                
                {consentStatus?.consent_submitted && (
                  <div className="bg-primary-50 rounded-lg p-3 text-sm">
                    <p><strong>Ouer/Voog:</strong> {consentStatus.parent_name}</p>
                  </div>
                )}
              </Card>

              {/* Digital Consent Form Modal */}
              {showConsentForm && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="font-heading text-xl font-bold">Ouerlike Toestemmingsvorm</h2>
                        <button 
                          onClick={() => setShowConsentForm(false)}
                          className="p-2 hover:bg-slate-100 rounded-full"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <p className="text-sm text-blue-800">
                          <strong>Leerder:</strong> {childData?.learner?.name} {childData?.learner?.surname}<br/>
                          <strong>Skool:</strong> {childData?.learner?.school_name || 'Nie gespesifiseer nie'}
                        </p>
                      </div>
                      
                      {/* Consent Items */}
                      <div className="space-y-3 mb-6">
                        <p className="font-semibold text-sm">Ek gee toestemming vir:</p>
                        
                        <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={consentForm.consent_program_participation}
                            onChange={(e) => setConsentForm({...consentForm, consent_program_participation: e.target.checked})}
                            className="mt-1"
                          />
                          <span className="text-sm">
                            <strong>Programdeelname</strong> - My kind mag deelneem aan die Lees is Duidelik program
                            <span className="text-accent-500 ml-1">*</span>
                          </span>
                        </label>
                        
                        <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={consentForm.consent_data_storage}
                            onChange={(e) => setConsentForm({...consentForm, consent_data_storage: e.target.checked})}
                            className="mt-1"
                          />
                          <span className="text-sm">
                            <strong>Data Berging</strong> - My kind se leesvorderingsdata mag digitaal gestoor word
                          </span>
                        </label>
                        
                        <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={consentForm.consent_progress_sharing}
                            onChange={(e) => setConsentForm({...consentForm, consent_progress_sharing: e.target.checked})}
                            className="mt-1"
                          />
                          <span className="text-sm">
                            <strong>Vordering Deel</strong> - My kind se vorderingsverslae mag met die skool gedeel word
                          </span>
                        </label>
                        
                        <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={consentForm.consent_audio_recordings}
                            onChange={(e) => setConsentForm({...consentForm, consent_audio_recordings: e.target.checked})}
                            className="mt-1"
                          />
                          <span className="text-sm">
                            <strong>Oudio Opnames</strong> - My kind se lees-hardop opnames mag vir assessering gebruik word
                          </span>
                        </label>
                      </div>
                      
                      {/* Parent Details */}
                      <div className="space-y-4 mb-6">
                        <p className="font-semibold text-sm">Ouer/Voog Besonderhede:</p>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1">Volle Naam <span className="text-accent-500">*</span></label>
                          <input
                            type="text"
                            value={consentForm.parent_name}
                            onChange={(e) => setConsentForm({...consentForm, parent_name: e.target.value})}
                            className="input-field"
                            placeholder="bv. Johan van der Berg"
                            data-testid="consent-parent-name"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1">Verhouding tot Kind <span className="text-accent-500">*</span></label>
                          <select
                            value={consentForm.parent_relationship}
                            onChange={(e) => setConsentForm({...consentForm, parent_relationship: e.target.value})}
                            className="input-field"
                            data-testid="consent-relationship"
                          >
                            <option value="Ouer">Ouer</option>
                            <option value="Voog">Voog</option>
                            <option value="Ouma/Oupa">Ouma/Oupa</option>
                            <option value="Ander familielid">Ander familielid</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1">Selfoonnommer <span className="text-accent-500">*</span></label>
                          <input
                            type="tel"
                            value={consentForm.parent_phone}
                            onChange={(e) => setConsentForm({...consentForm, parent_phone: e.target.value})}
                            className="input-field"
                            placeholder="bv. 0821234567"
                            data-testid="consent-phone"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1">E-posadres (opsioneel)</label>
                          <input
                            type="email"
                            value={consentForm.parent_email}
                            onChange={(e) => setConsentForm({...consentForm, parent_email: e.target.value})}
                            className="input-field"
                            placeholder="bv. ouer@epos.com"
                            data-testid="consent-email"
                          />
                        </div>
                      </div>
                      
                      {/* Digital Signature */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium mb-1">Digitale Handtekening <span className="text-accent-500">*</span></label>
                        <p className="text-xs text-text-muted mb-2">Tik jou volle naam hier as bevestiging</p>
                        <input
                          type="text"
                          value={consentForm.signature}
                          onChange={(e) => setConsentForm({...consentForm, signature: e.target.value})}
                          className="input-field font-signature text-lg"
                          placeholder="Tik jou volle naam"
                          data-testid="consent-signature"
                        />
                      </div>
                      
                      {/* Legal Notice */}
                      <div className="bg-slate-100 rounded-lg p-3 mb-6">
                        <p className="text-xs text-text-muted">
                          Deur hierdie vorm te voltooi, bevestig ek dat ek die wettige ouer/voog van bogenoemde kind is 
                          en dat ek toestemming gee vir deelname aan die Lees is Duidelik program. 
                          Alle persoonlike inligting word vertroulik hanteer volgens die POPIA-wetgewing.
                        </p>
                      </div>
                      
                      {/* Submit Button */}
                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          onClick={() => setShowConsentForm(false)}
                          className="flex-1"
                        >
                          Kanselleer
                        </Button>
                        <Button 
                          onClick={submitConsent}
                          disabled={consentSubmitting}
                          className="flex-1"
                          testId="submit-consent-btn"
                        >
                          {consentSubmitting ? "Besig..." : "Bevestig & Indien"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="text-center">
                  <div className="text-3xl font-bold text-primary-500">{childData.stats.exercises_completed}</div>
                  <div className="text-xs text-text-muted">Oefeninge</div>
                </Card>
                <Card className="text-center">
                  <div className="text-3xl font-bold text-primary-500">{childData.stats.average_score}%</div>
                  <div className="text-xs text-text-muted">Gemiddeld</div>
                </Card>
                <Card className="text-center">
                  <div className="text-3xl font-bold text-primary-500">{childData.stats.reading_wpm}</div>
                  <div className="text-xs text-text-muted">WPM</div>
                </Card>
              </div>

              {/* Subscription Status */}
              <Card testId="subscription-card">
                <h3 className="font-heading font-bold mb-3">Subskripsie Status</h3>
                <div className={`p-4 rounded-xl ${childData.subscription?.active ? "bg-primary-50" : "bg-accent-50"}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`font-semibold ${childData.subscription?.active ? "text-primary-700" : "text-accent-700"}`}>
                        {childData.subscription?.active ? "Aktief" : "Onaktief"}
                      </p>
                      <p className="text-sm text-text-muted">
                        {childData.subscription?.type === "trial" && `Proeftydperk - ${childData.subscription?.days_left} dae oor`}
                        {childData.subscription?.type === "monthly" && "Maandelikse subskripsie"}
                        {childData.subscription?.type === "lifetime" && "Lewenslange toegang"}
                        {!childData.subscription?.active && "Subskripsie benodig"}
                      </p>
                    </div>
                    {childData.subscription?.active ? (
                      <Check className="w-8 h-8 text-primary-500" />
                    ) : (
                      <X className="w-8 h-8 text-accent-500" />
                    )}
                  </div>
                </div>

                {/* Payment Options */}
                {(!childData.subscription?.active || childData.subscription?.type === "trial") && (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm text-text-secondary">Kies 'n plan om voort te gaan:</p>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handleCheckout("monthly")}
                        disabled={checkoutLoading}
                        className="p-4 border-2 border-slate-200 rounded-xl hover:border-primary-500 transition-colors"
                        data-testid="parent-checkout-monthly"
                      >
                        <div className="font-bold">R100/maand</div>
                        <div className="text-xs text-text-muted">Maandeliks</div>
                      </button>
                      <button
                        onClick={() => handleCheckout("lifetime")}
                        disabled={checkoutLoading}
                        className="p-4 border-2 border-primary-500 rounded-xl bg-primary-50"
                        data-testid="parent-checkout-lifetime"
                      >
                        <div className="font-bold text-primary-700">R399 eenmalig</div>
                        <div className="text-xs text-primary-600">Lewenslank</div>
                      </button>
                    </div>
                  </div>
                )}
              </Card>

              {/* Recent Results */}
              <Card testId="recent-results">
                <h3 className="font-heading font-bold mb-3">Onlangse Resultate</h3>
                {childData.recent_results?.length === 0 ? (
                  <p className="text-text-muted text-sm">Geen resultate nog nie</p>
                ) : (
                  <div className="space-y-2">
                    {childData.recent_results?.map((result, idx) => (
                      <div key={result.id || idx} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div>
                          <span className="font-medium text-sm capitalize">{result.exercise_type?.replace("_", " ")}</span>
                          <span className="text-xs text-text-muted ml-2">
                            {new Date(result.created_at).toLocaleDateString("af-ZA")}
                          </span>
                        </div>
                        <div className={`px-2 py-1 rounded text-sm font-medium ${
                          result.score >= 70 ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"
                        }`}>
                          {result.score}%
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      </div>
    </PageTransition>
  );
};

// Protected Route
const ProtectedRoute = ({ children, user, loading, requiredType }) => {
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Laai...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredType && user.user_type !== requiredType) {
    return <Navigate to={user.user_type === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  
  return children;
};

// Main App
function App() {
  const { user, loading, login, register, logout } = useAuth();

  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors />
      <AnimatePresence mode="wait">
        <Routes>
          <Route path="/" element={user ? <Navigate to={user.user_type === "admin" ? "/admin" : "/dashboard"} /> : <LandingPage />} />
          <Route path="/login" element={user ? <Navigate to={user.user_type === "admin" ? "/admin" : "/dashboard"} /> : <LoginPage onLogin={login} />} />
          <Route path="/register" element={user ? <Navigate to="/reading-test" /> : <RegisterPage onRegister={register} />} />
          
          <Route path="/reading-test" element={
            <ProtectedRoute user={user} loading={loading} requiredType="learner">
              <ReadingTestPage user={user} />
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard" element={
            <ProtectedRoute user={user} loading={loading} requiredType="learner">
              <LearnerDashboard user={user} onLogout={logout} />
            </ProtectedRoute>
          } />
          
          <Route path="/parent-dashboard" element={<ParentDashboard />} />
          
          <Route path="/exercise/:type" element={
            <ProtectedRoute user={user} loading={loading} requiredType="learner">
              <ExercisePage user={user} />
            </ProtectedRoute>
          } />
          
          <Route path="/woordbou" element={
            <ProtectedRoute user={user} loading={loading} requiredType="learner">
              <WoordbouExercisePage user={user} />
            </ProtectedRoute>
          } />
          
          <Route path="/klanktoets" element={
            <ProtectedRoute user={user} loading={loading} requiredType="learner">
              <KlanktoetsExercisePage user={user} />
            </ProtectedRoute>
          } />
          
          <Route path="/alfabet" element={
            <ProtectedRoute user={user} loading={loading} requiredType="learner">
              <AlphabetPage user={user} />
            </ProtectedRoute>
          } />
          
          <Route path="/subscription" element={
            <ProtectedRoute user={user} loading={loading} requiredType="learner">
              <SubscriptionPage user={user} />
            </ProtectedRoute>
          } />
          
          <Route path="/admin" element={
            <ProtectedRoute user={user} loading={loading} requiredType="admin">
              <AdminDashboard onLogout={logout} />
            </ProtectedRoute>
          } />
          
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          
          <Route path="/parent/:token" element={<ParentPortal />} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </BrowserRouter>
  );
}

export default App;
